//#region src/client.ts
function extractErrorMessage(body, fallback) {
	if (body != null && typeof body === "object") {
		const b = body;
		if (typeof b.message === "string") return b.message;
		if (Array.isArray(b.message) && b.message.length > 0 && typeof b.message[0] === "string") return b.message[0];
		if (b.error != null && typeof b.error === "object") {
			const nested = b.error;
			if (typeof nested.message === "string") return nested.message;
		}
		if (typeof b.error === "string") return b.error;
	}
	return fallback;
}
function createRequestFn(config) {
	const fetcher = config.fetcher ?? fetch;
	return async (method, path, options) => {
		let url = path;
		if (options?.params) for (const [key, value] of Object.entries(options.params)) url = url.replace(`{${key}}`, encodeURIComponent(value));
		if (options?.query) {
			const searchParams = new URLSearchParams();
			for (const [key, value] of Object.entries(options.query)) {
				if (value === void 0 || value === null) continue;
				if (Array.isArray(value)) for (const item of value) searchParams.append(key, String(item));
				else searchParams.set(key, String(value));
			}
			const qs = searchParams.toString();
			if (qs) url += `?${qs}`;
		}
		const fullUrl = config.baseUrl.replace(/\/$/, "") + url;
		const context = {
			method,
			path,
			url: fullUrl
		};
		try {
			let baseHeaders = {};
			if (config.headers) if (typeof config.headers === "function") baseHeaders = await config.headers();
			else baseHeaders = config.headers;
			const allHeaders = {
				...baseHeaders,
				...options?.headers
			};
			let init = {
				method,
				headers: allHeaders,
				signal: options?.signal
			};
			if (options?.body !== void 0) {
				const contentType = options?.contentType ?? "application/json";
				if (options.body instanceof FormData) init.body = options.body;
				else if (/^application\/json\s*(;|$)/i.test(contentType)) {
					init.body = JSON.stringify(options.body);
					allHeaders["Content-Type"] = contentType;
					init.headers = allHeaders;
				} else {
					init.body = typeof options.body === "string" ? options.body : JSON.stringify(options.body);
					allHeaders["Content-Type"] = contentType;
					init.headers = allHeaders;
				}
			}
			if (config.onRequest) init = await config.onRequest(fullUrl, init);
			if (config.timeout != null) {
				const timeoutSignal = AbortSignal.timeout(config.timeout);
				init.signal = init.signal ? AbortSignal.any([init.signal, timeoutSignal]) : timeoutSignal;
			}
			let response = await fetcher(fullUrl, init);
			if (config.onResponse) {
				const replaced = await config.onResponse(response, context);
				if (replaced) response = replaced;
			}
			if (!response.ok) {
				let errorBody;
				try {
					errorBody = await response.json();
				} catch {
					errorBody = await response.text().catch(() => void 0);
				}
				let error = {
					status: response.status,
					message: extractErrorMessage(errorBody, response.statusText),
					body: errorBody
				};
				if (config.onError) {
					const replaced = await config.onError(error, context);
					if (replaced) error = replaced;
				}
				if (config.throwOnError) throw error;
				return {
					data: null,
					error,
					response
				};
			}
			if (response.status === 204) return {
				data: void 0,
				error: null,
				response
			};
			const respType = options?.responseType ?? "json";
			if (respType === "blob") return {
				data: await response.blob(),
				error: null,
				response
			};
			if (respType === "stream") return {
				data: response.body,
				error: null,
				response
			};
			if (respType === "sse" || respType === "sse-raw") return {
				data: response,
				error: null,
				response
			};
			if (respType === "text") return {
				data: await response.text(),
				error: null,
				response
			};
			if ((response.headers.get("content-type") ?? "").includes("application/json")) {
				const text = await response.text();
				if (!text) return {
					data: null,
					error: null,
					response
				};
				try {
					return {
						data: JSON.parse(text),
						error: null,
						response
					};
				} catch {
					return {
						data: null,
						error: {
							status: response.status,
							message: "Invalid JSON response",
							body: text
						},
						response
					};
				}
			}
			const text = await response.text();
			if (!text) return {
				data: null,
				error: null,
				response
			};
			return {
				data: text,
				error: null,
				response
			};
		} catch (err) {
			return {
				data: null,
				error: {
					status: 0,
					message: err instanceof Error ? err.message : String(err),
					body: err
				},
				response: null
			};
		}
	};
}
//#endregion
//#region src/sse.ts
var SSEConnection = class {
	constructor(response, parse) {
		this.response = response;
		this.parse = parse;
		this.reader = null;
		this.buffer = "";
		this.closed = false;
		this.lastEventId = "";
		this.listeners = /* @__PURE__ */ new Map();
	}
	on(event, handler) {
		const list = this.listeners.get(event) ?? [];
		list.push(handler);
		this.listeners.set(event, list);
		return this;
	}
	close() {
		this.closed = true;
		this.reader?.cancel();
	}
	get lastId() {
		return this.lastEventId;
	}
	async *[Symbol.asyncIterator]() {
		const body = this.response.body;
		if (!body) return;
		this.reader = body.pipeThrough(new TextDecoderStream()).getReader();
		try {
			while (!this.closed) {
				const { done, value } = await this.reader.read();
				if (done) break;
				this.buffer += value;
				const parts = this.buffer.split("\n\n");
				this.buffer = parts.pop();
				for (const part of parts) {
					const event = this.parseEvent(part);
					if (event) {
						yield event;
						this.emit(event);
					}
				}
			}
		} finally {
			this.reader?.cancel();
		}
	}
	parseEvent(raw) {
		let data = "";
		let eventName;
		let id;
		let retry;
		for (const line of raw.split("\n")) if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trimStart();
		else if (line.startsWith("event:")) eventName = line.slice(6).trimStart();
		else if (line.startsWith("id:")) {
			id = line.slice(3).trimStart();
			this.lastEventId = id;
		} else if (line.startsWith("retry:")) {
			const val = parseInt(line.slice(6).trimStart(), 10);
			if (!isNaN(val)) retry = val;
		}
		if (!data) return null;
		return {
			data: this.parse(data),
			event: eventName,
			id,
			retry
		};
	}
	emit(event) {
		const eventType = event.event ?? "message";
		const handlers = this.listeners.get(eventType) ?? [];
		const messageHandlers = eventType !== "message" ? this.listeners.get("message") ?? [] : [];
		for (const h of [...handlers, ...messageHandlers]) h(event);
	}
};
//#endregion
//#region src/form-data.ts
function buildFormData(data) {
	const formData = new FormData();
	function appendValue(key, value) {
		if (value instanceof File) {
			formData.append(key, value, value.name);
			return;
		}
		if (value instanceof Blob) {
			formData.append(key, value, "blob");
			return;
		}
		if (value instanceof Date) {
			formData.append(key, value.toISOString());
			return;
		}
		if (typeof value === "number" || typeof value === "boolean") {
			formData.append(key, String(value));
			return;
		}
		if (typeof value === "string") {
			formData.append(key, value);
			return;
		}
	}
	if (data && typeof data === "object") for (const [key, value] of Object.entries(data)) {
		if (value === void 0 || value === null) continue;
		if (Array.isArray(value)) for (const item of value) {
			if (item === void 0 || item === null) continue;
			appendValue(key, item);
		}
		else appendValue(key, value);
	}
	return formData;
}
//#endregion
//#region src/access-request/index.ts
async function AccessRequest_requestAccess(request, options) {
	return request("POST", "/billing/access-requests", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAccessRequestController(request) {
	return { requestAccess: (options) => AccessRequest_requestAccess(request, options) };
}
//#endregion
//#region src/account-deletion/index.ts
async function AccountDeletion_deleteMyAccount(request, options) {
	return request("DELETE", "/users/me", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAccountDeletionController(request) {
	return { deleteMyAccount: (options) => AccountDeletion_deleteMyAccount(request, options) };
}
//#endregion
//#region src/accounting/index.ts
async function Accounting_connect(request, options) {
	return request("POST", "/accounting/connection", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_disconnect(request, options) {
	return request("DELETE", "/accounting/connection", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_getConnection(request, options) {
	return request("GET", "/accounting/connection", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_getVatMapping(request, options) {
	return request("GET", "/accounting/vat-mapping", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_pollNow(request, options) {
	return request("POST", "/accounting/poll", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_putVatMapping(request, options) {
	return request("PUT", "/accounting/vat-mapping", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_recheck(request, options) {
	return request("POST", "/accounting/connection/recheck", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Accounting_updateSettings(request, options) {
	return request("PATCH", "/accounting/settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAccountingController(request) {
	return {
		connect: (options) => Accounting_connect(request, options),
		disconnect: (options) => Accounting_disconnect(request, options),
		getConnection: (options) => Accounting_getConnection(request, options),
		getVatMapping: (options) => Accounting_getVatMapping(request, options),
		pollNow: (options) => Accounting_pollNow(request, options),
		putVatMapping: (options) => Accounting_putVatMapping(request, options),
		recheck: (options) => Accounting_recheck(request, options),
		updateSettings: (options) => Accounting_updateSettings(request, options)
	};
}
//#endregion
//#region src/address-suggestion/index.ts
async function AddressSuggestion_list(request, options) {
	const query = {};
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	return request("GET", "/geo/address-suggestions", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAddressSuggestionController(request) {
	return { list: (options) => AddressSuggestion_list(request, options) };
}
//#endregion
//#region src/admin-analytics/index.ts
async function AdminAnalytics_getCompanyAnalytics(request, options) {
	const params = { targetCompanyId: String(options.targetCompanyId) };
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/analytics/company/{targetCompanyId}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminAnalyticsController(request) {
	return { getCompanyAnalytics: (options) => AdminAnalytics_getCompanyAnalytics(request, options) };
}
//#endregion
//#region src/admin-billing/index.ts
async function AdminBilling_getPanel(request, options) {
	return request("GET", "/admin/billing/{targetCompanyId}/panel", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminBilling_provision(request, options) {
	return request("POST", "/admin/billing/{targetCompanyId}/provision", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminBilling_setPlan(request, options) {
	return request("PUT", "/admin/billing/{targetCompanyId}/plan", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminBilling_setQuotaOverride(request, options) {
	return request("PUT", "/admin/billing/{targetCompanyId}/quota-override", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminBilling_setStorageOverride(request, options) {
	return request("PUT", "/admin/billing/{targetCompanyId}/storage-override", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminBillingController(request) {
	return {
		getPanel: (options) => AdminBilling_getPanel(request, options),
		provision: (options) => AdminBilling_provision(request, options),
		setPlan: (options) => AdminBilling_setPlan(request, options),
		setQuotaOverride: (options) => AdminBilling_setQuotaOverride(request, options),
		setStorageOverride: (options) => AdminBilling_setStorageOverride(request, options)
	};
}
//#endregion
//#region src/admin-booking/index.ts
/** Assign a company user to a booking slot */
async function AdminBooking_assignUserToBookingSlot(request, options) {
	return request("POST", "/admin/bookings/booking-slots/assign-user/{bookingSlotId}/{companyUserId}", {
		params: {
			bookingSlotId: String(options.bookingSlotId),
			companyUserId: String(options.companyUserId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Cancel a booking slot from admin */
async function AdminBooking_cancelBooking(request, options) {
	return request("DELETE", "/admin/bookings/booking-slots/cancel/{bookingSlotId}", {
		params: { bookingSlotId: String(options.bookingSlotId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create company config with service, availability and form fields */
async function AdminBooking_createBookingServiceWithAvailabilityAndFields(request, options) {
	return request("POST", "/admin/bookings/service-availability", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete config */
async function AdminBooking_deleteConfig(request, options) {
	return request("DELETE", "/admin/bookings/config/{configId}", {
		params: { configId: String(options.configId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get booking funnel analytics for a company */
async function AdminBooking_getAnalytics(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/bookings/analytics", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Check whether a company user is free for a booking slot before assigning them */
async function AdminBooking_getAssigneeAvailability(request, options) {
	return request("GET", "/admin/bookings/booking-slots/assignee-availability/{bookingSlotId}/{companyUserId}", {
		params: {
			bookingSlotId: String(options.bookingSlotId),
			companyUserId: String(options.companyUserId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get campaign analytics (source, medium, campaign) for a company */
async function AdminBooking_getCampaignAnalytics(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/bookings/analytics/campaigns", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get company config */
async function AdminBooking_getCompanyConfig(request, options) {
	return request("GET", "/admin/bookings/config", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get paginated and filtered booking slots */
async function AdminBooking_getPaginatedBookingSlots(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.startDateTime !== void 0) query["startDateTime"] = options.query.startDateTime;
	if (options.query?.endDateTime !== void 0) query["endDateTime"] = options.query.endDateTime;
	if (options.query?.duration !== void 0) query["duration"] = options.query.duration;
	if (options.query?.assignedToUserId !== void 0) query["assignedToUserId"] = options.query.assignedToUserId;
	if (options.query?.serviceId !== void 0) query["serviceId"] = options.query.serviceId;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	return request("GET", "/admin/bookings/booking-slots", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get paginated company  searched users */
async function AdminBooking_getPaginatedUsers(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	return request("GET", "/admin/bookings/company-users", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get referrer analytics (traffic sources) for a company */
async function AdminBooking_getReferrerAnalytics(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/bookings/analytics/referrers", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get free availability windows for a service on a date, ignoring the minimum booking notice */
async function AdminBooking_getStaffServiceAvailability(request, options) {
	const params = { serviceId: String(options.serviceId) };
	const query = {};
	if (options.query?.date !== void 0) query["date"] = options.query.date;
	return request("GET", "/admin/bookings/availability/{serviceId}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove the custom logo for the public booking widget */
async function AdminBooking_removeWidgetLogo(request, options) {
	return request("DELETE", "/admin/bookings/config-settings/widget-logo", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reschedule an existing booking slot from admin */
async function AdminBooking_rescheduleSlot(request, options) {
	return request("PATCH", "/admin/bookings/booking-slots/reschedule/{bookingSlotId}", {
		params: { bookingSlotId: String(options.bookingSlotId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update services availability form fields under a booking config */
async function AdminBooking_updateBookingServiceWithAvailabilityAndFields(request, options) {
	return request("PATCH", "/admin/bookings/service-availability", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update booking config settings (tracking scripts, lead time) */
async function AdminBooking_updateConfigSettings(request, options) {
	return request("PATCH", "/admin/bookings/config-settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload a custom logo for the public booking widget */
async function AdminBooking_uploadWidgetLogo(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/admin/bookings/config-settings/widget-logo", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminBookingController(request) {
	return {
		assignUserToBookingSlot: (options) => AdminBooking_assignUserToBookingSlot(request, options),
		cancelBooking: (options) => AdminBooking_cancelBooking(request, options),
		createBookingServiceWithAvailabilityAndFields: (options) => AdminBooking_createBookingServiceWithAvailabilityAndFields(request, options),
		deleteConfig: (options) => AdminBooking_deleteConfig(request, options),
		getAnalytics: (options) => AdminBooking_getAnalytics(request, options),
		getAssigneeAvailability: (options) => AdminBooking_getAssigneeAvailability(request, options),
		getCampaignAnalytics: (options) => AdminBooking_getCampaignAnalytics(request, options),
		getCompanyConfig: (options) => AdminBooking_getCompanyConfig(request, options),
		getPaginatedBookingSlots: (options) => AdminBooking_getPaginatedBookingSlots(request, options),
		getPaginatedUsers: (options) => AdminBooking_getPaginatedUsers(request, options),
		getReferrerAnalytics: (options) => AdminBooking_getReferrerAnalytics(request, options),
		getStaffServiceAvailability: (options) => AdminBooking_getStaffServiceAvailability(request, options),
		removeWidgetLogo: (options) => AdminBooking_removeWidgetLogo(request, options),
		rescheduleSlot: (options) => AdminBooking_rescheduleSlot(request, options),
		updateBookingServiceWithAvailabilityAndFields: (options) => AdminBooking_updateBookingServiceWithAvailabilityAndFields(request, options),
		updateConfigSettings: (options) => AdminBooking_updateConfigSettings(request, options),
		uploadWidgetLogo: (options) => AdminBooking_uploadWidgetLogo(request, options)
	};
}
//#endregion
//#region src/admin-chain-cap/index.ts
async function AdminChainCap_clearGuardrails(request, options) {
	return request("PUT", "/admin/chain-cap/{parentCompanyId}/guardrails/clear", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminChainCap_clearPool(request, options) {
	return request("PUT", "/admin/chain-cap/{parentCompanyId}/pool/clear", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminChainCap_getCapBoard(request, options) {
	return request("GET", "/admin/chain-cap/{parentCompanyId}/cap-board", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminChainCap_setGuardrails(request, options) {
	return request("PUT", "/admin/chain-cap/{parentCompanyId}/guardrails", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AdminChainCap_setPool(request, options) {
	return request("PUT", "/admin/chain-cap/{parentCompanyId}/pool", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminChainCapController(request) {
	return {
		clearGuardrails: (options) => AdminChainCap_clearGuardrails(request, options),
		clearPool: (options) => AdminChainCap_clearPool(request, options),
		getCapBoard: (options) => AdminChainCap_getCapBoard(request, options),
		setGuardrails: (options) => AdminChainCap_setGuardrails(request, options),
		setPool: (options) => AdminChainCap_setPool(request, options)
	};
}
//#endregion
//#region src/admin-chain/index.ts
/** Add a member to a chain */
async function AdminChain_addMember(request, options) {
	return request("POST", "/admin/chains/members", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Download a chain's login credentials as CSV

Columns: Company, SubDomain, Login URL, Name, Email, Role, Password. Parent
company first, then members alphabetically, OWNER before ADMIN before the
rest. Every real login is listed; only seeded demo logins collapse to one
per role per company since they share a password. The password column is
filled only for seeded demo logins; real users set theirs via forgot-password.
Built fresh from the database on every request. */
async function AdminChain_downloadCredentialsCsv(request, options) {
	return request("GET", "/admin/chains/{parentCompanyId}/credentials.csv", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get chain detail */
async function AdminChain_getChainDetail(request, options) {
	return request("GET", "/admin/chains/{parentCompanyId}", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List chain members */
async function AdminChain_getChainMembers(request, options) {
	const params = { parentCompanyId: String(options.parentCompanyId) };
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	return request("GET", "/admin/chains/{parentCompanyId}/members", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the module on/off state for a whole chain */
async function AdminChain_getChainModules(request, options) {
	return request("GET", "/admin/chains/{parentCompanyId}/modules", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all chains */
async function AdminChain_listChains(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/admin/chains", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove member from chain */
async function AdminChain_removeMember(request, options) {
	return request("DELETE", "/admin/chains/members/{membershipId}", {
		params: { membershipId: String(options.membershipId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reset a chain to a fresh, usable state (wipes all module data)

Wipes every module's existing data for the parent and all member companies
(sales, orders, invoices, customer portal, courses, competence, staffing,
intranet and member-portal content), then restores only the defaults a fresh
chain needs to work: sales entitlement, default roles, the CRM and order
pipelines, contact categories, staffing taxonomies and the chain sales
pipeline. No demo content is seeded, so the chain lands empty but immediately
usable. Runs in the background; poll the status endpoint. Idempotent; safe to
re-run. In production only the demo chain may be reset; any other chain answers 403. */
async function AdminChain_resetChainData(request, options) {
	return request("POST", "/admin/chains/{parentCompanyId}/reset-data", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get chain reset-to-defaults progress */
async function AdminChain_resetChainDataStatus(request, options) {
	return request("GET", "/admin/chains/{parentCompanyId}/reset-data/status", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Search companies eligible to join a chain */
async function AdminChain_searchEligibleCompanies(request, options) {
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.excludeParentId !== void 0) query["excludeParentId"] = options.query.excludeParentId;
	return request("GET", "/admin/chains/eligible-companies", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Start a full demo-data seed for a chain (wipes old data first)

Wipes every module's existing data for the parent and all member companies
(sales leads/deals, orders, invoices, customer portal accounts, courses,
enrollments, competence, staffing, intranet and member-portal content),
then seeds a fresh demo set for each. Company settings, users and
memberships are untouched. Runs in the background; poll the status
endpoint. Re-running always wipes first, so data never duplicates.
In production only the demo chain may be seeded; any other chain answers 403. */
async function AdminChain_seedChainDemoData(request, options) {
	return request("POST", "/admin/chains/{parentCompanyId}/seed-demo-data", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get chain demo-data seed progress */
async function AdminChain_seedChainDemoDataStatus(request, options) {
	return request("GET", "/admin/chains/{parentCompanyId}/seed-demo-data/status", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed the ten-company demo chain

Creates the chain parent "Vadio Chain" and ten fictional Norwegian member
companies with memberships, premium billing, default roles, pipelines and
taxonomies, one demo OWNER login per company (shared password, no emails
sent) and every super admin as ADMIN on the parent. Then starts the same
background demo-data seed the Seed Data button runs, so the chain lands with
orders, courses, leads and the rest; poll seed-demo-data/status for it.
Touches only its own eleven companies, never another chain. Idempotent:
every step is create-if-missing, so re-running, or retrying after a failure
part-way through, never duplicates a company, membership or user. Returns
409 while a seed is already running. Available in every environment; the
demo chain lives on its own subdomains and never touches a real chain. */
async function AdminChain_seedDemoChain(request, options) {
	return request("POST", "/admin/chains/seed-demo-chain", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Enable or disable a whole chain

Sets `isActive` on the parent and every member company and flips all
memberships ACTIVE/INACTIVE. Disabling hides every member from the public
installer search immediately. Reversible via `active: true`. */
async function AdminChain_setChainActive(request, options) {
	return request("PATCH", "/admin/chains/{parentCompanyId}/active", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Set the module on/off state for a whole chain

`disabledModules` is written to the parent and every member company; `memberDisabledModules`
only to the members, so a module can stay on at HQ while the member companies lose it. */
async function AdminChain_setChainModules(request, options) {
	return request("PATCH", "/admin/chains/{parentCompanyId}/modules", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update chain membership */
async function AdminChain_updateMember(request, options) {
	return request("PATCH", "/admin/chains/members/{membershipId}", {
		params: { membershipId: String(options.membershipId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminChainController(request) {
	return {
		addMember: (options) => AdminChain_addMember(request, options),
		downloadCredentialsCsv: (options) => AdminChain_downloadCredentialsCsv(request, options),
		getChainDetail: (options) => AdminChain_getChainDetail(request, options),
		getChainMembers: (options) => AdminChain_getChainMembers(request, options),
		getChainModules: (options) => AdminChain_getChainModules(request, options),
		listChains: (options) => AdminChain_listChains(request, options),
		removeMember: (options) => AdminChain_removeMember(request, options),
		resetChainData: (options) => AdminChain_resetChainData(request, options),
		resetChainDataStatus: (options) => AdminChain_resetChainDataStatus(request, options),
		searchEligibleCompanies: (options) => AdminChain_searchEligibleCompanies(request, options),
		seedChainDemoData: (options) => AdminChain_seedChainDemoData(request, options),
		seedChainDemoDataStatus: (options) => AdminChain_seedChainDemoDataStatus(request, options),
		seedDemoChain: (options) => AdminChain_seedDemoChain(request, options),
		setChainActive: (options) => AdminChain_setChainActive(request, options),
		setChainModules: (options) => AdminChain_setChainModules(request, options),
		updateMember: (options) => AdminChain_updateMember(request, options)
	};
}
//#endregion
//#region src/admin-chain-seed-sse/index.ts
/** SSE stream for one chain's reset-to-defaults progress. */
async function AdminChainSeedSse_resetStream(request, options) {
	const params = { parentCompanyId: String(options.parentCompanyId) };
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	const result = await request("GET", "/admin/chains/{parentCompanyId}/reset-data/stream", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
/** SSE stream for one chain's demo-data seed progress. */
async function AdminChainSeedSse_stream(request, options) {
	const params = { parentCompanyId: String(options.parentCompanyId) };
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	const result = await request("GET", "/admin/chains/{parentCompanyId}/seed-demo-data/stream", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
function createAdminChainSeedSseController(request) {
	return {
		resetStream: (options) => AdminChainSeedSse_resetStream(request, options),
		stream: (options) => AdminChainSeedSse_stream(request, options)
	};
}
//#endregion
//#region src/admin/index.ts
/** Add email to SES suppression list */
async function Admin_addToSuppressionList(request, options) {
	return request("POST", "/admin/suppression-list", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Cancel a company's pending invite */
async function Admin_cancelCompanyInvite(request, options) {
	const params = { targetCompanyId: String(options.targetCompanyId) };
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	return request("DELETE", "/admin/companies/{targetCompanyId}/invites", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create admin */
async function Admin_createAdmin(request, options) {
	return request("POST", "/admin/admins", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete admin */
async function Admin_deleteAdmin(request, options) {
	return request("DELETE", "/admin/admins/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** export users to csv */
async function Admin_exportUsersToCSV(request, options) {
	const query = {};
	if (options.query?.plan !== void 0) query["plan"] = options.query.plan;
	if (options.query?.signUpFrom !== void 0) query["signUpFrom"] = options.query.signUpFrom;
	if (options.query?.signUpTo !== void 0) query["signUpTo"] = options.query.signUpTo;
	if (options.query?.lastSeenFrom !== void 0) query["lastSeenFrom"] = options.query.lastSeenFrom;
	if (options.query?.lastSeenTo !== void 0) query["lastSeenTo"] = options.query.lastSeenTo;
	return request("GET", "/admin/export/users", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Replace a user's password with a generated one and return it once */
async function Admin_generateUserPassword(request, options) {
	return request("POST", "/admin/user/{userId}/generate-password", {
		params: { userId: String(options.userId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get companies */
async function Admin_getCompanies(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	if (options.query?.plan !== void 0) query["plan"] = options.query.plan;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/admin/companies", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List a company's pending invites */
async function Admin_getCompanyInvites(request, options) {
	return request("GET", "/admin/companies/{targetCompanyId}/invites", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a standalone company's module on/off state */
async function Admin_getCompanyModules(request, options) {
	return request("GET", "/admin/companies/{targetCompanyId}/modules", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get company users details */
async function Admin_getCompanyUsers(request, options) {
	return request("GET", "/admin/{targetCompanyId}/users/details", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get single email log detail */
async function Admin_getEmailDetail(request, options) {
	return request("GET", "/admin/emails/{emailId}", {
		params: { emailId: String(options.emailId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get email logs */
async function Admin_getEmailLogs(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.template !== void 0) query["template"] = options.query.template;
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	return request("GET", "/admin/emails", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get email stats */
async function Admin_getEmailStats(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	return request("GET", "/admin/emails/stats", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get SMS failure alert settings */
async function Admin_getSmsAlertSettings(request, options) {
	return request("GET", "/admin/sms/alert-settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get single SMS log detail */
async function Admin_getSmsDetail(request, options) {
	return request("GET", "/admin/sms/{smsLogId}", {
		params: { smsLogId: String(options.smsLogId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get SMS logs */
async function Admin_getSmsLogs(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	return request("GET", "/admin/sms", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get SMS stats */
async function Admin_getSmsStats(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	return request("GET", "/admin/sms/stats", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List SES suppression list */
async function Admin_getSuppressionList(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/admin/suppression-list", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List globally unsubscribed emails */
async function Admin_getUnsubscribeList(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/admin/unsubscribe-list", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get user details */
async function Admin_getUserDetails(request, options) {
	return request("GET", "/admin/user/details/{userId}", {
		params: { userId: String(options.userId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Invite a user into any company */
async function Admin_inviteCompanyMember(request, options) {
	return request("POST", "/admin/companies/{targetCompanyId}/invites", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get admins */
async function Admin_listAdmins(request, options) {
	return request("GET", "/admin/admins", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get users */
async function Admin_listUsers(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	if (options.query?.plan !== void 0) query["plan"] = options.query.plan;
	if (options.query?.signUpFrom !== void 0) query["signUpFrom"] = options.query.signUpFrom;
	if (options.query?.signUpTo !== void 0) query["signUpTo"] = options.query.signUpTo;
	if (options.query?.lastSeenFrom !== void 0) query["lastSeenFrom"] = options.query.lastSeenFrom;
	if (options.query?.lastSeenTo !== void 0) query["lastSeenTo"] = options.query.lastSeenTo;
	if (options.query?.minMinutes !== void 0) query["minMinutes"] = options.query.minMinutes;
	if (options.query?.maxMinutes !== void 0) query["maxMinutes"] = options.query.maxMinutes;
	if (options.query?.minStorage !== void 0) query["minStorage"] = options.query.minStorage;
	if (options.query?.maxStorage !== void 0) query["maxStorage"] = options.query.maxStorage;
	return request("GET", "/admin/users", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove a user from a company */
async function Admin_removeCompanyUser(request, options) {
	return request("DELETE", "/admin/{targetCompanyId}/users/{userId}", {
		params: {
			targetCompanyId: String(options.targetCompanyId),
			userId: String(options.userId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove email from SES suppression list */
async function Admin_removeFromSuppressionList(request, options) {
	return request("DELETE", "/admin/suppression-list/{email}", {
		params: { email: String(options.email) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove an email from the unsubscribe list */
async function Admin_removeFromUnsubscribeList(request, options) {
	return request("DELETE", "/admin/unsubscribe-list/{email}", {
		params: { email: String(options.email) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reset a user's password */
async function Admin_resetUserPassword(request, options) {
	return request("POST", "/admin/user/{userId}/reset-password", {
		params: { userId: String(options.userId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed CRM example data for a company */
async function Admin_seedCrmDemoData(request, options) {
	return request("POST", "/admin/companies/{targetCompanyId}/seed-crm-demo", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Set a standalone company's module on/off state

A disabled module vanishes from the company's UI, routes and API. Chain
companies are managed from the chain page, not here. */
async function Admin_setCompanyModules(request, options) {
	return request("PATCH", "/admin/companies/{targetCompanyId}/modules", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Sync SES suppression list from AWS */
async function Admin_syncSuppressionList(request, options) {
	return request("POST", "/admin/suppression-list/sync", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Extend company trial date */
async function Admin_updateCompanyTrialEndDate(request, options) {
	return request("PATCH", "/admin/admins/{targetCompanyId}/update-trial", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update SMS failure alert settings */
async function Admin_updateSmsAlertSettings(request, options) {
	return request("PATCH", "/admin/sms/alert-settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminController(request) {
	return {
		addToSuppressionList: (options) => Admin_addToSuppressionList(request, options),
		cancelCompanyInvite: (options) => Admin_cancelCompanyInvite(request, options),
		createAdmin: (options) => Admin_createAdmin(request, options),
		deleteAdmin: (options) => Admin_deleteAdmin(request, options),
		exportUsersToCSV: (options) => Admin_exportUsersToCSV(request, options),
		generateUserPassword: (options) => Admin_generateUserPassword(request, options),
		getCompanies: (options) => Admin_getCompanies(request, options),
		getCompanyInvites: (options) => Admin_getCompanyInvites(request, options),
		getCompanyModules: (options) => Admin_getCompanyModules(request, options),
		getCompanyUsers: (options) => Admin_getCompanyUsers(request, options),
		getEmailDetail: (options) => Admin_getEmailDetail(request, options),
		getEmailLogs: (options) => Admin_getEmailLogs(request, options),
		getEmailStats: (options) => Admin_getEmailStats(request, options),
		getSmsAlertSettings: (options) => Admin_getSmsAlertSettings(request, options),
		getSmsDetail: (options) => Admin_getSmsDetail(request, options),
		getSmsLogs: (options) => Admin_getSmsLogs(request, options),
		getSmsStats: (options) => Admin_getSmsStats(request, options),
		getSuppressionList: (options) => Admin_getSuppressionList(request, options),
		getUnsubscribeList: (options) => Admin_getUnsubscribeList(request, options),
		getUserDetails: (options) => Admin_getUserDetails(request, options),
		inviteCompanyMember: (options) => Admin_inviteCompanyMember(request, options),
		listAdmins: (options) => Admin_listAdmins(request, options),
		listUsers: (options) => Admin_listUsers(request, options),
		removeCompanyUser: (options) => Admin_removeCompanyUser(request, options),
		removeFromSuppressionList: (options) => Admin_removeFromSuppressionList(request, options),
		removeFromUnsubscribeList: (options) => Admin_removeFromUnsubscribeList(request, options),
		resetUserPassword: (options) => Admin_resetUserPassword(request, options),
		seedCrmDemoData: (options) => Admin_seedCrmDemoData(request, options),
		setCompanyModules: (options) => Admin_setCompanyModules(request, options),
		syncSuppressionList: (options) => Admin_syncSuppressionList(request, options),
		updateCompanyTrialEndDate: (options) => Admin_updateCompanyTrialEndDate(request, options),
		updateSmsAlertSettings: (options) => Admin_updateSmsAlertSettings(request, options)
	};
}
//#endregion
//#region src/admin-mcp/index.ts
/** Get admin MCP adoption: connected and active super admins */
async function AdminMcp_adoption(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	return request("GET", "/admin/mcp/adoption", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the calls admin MCP connections made */
async function AdminMcp_calls(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.actorUserId !== void 0) query["actorUserId"] = options.query.actorUserId;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.targetCompanyId !== void 0) query["targetCompanyId"] = options.query.targetCompanyId;
	if (options.query?.targetUserId !== void 0) query["targetUserId"] = options.query.targetUserId;
	if (options.query?.method !== void 0) query["method"] = options.query.method;
	if (options.query?.statusClass !== void 0) query["statusClass"] = options.query.statusClass;
	if (options.query?.path !== void 0) query["path"] = options.query.path;
	if (options.query?.contentRead !== void 0) query["contentRead"] = options.query.contentRead;
	return request("GET", "/admin/mcp/calls", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the admin MCP connection context */
async function AdminMcp_context(request, options) {
	return request("GET", "/admin/mcp/context", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the admin agent OpenAPI spec */
async function AdminMcp_spec(request, options) {
	return request("GET", "/admin/mcp/spec", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminMcpController(request) {
	return {
		adoption: (options) => AdminMcp_adoption(request, options),
		calls: (options) => AdminMcp_calls(request, options),
		context: (options) => AdminMcp_context(request, options),
		spec: (options) => AdminMcp_spec(request, options)
	};
}
//#endregion
//#region src/admin-meeting-telemetry/index.ts
/**
* Erase every piece of meeting telemetry about a user
*
* Delete the user's sessions, raw events and samples, and remove them from meeting summaries. Writes an audit row.
*/
async function AdminMeetingTelemetry_eraseUser(request, options) {
	return request("DELETE", "/admin/meeting-telemetry/users/{userId}/sessions", {
		params: { userId: String(options.userId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a markdown incident digest of a meeting's call problems
*
* Get one markdown document for a meeting: header, environment per participant, the timeline with repeats collapsed, p50 and p95 metrics, rule hits with remediation, up to 3 similar recent sessions and what data is missing. The concise format pseudonymises people as P1 (host), keeps app and media server events at warn and above, and stays under about 12 KB; the detailed format adds names, emails and ids, every event at info and above with every attribute, and stays under about 160 KB, dropping info lines first when it must.
*/
async function AdminMeetingTelemetry_getDigest(request, options) {
	const params = { meetingId: String(options.meetingId) };
	const query = {};
	if (options.query?.format !== void 0) query["format"] = options.query.format;
	return request("GET", "/admin/meeting-telemetry/meetings/{meetingId}/digest", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get one meeting's call telemetry summary and every participant session
*
* Get the meeting telemetry summary, its meeting-level rule hits and up to 200 participant sessions with browser, OS, device, versions, network, phases, counts, flags, rule hits and quality score. Works for live meetings too by summarising the sessions on the fly.
*/
async function AdminMeetingTelemetry_getMeeting(request, options) {
	return request("GET", "/admin/meeting-telemetry/meetings/{meetingId}", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get how often a flag or error code hits sessions, grouped by browser, OS, SDK, company or network
*
* Get the share of sessions with a flag or an error code over the last 24h or 7d, grouped by browser, os, platform, sdkVersion, companyId, country or asn, against the baseline of all sessions. Returns the top 10 groups with their lift over the baseline, read from hourly rollups.
*/
async function AdminMeetingTelemetry_getPopulation(request, options) {
	const query = {};
	if (options.query?.flag !== void 0) query["flag"] = options.query.flag;
	if (options.query?.errorCode !== void 0) query["errorCode"] = options.query.errorCode;
	if (options.query?.groupBy !== void 0) query["groupBy"] = options.query.groupBy;
	if (options.query?.range !== void 0) query["range"] = options.query.range;
	return request("GET", "/admin/meeting-telemetry/population", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the meeting telemetry raw retention setting */
async function AdminMeetingTelemetry_getSettings(request, options) {
	return request("GET", "/admin/meeting-telemetry/settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List every call-quality flag with its thresholds and remediation
*
* List the static rules catalogue: each flag the telemetry rules can raise, whether it looks at one session or the whole meeting, what it means, the thresholds it uses and what support should tell the participant.
*/
async function AdminMeetingTelemetry_listFlags(request, options) {
	return request("GET", "/admin/meeting-telemetry/flags", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List meetings with call telemetry, filtered by company, time, flag, error code, platform, browser, OS, user or title
*
* List meeting call-quality summaries newest first, filtered by companyId, from and to, flag, errorCode, platform, browser and os prefix, userId of a participant, or a title search. Returns compact rows, 20 per page by default and at most 50, with an opaque cursor for the next page. Only meetings whose telemetry has been finalised appear.
*/
async function AdminMeetingTelemetry_listMeetings(request, options) {
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.flag !== void 0) query["flag"] = options.query.flag;
	if (options.query?.errorCode !== void 0) query["errorCode"] = options.query.errorCode;
	if (options.query?.platform !== void 0) query["platform"] = options.query.platform;
	if (options.query?.browser !== void 0) query["browser"] = options.query.browser;
	if (options.query?.os !== void 0) query["os"] = options.query.os;
	if (options.query?.userId !== void 0) query["userId"] = options.query.userId;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/admin/meeting-telemetry/meetings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a session's downsampled call-quality metrics with threshold breaches marked
*
* List one participant session's WebRTC metrics downsampled into at most maxPoints buckets, 30 by default and at most 60, optionally limited by from and to offsets in milliseconds from the meeting start. Each point carries round trip time, loss, jitter, frame rates, mic level, concealment, frozen video share and bitrates, plus the thresholds it crossed.
*/
async function AdminMeetingTelemetry_listSamples(request, options) {
	const params = {
		meetingId: String(options.meetingId),
		sessionId: String(options.sessionId)
	};
	const query = {};
	if (options.query?.maxPoints !== void 0) query["maxPoints"] = options.query.maxPoints;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/meeting-telemetry/meetings/{meetingId}/sessions/{sessionId}/samples", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a meeting's telemetry timeline of client, server, join, pipeline and recording events
*
* List one page of the merged meeting timeline in time order, filtered by sessionId, minLevel, event types or namespaces, and from and to offsets in milliseconds from the meeting start. Identical repeats collapse into one line with a count, and omitted counts the events below minLevel per level. minLevel defaults to warn for the admin MCP and info for the panel. Returns 100 lines by default and at most 300, with an opaque cursor.
*/
async function AdminMeetingTelemetry_listTimeline(request, options) {
	const params = { meetingId: String(options.meetingId) };
	const query = {};
	if (options.query?.sessionId !== void 0) query["sessionId"] = options.query.sessionId;
	if (options.query?.participantKey !== void 0) query["participantKey"] = options.query.participantKey;
	if (options.query?.minLevel !== void 0) query["minLevel"] = options.query.minLevel;
	if (options.query?.types !== void 0) query["types"] = options.query.types;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/admin/meeting-telemetry/meetings/{meetingId}/timeline", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a user's recent meeting telemetry sessions
*
* List one user's participant sessions across meetings newest first, 20 per page with an opaque cursor, each with platform, browser, OS, join and leave times, flags, error codes and quality score.
*/
async function AdminMeetingTelemetry_listUserSessions(request, options) {
	const params = { userId: String(options.userId) };
	const query = {};
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/admin/meeting-telemetry/users/{userId}/sessions", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Change how many days raw meeting telemetry is kept
*
* Change the raw event and sample retention, at least 30 days. Applies the new expiry to both time series collections and writes an audit row.
*/
async function AdminMeetingTelemetry_updateSettings(request, options) {
	return request("PATCH", "/admin/meeting-telemetry/settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminMeetingTelemetryController(request) {
	return {
		eraseUser: (options) => AdminMeetingTelemetry_eraseUser(request, options),
		getDigest: (options) => AdminMeetingTelemetry_getDigest(request, options),
		getMeeting: (options) => AdminMeetingTelemetry_getMeeting(request, options),
		getPopulation: (options) => AdminMeetingTelemetry_getPopulation(request, options),
		getSettings: (options) => AdminMeetingTelemetry_getSettings(request, options),
		listFlags: (options) => AdminMeetingTelemetry_listFlags(request, options),
		listMeetings: (options) => AdminMeetingTelemetry_listMeetings(request, options),
		listSamples: (options) => AdminMeetingTelemetry_listSamples(request, options),
		listTimeline: (options) => AdminMeetingTelemetry_listTimeline(request, options),
		listUserSessions: (options) => AdminMeetingTelemetry_listUserSessions(request, options),
		updateSettings: (options) => AdminMeetingTelemetry_updateSettings(request, options)
	};
}
//#endregion
//#region src/admin-roles/index.ts
/**
* Assign one of a target company's roles to one of its members
*
* Platform-admin assignment from outside the company. The last-owner safeguard
* still applies (400 LAST_OWNER); the self-change and owner-only gates cannot bind an outsider.
*/
async function AdminRoles_assignRole(request, options) {
	return request("PATCH", "/admin/companies/{targetCompanyId}/roles/assign", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a custom role for a target company */
async function AdminRoles_createRole(request, options) {
	return request("POST", "/admin/companies/{targetCompanyId}/roles", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a custom role for a target company */
async function AdminRoles_deleteRole(request, options) {
	return request("DELETE", "/admin/companies/{targetCompanyId}/roles/{id}", {
		params: {
			targetCompanyId: String(options.targetCompanyId),
			id: String(options.id)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Duplicate a role of a target company into a new editable custom role */
async function AdminRoles_duplicateRole(request, options) {
	return request("POST", "/admin/companies/{targetCompanyId}/roles/{id}/duplicate", {
		params: {
			targetCompanyId: String(options.targetCompanyId),
			id: String(options.id)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** The permission catalog for a target company, scoped to its chain status */
async function AdminRoles_getCatalog(request, options) {
	return request("GET", "/admin/companies/{targetCompanyId}/roles/catalog", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single role for a target company */
async function AdminRoles_getRole(request, options) {
	return request("GET", "/admin/companies/{targetCompanyId}/roles/{id}", {
		params: {
			targetCompanyId: String(options.targetCompanyId),
			id: String(options.id)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all roles for a target company with holder counts */
async function AdminRoles_listRoles(request, options) {
	return request("GET", "/admin/companies/{targetCompanyId}/roles", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a custom role for a target company (optimistic concurrency via version) */
async function AdminRoles_updateRole(request, options) {
	return request("PATCH", "/admin/companies/{targetCompanyId}/roles/{id}", {
		params: {
			targetCompanyId: String(options.targetCompanyId),
			id: String(options.id)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminRolesController(request) {
	return {
		assignRole: (options) => AdminRoles_assignRole(request, options),
		createRole: (options) => AdminRoles_createRole(request, options),
		deleteRole: (options) => AdminRoles_deleteRole(request, options),
		duplicateRole: (options) => AdminRoles_duplicateRole(request, options),
		getCatalog: (options) => AdminRoles_getCatalog(request, options),
		getRole: (options) => AdminRoles_getRole(request, options),
		listRoles: (options) => AdminRoles_listRoles(request, options),
		updateRole: (options) => AdminRoles_updateRole(request, options)
	};
}
//#endregion
//#region src/admin-transcription/index.ts
/** Get all audio tracks for a meeting */
async function AdminTranscription_getMeetingTracks(request, options) {
	return request("GET", "/admin/transcriptions/meetings/{meetingId}/tracks", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get paginated list of meetings with transcription data */
async function AdminTranscription_getMeetings(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/admin/transcriptions/meetings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get transcription overview stats */
async function AdminTranscription_getOverview(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	return request("GET", "/admin/transcriptions/overview", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove duplicate stuck audio tracks, re-enqueue unprocessed ones, and trigger pending summaries */
async function AdminTranscription_reconcileStuckTracks(request, options) {
	return request("POST", "/admin/transcriptions/reconcile-stuck", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Retry all failed tracks for a meeting */
async function AdminTranscription_retryAllFailed(request, options) {
	return request("POST", "/admin/transcriptions/meetings/{meetingId}/retry-all", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Retry a single failed audio track */
async function AdminTranscription_retryTrack(request, options) {
	return request("POST", "/admin/transcriptions/tracks/{trackId}/retry", {
		params: { trackId: String(options.trackId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminTranscriptionController(request) {
	return {
		getMeetingTracks: (options) => AdminTranscription_getMeetingTracks(request, options),
		getMeetings: (options) => AdminTranscription_getMeetings(request, options),
		getOverview: (options) => AdminTranscription_getOverview(request, options),
		reconcileStuckTracks: (options) => AdminTranscription_reconcileStuckTracks(request, options),
		retryAllFailed: (options) => AdminTranscription_retryAllFailed(request, options),
		retryTrack: (options) => AdminTranscription_retryTrack(request, options)
	};
}
//#endregion
//#region src/admin-user-meetings/index.ts
/** Get the facts of one meeting a user is part of (no summary or transcript text) */
async function AdminUserMeetings_getUserMeeting(request, options) {
	return request("GET", "/admin/users/{userId}/meetings/{meetingId}", {
		params: {
			userId: String(options.userId),
			meetingId: String(options.meetingId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the meetings a user is part of, newest first */
async function AdminUserMeetings_listUserMeetings(request, options) {
	const params = { userId: String(options.userId) };
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	return request("GET", "/admin/users/{userId}/meetings", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAdminUserMeetingsController(request) {
	return {
		getUserMeeting: (options) => AdminUserMeetings_getUserMeeting(request, options),
		listUserMeetings: (options) => AdminUserMeetings_listUserMeetings(request, options)
	};
}
//#endregion
//#region src/agenda/index.ts
/** Meetings, bookings, inspections, CRM next steps and planned orders in one time-sorted list 🗓️ */
async function Agenda_list(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.kinds !== void 0) query["kinds"] = options.query.kinds;
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.userIds !== void 0) query["userIds"] = options.query.userIds;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/agenda", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAgendaController(request) {
	return { list: (options) => Agenda_list(request, options) };
}
//#endregion
//#region src/agent/index.ts
async function Agent_getSttConfig(request, options) {
	return request("POST", "/agent/stt-config", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Agent_reportFailure(request, options) {
	return request("POST", "/agent/report-failure", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Agent_shouldTranscribe(request, options) {
	return request("POST", "/agent/should-transcribe", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Agent_submitAudioTracks(request, options) {
	return request("POST", "/agent/submit-audio-tracks", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAgentController(request) {
	return {
		getSttConfig: (options) => Agent_getSttConfig(request, options),
		reportFailure: (options) => Agent_reportFailure(request, options),
		shouldTranscribe: (options) => Agent_shouldTranscribe(request, options),
		submitAudioTracks: (options) => Agent_submitAudioTracks(request, options)
	};
}
//#endregion
//#region src/agent-run/index.ts
async function AgentRun_retry(request, options) {
	return request("POST", "/agent-runs/{runId}/retry", {
		params: { runId: String(options.runId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AgentRun_successor(request, options) {
	return request("GET", "/agent-runs/{runId}/successor", {
		params: { runId: String(options.runId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAgentRunController(request) {
	return {
		retry: (options) => AgentRun_retry(request, options),
		successor: (options) => AgentRun_successor(request, options)
	};
}
//#endregion
//#region src/agent-run-sse/index.ts
async function AgentRunSse_stream(request, options) {
	const params = { runId: String(options.runId) };
	const query = {};
	if (options.query?.after !== void 0) query["after"] = options.query.after;
	const result = await request("GET", "/agent-runs/{runId}/stream", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
function createAgentRunSseController(request) {
	return { stream: (options) => AgentRunSse_stream(request, options) };
}
//#endregion
//#region src/agreement/index.ts
/** Archive a published agreement. */
async function Agreement_archive(request, options) {
	return request("POST", "/member-portal/manage/agreements/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a new agreement (status defaults to DRAFT).
Accepts multipart/form-data with optional `contractFile` and `supportingDocs` fields. */
async function Agreement_create(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/member-portal/manage/agreements", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all agreements (all statuses), with optional search/status/supplierId/category filter. */
async function Agreement_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.supplierId !== void 0) query["supplierId"] = options.query.supplierId;
	if (options.query?.group !== void 0) query["group"] = options.query.group;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	return request("GET", "/member-portal/manage/agreements", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single agreement by ID. */
async function Agreement_findOne(request, options) {
	return request("GET", "/member-portal/manage/agreements/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Distinct agreement categories for the filter picker, optionally scoped to one agreement group. */
async function Agreement_listCategories(request, options) {
	const query = {};
	if (options.query?.group !== void 0) query["group"] = options.query.group;
	return request("GET", "/member-portal/manage/agreements/categories", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Manual "notify members" action. Fires a member_portal.agreement_published notification. */
async function Agreement_notify(request, options) {
	return request("POST", "/member-portal/manage/agreements/{id}/notify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Render the branded notification email + resolved recipient count for
a given audience, so the authoring UI can preview before sending. */
async function Agreement_notifyPreview(request, options) {
	return request("POST", "/member-portal/manage/agreements/{id}/notify-preview", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Transition agreement from DRAFT or SCHEDULED → PUBLISHED.
Stamps publishedAt + publishedByUserId. Fires `member_portal.agreement_published` notification. */
async function Agreement_publish(request, options) {
	return request("POST", "/member-portal/manage/agreements/{id}/publish", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete an agreement. */
async function Agreement_remove(request, options) {
	return request("DELETE", "/member-portal/manage/agreements/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove the persisted contract file from an agreement. */
async function Agreement_removeContract(request, options) {
	return request("DELETE", "/member-portal/manage/agreements/{id}/contract", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove a single persisted supporting document from an agreement.
Body must include `url` matching an existing entry in supportingDocs. */
async function Agreement_removeSupportingDoc(request, options) {
	return request("DELETE", "/member-portal/manage/agreements/{id}/supporting-docs", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Schedule the agreement for future publication.
Body must include `scheduledAt` (ISO 8601 date-time string). */
async function Agreement_schedule(request, options) {
	return request("POST", "/member-portal/manage/agreements/{id}/schedule", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an existing agreement.
Accepts multipart/form-data with optional `contractFile` and `supportingDocs` fields. */
async function Agreement_update(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PUT", "/member-portal/manage/agreements/{id}", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Chain-parent oversight: member companies grouped with the agreements they marked as in use. */
async function Agreement_usageOverview(request, options) {
	return request("GET", "/member-portal/manage/agreements/usage-overview", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAgreementController(request) {
	return {
		archive: (options) => Agreement_archive(request, options),
		create: (options) => Agreement_create(request, options),
		findAll: (options) => Agreement_findAll(request, options),
		findOne: (options) => Agreement_findOne(request, options),
		listCategories: (options) => Agreement_listCategories(request, options),
		notify: (options) => Agreement_notify(request, options),
		notifyPreview: (options) => Agreement_notifyPreview(request, options),
		publish: (options) => Agreement_publish(request, options),
		remove: (options) => Agreement_remove(request, options),
		removeContract: (options) => Agreement_removeContract(request, options),
		removeSupportingDoc: (options) => Agreement_removeSupportingDoc(request, options),
		schedule: (options) => Agreement_schedule(request, options),
		update: (options) => Agreement_update(request, options),
		usageOverview: (options) => Agreement_usageOverview(request, options)
	};
}
//#endregion
//#region src/agreement-viewer/index.ts
/** List published agreements visible to the requesting member company.
Supports filter by supplierId/category and sort by discountDesc or validUntilAsc.
Each result includes a derived `expiresSoon` boolean. */
async function AgreementViewer_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.supplierId !== void 0) query["supplierId"] = options.query.supplierId;
	if (options.query?.group !== void 0) query["group"] = options.query.group;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	return request("GET", "/member-portal/agreements", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a published agreement detail by ID (404 if not visible or not published).

Response shape:
- All agreement fields
- `expiresSoon`: derived from validUntil + expiryReminderDays
- `supplier`: supplier summary (id, name, slug, logoUrl, websiteUrl, categories)
- `contacts`: linked SUPPLIER-type contacts
(id, name, role, email, phone, photoUrl, productArea) */
async function AgreementViewer_findOne(request, options) {
	return request("GET", "/member-portal/agreements/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Mark that the requesting member company uses this agreement. */
async function AgreementViewer_markUsage(request, options) {
	return request("POST", "/member-portal/agreements/{id}/use", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Clear the "uses this agreement" mark for the requesting member company. */
async function AgreementViewer_unmarkUsage(request, options) {
	return request("DELETE", "/member-portal/agreements/{id}/use", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAgreementViewerController(request) {
	return {
		findAll: (options) => AgreementViewer_findAll(request, options),
		findOne: (options) => AgreementViewer_findOne(request, options),
		markUsage: (options) => AgreementViewer_markUsage(request, options),
		unmarkUsage: (options) => AgreementViewer_unmarkUsage(request, options)
	};
}
//#endregion
//#region src/ai-ops/index.ts
async function AiOps_getCost(request, options) {
	const query = {};
	if (options.query?.window !== void 0) query["window"] = options.query.window;
	return request("GET", "/admin/ai-ops/cost", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read an inspection's AI outputs */
async function AiOps_getInspectionOutputs(request, options) {
	return request("GET", "/admin/ai-ops/inspections/{inspectionId}/outputs", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read what the parser extracted from an uploaded invoice */
async function AiOps_getInvoiceParse(request, options) {
	return request("GET", "/admin/ai-ops/invoices/{invoiceId}/parse", {
		params: { invoiceId: String(options.invoiceId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_getMeetingPipeline(request, options) {
	return request("GET", "/admin/ai-ops/meetings/{meetingId}/pipeline", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read a meeting's AI summary versions */
async function AiOps_getMeetingSummaries(request, options) {
	return request("GET", "/admin/ai-ops/meetings/{meetingId}/summaries", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read a page of a meeting's transcript */
async function AiOps_getMeetingTranscript(request, options) {
	const params = { meetingId: String(options.meetingId) };
	const query = {};
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/admin/ai-ops/meetings/{meetingId}/transcript", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_getProviderChain(request, options) {
	return request("GET", "/admin/ai-ops/provider-chain", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_getRun(request, options) {
	return request("GET", "/admin/ai-ops/runs/{runId}", {
		params: { runId: String(options.runId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_getSummary(request, options) {
	const query = {};
	if (options.query?.window !== void 0) query["window"] = options.query.window;
	return request("GET", "/admin/ai-ops/summary", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_listMeetingsAtRisk(request, options) {
	const query = {};
	if (options.query?.window !== void 0) query["window"] = options.query.window;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/admin/ai-ops/meetings-at-risk", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_listRuns(request, options) {
	const query = {};
	if (options.query?.window !== void 0) query["window"] = options.query.window;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.failureClass !== void 0) query["failureClass"] = options.query.failureClass;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	return request("GET", "/admin/ai-ops/runs", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Find meetings a person was in around a time */
async function AiOps_lookupMeetings(request, options) {
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	if (options.query?.at !== void 0) query["at"] = options.query.at;
	if (options.query?.windowHours !== void 0) query["windowHours"] = options.query.windowHours;
	return request("GET", "/admin/ai-ops/meetings/lookup", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function AiOps_rebuildMeetingFromStorage(request, options) {
	return request("POST", "/admin/ai-ops/meetings/{meetingId}/rebuild-from-storage", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reset a stuck meeting's repair attempts and run the repair job on it */
async function AiOps_repairMeeting(request, options) {
	return request("POST", "/admin/ai-ops/meetings/{meetingId}/repair", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Queue a summary for a meeting whose transcript finished but has no summary */
async function AiOps_summarizeMeeting(request, options) {
	return request("POST", "/admin/ai-ops/meetings/{meetingId}/summarize", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAiOpsController(request) {
	return {
		getCost: (options) => AiOps_getCost(request, options),
		getInspectionOutputs: (options) => AiOps_getInspectionOutputs(request, options),
		getInvoiceParse: (options) => AiOps_getInvoiceParse(request, options),
		getMeetingPipeline: (options) => AiOps_getMeetingPipeline(request, options),
		getMeetingSummaries: (options) => AiOps_getMeetingSummaries(request, options),
		getMeetingTranscript: (options) => AiOps_getMeetingTranscript(request, options),
		getProviderChain: (options) => AiOps_getProviderChain(request, options),
		getRun: (options) => AiOps_getRun(request, options),
		getSummary: (options) => AiOps_getSummary(request, options),
		listMeetingsAtRisk: (options) => AiOps_listMeetingsAtRisk(request, options),
		listRuns: (options) => AiOps_listRuns(request, options),
		lookupMeetings: (options) => AiOps_lookupMeetings(request, options),
		rebuildMeetingFromStorage: (options) => AiOps_rebuildMeetingFromStorage(request, options),
		repairMeeting: (options) => AiOps_repairMeeting(request, options),
		summarizeMeeting: (options) => AiOps_summarizeMeeting(request, options)
	};
}
//#endregion
//#region src/analytics/index.ts
/** Get platform overview analytics */
async function Analytics_getAnalyticsOverview(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	return request("GET", "/admin/analytics/overview", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get chain member feature usage ranking */
async function Analytics_getChainFeatureUsage(request, options) {
	const params = { targetCompanyId: String(options.targetCompanyId) };
	const query = {};
	if (options.query?.days !== void 0) query["days"] = options.query.days;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/analytics/chains/{targetCompanyId}/feature-usage", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get company activity analytics */
async function Analytics_getCompanyActivity(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	if (options.query?.riskLevel !== void 0) query["riskLevel"] = options.query.riskLevel;
	return request("GET", "/admin/analytics/companies", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get company detail analytics */
async function Analytics_getCompanyDetail(request, options) {
	const params = { targetCompanyId: String(options.targetCompanyId) };
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/analytics/companies/{targetCompanyId}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get company feature usage split by module */
async function Analytics_getCompanyFeatureUsage(request, options) {
	const params = { targetCompanyId: String(options.targetCompanyId) };
	const query = {};
	if (options.query?.days !== void 0) query["days"] = options.query.days;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/admin/analytics/companies/{targetCompanyId}/feature-usage", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get platform feature adoption */
async function Analytics_getFeatureAdoption(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	return request("GET", "/admin/analytics/features", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get leaderboard analytics */
async function Analytics_getLeaderboard(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/admin/analytics/leaderboard", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get MCP adoption summary */
async function Analytics_getMcpAdoption(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	return request("GET", "/admin/analytics/mcp-adoption", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List companies by MCP usage */
async function Analytics_getMcpAdoptionCompanies(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	return request("GET", "/admin/analytics/mcp-adoption/companies", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get mobile app adoption summary */
async function Analytics_getMobileAdoption(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	return request("GET", "/admin/analytics/mobile-adoption", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List users by mobile app usage */
async function Analytics_getMobileAdoptionUsers(request, options) {
	const query = {};
	if (options.query?.period !== void 0) query["period"] = options.query.period;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	return request("GET", "/admin/analytics/mobile-adoption/users", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAnalyticsController(request) {
	return {
		getAnalyticsOverview: (options) => Analytics_getAnalyticsOverview(request, options),
		getChainFeatureUsage: (options) => Analytics_getChainFeatureUsage(request, options),
		getCompanyActivity: (options) => Analytics_getCompanyActivity(request, options),
		getCompanyDetail: (options) => Analytics_getCompanyDetail(request, options),
		getCompanyFeatureUsage: (options) => Analytics_getCompanyFeatureUsage(request, options),
		getFeatureAdoption: (options) => Analytics_getFeatureAdoption(request, options),
		getLeaderboard: (options) => Analytics_getLeaderboard(request, options),
		getMcpAdoption: (options) => Analytics_getMcpAdoption(request, options),
		getMcpAdoptionCompanies: (options) => Analytics_getMcpAdoptionCompanies(request, options),
		getMobileAdoption: (options) => Analytics_getMobileAdoption(request, options),
		getMobileAdoptionUsers: (options) => Analytics_getMobileAdoptionUsers(request, options)
	};
}
//#endregion
//#region src/announcement/index.ts
async function Announcement_archive(request, options) {
	return request("POST", "/announcements/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_create(request, options) {
	return request("POST", "/announcements", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	return request("GET", "/announcements", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_findOne(request, options) {
	return request("GET", "/announcements/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_getStats(request, options) {
	return request("GET", "/announcements/{id}/stats", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_notify(request, options) {
	return request("POST", "/announcements/{id}/notify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_notifyPreview(request, options) {
	return request("POST", "/announcements/{id}/notify-preview", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_publish(request, options) {
	return request("POST", "/announcements/{id}/publish", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Announcement_update(request, options) {
	return request("PATCH", "/announcements/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAnnouncementController(request) {
	return {
		archive: (options) => Announcement_archive(request, options),
		create: (options) => Announcement_create(request, options),
		findAll: (options) => Announcement_findAll(request, options),
		findOne: (options) => Announcement_findOne(request, options),
		getStats: (options) => Announcement_getStats(request, options),
		notify: (options) => Announcement_notify(request, options),
		notifyPreview: (options) => Announcement_notifyPreview(request, options),
		publish: (options) => Announcement_publish(request, options),
		update: (options) => Announcement_update(request, options)
	};
}
//#endregion
//#region src/article/index.ts
/** Get the chain's about page (or null if it has never been authored). */
async function Article_getCurrent(request, options) {
	return request("GET", "/member-portal/manage/about-page", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Send a manual "notify members" push for the about page. */
async function Article_notify(request, options) {
	return request("POST", "/member-portal/manage/about-page/notify", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Publish the about page (DRAFT → PUBLISHED). */
async function Article_publish(request, options) {
	return request("POST", "/member-portal/manage/about-page/publish", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create or update the chain's about page (upsert; multipart).

Accepts a `file` field for the cover image and zero or more `attachments`
files to append. */
async function Article_upsert(request, options) {
	return request("PUT", "/member-portal/manage/about-page", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createArticleController(request) {
	return {
		getCurrent: (options) => Article_getCurrent(request, options),
		notify: (options) => Article_notify(request, options),
		publish: (options) => Article_publish(request, options),
		upsert: (options) => Article_upsert(request, options)
	};
}
//#endregion
//#region src/article-viewer/index.ts
/** Get the published about page for the member, or null if none. */
async function ArticleViewer_getCurrent(request, options) {
	return request("GET", "/member-portal/about-page", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createArticleViewerController(request) {
	return { getCurrent: (options) => ArticleViewer_getCurrent(request, options) };
}
//#endregion
//#region src/audit/index.ts
async function Audit_getRetention(request, options) {
	return request("GET", "/audit/retention", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Audit_list(request, options) {
	const query = {};
	if (options.query?.actorId !== void 0) query["actorId"] = options.query.actorId;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.targetType !== void 0) query["targetType"] = options.query.targetType;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/audit", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Audit_timeline(request, options) {
	const params = {
		targetType: String(options.targetType),
		targetId: String(options.targetId)
	};
	const query = {};
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/audit/timeline/{targetType}/{targetId}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAuditController(request) {
	return {
		getRetention: (options) => Audit_getRetention(request, options),
		list: (options) => Audit_list(request, options),
		timeline: (options) => Audit_timeline(request, options)
	};
}
//#endregion
//#region src/auth/index.ts
async function Auth_acceptInvite(request, options) {
	const query = {};
	if (options.query?.token !== void 0) query["token"] = options.query.token;
	return request("PUT", "/auth/accept-invite", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Auth_getInvitations(request, options) {
	return request("GET", "/auth/invitations", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Auth_previewInvite(request, options) {
	const query = {};
	if (options.query?.token !== void 0) query["token"] = options.query.token;
	return request("GET", "/auth/invite-preview", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Auth_previewNewMemberInvite(request, options) {
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	if (options.query?.companyID !== void 0) query["companyID"] = options.query.companyID;
	return request("GET", "/auth/new-member-invite-preview", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* The pre-ADR-0046 create-then-verify signup. It stays alive ONLY for installed mobile builds that
* shipped before the pending-signup endpoints existed and cannot be updated over the air fast
* enough; new clients use `POST /auth/signup/*`, which proves the mailbox before any User exists.
*
* A User created here has no `mailboxProvenAt`, so it can never be automatically linked to a social
* identity — the address-squatting hole this route leaves open is closed at the link, not here.
* Remove the route once its traffic reaches zero.
*/
async function Auth_register(request, options) {
	return request("POST", "/auth/register", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Auth_registerByCompanyID(request, options) {
	return request("POST", "/auth/register-by-company", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createAuthController(request) {
	return {
		acceptInvite: (options) => Auth_acceptInvite(request, options),
		getInvitations: (options) => Auth_getInvitations(request, options),
		previewInvite: (options) => Auth_previewInvite(request, options),
		previewNewMemberInvite: (options) => Auth_previewNewMemberInvite(request, options),
		register: (options) => Auth_register(request, options),
		registerByCompanyID: (options) => Auth_registerByCompanyID(request, options)
	};
}
//#endregion
//#region src/blackout-date/index.ts
/** Create blackout date(s) from a date range */
async function BlackoutDate_create(request, options) {
	return request("POST", "/admin/bookings/blackout-dates", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a blackout date */
async function BlackoutDate_delete(request, options) {
	return request("DELETE", "/admin/bookings/blackout-dates/{blackoutDateId}", {
		params: { blackoutDateId: String(options.blackoutDateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List blackout dates for a company */
async function BlackoutDate_list(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/admin/bookings/blackout-dates", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createBlackoutDateController(request) {
	return {
		create: (options) => BlackoutDate_create(request, options),
		delete: (options) => BlackoutDate_delete(request, options),
		list: (options) => BlackoutDate_list(request, options)
	};
}
//#endregion
//#region src/booking-api/index.ts
/**
* Cancel a booking
*
* Cancels the booking and its meeting, frees the assigned user's calendar and emails the client, with the
* reason when one is given. Cancelling twice is refused with 400. An unknown booking returns 404.
*/
async function BookingApi_cancelBooking(request, options) {
	return request("POST", "/bookings/{bookingId}/cancel", {
		params: { bookingId: String(options.bookingId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a booking
*
* Books an availability window on a day for `client`, exactly as the public booking page does: the
* meeting is created with the window's assigned user, the client gets the confirmation email and reminders,
* and a CRM lead opens when the service routes to a pipeline. Answer the service's own form fields in
* `formData`. No captcha is needed here, so call it from your server, never from a browser.
* Refused with 400 when the window is already taken, starts inside the lead time, does not belong
* to the service, or a required field has no answer.
*/
async function BookingApi_createBooking(request, options) {
	return request("POST", "/bookings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a booking
*
* Returns one booking: when it is, which service, who is assigned to it, the client's details with the
* CRM contact they were matched to, and their answers to the service's own form fields. A booking from
* another company returns 404.
*/
async function BookingApi_getBooking(request, options) {
	return request("GET", "/bookings/{bookingId}", {
		params: { bookingId: String(options.bookingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a service's free windows for a day
*
* Returns the availability windows still free on the given day: windows already booked, inside the
* lead time, on a blackout date, or clashing with the assigned user's connected calendar are left out. Send the
* `id` of the chosen window as `availabilityId` when creating the booking. A service from another
* company returns 404.
*/
async function BookingApi_getBookingServiceAvailability(request, options) {
	const params = { serviceId: String(options.serviceId) };
	const query = {};
	if (options.query?.date !== void 0) query["date"] = options.query.date;
	return request("GET", "/bookings/services/{serviceId}/availability", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a service's working days
*
* Returns the weekdays the service has at least one availability window on, so a date picker can grey
* out the rest. A service from another company returns 404.
*/
async function BookingApi_listBookingServiceWorkingDays(request, options) {
	return request("GET", "/bookings/services/{serviceId}/working-days", {
		params: { serviceId: String(options.serviceId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List booking services
*
* Returns the company's active booking services with their form fields and weekly availability windows,
* along with the lead time and the days the company has closed. This is the first call when rendering
* a booking flow of your own; follow it with the availability endpoint for the day the client picks.
*/
async function BookingApi_listBookingServices(request, options) {
	return request("GET", "/bookings/services", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List bookings
*
* Lists the company's bookings, latest start first, 25 to a page. Narrow it with `from` and `to`,
* a `serviceId`, the `assignedUserId` of the company user assigned to them or a `status`. Without the
* `bookings.read.all` permission the list holds only bookings assigned to the caller; an API key sees
* every booking.
*/
async function BookingApi_listBookings(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.serviceId !== void 0) query["serviceId"] = options.query.serviceId;
	if (options.query?.assignedUserId !== void 0) query["assignedUserId"] = options.query.assignedUserId;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	return request("GET", "/bookings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Reschedule a booking
*
* Moves the booking to another availability window and day. The assigned user and the client are told of the
* new time, and the assigned user's calendar events follow. A cancelled booking cannot be moved. An unknown booking returns 404.
*/
async function BookingApi_rescheduleBooking(request, options) {
	return request("PATCH", "/bookings/{bookingId}/reschedule", {
		params: { bookingId: String(options.bookingId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createBookingApiController(request) {
	return {
		cancelBooking: (options) => BookingApi_cancelBooking(request, options),
		createBooking: (options) => BookingApi_createBooking(request, options),
		getBooking: (options) => BookingApi_getBooking(request, options),
		getBookingServiceAvailability: (options) => BookingApi_getBookingServiceAvailability(request, options),
		listBookingServiceWorkingDays: (options) => BookingApi_listBookingServiceWorkingDays(request, options),
		listBookingServices: (options) => BookingApi_listBookingServices(request, options),
		listBookings: (options) => BookingApi_listBookings(request, options),
		rescheduleBooking: (options) => BookingApi_rescheduleBooking(request, options)
	};
}
//#endregion
//#region src/brand/index.ts
/** Resolve the Brand for the caller's company

The authenticated read of the resolver seam. CompanyGuard rejects a caller who is not a member of
the addressed company (403), so a caller can only surface their own accountable-company brand.
Live chrome omits `servicerCompanyId`: the chrome resolves through the chain (a member wears its
parent's brand); a chain-sourced surface passes the servicing member for the "Serviced by" mark. */
async function Brand_resolveBrand(request, options) {
	const query = {};
	if (options.query?.servicerCompanyId !== void 0) query["servicerCompanyId"] = options.query.servicerCompanyId;
	return request("GET", "/brand/resolve", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createBrandController(request) {
	return { resolveBrand: (options) => Brand_resolveBrand(request, options) };
}
//#endregion
//#region src/branding/index.ts
/** Archive a published branding asset. */
async function Branding_archive(request, options) {
	return request("POST", "/member-portal/manage/branding/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload a new branding asset (status defaults to DRAFT).
Accepts multipart/form-data. The `file` field is required.
Allowed extensions: AI, EPS, PNG, JPG, JPEG, PDF, INDD, DOCX. Max 100 MB.
Image types (PNG, JPG) generate a previewUrl; binary types do not. */
async function Branding_create(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/member-portal/manage/branding", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all branding assets (all statuses), with optional search/status/section filter. */
async function Branding_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.section !== void 0) query["section"] = options.query.section;
	return request("GET", "/member-portal/manage/branding", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single branding asset by ID. */
async function Branding_findOne(request, options) {
	return request("GET", "/member-portal/manage/branding/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Send a manual "notify members" push for this branding asset.
Best-effort: swallows missing notification-type errors until Phase 4. */
async function Branding_notify(request, options) {
	return request("POST", "/member-portal/manage/branding/{id}/notify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Transition a branding asset from DRAFT or SCHEDULED → PUBLISHED.
Stamps publishedAt/publishedByUserId and fires the publish notification. */
async function Branding_publish(request, options) {
	return request("POST", "/member-portal/manage/branding/{id}/publish", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a branding asset (sets deletedAt; never null). */
async function Branding_remove(request, options) {
	return request("DELETE", "/member-portal/manage/branding/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Schedule a branding asset for future publication.
Body must include `scheduledAt` (ISO 8601 date-time string). */
async function Branding_schedule(request, options) {
	return request("POST", "/member-portal/manage/branding/{id}/schedule", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update branding asset metadata and optionally replace the file.

When a `file` is provided, this replaces the stored binary on the SAME
record id (stable id = stable download link). The old binary is left in
storage. Without a file, only the metadata fields in the body are updated. */
async function Branding_update(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PUT", "/member-portal/manage/branding/{id}", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createBrandingController(request) {
	return {
		archive: (options) => Branding_archive(request, options),
		create: (options) => Branding_create(request, options),
		findAll: (options) => Branding_findAll(request, options),
		findOne: (options) => Branding_findOne(request, options),
		notify: (options) => Branding_notify(request, options),
		publish: (options) => Branding_publish(request, options),
		remove: (options) => Branding_remove(request, options),
		schedule: (options) => Branding_schedule(request, options),
		update: (options) => Branding_update(request, options)
	};
}
//#endregion
//#region src/branding-viewer/index.ts
/** List published branding assets visible to the requesting member company.
Filterable by section and legacy flag. */
async function BrandingViewer_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.section !== void 0) query["section"] = options.query.section;
	if (options.query?.legacy !== void 0) query["legacy"] = options.query.legacy;
	return request("GET", "/member-portal/branding", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single published branding asset by ID.
Returns 404 if not found, not published, or not visible to this member. */
async function BrandingViewer_findOne(request, options) {
	return request("GET", "/member-portal/branding/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createBrandingViewerController(request) {
	return {
		findAll: (options) => BrandingViewer_findAll(request, options),
		findOne: (options) => BrandingViewer_findOne(request, options)
	};
}
//#endregion
//#region src/calendar/index.ts
async function Calendar_deleteCalendar(request, options) {
	return request("DELETE", "/calendars/calendars/{calendarId}", {
		params: { calendarId: String(options.calendarId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List the caller's connected calendars
*
* Returns the Google and Outlook calendars the caller connected in this company. With an API key, the caller is the member who created the key; other members' calendars are never returned. An empty list means no calendar is connected.
*/
async function Calendar_getCalendars(request, options) {
	return request("GET", "/calendars/calendars", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Calendar_getMicrosoftAdminConsentUrl(request, options) {
	return request("GET", "/calendars/microsoft-admin-consent-url", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Calendar_getRedirectURL(request, options) {
	return request("GET", "/calendars/redirect-uri/{provider}", {
		params: { provider: String(options.provider) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Calendar_handleAdminConsentCallback(request, options) {
	const query = {};
	if (options.query?.state !== void 0) query["state"] = options.query.state;
	if (options.query?.admin_consent !== void 0) query["admin_consent"] = options.query.admin_consent;
	if (options.query?.tenant !== void 0) query["tenant"] = options.query.tenant;
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.error !== void 0) query["error"] = options.query.error;
	if (options.query?.error_description !== void 0) query["error_description"] = options.query.error_description;
	return request("GET", "/calendars/admin-consent-callback", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Calendar_handleRedirect(request, options) {
	const query = {};
	if (options.query?.code !== void 0) query["code"] = options.query.code;
	if (options.query?.state !== void 0) query["state"] = options.query.state;
	return request("GET", "/calendars/callback", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update sync busy times setting for a calendar */
async function Calendar_updateSyncSettings(request, options) {
	return request("PATCH", "/calendars/calendars/{calendarId}/sync-settings", {
		params: { calendarId: String(options.calendarId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCalendarController(request) {
	return {
		deleteCalendar: (options) => Calendar_deleteCalendar(request, options),
		getCalendars: (options) => Calendar_getCalendars(request, options),
		getMicrosoftAdminConsentUrl: (options) => Calendar_getMicrosoftAdminConsentUrl(request, options),
		getRedirectURL: (options) => Calendar_getRedirectURL(request, options),
		handleAdminConsentCallback: (options) => Calendar_handleAdminConsentCallback(request, options),
		handleRedirect: (options) => Calendar_handleRedirect(request, options),
		updateSyncSettings: (options) => Calendar_updateSyncSettings(request, options)
	};
}
//#endregion
//#region src/campaign/index.ts
/** Upload asset files for a campaign.
Accepts multipart/form-data with up to 10 files in the `files` field.
New files are appended to the existing `assets` array. */
async function Campaign_addAssets(request, options) {
	return request("POST", "/member-portal/manage/campaigns/{id}/assets", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Archive a published campaign. */
async function Campaign_archive(request, options) {
	return request("POST", "/member-portal/manage/campaigns/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a new campaign (status defaults to DRAFT). */
async function Campaign_create(request, options) {
	return request("POST", "/member-portal/manage/campaigns", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all campaigns (all statuses), with optional search/status filter. */
async function Campaign_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	return request("GET", "/member-portal/manage/campaigns", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single campaign by ID. */
async function Campaign_findOne(request, options) {
	return request("GET", "/member-portal/manage/campaigns/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Send a manual "notify members" push for this campaign.
Dispatches a notification to all audience-resolved member users. */
async function Campaign_notify(request, options) {
	return request("POST", "/member-portal/manage/campaigns/{id}/notify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Render the branded notification email + resolved recipient count for
a given audience, so the authoring UI can preview before sending. */
async function Campaign_notifyPreview(request, options) {
	return request("POST", "/member-portal/manage/campaigns/{id}/notify-preview", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Transition campaign from DRAFT or SCHEDULED → PUBLISHED.
Stamps publishedAt, publishedByUserId, and fires the publish notification. */
async function Campaign_publish(request, options) {
	return request("POST", "/member-portal/manage/campaigns/{id}/publish", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a campaign. */
async function Campaign_remove(request, options) {
	return request("DELETE", "/member-portal/manage/campaigns/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Schedule the campaign for future publication.
Body must include `scheduledAt` (ISO 8601 date-time string). */
async function Campaign_schedule(request, options) {
	return request("POST", "/member-portal/manage/campaigns/{id}/schedule", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an existing campaign. */
async function Campaign_update(request, options) {
	return request("PUT", "/member-portal/manage/campaigns/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCampaignController(request) {
	return {
		addAssets: (options) => Campaign_addAssets(request, options),
		archive: (options) => Campaign_archive(request, options),
		create: (options) => Campaign_create(request, options),
		findAll: (options) => Campaign_findAll(request, options),
		findOne: (options) => Campaign_findOne(request, options),
		notify: (options) => Campaign_notify(request, options),
		notifyPreview: (options) => Campaign_notifyPreview(request, options),
		publish: (options) => Campaign_publish(request, options),
		remove: (options) => Campaign_remove(request, options),
		schedule: (options) => Campaign_schedule(request, options),
		update: (options) => Campaign_update(request, options)
	};
}
//#endregion
//#region src/campaign-viewer/index.ts
/** List published campaigns visible to the requesting member company.
Results include `derivedState` and are sorted active → upcoming → past. */
async function CampaignViewer_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/member-portal/campaigns", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single published campaign by ID (404 if not visible or not published).
Includes `derivedState` and `isSubscribed` for the requesting user. */
async function CampaignViewer_findOne(request, options) {
	return request("GET", "/member-portal/campaigns/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Subscribe the current user to update notifications for this campaign. */
async function CampaignViewer_subscribe(request, options) {
	return request("POST", "/member-portal/campaigns/{id}/subscribe", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Unsubscribe the current user from update notifications for this campaign. */
async function CampaignViewer_unsubscribe(request, options) {
	return request("DELETE", "/member-portal/campaigns/{id}/subscribe", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCampaignViewerController(request) {
	return {
		findAll: (options) => CampaignViewer_findAll(request, options),
		findOne: (options) => CampaignViewer_findOne(request, options),
		subscribe: (options) => CampaignViewer_subscribe(request, options),
		unsubscribe: (options) => CampaignViewer_unsubscribe(request, options)
	};
}
//#endregion
//#region src/catalog/index.ts
async function Catalog_archive(request, options) {
	return request("POST", "/crm/catalog/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_bulkArchive(request, options) {
	return request("POST", "/crm/catalog/bulk-archive", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_bulkRestore(request, options) {
	return request("POST", "/crm/catalog/bulk-restore", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_create(request, options) {
	return request("POST", "/crm/catalog", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_list(request, options) {
	return request("GET", "/crm/catalog", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_removeImage(request, options) {
	return request("DELETE", "/crm/catalog/{id}/image", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_restore(request, options) {
	return request("POST", "/crm/catalog/{id}/restore", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_update(request, options) {
	return request("PATCH", "/crm/catalog/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Catalog_uploadImage(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/crm/catalog/{id}/image", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCatalogController(request) {
	return {
		archive: (options) => Catalog_archive(request, options),
		bulkArchive: (options) => Catalog_bulkArchive(request, options),
		bulkRestore: (options) => Catalog_bulkRestore(request, options),
		create: (options) => Catalog_create(request, options),
		list: (options) => Catalog_list(request, options),
		removeImage: (options) => Catalog_removeImage(request, options),
		restore: (options) => Catalog_restore(request, options),
		update: (options) => Catalog_update(request, options),
		uploadImage: (options) => Catalog_uploadImage(request, options)
	};
}
//#endregion
//#region src/chain-analytics/index.ts
/** CSV export for any analytics tab (?tab=orders|company-rankings|revenue) */
async function ChainAnalytics_exportCsv(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.tab !== void 0) query["tab"] = options.query.tab;
	return request("GET", "/chain/analytics/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Capacity utilization per member company */
async function ChainAnalytics_getCapacity(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/capacity", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Company rankings sorted by completion rate */
async function ChainAnalytics_getCompanyRankings(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/company-rankings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Geographic distribution of orders by member region */
async function ChainAnalytics_getGeographic(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/geographic", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Order analytics with volume trend, completion rate, response time, revenue */
async function ChainAnalytics_getOrders(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/orders", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Executive overview KPIs */
async function ChainAnalytics_getOverview(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/overview", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Customer feedback ratings per bucket, scaled to 1-5 */
async function ChainAnalytics_getRatings(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/ratings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Revenue breakdown (labor/material/total) with trend and per-company split */
async function ChainAnalytics_getRevenue(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/chain/analytics/revenue", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainAnalyticsController(request) {
	return {
		exportCsv: (options) => ChainAnalytics_exportCsv(request, options),
		getCapacity: (options) => ChainAnalytics_getCapacity(request, options),
		getCompanyRankings: (options) => ChainAnalytics_getCompanyRankings(request, options),
		getGeographic: (options) => ChainAnalytics_getGeographic(request, options),
		getOrders: (options) => ChainAnalytics_getOrders(request, options),
		getOverview: (options) => ChainAnalytics_getOverview(request, options),
		getRatings: (options) => ChainAnalytics_getRatings(request, options),
		getRevenue: (options) => ChainAnalytics_getRevenue(request, options)
	};
}
//#endregion
//#region src/chain-boards/index.ts
async function ChainBoards_getBoard(request, options) {
	const query = {};
	if (options.query?.memberCompanyId !== void 0) query["memberCompanyId"] = options.query.memberCompanyId;
	if (options.query?.outcome !== void 0) query["outcome"] = options.query.outcome;
	if (options.query?.assignment !== void 0) query["assignment"] = options.query.assignment;
	return request("GET", "/crm/chain-boards/board", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainBoards_getCandidates(request, options) {
	return request("GET", "/crm/chain-boards/leads/{id}/candidates", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainBoards_getParentIncoming(request, options) {
	const query = {};
	if (options.query?.bucket !== void 0) query["bucket"] = options.query.bucket;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	return request("GET", "/crm/chain-boards/parent-incoming", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainBoards_reclaim(request, options) {
	return request("POST", "/crm/chain-boards/reclaim", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainBoards_recommendedMembers(request, options) {
	return request("POST", "/crm/chain-boards/recommended-members", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainBoards_reject(request, options) {
	return request("POST", "/crm/chain-boards/reject", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainBoards_reopen(request, options) {
	return request("POST", "/crm/chain-boards/leads/{id}/reopen", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainBoardsController(request) {
	return {
		getBoard: (options) => ChainBoards_getBoard(request, options),
		getCandidates: (options) => ChainBoards_getCandidates(request, options),
		getParentIncoming: (options) => ChainBoards_getParentIncoming(request, options),
		reclaim: (options) => ChainBoards_reclaim(request, options),
		recommendedMembers: (options) => ChainBoards_recommendedMembers(request, options),
		reject: (options) => ChainBoards_reject(request, options),
		reopen: (options) => ChainBoards_reopen(request, options)
	};
}
//#endregion
//#region src/chain/index.ts
async function Chain_createMembership(request, options) {
	return request("POST", "/chain/members", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Chain_getAggregatedCounts(request, options) {
	return request("GET", "/chain/members/aggregate", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Resolve the calling company's chain membership (parent metadata only)
*
* Available to MEMBER role and up so non-admin chain members can
* still discover whether they're part of a chain (drives `isChain` /
* `isMember` in the frontend, which controls Job Board + Member Portal
* navigation). The returned payload is limited to the parent company's
* id/name/slug + membership metadata — no member list, no chain settings.
*/
async function Chain_getChainInfo(request, options) {
	return request("GET", "/chain/chain-info", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Chain_getMembers(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.memberId !== void 0) query["memberId"] = options.query.memberId;
	return request("GET", "/chain/members", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainController(request) {
	return {
		createMembership: (options) => Chain_createMembership(request, options),
		getAggregatedCounts: (options) => Chain_getAggregatedCounts(request, options),
		getChainInfo: (options) => Chain_getChainInfo(request, options),
		getMembers: (options) => Chain_getMembers(request, options)
	};
}
//#endregion
//#region src/chain-coverage/index.ts
async function ChainCoverage_getCoverage(request, options) {
	return request("GET", "/chain/coverage", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainCoverageController(request) {
	return { getCoverage: (options) => ChainCoverage_getCoverage(request, options) };
}
//#endregion
//#region src/chain-dashboard/index.ts
/**
* Chain dashboard health
*
* Order pipeline breakdown by status and 12-week completion trend.
*/
async function ChainDashboard_getHealth(request, options) {
	return request("GET", "/chain/dashboard/health", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Chain dashboard KPIs
*
* Returns aggregated KPI cards: member counts, active orders,
* weekly completions, avg response time, capacity utilization.
*/
async function ChainDashboard_getKpis(request, options) {
	return request("GET", "/chain/dashboard/kpis", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Member-scoped KPIs for chain observer view
*
* Returns member-specific metrics (orders, active/completed
* counts, completion rate, avg response time, recent activity). Caller
* must be ADMIN of the parent chain company; returns 404 if the target
* company is not an active member of the caller's chain.
*/
async function ChainDashboard_getMemberMetrics(request, options) {
	return request("GET", "/chain/members/{memberId}/metrics", {
		params: { memberId: String(options.memberId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Chain dashboard performers
*
* Top 5 and bottom 5 member companies by completion rate.
*/
async function ChainDashboard_getPerformers(request, options) {
	return request("GET", "/chain/dashboard/performers", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainDashboardController(request) {
	return {
		getHealth: (options) => ChainDashboard_getHealth(request, options),
		getKpis: (options) => ChainDashboard_getKpis(request, options),
		getMemberMetrics: (options) => ChainDashboard_getMemberMetrics(request, options),
		getPerformers: (options) => ChainDashboard_getPerformers(request, options)
	};
}
//#endregion
//#region src/chain-distribution/index.ts
async function ChainDistribution_assign(request, options) {
	return request("POST", "/crm/chain-distribution/leads/{id}/assign", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_getConfig(request, options) {
	return request("GET", "/crm/chain-distribution/config", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_pickup(request, options) {
	return request("POST", "/crm/chain-distribution/leads/{id}/pickup", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_reassign(request, options) {
	return request("POST", "/crm/chain-distribution/leads/{id}/reassign", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_release(request, options) {
	return request("POST", "/crm/chain-distribution/leads/{id}/release", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_setMemberPause(request, options) {
	return request("PUT", "/crm/chain-distribution/config/member-pause", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_sweepLapsed(request, options) {
	return request("POST", "/crm/chain-distribution/sweep-lapsed", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_sweepMembership(request, options) {
	return request("POST", "/crm/chain-distribution/sweep-membership", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_unassign(request, options) {
	return request("POST", "/crm/chain-distribution/leads/{id}/unassign", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainDistribution_upsertConfig(request, options) {
	return request("PUT", "/crm/chain-distribution/config", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainDistributionController(request) {
	return {
		assign: (options) => ChainDistribution_assign(request, options),
		getConfig: (options) => ChainDistribution_getConfig(request, options),
		pickup: (options) => ChainDistribution_pickup(request, options),
		reassign: (options) => ChainDistribution_reassign(request, options),
		release: (options) => ChainDistribution_release(request, options),
		setMemberPause: (options) => ChainDistribution_setMemberPause(request, options),
		sweepLapsed: (options) => ChainDistribution_sweepLapsed(request, options),
		sweepMembership: (options) => ChainDistribution_sweepMembership(request, options),
		unassign: (options) => ChainDistribution_unassign(request, options),
		upsertConfig: (options) => ChainDistribution_upsertConfig(request, options)
	};
}
//#endregion
//#region src/chain-leaderboard/index.ts
async function ChainLeaderboard_getLeaderboard(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/crm/chain/leaderboard", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainLeaderboardController(request) {
	return { getLeaderboard: (options) => ChainLeaderboard_getLeaderboard(request, options) };
}
//#endregion
//#region src/chain-orders/index.ts
async function ChainOrders_cancelMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/cancel", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_completeMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/complete", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_keepMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/keep", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_linkContactMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/link-contact", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_listMessagesMember(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/orders/chain/member/{id}/messages", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_markMessagesReadMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/messages/read", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_member(request, options) {
	return request("GET", "/orders/chain/member", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_memberOne(request, options) {
	return request("GET", "/orders/chain/member/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_moveMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/move", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_parent(request, options) {
	return request("GET", "/orders/chain/parent", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_reverseMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/reverse-completion", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_sendMessageMember(request, options) {
	return request("POST", "/orders/chain/member/{id}/messages", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_updateLinesMember(request, options) {
	return request("PUT", "/orders/chain/member/{id}/lines", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function ChainOrders_updateMember(request, options) {
	return request("PATCH", "/orders/chain/member/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainOrdersController(request) {
	return {
		cancelMember: (options) => ChainOrders_cancelMember(request, options),
		completeMember: (options) => ChainOrders_completeMember(request, options),
		keepMember: (options) => ChainOrders_keepMember(request, options),
		linkContactMember: (options) => ChainOrders_linkContactMember(request, options),
		listMessagesMember: (options) => ChainOrders_listMessagesMember(request, options),
		markMessagesReadMember: (options) => ChainOrders_markMessagesReadMember(request, options),
		member: (options) => ChainOrders_member(request, options),
		memberOne: (options) => ChainOrders_memberOne(request, options),
		moveMember: (options) => ChainOrders_moveMember(request, options),
		parent: (options) => ChainOrders_parent(request, options),
		reverseMember: (options) => ChainOrders_reverseMember(request, options),
		sendMessageMember: (options) => ChainOrders_sendMessageMember(request, options),
		updateLinesMember: (options) => ChainOrders_updateLinesMember(request, options),
		updateMember: (options) => ChainOrders_updateMember(request, options)
	};
}
//#endregion
//#region src/chain-oversight/index.ts
/** Aggregate AI token usage across the chain's ACTIVE member companies */
async function ChainOversight_getOversight(request, options) {
	return request("GET", "/chain/billing/oversight", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainOversightController(request) {
	return { getOversight: (options) => ChainOversight_getOversight(request, options) };
}
//#endregion
//#region src/chain-website/index.ts
/** Autocomplete address/postcode suggestions for the search box.

Backed by Kartverket, the same source the in-app address picker uses, so a
chain website can offer typeahead without shipping a Google Maps key to the
browser. Fewer than 2 characters returns an empty list. */
async function ChainWebsite_addressSuggestionsList(request, options) {
	const params = { parentCompanyId: String(options.parentCompanyId) };
	const query = {};
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	return request("GET", "/chain-website/{parentCompanyId}/companies/address-suggestions", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the employees of a chain member company. */
async function ChainWebsite_getEmployees(request, options) {
	return request("GET", "/chain-website/{parentCompanyId}/companies/{slug}/employees", {
		params: {
			parentCompanyId: String(options.parentCompanyId),
			slug: String(options.slug)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Fetch a chain member company public profile by slug. */
async function ChainWebsite_getProfile(request, options) {
	return request("GET", "/chain-website/{parentCompanyId}/companies/{slug}", {
		params: {
			parentCompanyId: String(options.parentCompanyId),
			slug: String(options.slug)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List every ACTIVE member company of a chain. */
async function ChainWebsite_listMembers(request, options) {
	return request("GET", "/chain-website/{parentCompanyId}/companies/members", {
		params: { parentCompanyId: String(options.parentCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Search a chain's publicly-listed member companies by name, postcode or address.

`parentCompanyId` identifies the chain whose ACTIVE members are searched;
each chain website passes its own id. Passing nothing at all lists every
member.

`name` matches company names and is independent of the geocoder, so a
website with a single search box can send the visitor's term as both `name`
and `address`: name matches come back first, coverage matches after them. */
async function ChainWebsite_search(request, options) {
	const params = { parentCompanyId: String(options.parentCompanyId) };
	const query = {};
	if (options.query?.postcode !== void 0) query["postcode"] = options.query.postcode;
	if (options.query?.address !== void 0) query["address"] = options.query.address;
	if (options.query?.name !== void 0) query["name"] = options.query.name;
	if (options.query?.lat !== void 0) query["lat"] = options.query.lat;
	if (options.query?.lng !== void 0) query["lng"] = options.query.lng;
	return request("GET", "/chain-website/{parentCompanyId}/companies/search", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Submit a public quote-request inquiry. Rate-limited to 10/hr per slug per IP. */
async function ChainWebsite_submitInquiry(request, options) {
	return request("POST", "/chain-website/{parentCompanyId}/companies/{slug}/inquiries", {
		params: {
			parentCompanyId: String(options.parentCompanyId),
			slug: String(options.slug)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChainWebsiteController(request) {
	return {
		addressSuggestionsList: (options) => ChainWebsite_addressSuggestionsList(request, options),
		getEmployees: (options) => ChainWebsite_getEmployees(request, options),
		getProfile: (options) => ChainWebsite_getProfile(request, options),
		listMembers: (options) => ChainWebsite_listMembers(request, options),
		search: (options) => ChainWebsite_search(request, options),
		submitInquiry: (options) => ChainWebsite_submitInquiry(request, options)
	};
}
//#endregion
//#region src/checklist/index.ts
/**
* Attach a checklist to a meeting
*
* Links an existing checklist to a meeting.
* A checklist cannot be attached to the same meeting more than once.
*/
async function Checklist_attachChecklistToMeeting(request, options) {
	return request("POST", "/check-list/{checklistId}/attach-to-meeting/{meetingId}", {
		params: {
			meetingId: String(options.meetingId),
			checklistId: String(options.checklistId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a new checklist under a company
*
* Creates a checklist with optional checklist items
* and associates it with the given company.
*/
async function Checklist_createChecklist(request, options) {
	return request("POST", "/check-list", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete a checklist
*
* Permanently deletes a checklist and its items
* by checklist ID within the given company.
*/
async function Checklist_deleteChecklist(request, options) {
	return request("DELETE", "/check-list/{checklistId}", {
		params: { checklistId: String(options.checklistId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Detach a checklist from a meeting
*
* Removes the checklist from a meeting. Cannot detach if any checklist item is completed.
*/
async function Checklist_detachChecklistFromMeeting(request, options) {
	return request("POST", "/check-list/{meetingId}/detach-checklist", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Duplicate an existing checklist with items
*
* Creates a new checklist (copy) with all items
* duplicated under the given company.
*/
async function Checklist_duplicateChecklist(request, options) {
	return request("POST", "/check-list/duplicate/{checklistId}", {
		params: { checklistId: String(options.checklistId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a checklist by ID
*
* Retrieves a checklist (with its items and comments)
* under a specific company.
*/
async function Checklist_getChecklist(request, options) {
	return request("GET", "/check-list/{checklistId}", {
		params: { checklistId: String(options.checklistId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get all checklists assigned to a meeting
*
* Returns checklists and their items for a specific meeting, including completion status
*/
async function Checklist_getChecklistsForMeeting(request, options) {
	return request("GET", "/check-list/{meetingId}/checklists", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get all meetings assigned to a checklist
*
* Returns meetings for a specific checklist
*/
async function Checklist_getMeetingsForChecklist(request, options) {
	return request("GET", "/check-list/{checklistId}/meetings", {
		params: { checklistId: String(options.checklistId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get paginated and filtered checklists
*
* Returns a paginated list of checklists for a company.
* Supports filtering by title, description, and other query parameters.
*/
async function Checklist_getPaginatedChecklists(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.title !== void 0) query["title"] = options.query.title;
	return request("GET", "/check-list/list", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update a checklist
*
* Updates checklist details and its items by checklist ID
* within the given company.
*/
async function Checklist_updateChecklist(request, options) {
	return request("PUT", "/check-list/{checklistId}", {
		params: { checklistId: String(options.checklistId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChecklistController(request) {
	return {
		attachChecklistToMeeting: (options) => Checklist_attachChecklistToMeeting(request, options),
		createChecklist: (options) => Checklist_createChecklist(request, options),
		deleteChecklist: (options) => Checklist_deleteChecklist(request, options),
		detachChecklistFromMeeting: (options) => Checklist_detachChecklistFromMeeting(request, options),
		duplicateChecklist: (options) => Checklist_duplicateChecklist(request, options),
		getChecklist: (options) => Checklist_getChecklist(request, options),
		getChecklistsForMeeting: (options) => Checklist_getChecklistsForMeeting(request, options),
		getMeetingsForChecklist: (options) => Checklist_getMeetingsForChecklist(request, options),
		getPaginatedChecklists: (options) => Checklist_getPaginatedChecklists(request, options),
		updateChecklist: (options) => Checklist_updateChecklist(request, options)
	};
}
//#endregion
//#region src/checklist-item/index.ts
/**
* Add or update comment on a checklist item
*
* Creates or updates a single comment field for the meeting checklist item
*/
async function ChecklistItem_addComment(request, options) {
	return request("PATCH", "/checklist-item/{meetingChecklistItemId}/comment", {
		params: { meetingChecklistItemId: String(options.meetingChecklistItemId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete comment from a meeting checklist item
*
* Clears the comment field (sets it to null)
*/
async function ChecklistItem_deleteComment(request, options) {
	return request("DELETE", "/checklist-item/{meetingChecklistItemId}/comment", {
		params: { meetingChecklistItemId: String(options.meetingChecklistItemId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete a checklist item
*
* Deletes a single checklist item by ID under a company
*/
async function ChecklistItem_deleteItem(request, options) {
	return request("DELETE", "/checklist-item/{itemId}", {
		params: { itemId: String(options.itemId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Check / Uncheck a checklist item for a specific meeting
*
* Updates the completion status of a checklist item for a given meeting.
*/
async function ChecklistItem_toggleItemStatus(request, options) {
	return request("PATCH", "/checklist-item/{meetingChecklistItemId}/status", {
		params: { meetingChecklistItemId: String(options.meetingChecklistItemId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createChecklistItemController(request) {
	return {
		addComment: (options) => ChecklistItem_addComment(request, options),
		deleteComment: (options) => ChecklistItem_deleteComment(request, options),
		deleteItem: (options) => ChecklistItem_deleteItem(request, options),
		toggleItemStatus: (options) => ChecklistItem_toggleItemStatus(request, options)
	};
}
//#endregion
//#region src/client-booking/index.ts
/** Book a time slot for a specific service */
async function ClientBooking_bookSlot(request, options) {
	return request("POST", "/client/bookings/slots", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Cancel a booking slot */
async function ClientBooking_cancelBooking(request, options) {
	return request("DELETE", "/client/bookings/slots/cancel/{bookingSlotId}", {
		params: { bookingSlotId: String(options.bookingSlotId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Generate a calendar event URL (Google or Outlook) for a booking slot */
async function ClientBooking_generateCalendarUrl(request, options) {
	return request("GET", "/client/bookings/slots/{bookingSlotId}/calendar/{calendarType}", {
		params: {
			bookingSlotId: String(options.bookingSlotId),
			calendarType: String(options.calendarType)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get available time slots for a given service on a specific date */
async function ClientBooking_getServiceAvailability(request, options) {
	const params = { serviceId: String(options.serviceId) };
	const query = {};
	if (options.query?.date !== void 0) query["date"] = options.query.date;
	return request("GET", "/client/bookings/availability/{serviceId}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get all booking services and their associated intake form fields by company slug */
async function ClientBooking_getServicesWithFormFields(request, options) {
	return request("GET", "/client/bookings/company-services/{slug}", {
		params: { slug: String(options.slug) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get details of a booked slot by slot ID */
async function ClientBooking_getSlot(request, options) {
	return request("GET", "/client/bookings/slots/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get configured working days for a given service */
async function ClientBooking_getWorkingDaysByServiceId(request, options) {
	return request("GET", "/client/bookings/working-days/{serviceId}", {
		params: { serviceId: String(options.serviceId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reschedule an existing booking slot */
async function ClientBooking_rescheduleSlot(request, options) {
	return request("PATCH", "/client/bookings/slots/reschedule/{bookingSlotId}", {
		params: { bookingSlotId: String(options.bookingSlotId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Track a booking funnel analytics event */
async function ClientBooking_trackAnalyticsEvent(request, options) {
	return request("POST", "/client/bookings/analytics/track", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Verify CAPTCHA token for bot protection */
async function ClientBooking_verifyCaptcha(request, options) {
	return request("POST", "/client/bookings/verify-captcha", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createClientBookingController(request) {
	return {
		bookSlot: (options) => ClientBooking_bookSlot(request, options),
		cancelBooking: (options) => ClientBooking_cancelBooking(request, options),
		generateCalendarUrl: (options) => ClientBooking_generateCalendarUrl(request, options),
		getServiceAvailability: (options) => ClientBooking_getServiceAvailability(request, options),
		getServicesWithFormFields: (options) => ClientBooking_getServicesWithFormFields(request, options),
		getSlot: (options) => ClientBooking_getSlot(request, options),
		getWorkingDaysByServiceId: (options) => ClientBooking_getWorkingDaysByServiceId(request, options),
		rescheduleSlot: (options) => ClientBooking_rescheduleSlot(request, options),
		trackAnalyticsEvent: (options) => ClientBooking_trackAnalyticsEvent(request, options),
		verifyCaptcha: (options) => ClientBooking_verifyCaptcha(request, options)
	};
}
//#endregion
//#region src/clients/index.ts
async function Clients_createClient(request, options) {
	return request("POST", "/clients", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Clients_deleteClient(request, options) {
	return request("DELETE", "/clients/{clientID}", {
		params: { clientID: String(options.clientID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Clients_getClients(request, options) {
	const query = {};
	if (options.query?.sortDirection !== void 0) query["sortDirection"] = options.query.sortDirection;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	return request("GET", "/clients", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Clients_searchClient(request, options) {
	const query = {};
	if (options.query?.query !== void 0) query["query"] = options.query.query;
	return request("GET", "/clients/search", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Clients_updateClient(request, options) {
	return request("PATCH", "/clients/{clientID}", {
		params: { clientID: String(options.clientID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createClientsController(request) {
	return {
		createClient: (options) => Clients_createClient(request, options),
		deleteClient: (options) => Clients_deleteClient(request, options),
		getClients: (options) => Clients_getClients(request, options),
		searchClient: (options) => Clients_searchClient(request, options),
		updateClient: (options) => Clients_updateClient(request, options)
	};
}
//#endregion
//#region src/communication/index.ts
/** Attach a single file to a draft (10 MB, PDF/image/office types). */
async function Communication_addAttachment(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/member-portal/manage/communications/{id}/attachment", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Archive a sent communication. */
async function Communication_archive(request, options) {
	return request("POST", "/member-portal/manage/communications/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a draft communication. */
async function Communication_create(request, options) {
	return request("POST", "/member-portal/manage/communications", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Download one company's answer as the branded answer document. */
async function Communication_downloadAnswerPdf(request, options) {
	return request("GET", "/member-portal/manage/communications/{id}/responses/{memberCompanyId}/pdf", {
		params: {
			id: String(options.id),
			memberCompanyId: String(options.memberCompanyId)
		},
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Copy a communication into a fresh draft with its audience and roles cleared. */
async function Communication_duplicate(request, options) {
	return request("POST", "/member-portal/manage/communications/{id}/duplicate", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Download every targeted company's answer as one CSV row, one column per form field. */
async function Communication_exportResponses(request, options) {
	return request("GET", "/member-portal/manage/communications/{id}/responses/export", {
		params: { id: String(options.id) },
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List communications, filtered by status and title search, sorted newest, oldest or due soonest. */
async function Communication_findAll(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/member-portal/manage/communications", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get one communication by id. */
async function Communication_findOne(request, options) {
	return request("GET", "/member-portal/manage/communications/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** One row per targeted company with its answer, plus the summary the Overview strip reads. */
async function Communication_findResponses(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/member-portal/manage/communications/{id}/responses", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** The send log: one row per send and re-notify, newest first. */
async function Communication_listSends(request, options) {
	return request("GET", "/member-portal/manage/communications/{id}/sends", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a draft. Refuses once the communication has been sent. */
async function Communication_remove(request, options) {
	return request("DELETE", "/member-portal/manage/communications/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove the draft's attachment. */
async function Communication_removeAttachment(request, options) {
	return request("DELETE", "/member-portal/manage/communications/{id}/attachment", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Re-notify the companies that have not answered yet on freshly chosen channels. */
async function Communication_renotify(request, options) {
	return request("POST", "/member-portal/manage/communications/{id}/renotify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Send a draft: freeze the audience, roles and brand, then notify on the chosen channels. */
async function Communication_send(request, options) {
	return request("POST", "/member-portal/manage/communications/{id}/send", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Count who a given audience, role and channel choice would reach, without sending. */
async function Communication_sendPreview(request, options) {
	return request("POST", "/member-portal/manage/communications/{id}/send-preview", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a draft. Refuses with COMMUNICATION_FROZEN once the communication has been sent. */
async function Communication_update(request, options) {
	return request("PATCH", "/member-portal/manage/communications/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCommunicationController(request) {
	return {
		addAttachment: (options) => Communication_addAttachment(request, options),
		archive: (options) => Communication_archive(request, options),
		create: (options) => Communication_create(request, options),
		downloadAnswerPdf: (options) => Communication_downloadAnswerPdf(request, options),
		duplicate: (options) => Communication_duplicate(request, options),
		exportResponses: (options) => Communication_exportResponses(request, options),
		findAll: (options) => Communication_findAll(request, options),
		findOne: (options) => Communication_findOne(request, options),
		findResponses: (options) => Communication_findResponses(request, options),
		listSends: (options) => Communication_listSends(request, options),
		remove: (options) => Communication_remove(request, options),
		removeAttachment: (options) => Communication_removeAttachment(request, options),
		renotify: (options) => Communication_renotify(request, options),
		send: (options) => Communication_send(request, options),
		sendPreview: (options) => Communication_sendPreview(request, options),
		update: (options) => Communication_update(request, options)
	};
}
//#endregion
//#region src/communication-media/index.ts
async function CommunicationMedia_streamAttachment(request, options) {
	return request("GET", "/member-portal/manage/communications/{id}/attachment", {
		params: { id: String(options.id) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CommunicationMedia_streamResponseSignature(request, options) {
	return request("GET", "/member-portal/manage/communications/{id}/responses/{memberCompanyId}/signature", {
		params: {
			id: String(options.id),
			memberCompanyId: String(options.memberCompanyId)
		},
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCommunicationMediaController(request) {
	return {
		streamAttachment: (options) => CommunicationMedia_streamAttachment(request, options),
		streamResponseSignature: (options) => CommunicationMedia_streamResponseSignature(request, options)
	};
}
//#endregion
//#region src/communication-viewer/index.ts
/** List the communications this company was sent, newest first. */
async function CommunicationViewer_findAll(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/member-portal/communications", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read one communication this company was sent. */
async function CommunicationViewer_findOne(request, options) {
	return request("GET", "/member-portal/communications/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Answer on behalf of this company. Refuses with COMMUNICATION_ANSWERED once a colleague has. */
async function CommunicationViewer_submitResponse(request, options) {
	return request("POST", "/member-portal/communications/{id}/response", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCommunicationViewerController(request) {
	return {
		findAll: (options) => CommunicationViewer_findAll(request, options),
		findOne: (options) => CommunicationViewer_findOne(request, options),
		submitResponse: (options) => CommunicationViewer_submitResponse(request, options)
	};
}
//#endregion
//#region src/communication-viewer-media/index.ts
async function CommunicationViewerMedia_streamAttachment(request, options) {
	return request("GET", "/member-portal/communications/{id}/attachment", {
		params: { id: String(options.id) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CommunicationViewerMedia_streamOwnSignature(request, options) {
	return request("GET", "/member-portal/communications/{id}/response/signature", {
		params: { id: String(options.id) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCommunicationViewerMediaController(request) {
	return {
		streamAttachment: (options) => CommunicationViewerMedia_streamAttachment(request, options),
		streamOwnSignature: (options) => CommunicationViewerMedia_streamOwnSignature(request, options)
	};
}
//#endregion
//#region src/companies/index.ts
async function Companies_checkAvailability(request, options) {
	return request("GET", "/companies/check-availability/{subDomain}", {
		params: { subDomain: String(options.subDomain) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Companies_createCompany(request, options) {
	return request("POST", "/companies", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get list of connected companies */
async function Companies_getCompanies(request, options) {
	return request("GET", "/companies", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Companies_lookupRegistryIndustry(request, options) {
	const query = {};
	if (options.query?.orgNumber !== void 0) query["orgNumber"] = options.query.orgNumber;
	return request("GET", "/companies/registry/industry", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Companies_searchRegistry(request, options) {
	const query = {};
	if (options.query?.query !== void 0) query["query"] = options.query.query;
	return request("GET", "/companies/registry/search", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompaniesController(request) {
	return {
		checkAvailability: (options) => Companies_checkAvailability(request, options),
		createCompany: (options) => Companies_createCompany(request, options),
		getCompanies: (options) => Companies_getCompanies(request, options),
		lookupRegistryIndustry: (options) => Companies_lookupRegistryIndustry(request, options),
		searchRegistry: (options) => Companies_searchRegistry(request, options)
	};
}
//#endregion
//#region src/company/index.ts
/**
* Assign a per-company role to a member
*
* Binds a RoleEntity to a company user, enforcing the assignment
* safeguards server-side (400 ROLE_SELF_CHANGE / OWNER_ROLE_RESTRICTED / LAST_OWNER).
* Dual-writes the legacy enum for a default role during rollout.
*/
async function Company_assignRole(request, options) {
	return request("PATCH", "/company/assign-role", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_changeRole(request, options) {
	return request("PATCH", "/company/change-role", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Mark a getting-started step as done for the current user */
async function Company_completeMyGettingStartedStep(request, options) {
	return request("POST", "/company/me/getting-started/{step}", {
		params: { step: String(options.step) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_deleteCompany(request, options) {
	return request("DELETE", "/company", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_deleteInvitation(request, options) {
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	return request("DELETE", "/company/invites", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Hide the getting-started card on the dashboard for the current user */
async function Company_dismissMyGettingStarted(request, options) {
	return request("DELETE", "/company/me/getting-started", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get company capacity
*
* Returns the max capacity and current active order count for a company.
*/
async function Company_getCapacity(request, options) {
	return request("GET", "/company/capacity", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_getCompany(request, options) {
	return request("GET", "/company", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get per-member order metrics for a parent company
*
* Returns aggregated order statistics for each active member company.
* Only accessible by admin/owner of the parent company (or via chain access).
*/
async function Company_getMemberOrderMetrics(request, options) {
	return request("GET", "/company/members/order-metrics", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get current user's getting-started progress for a company */
async function Company_getMyGettingStarted(request, options) {
	return request("GET", "/company/me/getting-started", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get current user's meeting settings for a company */
async function Company_getMyMeetingSettings(request, options) {
	return request("GET", "/company/me/meeting-settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_inviteMembers(request, options) {
	return request("POST", "/company/invites/invite", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_invitedMembers(request, options) {
	return request("GET", "/company/invites", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_removeCompanyUser(request, options) {
	return request("PATCH", "/company/remove-user", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update AI Summary Transcription Language
*
* Updates the `aiSummaryTranscriptionLang` field of a company's billing entity.
*/
async function Company_updateAiSummaryTranscriptionLang(request, options) {
	return request("PATCH", "/company/lang", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_updateBanner(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PATCH", "/company/banner", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_updateCompany(request, options) {
	return request("PATCH", "/company", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update company user flags
*
* Shallow-merges the provided flags into the current user's flags on this company.
*/
async function Company_updateCompanyUserFlags(request, options) {
	return request("PATCH", "/company/flags", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_updateInvitation(request, options) {
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	return request("PATCH", "/company/invites", {
		query,
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Company_updateLogo(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PATCH", "/company/logo", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update the company meeting policy
*
* Sets whether people the room already trusts may skip the Pre-join.
*/
async function Company_updateMeetingPolicy(request, options) {
	return request("PATCH", "/company/meeting-policy", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update current user's meeting settings for a company */
async function Company_updateMyMeetingSettings(request, options) {
	return request("PATCH", "/company/me/meeting-settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompanyController(request) {
	return {
		assignRole: (options) => Company_assignRole(request, options),
		changeRole: (options) => Company_changeRole(request, options),
		completeMyGettingStartedStep: (options) => Company_completeMyGettingStartedStep(request, options),
		deleteCompany: (options) => Company_deleteCompany(request, options),
		deleteInvitation: (options) => Company_deleteInvitation(request, options),
		dismissMyGettingStarted: (options) => Company_dismissMyGettingStarted(request, options),
		getCapacity: (options) => Company_getCapacity(request, options),
		getCompany: (options) => Company_getCompany(request, options),
		getMemberOrderMetrics: (options) => Company_getMemberOrderMetrics(request, options),
		getMyGettingStarted: (options) => Company_getMyGettingStarted(request, options),
		getMyMeetingSettings: (options) => Company_getMyMeetingSettings(request, options),
		inviteMembers: (options) => Company_inviteMembers(request, options),
		invitedMembers: (options) => Company_invitedMembers(request, options),
		removeCompanyUser: (options) => Company_removeCompanyUser(request, options),
		updateAiSummaryTranscriptionLang: (options) => Company_updateAiSummaryTranscriptionLang(request, options),
		updateBanner: (options) => Company_updateBanner(request, options),
		updateCompany: (options) => Company_updateCompany(request, options),
		updateCompanyUserFlags: (options) => Company_updateCompanyUserFlags(request, options),
		updateInvitation: (options) => Company_updateInvitation(request, options),
		updateLogo: (options) => Company_updateLogo(request, options),
		updateMeetingPolicy: (options) => Company_updateMeetingPolicy(request, options),
		updateMyMeetingSettings: (options) => Company_updateMyMeetingSettings(request, options)
	};
}
//#endregion
//#region src/company-public/index.ts
/** Resolve the public Brand for a host

Unauthenticated. Routes through the public resolution seam, which serves brand only for a verified
(ACTIVE) domain of an entitled tenant and returns the identical platform PublicBrandDto for an
unknown host, an unverified (PENDING/FAILED) domain, and an unentitled tenant, so a caller cannot
distinguish them. The payload is the minimal allowlist, never the raw whitelabeling entity. */
async function CompanyPublic_getBranding(request, options) {
	const query = {};
	if (options.query?.domain !== void 0) query["domain"] = options.query.domain;
	if (options.query?.context !== void 0) query["context"] = options.query.context;
	return request("GET", "/company-public/branding", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Resolve public meeting capabilities for a host

Unauthenticated. Returns the tenant's non-brand meeting feature gates (hasDrawing) resolved from its
plan. An unknown or unresolvable host returns the no-capability default at 200, so this read never
reveals whether a host exists. */
async function CompanyPublic_getMeetingCapability(request, options) {
	const query = {};
	if (options.query?.domain !== void 0) query["domain"] = options.query.domain;
	if (options.query?.context !== void 0) query["context"] = options.query.context;
	return request("GET", "/company-public/meeting-capability", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompanyPublicController(request) {
	return {
		getBranding: (options) => CompanyPublic_getBranding(request, options),
		getMeetingCapability: (options) => CompanyPublic_getMeetingCapability(request, options)
	};
}
//#endregion
//#region src/company-service-area/index.ts
async function CompanyServiceArea_deleteServiceArea(request, options) {
	return request("DELETE", "/company/service-area", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyServiceArea_getServiceArea(request, options) {
	return request("GET", "/company/service-area", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyServiceArea_putServiceArea(request, options) {
	return request("PUT", "/company/service-area", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompanyServiceAreaController(request) {
	return {
		deleteServiceArea: (options) => CompanyServiceArea_deleteServiceArea(request, options),
		getServiceArea: (options) => CompanyServiceArea_getServiceArea(request, options),
		putServiceArea: (options) => CompanyServiceArea_putServiceArea(request, options)
	};
}
//#endregion
//#region src/company-white-labeling/index.ts
async function CompanyWhiteLabeling_checkCustomDomain(request, options) {
	return request("POST", "/company-whitelabeling/custom-domain/check", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_createCustomDomain(request, options) {
	return request("POST", "/company-whitelabeling/custom-domain", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_resetWhiteLabeling(request, options) {
	return request("DELETE", "/company-whitelabeling/reset-white-labeling/{type}", {
		params: { type: String(options.type) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateAppTitle(request, options) {
	return request("POST", "/company-whitelabeling/app-title", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateCustomDomain(request, options) {
	return request("PATCH", "/company-whitelabeling/custom-domain", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateCustomLogo(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/company-whitelabeling/custom-logo", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateCustomSMSContent(request, options) {
	return request("POST", "/company-whitelabeling/custom-sms-content", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateFavicon(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/company-whitelabeling/custom-favicon", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateSmsTemplates(request, options) {
	return request("PATCH", "/company-whitelabeling/sms-templates", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CompanyWhiteLabeling_updateThemeColor(request, options) {
	return request("PATCH", "/company-whitelabeling/theme-color", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompanyWhiteLabelingController(request) {
	return {
		checkCustomDomain: (options) => CompanyWhiteLabeling_checkCustomDomain(request, options),
		createCustomDomain: (options) => CompanyWhiteLabeling_createCustomDomain(request, options),
		resetWhiteLabeling: (options) => CompanyWhiteLabeling_resetWhiteLabeling(request, options),
		updateAppTitle: (options) => CompanyWhiteLabeling_updateAppTitle(request, options),
		updateCustomDomain: (options) => CompanyWhiteLabeling_updateCustomDomain(request, options),
		updateCustomLogo: (options) => CompanyWhiteLabeling_updateCustomLogo(request, options),
		updateCustomSMSContent: (options) => CompanyWhiteLabeling_updateCustomSMSContent(request, options),
		updateFavicon: (options) => CompanyWhiteLabeling_updateFavicon(request, options),
		updateSmsTemplates: (options) => CompanyWhiteLabeling_updateSmsTemplates(request, options),
		updateThemeColor: (options) => CompanyWhiteLabeling_updateThemeColor(request, options)
	};
}
//#endregion
//#region src/competence-approval/index.ts
/** Approve a pending request — creates or renews the competence record */
async function CompetenceApproval_approve(request, options) {
	return request("PATCH", "/competence/requests/{id}/approve", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Cancel your own pending approval request */
async function CompetenceApproval_cancel(request, options) {
	return request("PATCH", "/competence/requests/{id}/cancel", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List approval requests for the company (review queue) */
async function CompetenceApproval_list(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	return request("GET", "/competence/requests", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List evidence for an approval request
Accessible by a company admin or the requester. */
async function CompetenceApproval_listEvidence(request, options) {
	return request("GET", "/competence/requests/{id}/evidence", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the requesting user's own approval requests */
async function CompetenceApproval_listMine(request, options) {
	return request("GET", "/competence/requests/mine", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reject a pending request with a reason */
async function CompetenceApproval_reject(request, options) {
	return request("PATCH", "/competence/requests/{id}/reject", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Submit a competence approval request (self-reported competence) */
async function CompetenceApproval_submit(request, options) {
	return request("POST", "/competence/requests", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload an evidence file (JPG/PNG/PDF ≤10MB) to your own pending request */
async function CompetenceApproval_uploadEvidence(request, options) {
	return request("POST", "/competence/requests/{id}/evidence", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceApprovalController(request) {
	return {
		approve: (options) => CompetenceApproval_approve(request, options),
		cancel: (options) => CompetenceApproval_cancel(request, options),
		list: (options) => CompetenceApproval_list(request, options),
		listEvidence: (options) => CompetenceApproval_listEvidence(request, options),
		listMine: (options) => CompetenceApproval_listMine(request, options),
		reject: (options) => CompetenceApproval_reject(request, options),
		submit: (options) => CompetenceApproval_submit(request, options),
		uploadEvidence: (options) => CompetenceApproval_uploadEvidence(request, options)
	};
}
//#endregion
//#region src/competence-chain/index.ts
/** CSV export of per-company compliance */
async function CompetenceChain_exportCsv(request, options) {
	return request("GET", "/chain/analytics/competence/export", {
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Per-company compliance rankings across the chain */
async function CompetenceChain_getCompanies(request, options) {
	return request("GET", "/chain/analytics/competence/companies", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Expiry forecast (30/60/90 days) across the chain */
async function CompetenceChain_getForecast(request, options) {
	return request("GET", "/chain/analytics/competence/forecast", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Chain competence compliance overview (per type, required vs optional) */
async function CompetenceChain_getOverview(request, options) {
	return request("GET", "/chain/analytics/competence", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceChainController(request) {
	return {
		exportCsv: (options) => CompetenceChain_exportCsv(request, options),
		getCompanies: (options) => CompetenceChain_getCompanies(request, options),
		getForecast: (options) => CompetenceChain_getForecast(request, options),
		getOverview: (options) => CompetenceChain_getOverview(request, options)
	};
}
//#endregion
//#region src/competence-evidence/index.ts
/** Delete an evidence file (removes the storage object and the row) */
async function CompetenceEvidence_delete(request, options) {
	return request("DELETE", "/competence/evidence/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List evidence for a competence record
Accessible by a company admin or the record owner. */
async function CompetenceEvidence_list(request, options) {
	return request("GET", "/competence/records/{id}/evidence", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload an evidence file (JPG/PNG/PDF ≤10MB) for a competence record */
async function CompetenceEvidence_upload(request, options) {
	return request("POST", "/competence/records/{id}/evidence", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceEvidenceController(request) {
	return {
		delete: (options) => CompetenceEvidence_delete(request, options),
		list: (options) => CompetenceEvidence_list(request, options),
		upload: (options) => CompetenceEvidence_upload(request, options)
	};
}
//#endregion
//#region src/competence-export/index.ts
/** Export the competence matrix as CSV */
async function CompetenceExport_exportMatrixCsv(request, options) {
	const query = {};
	if (options.query?.employeeRole !== void 0) query["employeeRole"] = options.query.employeeRole;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.requiredOnly !== void 0) query["requiredOnly"] = options.query.requiredOnly;
	if (options.query?.hasIssuesOnly !== void 0) query["hasIssuesOnly"] = options.query.hasIssuesOnly;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDir !== void 0) query["sortDir"] = options.query.sortDir;
	return request("GET", "/competence/matrix/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Export the competence matrix as PDF */
async function CompetenceExport_exportMatrixPdf(request, options) {
	const query = {};
	if (options.query?.employeeRole !== void 0) query["employeeRole"] = options.query.employeeRole;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.requiredOnly !== void 0) query["requiredOnly"] = options.query.requiredOnly;
	if (options.query?.hasIssuesOnly !== void 0) query["hasIssuesOnly"] = options.query.hasIssuesOnly;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDir !== void 0) query["sortDir"] = options.query.sortDir;
	return request("GET", "/competence/matrix/export/pdf", {
		query,
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Export an employee's competence profile as PDF
Accessible by a company admin or the employee themselves. */
async function CompetenceExport_exportProfilePdf(request, options) {
	return request("GET", "/competence/user/{userId}/export/pdf", {
		params: { userId: String(options.userId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceExportController(request) {
	return {
		exportMatrixCsv: (options) => CompetenceExport_exportMatrixCsv(request, options),
		exportMatrixPdf: (options) => CompetenceExport_exportMatrixPdf(request, options),
		exportProfilePdf: (options) => CompetenceExport_exportProfilePdf(request, options)
	};
}
//#endregion
//#region src/competence-internal/index.ts
/** [Internal] Run the competence expiry check for this company
Recomputes time-based status and fires expiry alerts (60/30/0 thresholds),
scoped to the acting company. Idempotent — safe to re-run. */
async function CompetenceInternal_runExpiryCheck(request, options) {
	return request("POST", "/competence/internal/run-expiry-check", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceInternalController(request) {
	return { runExpiryCheck: (options) => CompetenceInternal_runExpiryCheck(request, options) };
}
//#endregion
//#region src/competence-record/index.ts
/** Create a competence record */
async function CompetenceRecord_create(request, options) {
	return request("POST", "/competence/records", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the change history for a competence record
Accessible by the record owner or a company admin. */
async function CompetenceRecord_getHistory(request, options) {
	return request("GET", "/competence/records/{id}/history", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Competence matrix — employees × applicable types grid
Readable by any company member (read-only view); mutating the records
underneath stays ADMIN-gated on the record write endpoints. */
async function CompetenceRecord_getMatrix(request, options) {
	const query = {};
	if (options.query?.employeeRole !== void 0) query["employeeRole"] = options.query.employeeRole;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.requiredOnly !== void 0) query["requiredOnly"] = options.query.requiredOnly;
	if (options.query?.hasIssuesOnly !== void 0) query["hasIssuesOnly"] = options.query.hasIssuesOnly;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDir !== void 0) query["sortDir"] = options.query.sortDir;
	return request("GET", "/competence/matrix", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Per-employee competence profile + compliance score
Accessible by the employee themselves or a company admin. */
async function CompetenceRecord_getUserProfile(request, options) {
	return request("GET", "/competence/user/{userId}", {
		params: { userId: String(options.userId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a competence record
Removes the record along with its evidence files and history so a
competence type with linked records can subsequently be deleted. */
async function CompetenceRecord_remove(request, options) {
	return request("DELETE", "/competence/records/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update / renew a competence record */
async function CompetenceRecord_update(request, options) {
	return request("PATCH", "/competence/records/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceRecordController(request) {
	return {
		create: (options) => CompetenceRecord_create(request, options),
		getHistory: (options) => CompetenceRecord_getHistory(request, options),
		getMatrix: (options) => CompetenceRecord_getMatrix(request, options),
		getUserProfile: (options) => CompetenceRecord_getUserProfile(request, options),
		remove: (options) => CompetenceRecord_remove(request, options),
		update: (options) => CompetenceRecord_update(request, options)
	};
}
//#endregion
//#region src/competence-type/index.ts
/** Soft-archive a competence type
Hides it from the matrix, pickers and compliance denominators. Existing records
are preserved. */
async function CompetenceType_archive(request, options) {
	return request("PATCH", "/competence-types/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a competence type
scope=CHAIN if isChainAccess, else scope=COMPANY. Case-insensitive name uniqueness. */
async function CompetenceType_create(request, options) {
	return request("POST", "/competence-types", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Per-company usage breakdown for a competence type before archiving
*
* Groups existing records by owning company so the caller (chain
* parent or company admin) sees the blast area before archiving hides the
* type chain/company-wide.
*/
async function CompetenceType_getArchiveImpact(request, options) {
	return request("GET", "/competence-types/{id}/archive-impact", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a single competence type by id, including its `recordCount`
*
* Used by detail/destructive-action surfaces (archive + hard
* delete dialogs) so the UI shows the authoritative count, not one derived
* from a stale matrix snapshot.
*/
async function CompetenceType_getOne(request, options) {
	return request("GET", "/competence-types/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Permanently delete a competence type
Refuses if any records exist unless `force=true`, in which case records,
evidence and history are cascade-deleted. Admin-only and not exposed by
default UI flows — use with care. */
async function CompetenceType_hardDelete(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.force !== void 0) query["force"] = options.query.force;
	return request("DELETE", "/competence-types/{id}/hard", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List applicable competence types for a company
Returns own COMPANY types ∪ parent CHAIN types (if member) ∪ own CHAIN types (if parent).
Excludes archived types unless `includeArchived=true`. */
async function CompetenceType_list(request, options) {
	const query = {};
	if (options.query?.includeArchived !== void 0) query["includeArchived"] = options.query.includeArchived;
	return request("GET", "/competence-types", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a competence type (soft-archive)
Backwards-compatible DELETE — maps to soft archive. */
async function CompetenceType_remove(request, options) {
	return request("DELETE", "/competence-types/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Restore a previously archived competence type
Refuses if an active type with the same name now exists. */
async function CompetenceType_restore(request, options) {
	return request("PATCH", "/competence-types/{id}/restore", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a competence type
CHAIN types require chain access; COMPANY types require ownership. */
async function CompetenceType_update(request, options) {
	return request("PATCH", "/competence-types/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCompetenceTypeController(request) {
	return {
		archive: (options) => CompetenceType_archive(request, options),
		create: (options) => CompetenceType_create(request, options),
		getArchiveImpact: (options) => CompetenceType_getArchiveImpact(request, options),
		getOne: (options) => CompetenceType_getOne(request, options),
		hardDelete: (options) => CompetenceType_hardDelete(request, options),
		list: (options) => CompetenceType_list(request, options),
		remove: (options) => CompetenceType_remove(request, options),
		restore: (options) => CompetenceType_restore(request, options),
		update: (options) => CompetenceType_update(request, options)
	};
}
//#endregion
//#region src/config-push/index.ts
/** List push history (paginated, newest first). */
async function ConfigPush_getHistory(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.configType !== void 0) query["configType"] = options.query.configType;
	return request("GET", "/config-pushes", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Member company ids that already received a given config. */
async function ConfigPush_getPushedMembers(request, options) {
	const query = {};
	if (options.query?.configId !== void 0) query["configId"] = options.query.configId;
	return request("GET", "/config-pushes/pushed-members", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Per-member status for a specific push. */
async function ConfigPush_getReceipts(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/config-pushes/{id}/receipts", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Push a config to member companies. */
async function ConfigPush_push(request, options) {
	return request("POST", "/config-push", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Re-push a config (creates a new push record, overwrites member configs). */
async function ConfigPush_rePush(request, options) {
	return request("POST", "/config-pushes/{id}/re-push", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createConfigPushController(request) {
	return {
		getHistory: (options) => ConfigPush_getHistory(request, options),
		getPushedMembers: (options) => ConfigPush_getPushedMembers(request, options),
		getReceipts: (options) => ConfigPush_getReceipts(request, options),
		push: (options) => ConfigPush_push(request, options),
		rePush: (options) => ConfigPush_rePush(request, options)
	};
}
//#endregion
//#region src/contact-category/index.ts
/** Count non-deleted contacts on a category (for delete-reassign UI). */
async function ContactCategory_contactCount(request, options) {
	return request("GET", "/member-portal/manage/contact-categories/{id}/contact-count", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a new contact category. */
async function ContactCategory_create(request, options) {
	return request("POST", "/member-portal/manage/contact-categories", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all non-deleted contact categories, ordered by sortOrder. */
async function ContactCategory_list(request, options) {
	return request("GET", "/member-portal/manage/contact-categories", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a category, optionally reassigning its contacts.

If the category still has contacts and no `reassignTo` is passed, returns 409
with `{ code: 'CATEGORY_HAS_CONTACTS', contactsCount }`. The last remaining
category cannot be deleted. */
async function ContactCategory_remove(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.reassignTo !== void 0) query["reassignTo"] = options.query.reassignTo;
	return request("DELETE", "/member-portal/manage/contact-categories/{id}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an existing contact category. */
async function ContactCategory_update(request, options) {
	return request("PUT", "/member-portal/manage/contact-categories/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createContactCategoryController(request) {
	return {
		contactCount: (options) => ContactCategory_contactCount(request, options),
		create: (options) => ContactCategory_create(request, options),
		list: (options) => ContactCategory_list(request, options),
		remove: (options) => ContactCategory_remove(request, options),
		update: (options) => ContactCategory_update(request, options)
	};
}
//#endregion
//#region src/contact/index.ts
/** Create a new contact. */
async function Contact_create(request, options) {
	return request("POST", "/member-portal/manage/contacts", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all contacts (incl. hidden), with optional search/type filter. */
async function Contact_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.contactType !== void 0) query["contactType"] = options.query.contactType;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	if (options.query?.mine !== void 0) query["mine"] = options.query.mine;
	return request("GET", "/member-portal/manage/contacts", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single contact by ID. */
async function Contact_findOne(request, options) {
	return request("GET", "/member-portal/manage/contacts/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the contacts assigned to a member company as its own ("your contacts"). */
async function Contact_getDedicated(request, options) {
	return request("GET", "/member-portal/manage/contacts/dedicated/{memberCompanyId}", {
		params: { memberCompanyId: String(options.memberCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Bulk-import contacts from a CSV file.

Accepts multipart/form-data with a `file` field containing a CSV.
First row must be headers (see ContactCsvService for column contract).
Bad rows are accumulated into the errors array; they do not abort the batch. */
async function Contact_importCsv(request, options) {
	return request("POST", "/member-portal/manage/contacts/import-csv", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a contact. */
async function Contact_remove(request, options) {
	return request("DELETE", "/member-portal/manage/contacts/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Clear the contact photo (falls back to initials in the UI). */
async function Contact_removePhoto(request, options) {
	return request("DELETE", "/member-portal/manage/contacts/{id}/photo", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Replace the contacts assigned to a member company as its own. */
async function Contact_setDedicated(request, options) {
	return request("PUT", "/member-portal/manage/contacts/dedicated/{memberCompanyId}", {
		params: { memberCompanyId: String(options.memberCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an existing contact. */
async function Contact_update(request, options) {
	return request("PUT", "/member-portal/manage/contacts/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload or replace the contact photo.

Accepts multipart/form-data with a `file` image field. The image is
resized to a square avatar and stored; the resulting URL lands in photoUrl. */
async function Contact_uploadPhoto(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PUT", "/member-portal/manage/contacts/{id}/photo", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createContactController(request) {
	return {
		create: (options) => Contact_create(request, options),
		findAll: (options) => Contact_findAll(request, options),
		findOne: (options) => Contact_findOne(request, options),
		getDedicated: (options) => Contact_getDedicated(request, options),
		importCsv: (options) => Contact_importCsv(request, options),
		remove: (options) => Contact_remove(request, options),
		removePhoto: (options) => Contact_removePhoto(request, options),
		setDedicated: (options) => Contact_setDedicated(request, options),
		update: (options) => Contact_update(request, options),
		uploadPhoto: (options) => Contact_uploadPhoto(request, options)
	};
}
//#endregion
//#region src/contact-viewer/index.ts
/** Mark a contact as a favorite for the current user (idempotent). */
async function ContactViewer_addFavorite(request, options) {
	return request("POST", "/member-portal/contacts/{id}/favorite", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List visible contacts for the requesting member company.

Query param `type` accepts HEAD_OFFICE, PARTNER or SUPPLIER; `supplierId`
narrows the SUPPLIER list to one supplier. */
async function ContactViewer_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.supplierId !== void 0) query["supplierId"] = options.query.supplierId;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	if (options.query?.favoritesOnly !== void 0) query["favoritesOnly"] = options.query.favoritesOnly;
	return request("GET", "/member-portal/contacts", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single visible contact by ID (404 if not visible). */
async function ContactViewer_findOne(request, options) {
	return request("GET", "/member-portal/contacts/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the contact types (custom categories) the member's chain exposes. */
async function ContactViewer_listCategories(request, options) {
	return request("GET", "/member-portal/contacts/categories", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the current user's favorite contact IDs in this directory. */
async function ContactViewer_listFavorites(request, options) {
	return request("GET", "/member-portal/contacts/favorites", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the contacts the chain assigned to the requesting member company. */
async function ContactViewer_listMine(request, options) {
	return request("GET", "/member-portal/contacts/mine", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove a contact from the current user's favorites (idempotent). */
async function ContactViewer_removeFavorite(request, options) {
	return request("DELETE", "/member-portal/contacts/{id}/favorite", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createContactViewerController(request) {
	return {
		addFavorite: (options) => ContactViewer_addFavorite(request, options),
		findAll: (options) => ContactViewer_findAll(request, options),
		findOne: (options) => ContactViewer_findOne(request, options),
		listCategories: (options) => ContactViewer_listCategories(request, options),
		listFavorites: (options) => ContactViewer_listFavorites(request, options),
		listMine: (options) => ContactViewer_listMine(request, options),
		removeFavorite: (options) => ContactViewer_removeFavorite(request, options)
	};
}
//#endregion
//#region src/course-analytics/index.ts
/** CSV export of chain course analytics
NOTE: Must be declared BEFORE /:id to avoid route collision. */
async function CourseAnalytics_exportCourseAnalytics(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	return request("GET", "/chain/analytics/courses/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Chain-wide course analytics overview */
async function CourseAnalytics_getChainCourseAnalytics(request, options) {
	const query = {};
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	return request("GET", "/chain/analytics/courses", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Per-course analytics detail */
async function CourseAnalytics_getCourseAnalytics(request, options) {
	return request("GET", "/chain/analytics/courses/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCourseAnalyticsController(request) {
	return {
		exportCourseAnalytics: (options) => CourseAnalytics_exportCourseAnalytics(request, options),
		getChainCourseAnalytics: (options) => CourseAnalytics_getChainCourseAnalytics(request, options),
		getCourseAnalytics: (options) => CourseAnalytics_getCourseAnalytics(request, options)
	};
}
//#endregion
//#region src/course-category/index.ts
/** Create a new course category (ADMIN+ or EDITOR only) */
async function CourseCategory_create(request, options) {
	return request("POST", "/course-categories", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a course category — blocked if courses are assigned to it (ADMIN+ or EDITOR only) */
async function CourseCategory_delete(request, options) {
	return request("DELETE", "/course-categories/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all course categories for a company */
async function CourseCategory_findAll(request, options) {
	return request("GET", "/course-categories", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reorder course categories (ADMIN+ or EDITOR only)
Must be declared BEFORE :id to avoid param collision. */
async function CourseCategory_reorder(request, options) {
	return request("PATCH", "/course-categories/reorder", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a course category (ADMIN+ or EDITOR only) */
async function CourseCategory_update(request, options) {
	return request("PATCH", "/course-categories/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCourseCategoryController(request) {
	return {
		create: (options) => CourseCategory_create(request, options),
		delete: (options) => CourseCategory_delete(request, options),
		findAll: (options) => CourseCategory_findAll(request, options),
		reorder: (options) => CourseCategory_reorder(request, options),
		update: (options) => CourseCategory_update(request, options)
	};
}
//#endregion
//#region src/course/index.ts
/** Cancel a PUBLISHED course — transitions to CANCELLED */
async function Course_cancel(request, options) {
	return request("POST", "/courses/{id}/cancel", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Mark a PUBLISHED course as COMPLETED (archive) */
async function Course_complete(request, options) {
	return request("POST", "/courses/{id}/complete", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a course (CHAIN scope: ADMIN/EDITOR only; COMPANY scope: ADMIN only) */
async function Course_create(request, options) {
	return request("POST", "/courses", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a DRAFT course (400 if not DRAFT) */
async function Course_delete(request, options) {
	return request("DELETE", "/courses/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List available courses with visibility rules (CHAIN from parent + COMPANY from own) */
async function Course_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.ownerCompanyId !== void 0) query["ownerCompanyId"] = options.query.ownerCompanyId;
	return request("GET", "/courses", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single course with its materials */
async function Course_findOne(request, options) {
	return request("GET", "/courses/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Publish a DRAFT course — transitions to PUBLISHED and sends notification */
async function Course_publish(request, options) {
	return request("POST", "/courses/{id}/publish", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Chain parent governance: unpublish a member company's PUBLISHED course (back to DRAFT) */
async function Course_unpublish(request, options) {
	return request("POST", "/courses/{id}/unpublish", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a DRAFT course (ADMIN/EDITOR only) */
async function Course_update(request, options) {
	return request("PATCH", "/courses/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCourseController(request) {
	return {
		cancel: (options) => Course_cancel(request, options),
		complete: (options) => Course_complete(request, options),
		create: (options) => Course_create(request, options),
		delete: (options) => Course_delete(request, options),
		findAll: (options) => Course_findAll(request, options),
		findOne: (options) => Course_findOne(request, options),
		publish: (options) => Course_publish(request, options),
		unpublish: (options) => Course_unpublish(request, options),
		update: (options) => Course_update(request, options)
	};
}
//#endregion
//#region src/course-material/index.ts
/** Add a link material for a course (ADMIN+ or EDITOR only) */
async function CourseMaterial_addLink(request, options) {
	return request("POST", "/courses/{id}/materials/link", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a course material (ADMIN+ or EDITOR only) */
async function CourseMaterial_delete(request, options) {
	return request("DELETE", "/courses/{id}/materials/{materialId}", {
		params: {
			id: String(options.id),
			materialId: String(options.materialId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all materials for a course */
async function CourseMaterial_findAll(request, options) {
	return request("GET", "/courses/{id}/materials", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reorder materials for a course (ADMIN+ or EDITOR only)
Must be declared BEFORE :materialId to avoid param collision. */
async function CourseMaterial_reorder(request, options) {
	return request("PATCH", "/courses/{id}/materials/reorder", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a course material (ADMIN+ or EDITOR only) */
async function CourseMaterial_update(request, options) {
	return request("PATCH", "/courses/{id}/materials/{materialId}", {
		params: {
			id: String(options.id),
			materialId: String(options.materialId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload a file material for a course (ADMIN+ or EDITOR only) */
async function CourseMaterial_uploadFile(request, options) {
	return request("POST", "/courses/{id}/materials/file", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCourseMaterialController(request) {
	return {
		addLink: (options) => CourseMaterial_addLink(request, options),
		delete: (options) => CourseMaterial_delete(request, options),
		findAll: (options) => CourseMaterial_findAll(request, options),
		reorder: (options) => CourseMaterial_reorder(request, options),
		update: (options) => CourseMaterial_update(request, options),
		uploadFile: (options) => CourseMaterial_uploadFile(request, options)
	};
}
//#endregion
//#region src/crm-activity/index.ts
async function CrmActivity_appendNote(request, options) {
	return request("POST", "/crm/activity/notes", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmActivity_deleteNote(request, options) {
	return request("DELETE", "/crm/activity/notes/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmActivity_editNote(request, options) {
	return request("PATCH", "/crm/activity/notes/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmActivity_logCall(request, options) {
	return request("POST", "/crm/activity/calls", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmActivity_read(request, options) {
	const query = {};
	if (options.query?.targetType !== void 0) query["targetType"] = options.query.targetType;
	if (options.query?.targetId !== void 0) query["targetId"] = options.query.targetId;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/crm/activity", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmActivity_rollup(request, options) {
	const query = {};
	if (options.query?.targetType !== void 0) query["targetType"] = options.query.targetType;
	if (options.query?.targetId !== void 0) query["targetId"] = options.query.targetId;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/crm/activity/rollup", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmActivity_setPin(request, options) {
	return request("PATCH", "/crm/activity/{id}/pin", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmActivityController(request) {
	return {
		appendNote: (options) => CrmActivity_appendNote(request, options),
		deleteNote: (options) => CrmActivity_deleteNote(request, options),
		editNote: (options) => CrmActivity_editNote(request, options),
		logCall: (options) => CrmActivity_logCall(request, options),
		read: (options) => CrmActivity_read(request, options),
		rollup: (options) => CrmActivity_rollup(request, options),
		setPin: (options) => CrmActivity_setPin(request, options)
	};
}
//#endregion
//#region src/crm-company/index.ts
/**
* Archive a CRM company
*
* Archives the company. A company with active contacts or open deals needs a decision first: without `mode` the response has `status` `needs_decision` with the counts, and nothing is archived. Send again with `mode` `detach` or `cascade`. A company shared with you through your chain returns 403; only its owner can archive it.
*/
async function CrmCompany_archive(request, options) {
	return request("POST", "/crm/companies/{id}/archive", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmCompany_batchArchive(request, options) {
	return request("POST", "/crm/companies/batch-archive", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmCompany_batchRestore(request, options) {
	return request("POST", "/crm/companies/batch-restore", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a CRM company
*
* Creates a business customer. When another company already has the org number, nothing is created and the response has `status` `duplicate` with the matches. Send `force: true` to create it anyway; both are then flagged as possible duplicates.
*/
async function CrmCompany_create(request, options) {
	return request("POST", "/crm/companies", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmCompany_exportCompanies(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.registryStatus !== void 0) query["registryStatus"] = options.query.registryStatus;
	if (options.query?.naceCode !== void 0) query["naceCode"] = options.query.naceCode;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDir !== void 0) query["sortDir"] = options.query.sortDir;
	if (options.query?.includeArchived !== void 0) query["includeArchived"] = options.query.includeArchived;
	if (options.query?.archivedOnly !== void 0) query["archivedOnly"] = options.query.archivedOnly;
	if (options.query?.chainRecordId !== void 0) query["chainRecordId"] = options.query.chainRecordId;
	if (options.query?.chainScope !== void 0) query["chainScope"] = options.query.chainScope;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/companies/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmCompany_filterOptions(request, options) {
	return request("GET", "/crm/companies/filter-options", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List CRM companies
*
* Returns one page of the business customers in your CRM, sorted by name unless `sortBy` says otherwise. Archived companies are left out by default.
*
* Query parameters:
*
* - `search`: matches the name ignoring case, the org number, or the company number when the term is a whole number.
* - `registryStatus`: only companies with this registry status, for example `Active`.
* - `naceCode`: only companies with this industry code, for example `43.210`.
* - `page`: page number, starting at 1. Defaults to 1.
* - `limit`: companies per page, 1 to 100. Defaults to 50.
* - `sortBy`: `name` (default), `createdAt` or `lastActivityAt`.
* - `sortDir`: `asc` or `desc`. Defaults to `asc`, and to `desc` when sorting by `lastActivityAt`.
* - `includeArchived`: `true` adds archived companies to the list.
* - `archivedOnly`: `true` returns only archived companies. Wins over `includeArchived`.
* - `createdFrom`, `createdTo`: ISO 8601 date-times. Only companies created within the range, both ends included.
* - `chainRecordId`: a record assigned to you through your chain. Lists the companies of the chain company that owns it instead of your own.
* - `chainScope`: what kind of record `chainRecordId` is: `Deal` (default), `Contact`, `Company` or `Order`.
*/
async function CrmCompany_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.registryStatus !== void 0) query["registryStatus"] = options.query.registryStatus;
	if (options.query?.naceCode !== void 0) query["naceCode"] = options.query.naceCode;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDir !== void 0) query["sortDir"] = options.query.sortDir;
	if (options.query?.includeArchived !== void 0) query["includeArchived"] = options.query.includeArchived;
	if (options.query?.archivedOnly !== void 0) query["archivedOnly"] = options.query.archivedOnly;
	if (options.query?.chainRecordId !== void 0) query["chainRecordId"] = options.query.chainRecordId;
	if (options.query?.chainScope !== void 0) query["chainScope"] = options.query.chainScope;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/companies", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a CRM company
*
* Returns the company with its tags and custom field values. Archived companies and ids from another company return 404. A company shared with you through a chain lead comes back with `sharedViaChain` set.
*/
async function CrmCompany_findOne(request, options) {
	return request("GET", "/crm/companies/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmCompany_refresh(request, options) {
	return request("POST", "/crm/companies/{id}/refresh", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Restore a CRM company
*
* Brings back an archived company, together with the contacts and deals archived or detached with it. Restoring a company that is not archived changes nothing.
*/
async function CrmCompany_restore(request, options) {
	return request("POST", "/crm/companies/{id}/restore", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmCompany_searchRegistry(request, options) {
	const query = {};
	if (options.query?.query !== void 0) query["query"] = options.query.query;
	return request("GET", "/crm/companies/registry/search", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update a CRM company
*
* Changes the company's name or registry fields. Only the fields you send change. An org number another company already has returns `status` `duplicate` and saves nothing, unless you send `force: true`.
*/
async function CrmCompany_update(request, options) {
	return request("PATCH", "/crm/companies/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmCompanyController(request) {
	return {
		archive: (options) => CrmCompany_archive(request, options),
		batchArchive: (options) => CrmCompany_batchArchive(request, options),
		batchRestore: (options) => CrmCompany_batchRestore(request, options),
		create: (options) => CrmCompany_create(request, options),
		exportCompanies: (options) => CrmCompany_exportCompanies(request, options),
		filterOptions: (options) => CrmCompany_filterOptions(request, options),
		findAll: (options) => CrmCompany_findAll(request, options),
		findOne: (options) => CrmCompany_findOne(request, options),
		refresh: (options) => CrmCompany_refresh(request, options),
		restore: (options) => CrmCompany_restore(request, options),
		searchRegistry: (options) => CrmCompany_searchRegistry(request, options),
		update: (options) => CrmCompany_update(request, options)
	};
}
//#endregion
//#region src/crm-contacts/index.ts
/**
* Archive a contact
*
* Archives the contact, which hides it from the default list. Archiving an archived contact changes nothing. A contact shared with you through your chain returns 403; only its owner can archive it.
*/
async function CrmContacts_archiveContact(request, options) {
	return request("POST", "/crm/contacts/{contactId}/archive", {
		params: { contactId: String(options.contactId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmContacts_batchArchiveContacts(request, options) {
	return request("POST", "/crm/contacts/batch-archive", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmContacts_batchRestoreContacts(request, options) {
	return request("POST", "/crm/contacts/batch-restore", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmContacts_countContacts(request, options) {
	return request("GET", "/crm/contacts/count", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a contact
*
* Creates a person in your CRM. When other contacts already have the email or phone, nothing is created and the response has `outcome` `duplicate` with the matches. Send `force: true` to create it anyway; the contacts are then flagged as possible duplicates.
*/
async function CrmContacts_createContact(request, options) {
	return request("POST", "/crm/contacts", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmContacts_exportContacts(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDirection !== void 0) query["sortDirection"] = options.query.sortDirection;
	if (options.query?.includeArchived !== void 0) query["includeArchived"] = options.query.includeArchived;
	if (options.query?.archivedOnly !== void 0) query["archivedOnly"] = options.query.archivedOnly;
	if (options.query?.chainRecordId !== void 0) query["chainRecordId"] = options.query.chainRecordId;
	if (options.query?.chainScope !== void 0) query["chainScope"] = options.query.chainScope;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/contacts/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a contact
*
* Returns the contact with its tags and custom field values, archived or not. An id from another company returns 404. A contact shared with you through a chain lead comes back with `sharedViaChain` set.
*/
async function CrmContacts_getContact(request, options) {
	return request("GET", "/crm/contacts/{contactId}", {
		params: { contactId: String(options.contactId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List contacts
*
* Returns one page of the people in your CRM, sorted by name unless `sortBy` says otherwise. Archived contacts are left out by default.
*
* Query parameters:
*
* - `search`: matches the name, email or phone ignoring case, or the contact number when the term is a whole number.
* - `page`: page number, starting at 1. Defaults to 1.
* - `limit`: contacts per page, 1 to 200. Defaults to 50.
* - `sortBy`: `name` (default), `createdAt` or `lastActivityAt`.
* - `sortDirection`: `asc` or `desc`. Defaults to `asc`, and to `desc` when sorting by `lastActivityAt`.
* - `includeArchived`: `true` adds archived contacts to the list.
* - `archivedOnly`: `true` returns only archived contacts. Wins over `includeArchived`.
* - `createdFrom`, `createdTo`: ISO 8601 date-times. Only contacts created within the range, both ends included.
* - `chainRecordId`: a record assigned to you through your chain. Lists the contacts of the chain company that owns it instead of your own.
* - `chainScope`: what kind of record `chainRecordId` is: `Deal` (default), `Contact`, `Company` or `Order`.
*/
async function CrmContacts_getContacts(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDirection !== void 0) query["sortDirection"] = options.query.sortDirection;
	if (options.query?.includeArchived !== void 0) query["includeArchived"] = options.query.includeArchived;
	if (options.query?.archivedOnly !== void 0) query["archivedOnly"] = options.query.archivedOnly;
	if (options.query?.chainRecordId !== void 0) query["chainRecordId"] = options.query.chainRecordId;
	if (options.query?.chainScope !== void 0) query["chainScope"] = options.query.chainScope;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/contacts", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Restore a contact
*
* Brings back an archived contact. Restoring a contact that is not archived changes nothing. A contact shared with you through your chain returns 403.
*/
async function CrmContacts_restoreContact(request, options) {
	return request("POST", "/crm/contacts/{contactId}/restore", {
		params: { contactId: String(options.contactId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmContacts_setPrimaryContact(request, options) {
	return request("PATCH", "/crm/contacts/{contactId}/set-primary", {
		params: { contactId: String(options.contactId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update a contact
*
* Changes the contact. Only the fields you send change. A new email or phone another contact already has returns `outcome` `duplicate` and saves nothing, unless you send `force: true`.
*/
async function CrmContacts_updateContact(request, options) {
	return request("PATCH", "/crm/contacts/{contactId}", {
		params: { contactId: String(options.contactId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmContactsController(request) {
	return {
		archiveContact: (options) => CrmContacts_archiveContact(request, options),
		batchArchiveContacts: (options) => CrmContacts_batchArchiveContacts(request, options),
		batchRestoreContacts: (options) => CrmContacts_batchRestoreContacts(request, options),
		countContacts: (options) => CrmContacts_countContacts(request, options),
		createContact: (options) => CrmContacts_createContact(request, options),
		exportContacts: (options) => CrmContacts_exportContacts(request, options),
		getContact: (options) => CrmContacts_getContact(request, options),
		getContacts: (options) => CrmContacts_getContacts(request, options),
		restoreContact: (options) => CrmContacts_restoreContact(request, options),
		setPrimaryContact: (options) => CrmContacts_setPrimaryContact(request, options),
		updateContact: (options) => CrmContacts_updateContact(request, options)
	};
}
//#endregion
//#region src/crm-entitlement-admin/index.ts
async function CrmEntitlementAdmin_getState(request, options) {
	return request("GET", "/admin/crm/entitlement/{targetCompanyId}", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmEntitlementAdmin_provision(request, options) {
	return request("PUT", "/admin/crm/entitlement/{targetCompanyId}", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmEntitlementAdminController(request) {
	return {
		getState: (options) => CrmEntitlementAdmin_getState(request, options),
		provision: (options) => CrmEntitlementAdmin_provision(request, options)
	};
}
//#endregion
//#region src/crm-entitlement/index.ts
async function CrmEntitlement_countBuriedInboundLeads(request, options) {
	return request("GET", "/crm/entitlement/buried-inbound-leads/count", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmEntitlement_getEntitlement(request, options) {
	return request("GET", "/crm/entitlement", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmEntitlementController(request) {
	return {
		countBuriedInboundLeads: (options) => CrmEntitlement_countBuriedInboundLeads(request, options),
		getEntitlement: (options) => CrmEntitlement_getEntitlement(request, options)
	};
}
//#endregion
//#region src/crm-import/index.ts
async function CrmImport_cancelJob(request, options) {
	return request("POST", "/crm/import/{jobId}/cancel", {
		params: { jobId: String(options.jobId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmImport_createJob(request, options) {
	return request("POST", "/crm/import", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmImport_getFields(request, options) {
	const query = {};
	if (options.query?.entityScope !== void 0) query["entityScope"] = options.query.entityScope;
	return request("GET", "/crm/import/fields", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmImport_getJob(request, options) {
	return request("GET", "/crm/import/{jobId}", {
		params: { jobId: String(options.jobId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmImport_processJob(request, options) {
	return request("POST", "/crm/import/{jobId}/process", {
		params: { jobId: String(options.jobId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmImport_undoJob(request, options) {
	return request("POST", "/crm/import/{jobId}/undo", {
		params: { jobId: String(options.jobId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmImportController(request) {
	return {
		cancelJob: (options) => CrmImport_cancelJob(request, options),
		createJob: (options) => CrmImport_createJob(request, options),
		getFields: (options) => CrmImport_getFields(request, options),
		getJob: (options) => CrmImport_getJob(request, options),
		processJob: (options) => CrmImport_processJob(request, options),
		undoJob: (options) => CrmImport_undoJob(request, options)
	};
}
//#endregion
//#region src/crm-reporting/index.ts
async function CrmReporting_getReport(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	return request("GET", "/crm/reporting", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmReportingController(request) {
	return { getReport: (options) => CrmReporting_getReport(request, options) };
}
//#endregion
//#region src/crm-scheduled-activity/index.ts
async function CrmScheduledActivity_cancel(request, options) {
	return request("POST", "/crm/lead-deals/{id}/scheduled-activities/{activityId}/cancel", {
		params: {
			id: String(options.id),
			activityId: String(options.activityId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmScheduledActivity_complete(request, options) {
	return request("POST", "/crm/lead-deals/{id}/scheduled-activities/{activityId}/complete", {
		params: {
			id: String(options.id),
			activityId: String(options.activityId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmScheduledActivity_create(request, options) {
	return request("POST", "/crm/lead-deals/{id}/scheduled-activities", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmScheduledActivity_listUpcoming(request, options) {
	return request("GET", "/crm/lead-deals/{id}/scheduled-activities", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CrmScheduledActivity_update(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/scheduled-activities/{activityId}", {
		params: {
			id: String(options.id),
			activityId: String(options.activityId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCrmScheduledActivityController(request) {
	return {
		cancel: (options) => CrmScheduledActivity_cancel(request, options),
		complete: (options) => CrmScheduledActivity_complete(request, options),
		create: (options) => CrmScheduledActivity_create(request, options),
		listUpcoming: (options) => CrmScheduledActivity_listUpcoming(request, options),
		update: (options) => CrmScheduledActivity_update(request, options)
	};
}
//#endregion
//#region src/custom-field/index.ts
async function CustomField_create(request, options) {
	return request("POST", "/crm/custom-fields", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_filter(request, options) {
	return request("POST", "/crm/custom-fields/filter", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_getMeta(request, options) {
	return request("GET", "/crm/custom-fields/meta", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_list(request, options) {
	const query = {};
	if (options.query?.entityScope !== void 0) query["entityScope"] = options.query.entityScope;
	if (options.query?.recordId !== void 0) query["recordId"] = options.query.recordId;
	return request("GET", "/crm/custom-fields", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_listValues(request, options) {
	const query = {};
	if (options.query?.entityScope !== void 0) query["entityScope"] = options.query.entityScope;
	if (options.query?.recordId !== void 0) query["recordId"] = options.query.recordId;
	return request("GET", "/crm/custom-fields/values", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_remove(request, options) {
	return request("DELETE", "/crm/custom-fields/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_reorder(request, options) {
	return request("POST", "/crm/custom-fields/reorder", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_setValues(request, options) {
	return request("PUT", "/crm/custom-fields/values", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function CustomField_update(request, options) {
	return request("PATCH", "/crm/custom-fields/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCustomFieldController(request) {
	return {
		create: (options) => CustomField_create(request, options),
		filter: (options) => CustomField_filter(request, options),
		getMeta: (options) => CustomField_getMeta(request, options),
		list: (options) => CustomField_list(request, options),
		listValues: (options) => CustomField_listValues(request, options),
		remove: (options) => CustomField_remove(request, options),
		reorder: (options) => CustomField_reorder(request, options),
		setValues: (options) => CustomField_setValues(request, options),
		update: (options) => CustomField_update(request, options)
	};
}
//#endregion
//#region src/customer-account/index.ts
/**
* Permanently delete the authenticated customer's portal account
*
* Instant, irreversible wipe (ADR-0044): sessions, credentials and pending
* verifications are removed, then the account itself. Confirmation is a destructive dialog on the
* client; there is no server-side grace period or second proof step. The tenant's orders and CRM
* contact survive and only lose their portal back-reference. A goodbye email is queued afterwards.
*/
async function CustomerAccount_deleteOwnAccount(request, options) {
	return request("DELETE", "/customer/me", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCustomerAccountController(request) {
	return { deleteOwnAccount: (options) => CustomerAccount_deleteOwnAccount(request, options) };
}
//#endregion
//#region src/customer-link/index.ts
/**
* Finish the customer's upload
*
* Needs every part. Joins them and queues the video check, answering `CHECKING`; the phone
* then polls until `ACCEPTED` or `REFUSED`. A repeated call answers the session as it stands.
* 409 `UPLOAD_PARTS_MISSING` or `UPLOAD_NOT_RECEIVING`.
*/
async function CustomerLink_completeUpload(request, options) {
	return request("POST", "/public/record-and-send/links/{secret}/upload/complete", {
		params: { secret: String(options.secret) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Open a customer link
*
* `LIVE` serves the welcome page; `ALREADY_SENT`, `EXPIRED` and `PAUSED` serve the matching
* page with the company's brand. A secret that matches nothing is a 404 with no body. The first live
* open marks the Video request Opened.
*/
async function CustomerLink_lookup(request, options) {
	return request("GET", "/public/record-and-send/links/{secret}", {
		params: { secret: String(options.secret) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Read the customer's upload
*
* Lists the part numbers already stored, which is how the phone resumes, and the state the
* phone polls after `complete`. 404 `UPLOAD_NOT_STARTED` before the first Send.
*/
async function CustomerLink_readUpload(request, options) {
	return request("GET", "/public/record-and-send/links/{secret}/upload", {
		params: { secret: String(options.secret) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Send the customer's link to them again
*
* The one resend, 5 minutes after the last send. Answers `SENT` with where it went, or
* `REFUSED` with `LINK_JUST_SENT` or `SENDING_FAILED`.
*/
async function CustomerLink_resend(request, options) {
	return request("POST", "/public/record-and-send/links/{secret}/resend", {
		params: { secret: String(options.secret) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Start or resume the customer's upload (Send pressed)
*
* The same size, type and container again resumes the open session; anything else starts a
* new one. Opens a 24-hour window, restarted by each Send inside the link's 7 days. Refused with 409 and
* the link's state as `code` (`ALREADY_SENT`, `EXPIRED`, `PAUSED`), 409 `EXPIRED` for a new upload after
* the 7 days, and 413 `TOO_LARGE` for a declared size over the hard limit.
*/
async function CustomerLink_startUpload(request, options) {
	return request("POST", "/public/record-and-send/links/{secret}/upload", {
		params: { secret: String(options.secret) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Stream a Video type's intro video
*
* Honours a single `Range` (206, or 416 outside the file); a Video type with no intro is a 404.
*/
async function CustomerLink_streamIntro(request, options) {
	return request("GET", "/public/record-and-send/video-types/{publicId}/intro", {
		params: { publicId: String(options.publicId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Send one part of the customer's upload
*
* The body is the part's raw bytes as `application/octet-stream`, streamed into storage. Every
* part is `partSizeBytes` long except the last. The same part number again replaces the earlier part.
* 400 `PART_OUT_OF_RANGE` or `PART_SIZE_MISMATCH`, 409 `UPLOAD_NOT_RECEIVING`.
*/
async function CustomerLink_uploadPart(request, options) {
	return request("PUT", "/public/record-and-send/links/{secret}/upload/parts/{partNumber}", {
		params: {
			secret: String(options.secret),
			partNumber: String(options.partNumber)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createCustomerLinkController(request) {
	return {
		completeUpload: (options) => CustomerLink_completeUpload(request, options),
		lookup: (options) => CustomerLink_lookup(request, options),
		readUpload: (options) => CustomerLink_readUpload(request, options),
		resend: (options) => CustomerLink_resend(request, options),
		startUpload: (options) => CustomerLink_startUpload(request, options),
		streamIntro: (options) => CustomerLink_streamIntro(request, options),
		uploadPart: (options) => CustomerLink_uploadPart(request, options)
	};
}
//#endregion
//#region src/dead-letter/index.ts
async function DeadLetter_list(request, options) {
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.take !== void 0) query["take"] = options.query.take;
	return request("GET", "/platform-events/operator/dead-letter", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DeadLetter_replay(request, options) {
	return request("POST", "/platform-events/operator/dead-letter/{id}/replay", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DeadLetter_resolve(request, options) {
	return request("POST", "/platform-events/operator/dead-letter/{id}/resolve", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DeadLetter_reveal(request, options) {
	return request("GET", "/platform-events/operator/dead-letter/{id}/reveal", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createDeadLetterController(request) {
	return {
		list: (options) => DeadLetter_list(request, options),
		replay: (options) => DeadLetter_replay(request, options),
		resolve: (options) => DeadLetter_resolve(request, options),
		reveal: (options) => DeadLetter_reveal(request, options)
	};
}
//#endregion
//#region src/deal-inspection/index.ts
async function DealInspection_getCard(request, options) {
	return request("GET", "/crm/deals/{dealId}/inspection-card", {
		params: { dealId: String(options.dealId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DealInspection_getStartContext(request, options) {
	return request("GET", "/crm/deals/{dealId}/inspection-start-context", {
		params: { dealId: String(options.dealId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DealInspection_linkDeal(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/deal-link", {
		params: { inspectionId: String(options.inspectionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DealInspection_reschedule(request, options) {
	return request("PATCH", "/inspection/inspections/{inspectionId}/schedule", {
		params: { inspectionId: String(options.inspectionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DealInspection_startFromDeal(request, options) {
	return request("POST", "/inspection/inspections/from-deal", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DealInspection_unlinkDeal(request, options) {
	return request("DELETE", "/inspection/inspections/{inspectionId}/deal-link", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createDealInspectionController(request) {
	return {
		getCard: (options) => DealInspection_getCard(request, options),
		getStartContext: (options) => DealInspection_getStartContext(request, options),
		linkDeal: (options) => DealInspection_linkDeal(request, options),
		reschedule: (options) => DealInspection_reschedule(request, options),
		startFromDeal: (options) => DealInspection_startFromDeal(request, options),
		unlinkDeal: (options) => DealInspection_unlinkDeal(request, options)
	};
}
//#endregion
//#region src/demo-data-seed/index.ts
/** Wipe and reseed all demo data for a company (or a whole chain via its parent id)

One endpoint for the full demo set: the chain reseed (intranet, courses,
competence, staffing, chain config, sales leads/deals, orders, customer portal
accounts) followed by the CRM demo seed (contacts, companies, lead-deals, saved
views, tags) for every target. Pass a chain parent id (e.g. the onboarded Elfag
parent) to fan out across every member company. Never touches company config,
memberships or users. `dryRun: true` reports the plan; `dryRun: false` requires
the confirmation phrase. Hidden in production. */
async function DemoDataSeed_seed(request, options) {
	return request("POST", "/admin/internal/seed-chain-demo-data", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createDemoDataSeedController(request) {
	return { seed: (options) => DemoDataSeed_seed(request, options) };
}
//#endregion
//#region src/device-test-provisioning/index.ts
/** Create a device-test company on STANDARD with an admin and a member */
async function DeviceTestProvisioning_createCompany(request, options) {
	return request("POST", "/device-test-provisioning/companies", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a device-test company with its admin and member accounts */
async function DeviceTestProvisioning_deleteCompany(request, options) {
	return request("DELETE", "/device-test-provisioning/companies/{targetCompanyId}", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List every company the device-test provisioning created */
async function DeviceTestProvisioning_listCompanies(request, options) {
	return request("GET", "/device-test-provisioning/companies", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createDeviceTestProvisioningController(request) {
	return {
		createCompany: (options) => DeviceTestProvisioning_createCompany(request, options),
		deleteCompany: (options) => DeviceTestProvisioning_deleteCompany(request, options),
		listCompanies: (options) => DeviceTestProvisioning_listCompanies(request, options)
	};
}
//#endregion
//#region src/device-token/index.ts
async function DeviceToken_register(request, options) {
	return request("POST", "/device-tokens", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function DeviceToken_unregister(request, options) {
	return request("DELETE", "/device-tokens/{token}", {
		params: { token: String(options.token) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createDeviceTokenController(request) {
	return {
		register: (options) => DeviceToken_register(request, options),
		unregister: (options) => DeviceToken_unregister(request, options)
	};
}
//#endregion
//#region src/e2-ecalendar/index.ts
async function E2ECalendar_listEvents(request, options) {
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	return request("GET", "/e2e/calendar-provider/events", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function E2ECalendar_reset(request, options) {
	return request("POST", "/e2e/calendar-provider/reset", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function E2ECalendar_setBusy(request, options) {
	return request("POST", "/e2e/calendar-provider/busy", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function E2ECalendar_setFailure(request, options) {
	return request("POST", "/e2e/calendar-provider/failure", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createE2ECalendarController(request) {
	return {
		listEvents: (options) => E2ECalendar_listEvents(request, options),
		reset: (options) => E2ECalendar_reset(request, options),
		setBusy: (options) => E2ECalendar_setBusy(request, options),
		setFailure: (options) => E2ECalendar_setFailure(request, options)
	};
}
//#endregion
//#region src/e2-e/index.ts
async function E2E_agendaReminderSweep(request, options) {
	return request("POST", "/e2e/agenda-reminder-sweep", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function E2E_clearPushes(request, options) {
	return request("DELETE", "/e2e/pushes", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Clear all stored SMS messages. */
async function E2E_clearSmsMessages(request, options) {
	return request("DELETE", "/e2e/sms", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Provision a fresh MEMBER-role user on an existing isolated-context
* company. Returns credentials + a bearer token so browser specs can log
* in as a non-admin to verify role-scoped UI behaviour without sharing a
* global member account across parallel workers.
*/
async function E2E_createMemberUser(request, options) {
	return request("POST", "/e2e/create-member-user", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a notification directly in the DB, bypassing BullMQ.
* Used by browser E2E tests to seed notification data.
*/
async function E2E_createNotification(request, options) {
	return request("POST", "/e2e/create-notification", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Emit a notification event directly to a user's SSE channel via Redis pub/sub.
* Bypasses BullMQ to test SSE transport in isolation.
*/
async function E2E_emitNotification(request, options) {
	return request("POST", "/e2e/emit-notification", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Run the hourly follow-up reminder sweep on demand against a caller-supplied clock, so a spec can see the morning
* and overdue reminders without waiting a day. Test-only (E2EOnlyGuard).
*/
async function E2E_followUpReminderSweep(request, options) {
	return request("POST", "/e2e/follow-up-reminder-sweep", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Force-assign an order's installer fields directly via Prisma, bypassing
* the production `assignInstaller` validation (which requires the assignee
* to be a member of `assignedCompanyId`). Used by browser specs that need
* to set `assigneeUserId` on a seeded order whose `assignedCompanyId`
* points at the chain's child company rather than the tenant company.
*/
async function E2E_forceAssignOrder(request, options) {
	return request("POST", "/e2e/force-assign-order", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Read the pre-flight gate for a company without driving a run, so the refusal source and cycle reset
* are assertable directly rather than through whichever feature happens to trigger. Test-only.
*/
async function E2E_generationGate(request, options) {
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.pool !== void 0) query["pool"] = options.query.pool;
	return request("GET", "/e2e/generation-gate", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Resolve a branded URL through the consolidated CompanyUrlService.brandedUrl so the URL-builder
* consolidation (task 4) is asserted deterministically: an entitled ACTIVE-domain tenant resolves
* to its custom host, every other tenant to the platform subdomain.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_getBrandedUrl(request, options) {
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.path !== void 0) query["path"] = options.query.path;
	return request("GET", "/e2e/branded-url", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Every push this backend decided to send or clear. Firebase is unconfigured here, so the
* dispatch decision itself — recipient, tag, copy and delivery options — is the only honest
* evidence a push happened.
*/
async function E2E_getPushes(request, options) {
	const query = {};
	if (options.query?.action !== void 0) query["action"] = options.query.action;
	if (options.query?.userId !== void 0) query["userId"] = options.query.userId;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.notificationTag !== void 0) query["notificationTag"] = options.query.notificationTag;
	return request("GET", "/e2e/pushes", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get all sent SMS messages, optionally filtered by recipient phone number. */
async function E2E_getSmsMessages(request, options) {
	const query = {};
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/e2e/sms", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Operator access-log entries for an event id (task 10 access-log proof). Reads the
* in-process trail so the soak asserts a replay/reveal was access-logged with the
* operator identity. Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsAccessLog(request, options) {
	const query = {};
	if (options.query?.eventId !== void 0) query["eventId"] = options.query.eventId;
	return request("GET", "/e2e/platform-events/access-log", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Platform-events dead-letter inspection. Task 8's poison proofs read the
* dead-letter rows for an event id. Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsDeadLetter(request, options) {
	const query = {};
	if (options.query?.eventId !== void 0) query["eventId"] = options.query.eventId;
	return request("GET", "/e2e/platform-events/dead-letter", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Force a redelivery of one event onto the event-fabric queue so a spec drives the
* at-least-once + idempotency path directly. The jobId is the eventId, so a redundant
* redelivery is deduped by the consumer's claim. Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsForceRedeliver(request, options) {
	return request("POST", "/e2e/platform-events/force-redeliver", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Platform-events outbox inspection. The deferred task 2/3 publish proofs read the
* relay claim/delivery stamps for an event id (optionally tenant-scoped) to assert
* the row published exactly once. Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsOutbox(request, options) {
	const query = {};
	if (options.query?.eventId !== void 0) query["eventId"] = options.query.eventId;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/e2e/platform-events/outbox", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function E2E_platformEventsPauseRelay(request, options) {
	return request("POST", "/e2e/platform-events/relay/pause", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Platform-events processed-event inspection. Task 7's idempotency proofs read the
* per-consumer claims for an event id. Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsProcessed(request, options) {
	const query = {};
	if (options.query?.eventId !== void 0) query["eventId"] = options.query.eventId;
	return request("GET", "/e2e/platform-events/processed", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Toggle the shared proving force-fail flag for a company (task 10 dead-letter proof).
* Every backend's proving consumer reads the flag, so a tenant dead-letters
* deterministically without a dedicated force-fail backend. Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsProvingForceFail(request, options) {
	return request("POST", "/e2e/platform-events/proving/force-fail", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Trigger the dead-letter reaper on demand (task 8 retention proofs). Test-only. */
async function E2E_platformEventsReapDeadLetters(request, options) {
	return request("POST", "/e2e/platform-events/dead-letter/reap", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Trigger the processed-event reaper on demand (task 7 dedupe-window proofs).
* Test-only (E2EOnlyGuard).
*/
async function E2E_platformEventsReapProcessed(request, options) {
	return request("POST", "/e2e/platform-events/processed/reap", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function E2E_platformEventsResumeRelay(request, options) {
	return request("POST", "/e2e/platform-events/relay/resume", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Run one relay drain cycle on demand so a spec converges without waiting on the
* poll interval. Returns the number of rows published this cycle. Test-only.
*/
async function E2E_platformEventsTriggerRelay(request, options) {
	return request("POST", "/e2e/platform-events/relay/trigger", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Resolve the exact device tokens the company-scoped push query would deliver to. Firebase is
* unconfigured in e2e (send is a no-op), so this exposes the delivery decision itself — the H3 fix
* that a company-B send targets only the B-context rows, deduped by token.
*/
async function E2E_pushScopedTokens(request, options) {
	return request("POST", "/e2e/push-scoped-tokens", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Make the mock gateway refuse every SMS to one number with a LinkMobility result code. */
async function E2E_rejectSms(request, options) {
	return request("POST", "/e2e/sms/rejections", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Reset the seeded tenant customer's password by rewriting the bcrypt
* hash in better-auth's `account` collection. Used by browser specs
* that actually change the password at runtime so the next test in the
* same worker can still log in with the seed credentials.
*/
async function E2E_resetCustomerPassword(request, options) {
	return request("POST", "/e2e/reset-customer-password", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Clear individual-installer assignments on all seeded orders so
* assignment-sensitive tests (member role-scoping, "Unassigned" column)
* start from a pristine state regardless of sibling mutation tests sharing
* the worker-scoped tenant.
*/
async function E2E_resetOrderAssignments(request, options) {
	return request("POST", "/e2e/reset-order-assignments", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Move a seeded order into the tenant's stage of a given category. Lets order-detail
* mutation tests re-run within the same worker without the prior mutation leaking into
* the next test.
*/
async function E2E_resetOrderStatus(request, options) {
	return request("POST", "/e2e/reset-order-status", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed one deterministic CRM My Work row (see ). Test-only. */
async function E2E_seedCrmMyWorkRow(request, options) {
	return request("POST", "/e2e/seed-crm-my-work-row", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Re-home a tenant order as chain-sourced (member accountable, endorser parent) so the portal
* renders the frozen endorsement mark (AC-10). Provisions the entitled, ACTIVE-branded endorser
* and the verified membership. Test-only (E2EOnlyGuard).
*/
async function E2E_seedEndorsedOrder(request, options) {
	return request("POST", "/e2e/seed-endorsed-order", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed a PENDING flagged-affiliation row directly so endorsement review-queue tests can exercise
* confirm/dismiss without driving the whole chainName migration. Returns the row id.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_seedFlaggedAffiliation(request, options) {
	return request("POST", "/e2e/seed-flagged-affiliation", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed every fixture the data-dependent Maestro mobile flows need (past
* meeting + AI summary, a joinable meeting, a client, a bookable booking,
* and a second billing-inactive company on the same user) in one idempotent
* call. Returns the stable ids so the local runner can export them as
* MAESTRO_E2E_* env vars. Only active in environment=e2e (E2EOnlyGuard).
*/
async function E2E_seedMobileFixtures(request, options) {
	return request("POST", "/e2e/seed-mobile-fixtures", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed one message onto an order thread with an explicit sender role and author name.
* Used by the portal E2E to plant a COMPANY-authored message with an internal author
* name and assert the portal read shows the company name, never the internal identity.
* Test-only (E2EOnlyGuard).
*/
async function E2E_seedOrderMessage(request, options) {
	return request("POST", "/e2e/seed-order-message", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* A purpose-built platform super admin for one test, with or without mailbox proof, so the social
* policy specs can prove a super admin auto-links (sign-in PRD AC 21) without writing an identity
* row, an audit event or a notice email onto the shared bootstrapped admin every suite signs in
* with. Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_seedSuperAdmin(request, options) {
	return request("POST", "/e2e/seed-super-admin", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Per-tenant data seeding for browser specs. Given a tenant the test
* harness already provisioned via `createIsolatedContext`, populate it
* with the same fixtures the old `E2ESeedService` used to create on
* startup (orders, forms, announcements, resources, quick links, courses,
* service types, auto-assignment config, child chain member). Idempotent
* per tenant. Call once per worker from the Playwright fixture.
*/
async function E2E_seedTenant(request, options) {
	return request("POST", "/e2e/seed-tenant", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Put an order's uploaded invoice draft in READY with the given lines, number and invoice
* date, as if the AI had parsed it, so specs never call the real model. Moves the existing
* upload when there is one, otherwise creates one with no stored file. Test-only (E2EOnlyGuard).
*/
async function E2E_seedUploadedInvoice(request, options) {
	return request("POST", "/e2e/seed-uploaded-invoice", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Freezes a draft communication into SENT with a frozen audience, so viewer specs have a sent fixture. */
async function E2E_sendCommunication(request, options) {
	return request("POST", "/e2e/send-communication", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Open a terminal AgentRun row carrying a known token count. Under the e2e dummy AI key every model
* pass falls back to its deterministic floor and reports no tokens, so a seeded run is the only way to
* exercise the real settle path (real unique index, real transaction). Test-only (E2EOnlyGuard).
*/
async function E2E_setAgentRun(request, options) {
	return request("POST", "/e2e/set-agent-run", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Directly overwrite CompanyBillingEntity fields (and company.trialEndAt) so
* subscription-status derivation tests can seed each of the six states without
* going through the Stripe webhook — which, in e2e mode, always returns the
* same static mock subscription and cannot target a specific tenant.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setBillingStatus(request, options) {
	return request("POST", "/e2e/set-billing-status", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Flip a chain membership's affiliationVerified flag directly so endorsement-resolution tests can
* seed the matched-vs-flagged state.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setChainMembershipVerification(request, options) {
	return request("POST", "/e2e/set-chain-membership-verification", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed a chain's shared pool counter directly so pool-exhaustion tests are deterministic.
* Test-only (E2EOnlyGuard).
*/
async function E2E_setChainPoolCycle(request, options) {
	return request("POST", "/e2e/set-chain-pool-cycle", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed a chain's shared token pool size directly so pool-governance tests are deterministic.
* Test-only (E2EOnlyGuard).
*/
async function E2E_setChainQuotaConfig(request, options) {
	return request("POST", "/e2e/set-chain-quota-config", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Set a company's GeoJSON `location` directly, bypassing the geocoding
* service (which is unconfigured in the e2e environment). Used by browser
* specs that need to exercise pages gated on a company having an anchor
* location (e.g. /settings/service-area draw toolbar).
*/
async function E2E_setCompanyLocation(request, options) {
	return request("POST", "/e2e/set-company-location", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed a per-company token allowance override (a chain member's guardrail). Test-only (E2EOnlyGuard). */
async function E2E_setCompanyQuotaOverride(request, options) {
	return request("POST", "/e2e/set-company-quota-override", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Stamp a legacy CompanyRole enum value on a company user and reset the
* permission-module fields (roleId, preMigrationRole) to their pre-migration
* state, so the role-migration e2e can construct an all-enum-roles fixture.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setCompanyUserEnumRole(request, options) {
	return request("POST", "/e2e/set-company-user-enum-role", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Bind a CompanyUser join row to a custom RoleEntity by userId, bypassing the
* assignment safeguards owned by task 5. Used by the permission-guard E2E to put
* a member on a stripped/allowed custom role and prove enforcement end to end.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setCompanyUserRole(request, options) {
	return request("POST", "/e2e/set-company-user-role", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Flip `mustChangePassword` on the seeded tenant customer so the real
* backend change-password handler routes to the forced-reset path.
* Paired with `/e2e/reset-customer-password` to restore the seed
* password after the test finishes.
*/
async function E2E_setCustomerMustChangePassword(request, options) {
	return request("POST", "/e2e/set-customer-must-change-password", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Toggle the surviving `extraRolesEnabled` prisma flag on the given company,
* used by the role-migration specs to exercise snapshot capture and preseed
* targeting (the super-admin toggle endpoint itself was retired).
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setExtraRoles(request, options) {
	return request("POST", "/e2e/set-extra-roles", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Give an order a date with no start time, as orders saved before start times were required look
* (CRM calendar AC-04). Test-only (E2EOnlyGuard).
*/
async function E2E_setLegacyPlannedDate(request, options) {
	return request("POST", "/e2e/set-legacy-planned-date", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Link a new-model order to the seeded tenant customer (sets `OrderEntity.customerId`)
* so the portal-order controller's session guard resolves ownership. The production
* birth flow owns this link; the portal projection task cannot reach it, so the portal
* E2E establishes it directly. Test-only (E2EOnlyGuard).
*/
async function E2E_setOrderCustomer(request, options) {
	return request("POST", "/e2e/set-order-customer", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Move an order into the tenant's stage of a given category by id, bypassing the
* production move validation. Used by CRM leaderboard tests that need a converted Order
* to reach a DONE-category stage (which counts as a lead-to-order conversion, B6) without
* driving the full pipeline. Test-only (E2EOnlyGuard).
*/
async function E2E_setOrderStatus(request, options) {
	return request("POST", "/e2e/set-order-status", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed a run-meter ledger claim directly so settle-idempotency tests can assert a redelivered settle
* spends nothing. Test-only (E2EOnlyGuard).
*/
async function E2E_setRunLedger(request, options) {
	return request("POST", "/e2e/set-run-ledger", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Stamp or clear `User.incompleteSince` so a spec can drive the BetterAuthGuard's
* SIGNUP_INCOMPLETE refusal without a real social exchange. The field is `input: false` on the
* auth instance and is written only inside `user.create.before` on the social path, so there is
* no public write for it. Test-only (E2EOnlyGuard).
*/
async function E2E_setSignupIncomplete(request, options) {
	return request("POST", "/e2e/set-signup-incomplete", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed a company's token quota cycle row directly so billing tests can put a tenant into a known
* tokens-remaining state (including exhausted or negative) without driving real AI runs. Upserts on
* the (companyId, cycleKey) unique. Only active in environment=e2e (E2EOnlyGuard).
*/
async function E2E_setTokenQuota(request, options) {
	return request("POST", "/e2e/set-token-quota", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Flip a User's active/deleted flags directly, so the auth specs can prove that no session token
* is ever issued to one (sign-in PRD AC 10). The product paths that set these are an admin
* console action and the asynchronous account purge; neither is a usable fixture — the purge
* removes the very identity rows the social exchange is supposed to refuse against.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setUserStatus(request, options) {
	return request("POST", "/e2e/set-user-status", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Seed a company's whitelabeling row directly so brand-resolution tests can put a tenant into an
* entitled + ACTIVE-domain state without the Cloudflare verification flow the real write routes run.
*
* Only active in environment=e2e (gated by E2EOnlyGuard).
*/
async function E2E_setWhiteLabeling(request, options) {
	return request("POST", "/e2e/set-whitelabeling", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Invoke the real MeteringService.settleRun seam for a seeded run. Test-only (E2EOnlyGuard); the
* production caller is the agent-run terminal settle.
*/
async function E2E_settleRun(request, options) {
	return request("POST", "/e2e/settle-run", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* The raw value the `verification` collection is holding for an address's password-reset code, so
* a spec can prove the code is stored HASHED and not in the clear (audit EP-02). There is nothing
* else to read it with: `verification` is a loose Mongo collection the better-auth adapter owns
* and no Prisma model covers it, which is why this goes through `$runCommandRaw`.
*
* The identifier is the library's own (`toOTPIdentifier` in `plugins/email-otp/utils.mjs`:
* `<type>-otp-<lowercased email>`), and the stored value is `<otp>[:<attempts>]`.
*
* Only active in environment=e2e (gated by E2EOnlyGuard), where the whole point is that the
* assertion cannot be faked by the very code it is testing.
*/
async function E2E_storedResetOtp(request, options) {
	const query = {};
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	return request("GET", "/e2e/stored-reset-otp", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createE2EController(request) {
	return {
		agendaReminderSweep: (options) => E2E_agendaReminderSweep(request, options),
		clearPushes: (options) => E2E_clearPushes(request, options),
		clearSmsMessages: (options) => E2E_clearSmsMessages(request, options),
		createMemberUser: (options) => E2E_createMemberUser(request, options),
		createNotification: (options) => E2E_createNotification(request, options),
		emitNotification: (options) => E2E_emitNotification(request, options),
		followUpReminderSweep: (options) => E2E_followUpReminderSweep(request, options),
		forceAssignOrder: (options) => E2E_forceAssignOrder(request, options),
		generationGate: (options) => E2E_generationGate(request, options),
		getBrandedUrl: (options) => E2E_getBrandedUrl(request, options),
		getPushes: (options) => E2E_getPushes(request, options),
		getSmsMessages: (options) => E2E_getSmsMessages(request, options),
		platformEventsAccessLog: (options) => E2E_platformEventsAccessLog(request, options),
		platformEventsDeadLetter: (options) => E2E_platformEventsDeadLetter(request, options),
		platformEventsForceRedeliver: (options) => E2E_platformEventsForceRedeliver(request, options),
		platformEventsOutbox: (options) => E2E_platformEventsOutbox(request, options),
		platformEventsPauseRelay: (options) => E2E_platformEventsPauseRelay(request, options),
		platformEventsProcessed: (options) => E2E_platformEventsProcessed(request, options),
		platformEventsProvingForceFail: (options) => E2E_platformEventsProvingForceFail(request, options),
		platformEventsReapDeadLetters: (options) => E2E_platformEventsReapDeadLetters(request, options),
		platformEventsReapProcessed: (options) => E2E_platformEventsReapProcessed(request, options),
		platformEventsResumeRelay: (options) => E2E_platformEventsResumeRelay(request, options),
		platformEventsTriggerRelay: (options) => E2E_platformEventsTriggerRelay(request, options),
		pushScopedTokens: (options) => E2E_pushScopedTokens(request, options),
		rejectSms: (options) => E2E_rejectSms(request, options),
		resetCustomerPassword: (options) => E2E_resetCustomerPassword(request, options),
		resetOrderAssignments: (options) => E2E_resetOrderAssignments(request, options),
		resetOrderStatus: (options) => E2E_resetOrderStatus(request, options),
		seedCrmMyWorkRow: (options) => E2E_seedCrmMyWorkRow(request, options),
		seedEndorsedOrder: (options) => E2E_seedEndorsedOrder(request, options),
		seedFlaggedAffiliation: (options) => E2E_seedFlaggedAffiliation(request, options),
		seedMobileFixtures: (options) => E2E_seedMobileFixtures(request, options),
		seedOrderMessage: (options) => E2E_seedOrderMessage(request, options),
		seedSuperAdmin: (options) => E2E_seedSuperAdmin(request, options),
		seedTenant: (options) => E2E_seedTenant(request, options),
		seedUploadedInvoice: (options) => E2E_seedUploadedInvoice(request, options),
		sendCommunication: (options) => E2E_sendCommunication(request, options),
		setAgentRun: (options) => E2E_setAgentRun(request, options),
		setBillingStatus: (options) => E2E_setBillingStatus(request, options),
		setChainMembershipVerification: (options) => E2E_setChainMembershipVerification(request, options),
		setChainPoolCycle: (options) => E2E_setChainPoolCycle(request, options),
		setChainQuotaConfig: (options) => E2E_setChainQuotaConfig(request, options),
		setCompanyLocation: (options) => E2E_setCompanyLocation(request, options),
		setCompanyQuotaOverride: (options) => E2E_setCompanyQuotaOverride(request, options),
		setCompanyUserEnumRole: (options) => E2E_setCompanyUserEnumRole(request, options),
		setCompanyUserRole: (options) => E2E_setCompanyUserRole(request, options),
		setCustomerMustChangePassword: (options) => E2E_setCustomerMustChangePassword(request, options),
		setExtraRoles: (options) => E2E_setExtraRoles(request, options),
		setLegacyPlannedDate: (options) => E2E_setLegacyPlannedDate(request, options),
		setOrderCustomer: (options) => E2E_setOrderCustomer(request, options),
		setOrderStatus: (options) => E2E_setOrderStatus(request, options),
		setRunLedger: (options) => E2E_setRunLedger(request, options),
		setSignupIncomplete: (options) => E2E_setSignupIncomplete(request, options),
		setTokenQuota: (options) => E2E_setTokenQuota(request, options),
		setUserStatus: (options) => E2E_setUserStatus(request, options),
		setWhiteLabeling: (options) => E2E_setWhiteLabeling(request, options),
		settleRun: (options) => E2E_settleRun(request, options),
		storedResetOtp: (options) => E2E_storedResetOtp(request, options)
	};
}
//#endregion
//#region src/e2-emeeting-telemetry/index.ts
/** Read the stored meeting diagnostics sessions and server events of one meeting */
async function E2EMeetingTelemetry_timeline(request, options) {
	return request("GET", "/e2e/meeting-telemetry/{meetingId}", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createE2EMeetingTelemetryController(request) {
	return { timeline: (options) => E2EMeetingTelemetry_timeline(request, options) };
}
//#endregion
//#region src/e2-erecord-and-send/index.ts
/** Move a Video request's send, expiry and upload-window times back by some minutes */
async function E2ERecordAndSend_ageRequest(request, options) {
	return request("POST", "/e2e/record-and-send/age-request", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** End a playback ticket's idle time now */
async function E2ERecordAndSend_expirePlaybackTicket(request, options) {
	return request("POST", "/e2e/record-and-send/expire-playback-ticket", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read the link secret of the last invite SMS to a phone */
async function E2ERecordAndSend_linkSecret(request, options) {
	const query = {};
	if (options.query?.phone !== void 0) query["phone"] = options.query.phone;
	return request("GET", "/e2e/record-and-send/link-secret", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reserve one invite through the invite limits and read each channel's verdict */
async function E2ERecordAndSend_reserveInvite(request, options) {
	return request("POST", "/e2e/record-and-send/reserve-invite", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Run the upload-window and Video request expiry sweeps now */
async function E2ERecordAndSend_runSweeps(request, options) {
	return request("POST", "/e2e/record-and-send/run-sweeps", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Set a company's SMS parts for one Oslo day, today unless daysAgo names an earlier one */
async function E2ERecordAndSend_setSmsPartsToday(request, options) {
	return request("POST", "/e2e/record-and-send/sms-parts-today", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the stored objects of a Video request's video */
async function E2ERecordAndSend_storedVideos(request, options) {
	return request("GET", "/e2e/record-and-send/requests/{requestId}/stored-videos", {
		params: { requestId: String(options.requestId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read a Submission's playback state and conversion attempts */
async function E2ERecordAndSend_submissionPlayback(request, options) {
	return request("GET", "/e2e/record-and-send/submissions/{submissionId}/playback", {
		params: { submissionId: String(options.submissionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read a Video request's status and send count */
async function E2ERecordAndSend_videoRequestState(request, options) {
	return request("GET", "/e2e/record-and-send/requests/{requestId}", {
		params: { requestId: String(options.requestId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the Video requests for one phone on one Video type */
async function E2ERecordAndSend_videoRequestsForPhone(request, options) {
	const query = {};
	if (options.query?.publicId !== void 0) query["publicId"] = options.query.publicId;
	if (options.query?.phone !== void 0) query["phone"] = options.query.phone;
	return request("GET", "/e2e/record-and-send/requests", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createE2ERecordAndSendController(request) {
	return {
		ageRequest: (options) => E2ERecordAndSend_ageRequest(request, options),
		expirePlaybackTicket: (options) => E2ERecordAndSend_expirePlaybackTicket(request, options),
		linkSecret: (options) => E2ERecordAndSend_linkSecret(request, options),
		reserveInvite: (options) => E2ERecordAndSend_reserveInvite(request, options),
		runSweeps: (options) => E2ERecordAndSend_runSweeps(request, options),
		setSmsPartsToday: (options) => E2ERecordAndSend_setSmsPartsToday(request, options),
		storedVideos: (options) => E2ERecordAndSend_storedVideos(request, options),
		submissionPlayback: (options) => E2ERecordAndSend_submissionPlayback(request, options),
		videoRequestState: (options) => E2ERecordAndSend_videoRequestState(request, options),
		videoRequestsForPhone: (options) => E2ERecordAndSend_videoRequestsForPhone(request, options)
	};
}
//#endregion
//#region src/e2-eroutes/index.ts
async function E2ERoutes_listRoutes(request, options) {
	return request("GET", "/e2e/routes", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createE2ERoutesController(request) {
	return { listRoutes: (options) => E2ERoutes_listRoutes(request, options) };
}
//#endregion
//#region src/email/index.ts
/**
* Resolve the Brand shown on the unsubscribe page
*
* Unauthenticated. Returns the sending tenant's minimal public Brand for the unsubscribe
* page so it renders the tenant's identity instead of the platform logo. A missing or unknown company
* falls back to the platform Brand through the resolver, so this read never reveals whether a company
* exists.
*/
async function Email_getUnsubscribeBrand(request, options) {
	const query = {};
	if (options.query?.company !== void 0) query["company"] = options.query.company;
	return request("GET", "/emails/unsubscribe-brand", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Email_handleRedirect(request, options) {
	return request("POST", "/emails/unsubscribe/{email}", {
		params: { email: String(options.email) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createEmailController(request) {
	return {
		getUnsubscribeBrand: (options) => Email_getUnsubscribeBrand(request, options),
		handleRedirect: (options) => Email_handleRedirect(request, options)
	};
}
//#endregion
//#region src/email-webhook/index.ts
/** Handle SES notifications via SNS */
async function EmailWebhook_handleSesWebhook(request, options) {
	return request("POST", "/webhooks/ses", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createEmailWebhookController(request) {
	return { handleSesWebhook: (options) => EmailWebhook_handleSesWebhook(request, options) };
}
//#endregion
//#region src/endorsement/index.ts
/** Confirm a flagged affiliation — flips the member's affiliationVerified (parent) */
async function Endorsement_confirmFlagged(request, options) {
	return request("POST", "/chain/endorsement/flagged/{flaggedId}/confirm", {
		params: { flaggedId: String(options.flaggedId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Dismiss a flagged affiliation — clears the flag, member stays dark (parent) */
async function Endorsement_dismissFlagged(request, options) {
	return request("POST", "/chain/endorsement/flagged/{flaggedId}/dismiss", {
		params: { flaggedId: String(options.flaggedId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read the chain-wide endorsement switch and member list (parent) */
async function Endorsement_getControls(request, options) {
	return request("GET", "/chain/endorsement", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Read the affiliation state for a chain member
*
* Any company member may read its own affiliation state (parent switch, verification,
* and its own opt-in).
*/
async function Endorsement_getMemberView(request, options) {
	return request("GET", "/chain/endorsement/member", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List flagged affiliations awaiting review (parent) */
async function Endorsement_listFlagged(request, options) {
	return request("GET", "/chain/endorsement/flagged", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Toggle the member's own "Part of [chain]" affiliation mark on its own work
*
* The resolver still gates the mark on the parent switch and the confirmed affiliation,
* so opting in never forces a mark the chain has not allowed.
*/
async function Endorsement_updateAffiliationDisplay(request, options) {
	return request("PATCH", "/chain/endorsement/member/affiliation-display", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Toggle the chain-wide endorsement switch (parent) */
async function Endorsement_updateSwitch(request, options) {
	return request("PATCH", "/chain/endorsement/switch", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createEndorsementController(request) {
	return {
		confirmFlagged: (options) => Endorsement_confirmFlagged(request, options),
		dismissFlagged: (options) => Endorsement_dismissFlagged(request, options),
		getControls: (options) => Endorsement_getControls(request, options),
		getMemberView: (options) => Endorsement_getMemberView(request, options),
		listFlagged: (options) => Endorsement_listFlagged(request, options),
		updateAffiliationDisplay: (options) => Endorsement_updateAffiliationDisplay(request, options),
		updateSwitch: (options) => Endorsement_updateSwitch(request, options)
	};
}
//#endregion
//#region src/engagement-cron-test/index.ts
async function EngagementCronTest_findTwoMonthInactiveUsers(request, options) {
	const query = {};
	if (options.query?.setInactiveFor !== void 0) query["setInactiveFor"] = options.query.setInactiveFor;
	return request("GET", "/engagement-cron-test/find/two-month/inactive/users", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function EngagementCronTest_sendManualYearlyReminder(request, options) {
	return request("GET", "/engagement-cron-test/send-manual/yearly-renewal/email", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function EngagementCronTest_sendReEngagementEmailForUser(request, options) {
	return request("GET", "/engagement-cron-test/send-manual/re-engagement/email", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createEngagementCronTestController(request) {
	return {
		findTwoMonthInactiveUsers: (options) => EngagementCronTest_findTwoMonthInactiveUsers(request, options),
		sendManualYearlyReminder: (options) => EngagementCronTest_sendManualYearlyReminder(request, options),
		sendReEngagementEmailForUser: (options) => EngagementCronTest_sendReEngagementEmailForUser(request, options)
	};
}
//#endregion
//#region src/enrollment/index.ts
/** Bulk enroll multiple users in a course (ADMIN+) */
async function Enrollment_bulkEnroll(request, options) {
	return request("POST", "/courses/{id}/enroll/bulk", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Bulk update enrollment status for multiple enrollments (ADMIN+)
NOTE: Must be declared BEFORE /:id/status to avoid param collision in NestJS routing. */
async function Enrollment_bulkUpdateStatus(request, options) {
	return request("PATCH", "/enrollments/bulk/status", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Cancel own enrollment in a course (only before startDate) */
async function Enrollment_cancelEnrollment(request, options) {
	return request("DELETE", "/courses/{id}/enroll", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get users eligible for enrollment (ADMIN+)
For CHAIN courses: users across all chain member companies.
For COMPANY courses: users in that company only.
Excludes already-enrolled users. Supports search, pagination, and companyId filter. */
async function Enrollment_getEnrollableUsers(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/courses/{id}/enrollable-users", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List enrollments for a course with status filter and pagination (ADMIN+) */
async function Enrollment_listEnrollments(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/courses/{id}/enrollments", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Self-enroll the current user in a course (any authenticated member) */
async function Enrollment_selfEnroll(request, options) {
	return request("POST", "/courses/{id}/enroll", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update enrollment status — COMPLETED, NO_SHOW, CANCELLED (ADMIN+) */
async function Enrollment_updateStatus(request, options) {
	return request("PATCH", "/enrollments/{id}/status", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createEnrollmentController(request) {
	return {
		bulkEnroll: (options) => Enrollment_bulkEnroll(request, options),
		bulkUpdateStatus: (options) => Enrollment_bulkUpdateStatus(request, options),
		cancelEnrollment: (options) => Enrollment_cancelEnrollment(request, options),
		getEnrollableUsers: (options) => Enrollment_getEnrollableUsers(request, options),
		listEnrollments: (options) => Enrollment_listEnrollments(request, options),
		selfEnroll: (options) => Enrollment_selfEnroll(request, options),
		updateStatus: (options) => Enrollment_updateStatus(request, options)
	};
}
//#endregion
//#region src/feedback-admin/index.ts
/** Get one feedback ticket with diagnostics */
async function FeedbackAdmin_detail(request, options) {
	return request("GET", "/admin/feedback/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List feedback tickets across companies */
async function FeedbackAdmin_list(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/admin/feedback", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a feedback ticket's triage status */
async function FeedbackAdmin_updateStatus(request, options) {
	return request("PATCH", "/admin/feedback/{id}/status", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createFeedbackAdminController(request) {
	return {
		detail: (options) => FeedbackAdmin_detail(request, options),
		list: (options) => FeedbackAdmin_list(request, options),
		updateStatus: (options) => FeedbackAdmin_updateStatus(request, options)
	};
}
//#endregion
//#region src/feedback/index.ts
/** Submit feedback (screenshot + annotations + diagnostics) */
async function Feedback_submit(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/feedback", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createFeedbackController(request) {
	return { submit: (options) => Feedback_submit(request, options) };
}
//#endregion
//#region src/form-admin/index.ts
/**
* Archive a form
*
* Transitions a PUBLISHED form to ARCHIVED status and sets archivedAt timestamp.
*/
async function FormAdmin_archiveForm(request, options) {
	return request("PATCH", "/forms/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a new form
*
* Creates a new form with DRAFT status and an auto-generated unique slug.
*/
async function FormAdmin_createForm(request, options) {
	return request("POST", "/forms", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Soft-delete a form
*
* Marks a DRAFT form as deleted. Returns 400 for published or archived forms.
*/
async function FormAdmin_deleteForm(request, options) {
	return request("DELETE", "/forms/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Duplicate a form
*
* Creates a deep copy with new UUIDs for all step/field IDs, a fresh slug, DRAFT status, and "(Copy)" name suffix.
*/
async function FormAdmin_duplicateForm(request, options) {
	return request("POST", "/forms/{id}/duplicate", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Export all submissions for a form as CSV
*
* Streams a CSV file with all submissions. Columns: Submission Date, [form fields], Flagged, IP Address.
* Must be declared BEFORE the :subId route so NestJS matches "export" as a literal, not a param.
*/
async function FormAdmin_exportSubmissions(request, options) {
	return request("GET", "/forms/{id}/submissions/export", {
		params: { id: String(options.id) },
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get analytics for a form
*
* Returns aggregated totals, daily breakdown, conversion rate, and step drop-off data.
* Supports range filtering: last7, last30 (default), last90, or all.
*/
async function FormAdmin_getAnalytics(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.range !== void 0) query["range"] = options.query.range;
	return request("GET", "/forms/{id}/analytics", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a form
*
* Returns one form with its full definition: steps, fields, branching rules, visibility conditions and
* branding. Returns 404 when the form does not exist in the company or is deleted.
*/
async function FormAdmin_getForm(request, options) {
	return request("GET", "/forms/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get referrer analytics for a form
*
* Returns traffic source breakdown with view and submission counts and per-source conversion rates.
* Supports range filtering: last7, last30 (default), last90, or all.
*/
async function FormAdmin_getReferrerAnalytics(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.range !== void 0) query["range"] = options.query.range;
	return request("GET", "/forms/{id}/analytics/referrers", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a form submission
*
* Returns one submission with every answer and the request details captured when it was sent: IP
* address, user agent, referrer, reCAPTCHA score and time spent per step.
*/
async function FormAdmin_getSubmission(request, options) {
	return request("GET", "/forms/{id}/submissions/{subId}", {
		params: {
			id: String(options.id),
			subId: String(options.subId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List the company's forms
*
* Returns the company's forms that are not deleted, newest first, each with its submission count and
* the time of its latest submission. The steps and fields of a form come from the single-form read.
*/
async function FormAdmin_listForms(request, options) {
	return request("GET", "/forms", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a form's submissions
*
* Returns one page of the form's submissions, newest first. `page` starts at 1 (default 1) and
* `pageSize` defaults to 20, at most 100. `search` matches any answer value case-insensitively.
* `createdFrom` and `createdTo` are ISO 8601 date-times that bound when the submission was received, both inclusive.
*/
async function FormAdmin_listSubmissions(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/forms/{id}/submissions", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Publish a form
*
* Transitions a DRAFT form to PUBLISHED status and sets publishedAt timestamp.
*/
async function FormAdmin_publishForm(request, options) {
	return request("PATCH", "/forms/{id}/publish", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Unarchive a form
*
* Transitions an ARCHIVED form back to DRAFT status and clears archivedAt.
*/
async function FormAdmin_unarchiveForm(request, options) {
	return request("PATCH", "/forms/{id}/unarchive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Unpublish a form
*
* Transitions a PUBLISHED form back to DRAFT status and clears publishedAt.
*/
async function FormAdmin_unpublishForm(request, options) {
	return request("PATCH", "/forms/{id}/unpublish", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Full-document save for a form
*
* Replaces steps, fields, conditions, and branding config entirely. Status changes use lifecycle endpoints.
*/
async function FormAdmin_updateForm(request, options) {
	return request("PATCH", "/forms/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Upload a per-form logo
*
* Uploads a logo scoped to this form and returns its url. Does not touch company
* whitelabeling. The returned url is persisted onto the form's branding by the full-document save.
*/
async function FormAdmin_uploadFormLogo(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/forms/{id}/logo", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createFormAdminController(request) {
	return {
		archiveForm: (options) => FormAdmin_archiveForm(request, options),
		createForm: (options) => FormAdmin_createForm(request, options),
		deleteForm: (options) => FormAdmin_deleteForm(request, options),
		duplicateForm: (options) => FormAdmin_duplicateForm(request, options),
		exportSubmissions: (options) => FormAdmin_exportSubmissions(request, options),
		getAnalytics: (options) => FormAdmin_getAnalytics(request, options),
		getForm: (options) => FormAdmin_getForm(request, options),
		getReferrerAnalytics: (options) => FormAdmin_getReferrerAnalytics(request, options),
		getSubmission: (options) => FormAdmin_getSubmission(request, options),
		listForms: (options) => FormAdmin_listForms(request, options),
		listSubmissions: (options) => FormAdmin_listSubmissions(request, options),
		publishForm: (options) => FormAdmin_publishForm(request, options),
		unarchiveForm: (options) => FormAdmin_unarchiveForm(request, options),
		unpublishForm: (options) => FormAdmin_unpublishForm(request, options),
		updateForm: (options) => FormAdmin_updateForm(request, options),
		uploadFormLogo: (options) => FormAdmin_uploadFormLogo(request, options)
	};
}
//#endregion
//#region src/form-public/index.ts
/**
* Get a published form by slug
*
* Returns the full form structure (steps, fields, branding) for a published form.
* Returns 404 for draft, archived, deleted, or non-existent forms.
*/
async function FormPublic_getPublicForm(request, options) {
	return request("GET", "/forms/public/{slug}", {
		params: { slug: String(options.slug) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Submit a form
*
* Validates submission data using form-engine (visible field logic + required checks),
* stores the submission, links uploaded files, and returns the submission ID.
* Rate-limited to 10 submissions per form per IP per hour.
*/
async function FormPublic_submitForm(request, options) {
	return request("POST", "/forms/public/{slug}/submit", {
		params: { slug: String(options.slug) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Track a form analytics event
*
* Fire-and-forget analytics tracking for public form renderer. Tracks views,
* step progression, and submission events. Returns 204 immediately.
*/
async function FormPublic_trackEvent(request, options) {
	return request("POST", "/forms/public/{slug}/track", {
		params: { slug: String(options.slug) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Upload a file for a form session
*
* Temporarily stores an uploaded file under a session ID. Files are linked to the submission
* when the form is submitted. Orphaned files (submissionId null after 24h) are cleaned up automatically.
*/
async function FormPublic_uploadFile(request, options) {
	return request("POST", "/forms/public/{slug}/upload", {
		params: { slug: String(options.slug) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createFormPublicController(request) {
	return {
		getPublicForm: (options) => FormPublic_getPublicForm(request, options),
		submitForm: (options) => FormPublic_submitForm(request, options),
		trackEvent: (options) => FormPublic_trackEvent(request, options),
		uploadFile: (options) => FormPublic_uploadFile(request, options)
	};
}
//#endregion
//#region src/guided-inspection/index.ts
async function GuidedInspection_completeFollowUp(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/follow-ups/{followUpId}/complete", {
		params: {
			inspectionId: String(options.inspectionId),
			followUpId: String(options.followUpId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function GuidedInspection_editFollowUp(request, options) {
	return request("PATCH", "/inspection/inspections/{inspectionId}/follow-ups/{followUpId}", {
		params: {
			inspectionId: String(options.inspectionId),
			followUpId: String(options.followUpId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function GuidedInspection_getTimeline(request, options) {
	return request("GET", "/inspection/inspections/{inspectionId}/timeline", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function GuidedInspection_listFollowUps(request, options) {
	return request("GET", "/inspection/inspections/{inspectionId}/follow-ups", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function GuidedInspection_recordSessionEvents(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/session-events", {
		params: { inspectionId: String(options.inspectionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function GuidedInspection_reopenFollowUp(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/follow-ups/{followUpId}/reopen", {
		params: {
			inspectionId: String(options.inspectionId),
			followUpId: String(options.followUpId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function GuidedInspection_saveFollowUp(request, options) {
	return request("PUT", "/inspection/inspections/{inspectionId}/follow-ups/{followUpId}", {
		params: {
			inspectionId: String(options.inspectionId),
			followUpId: String(options.followUpId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createGuidedInspectionController(request) {
	return {
		completeFollowUp: (options) => GuidedInspection_completeFollowUp(request, options),
		editFollowUp: (options) => GuidedInspection_editFollowUp(request, options),
		getTimeline: (options) => GuidedInspection_getTimeline(request, options),
		listFollowUps: (options) => GuidedInspection_listFollowUps(request, options),
		recordSessionEvents: (options) => GuidedInspection_recordSessionEvents(request, options),
		reopenFollowUp: (options) => GuidedInspection_reopenFollowUp(request, options),
		saveFollowUp: (options) => GuidedInspection_saveFollowUp(request, options)
	};
}
//#endregion
//#region src/health/index.ts
async function Health_checkHealth(request, options) {
	return request("GET", "/healths/check", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createHealthController(request) {
	return { checkHealth: (options) => Health_checkHealth(request, options) };
}
//#endregion
//#region src/identities/index.ts
async function Identities_disconnectIdentity(request, options) {
	return request("DELETE", "/auth/identities/{provider}", {
		params: { provider: String(options.provider) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Identities_listIdentities(request, options) {
	return request("GET", "/auth/identities", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Identities_setPassword(request, options) {
	return request("POST", "/auth/identities/password", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createIdentitiesController(request) {
	return {
		disconnectIdentity: (options) => Identities_disconnectIdentity(request, options),
		listIdentities: (options) => Identities_listIdentities(request, options),
		setPassword: (options) => Identities_setPassword(request, options)
	};
}
//#endregion
//#region src/impersonation/index.ts
/** Return the current impersonation session info if the caller is currently being impersonated */
async function Impersonation_current(request, options) {
	return request("GET", "/admin/impersonate/current", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List impersonation audit sessions */
async function Impersonation_list(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortOrder !== void 0) query["sortOrder"] = options.query.sortOrder;
	if (options.query?.superAdminId !== void 0) query["superAdminId"] = options.query.superAdminId;
	if (options.query?.targetUserId !== void 0) query["targetUserId"] = options.query.targetUserId;
	if (options.query?.active !== void 0) query["active"] = options.query.active;
	if (options.query?.startedFrom !== void 0) query["startedFrom"] = options.query.startedFrom;
	if (options.query?.startedTo !== void 0) query["startedTo"] = options.query.startedTo;
	return request("GET", "/admin/impersonate/sessions", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Start impersonating a user */
async function Impersonation_start(request, options) {
	return request("POST", "/admin/impersonate/start", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Stop the current impersonation session. Callable with the impersonation bearer token. */
async function Impersonation_stop(request, options) {
	return request("POST", "/admin/impersonate/stop", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createImpersonationController(request) {
	return {
		current: (options) => Impersonation_current(request, options),
		list: (options) => Impersonation_list(request, options),
		start: (options) => Impersonation_start(request, options),
		stop: (options) => Impersonation_stop(request, options)
	};
}
//#endregion
//#region src/incoming/index.ts
async function Incoming_assign(request, options) {
	return request("POST", "/crm/incoming/assign", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Incoming_claim(request, options) {
	return request("POST", "/crm/incoming/claim", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Incoming_count(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	if (options.query?.chain !== void 0) query["chain"] = options.query.chain;
	if (options.query?.owner !== void 0) query["owner"] = options.query.owner;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/incoming/count", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Incoming_list(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	if (options.query?.chain !== void 0) query["chain"] = options.query.chain;
	if (options.query?.owner !== void 0) query["owner"] = options.query.owner;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/incoming", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Incoming_reclaim(request, options) {
	return request("POST", "/crm/incoming/reclaim", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Incoming_reject(request, options) {
	return request("POST", "/crm/incoming/reject", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Incoming_release(request, options) {
	return request("POST", "/crm/incoming/release", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createIncomingController(request) {
	return {
		assign: (options) => Incoming_assign(request, options),
		claim: (options) => Incoming_claim(request, options),
		count: (options) => Incoming_count(request, options),
		list: (options) => Incoming_list(request, options),
		reclaim: (options) => Incoming_reclaim(request, options),
		reject: (options) => Incoming_reject(request, options),
		release: (options) => Incoming_release(request, options)
	};
}
//#endregion
//#region src/industry-preset/index.ts
/** Assign an industry preset to a company
NOTE: This must be defined before the parametric :id routes to avoid route collision */
async function IndustryPreset_assignToCompany(request, options) {
	return request("PATCH", "/admin/industry-presets/companies/{targetCompanyId}/industry-preset", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a new industry preset */
async function IndustryPreset_create(request, options) {
	return request("POST", "/admin/industry-presets", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all industry presets (admin) */
async function IndustryPreset_findAll(request, options) {
	return request("GET", "/admin/industry-presets", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List active industry presets (public — name and description only) */
async function IndustryPreset_findAllPublic(request, options) {
	return request("GET", "/industry-presets", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single industry preset */
async function IndustryPreset_findOne(request, options) {
	return request("GET", "/admin/industry-presets/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft delete an industry preset */
async function IndustryPreset_remove(request, options) {
	return request("DELETE", "/admin/industry-presets/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an industry preset */
async function IndustryPreset_update(request, options) {
	return request("PATCH", "/admin/industry-presets/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createIndustryPresetController(request) {
	return {
		assignToCompany: (options) => IndustryPreset_assignToCompany(request, options),
		create: (options) => IndustryPreset_create(request, options),
		findAll: (options) => IndustryPreset_findAll(request, options),
		findAllPublic: (options) => IndustryPreset_findAllPublic(request, options),
		findOne: (options) => IndustryPreset_findOne(request, options),
		remove: (options) => IndustryPreset_remove(request, options),
		update: (options) => IndustryPreset_update(request, options)
	};
}
//#endregion
//#region src/inspection-address-search/index.ts
/** The street address at the phone's position, for the start screen's "You're near" line. */
async function InspectionAddressSearch_reverse(request, options) {
	const query = {};
	if (options.query?.lat !== void 0) query["lat"] = options.query.lat;
	if (options.query?.lng !== void 0) query["lng"] = options.query.lng;
	return request("GET", "/inspection/address-search/reverse", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionAddressSearch_search(request, options) {
	const query = {};
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	return request("GET", "/inspection/address-search", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionAddressSearchController(request) {
	return {
		reverse: (options) => InspectionAddressSearch_reverse(request, options),
		search: (options) => InspectionAddressSearch_search(request, options)
	};
}
//#endregion
//#region src/inspection/index.ts
async function Inspection_complete(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/complete", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_completeCaptureUpload(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/captures/uploads/{captureId}/complete", {
		params: {
			inspectionId: String(options.inspectionId),
			captureId: String(options.captureId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_createCapture(request, options) {
	const params = { inspectionId: String(options.inspectionId) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/inspection/inspections/{inspectionId}/captures", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_discardCapture(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/captures/{captureId}/discard", {
		params: {
			inspectionId: String(options.inspectionId),
			captureId: String(options.captureId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_discardMedia(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/media/{mediaId}/discard", {
		params: {
			inspectionId: String(options.inspectionId),
			mediaId: String(options.mediaId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_editCaptureNote(request, options) {
	return request("PATCH", "/inspection/inspections/{inspectionId}/captures/{captureId}", {
		params: {
			inspectionId: String(options.inspectionId),
			captureId: String(options.captureId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_excludeMedia(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/media/{mediaId}/exclude", {
		params: {
			inspectionId: String(options.inspectionId),
			mediaId: String(options.mediaId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_finishFieldCapture(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/finish", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_getOne(request, options) {
	return request("GET", "/inspection/inspections/{inspectionId}", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_listCaptures(request, options) {
	return request("GET", "/inspection/inspections/{inspectionId}/captures", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_readCaptureUpload(request, options) {
	return request("GET", "/inspection/inspections/{inspectionId}/captures/uploads/{captureId}", {
		params: {
			inspectionId: String(options.inspectionId),
			captureId: String(options.captureId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_remove(request, options) {
	return request("DELETE", "/inspection/inspections/{inspectionId}", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_restoreMedia(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/media/{mediaId}/restore", {
		params: {
			inspectionId: String(options.inspectionId),
			mediaId: String(options.mediaId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_retryMedia(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/media/{mediaId}/retry", {
		params: {
			inspectionId: String(options.inspectionId),
			mediaId: String(options.mediaId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_saveItem(request, options) {
	return request("PUT", "/inspection/inspections/{inspectionId}/items/{itemId}", {
		params: {
			inspectionId: String(options.inspectionId),
			itemId: String(options.itemId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_start(request, options) {
	return request("POST", "/inspection/inspections", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_startCaptureUpload(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/captures/uploads", {
		params: { inspectionId: String(options.inspectionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Start a guided inspection: the instant start's site choice plus the published template version the phone
* rendered its steps from, pinned as given. A replayed idempotencyKey answers with the inspection it started first.
*/
async function Inspection_startGuided(request, options) {
	return request("POST", "/inspection/inspections/guided", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Start an instant inspection: a free field capture with no template, at a saved site or a new site at the
* phone's position. A replayed idempotencyKey answers with the inspection it started first.
*/
async function Inspection_startInstant(request, options) {
	return request("POST", "/inspection/inspections/instant", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_upcoming(request, options) {
	const query = {};
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	return request("GET", "/inspection/inspections/upcoming", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Send one part of a capture upload
*
* The body is the part's raw bytes as `application/octet-stream`, streamed into storage. Every
* part is `partSizeBytes` long except the last. The same part number again replaces the earlier part.
*/
async function Inspection_uploadCapturePart(request, options) {
	return request("PUT", "/inspection/inspections/{inspectionId}/captures/uploads/{captureId}/parts/{partNumber}", {
		params: {
			inspectionId: String(options.inspectionId),
			captureId: String(options.captureId),
			partNumber: String(options.partNumber)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Inspection_uploadMedia(request, options) {
	const params = {
		inspectionId: String(options.inspectionId),
		itemId: String(options.itemId)
	};
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/inspection/inspections/{inspectionId}/items/{itemId}/media", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionController(request) {
	return {
		complete: (options) => Inspection_complete(request, options),
		completeCaptureUpload: (options) => Inspection_completeCaptureUpload(request, options),
		createCapture: (options) => Inspection_createCapture(request, options),
		discardCapture: (options) => Inspection_discardCapture(request, options),
		discardMedia: (options) => Inspection_discardMedia(request, options),
		editCaptureNote: (options) => Inspection_editCaptureNote(request, options),
		excludeMedia: (options) => Inspection_excludeMedia(request, options),
		finishFieldCapture: (options) => Inspection_finishFieldCapture(request, options),
		getOne: (options) => Inspection_getOne(request, options),
		listCaptures: (options) => Inspection_listCaptures(request, options),
		readCaptureUpload: (options) => Inspection_readCaptureUpload(request, options),
		remove: (options) => Inspection_remove(request, options),
		restoreMedia: (options) => Inspection_restoreMedia(request, options),
		retryMedia: (options) => Inspection_retryMedia(request, options),
		saveItem: (options) => Inspection_saveItem(request, options),
		start: (options) => Inspection_start(request, options),
		startCaptureUpload: (options) => Inspection_startCaptureUpload(request, options),
		startGuided: (options) => Inspection_startGuided(request, options),
		startInstant: (options) => Inspection_startInstant(request, options),
		upcoming: (options) => Inspection_upcoming(request, options),
		uploadCapturePart: (options) => Inspection_uploadCapturePart(request, options),
		uploadMedia: (options) => Inspection_uploadMedia(request, options)
	};
}
//#endregion
//#region src/inspection-library/index.ts
async function InspectionLibrary_clone(request, options) {
	return request("POST", "/inspection/library/{starterId}/clone", {
		params: { starterId: String(options.starterId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionLibrary_listStarters(request, options) {
	return request("GET", "/inspection/library", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionLibraryController(request) {
	return {
		clone: (options) => InspectionLibrary_clone(request, options),
		listStarters: (options) => InspectionLibrary_listStarters(request, options)
	};
}
//#endregion
//#region src/inspection-list/index.ts
/**
* Who is free at a start time: each member against the meetings, self-booking slots and inspections already on
* their day. An inspection is read as a 90-minute block.
*/
async function InspectionList_availabilityAt(request, options) {
	const query = {};
	if (options.query?.at !== void 0) query["at"] = options.query.at;
	if (options.query?.memberIds !== void 0) query["memberIds"] = options.query.memberIds;
	if (options.query?.exceptInspectionId !== void 0) query["exceptInspectionId"] = options.query.exceptInspectionId;
	return request("GET", "/inspection/availability", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Take a booking off the schedule: the inspector is told and the row is deleted. Refused once anything has
* been captured; a started inspection is deleted, not cancelled.
*/
async function InspectionList_cancelScheduled(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/cancel", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionList_list(request, options) {
	const query = {};
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.chain !== void 0) query["chain"] = options.query.chain;
	if (options.query?.states !== void 0) query["states"] = options.query.states;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.inProgressFirst !== void 0) query["inProgressFirst"] = options.query.inProgressFirst;
	return request("GET", "/inspection/inspections", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Move the date or the assignee of any inspection, with or without a linked deal. */
async function InspectionList_reschedule(request, options) {
	return request("PATCH", "/inspection/inspections/{inspectionId}/reschedule", {
		params: { inspectionId: String(options.inspectionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionList_schedule(request, options) {
	return request("POST", "/inspection/inspections/schedule", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionList_startContext(request, options) {
	const query = {};
	if (options.query?.dealId !== void 0) query["dealId"] = options.query.dealId;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/inspection/start-context", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Change a booking the inspector has not started: name, site, time, inspector, template, deal and the note.
* Refused with INSPECTION_ALREADY_STARTED once anything has been captured.
*/
async function InspectionList_updateScheduled(request, options) {
	return request("PATCH", "/inspection/inspections/{inspectionId}", {
		params: { inspectionId: String(options.inspectionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionListController(request) {
	return {
		availabilityAt: (options) => InspectionList_availabilityAt(request, options),
		cancelScheduled: (options) => InspectionList_cancelScheduled(request, options),
		list: (options) => InspectionList_list(request, options),
		reschedule: (options) => InspectionList_reschedule(request, options),
		schedule: (options) => InspectionList_schedule(request, options),
		startContext: (options) => InspectionList_startContext(request, options),
		updateScheduled: (options) => InspectionList_updateScheduled(request, options)
	};
}
//#endregion
//#region src/inspection-media/index.ts
async function InspectionMedia_download(request, options) {
	return request("GET", "/inspection/media/{mediaId}/download", {
		params: { mediaId: String(options.mediaId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionMedia_thumbnail(request, options) {
	return request("GET", "/inspection/media/{mediaId}/thumbnail", {
		params: { mediaId: String(options.mediaId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionMediaController(request) {
	return {
		download: (options) => InspectionMedia_download(request, options),
		thumbnail: (options) => InspectionMedia_thumbnail(request, options)
	};
}
//#endregion
//#region src/inspection-media-legacy-open/index.ts
async function InspectionMediaLegacyOpen_open(request, options) {
	const params = {
		linkCompanyId: String(options.linkCompanyId),
		mediaId: String(options.mediaId)
	};
	const query = {};
	if (options.query?.sig !== void 0) query["sig"] = options.query.sig;
	return request("GET", "/companies/{linkCompanyId}/inspection/media/{mediaId}/open", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionMediaLegacyOpenController(request) {
	return { open: (options) => InspectionMediaLegacyOpen_open(request, options) };
}
//#endregion
//#region src/inspection-media-open/index.ts
async function InspectionMediaOpen_open(request, options) {
	const params = { mediaId: String(options.mediaId) };
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.sig !== void 0) query["sig"] = options.query.sig;
	return request("GET", "/inspection/media/{mediaId}/open", {
		params,
		query,
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionMediaOpenController(request) {
	return { open: (options) => InspectionMediaOpen_open(request, options) };
}
//#endregion
//#region src/inspection-report/index.ts
async function InspectionReport_acceptAllClauses(request, options) {
	return request("POST", "/inspection/reports/{reportId}/accept-all-clauses", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_acceptClause(request, options) {
	return request("POST", "/inspection/reports/{reportId}/accept-clause", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_answerQuestion(request, options) {
	return request("POST", "/inspection/reports/{reportId}/questions/{questionId}/answer", {
		params: {
			reportId: String(options.reportId),
			questionId: String(options.questionId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_attest(request, options) {
	return request("POST", "/inspection/reports/{reportId}/attest", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_correctClause(request, options) {
	return request("POST", "/inspection/reports/{reportId}/correct-clause", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_dismissQuestion(request, options) {
	return request("POST", "/inspection/reports/{reportId}/questions/{questionId}/dismiss", {
		params: {
			reportId: String(options.reportId),
			questionId: String(options.questionId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_download(request, options) {
	const params = {
		reportId: String(options.reportId),
		version: String(options.version)
	};
	const query = {};
	if (options.query?.prices !== void 0) query["prices"] = options.query.prices;
	return request("GET", "/inspection/reports/{reportId}/versions/{version}/download", {
		params,
		query,
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_editSelection(request, options) {
	return request("POST", "/inspection/reports/{reportId}/selection-edit", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_estimate(request, options) {
	return request("GET", "/inspection/reports/{reportId}/estimate", {
		params: { reportId: String(options.reportId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_firstPage(request, options) {
	return request("GET", "/inspection/reports/{reportId}/versions/{version}/first-page", {
		params: {
			reportId: String(options.reportId),
			version: String(options.version)
		},
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_getByInspection(request, options) {
	return request("GET", "/inspection/reports/by-inspection/{inspectionId}", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_preview(request, options) {
	return request("GET", "/inspection/reports/{reportId}/versions/{version}/preview", {
		params: {
			reportId: String(options.reportId),
			version: String(options.version)
		},
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_refine(request, options) {
	return request("POST", "/inspection/reports/{reportId}/refine", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_restore(request, options) {
	return request("POST", "/inspection/reports/{reportId}/restore", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_retry(request, options) {
	return request("POST", "/inspection/reports/{reportId}/retry", {
		params: { reportId: String(options.reportId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_saveEdits(request, options) {
	return request("POST", "/inspection/reports/{reportId}/edits", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_send(request, options) {
	return request("POST", "/inspection/reports/{reportId}/send", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionReport_workingCopy(request, options) {
	return request("GET", "/inspection/reports/{reportId}/versions/{version}/working-copy", {
		params: {
			reportId: String(options.reportId),
			version: String(options.version)
		},
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionReportController(request) {
	return {
		acceptAllClauses: (options) => InspectionReport_acceptAllClauses(request, options),
		acceptClause: (options) => InspectionReport_acceptClause(request, options),
		answerQuestion: (options) => InspectionReport_answerQuestion(request, options),
		attest: (options) => InspectionReport_attest(request, options),
		correctClause: (options) => InspectionReport_correctClause(request, options),
		dismissQuestion: (options) => InspectionReport_dismissQuestion(request, options),
		download: (options) => InspectionReport_download(request, options),
		editSelection: (options) => InspectionReport_editSelection(request, options),
		estimate: (options) => InspectionReport_estimate(request, options),
		firstPage: (options) => InspectionReport_firstPage(request, options),
		getByInspection: (options) => InspectionReport_getByInspection(request, options),
		preview: (options) => InspectionReport_preview(request, options),
		refine: (options) => InspectionReport_refine(request, options),
		restore: (options) => InspectionReport_restore(request, options),
		retry: (options) => InspectionReport_retry(request, options),
		saveEdits: (options) => InspectionReport_saveEdits(request, options),
		send: (options) => InspectionReport_send(request, options),
		workingCopy: (options) => InspectionReport_workingCopy(request, options)
	};
}
//#endregion
//#region src/inspection-site/index.ts
async function InspectionSite_archive(request, options) {
	return request("POST", "/inspection/sites/{siteId}/archive", {
		params: { siteId: String(options.siteId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_bulkDismiss(request, options) {
	return request("POST", "/inspection/sites/bulk-dismiss", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_bulkSnooze(request, options) {
	return request("POST", "/inspection/sites/bulk-snooze", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_create(request, options) {
	return request("POST", "/inspection/sites", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_dismiss(request, options) {
	return request("POST", "/inspection/sites/{siteId}/dismiss", {
		params: { siteId: String(options.siteId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sortDir !== void 0) query["sortDir"] = options.query.sortDir;
	return request("GET", "/inspection/sites", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_findOne(request, options) {
	return request("GET", "/inspection/sites/{siteId}", {
		params: { siteId: String(options.siteId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_markDue(request, options) {
	return request("POST", "/inspection/sites/{siteId}/mark-due", {
		params: { siteId: String(options.siteId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Saved sites near the phone, nearest first, for the start screen's "Your location" and suggestions. */
async function InspectionSite_nearby(request, options) {
	const query = {};
	if (options.query?.lat !== void 0) query["lat"] = options.query.lat;
	if (options.query?.lng !== void 0) query["lng"] = options.query.lng;
	if (options.query?.radiusMeters !== void 0) query["radiusMeters"] = options.query.radiusMeters;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/inspection/sites/nearby", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_resetReminder(request, options) {
	return request("POST", "/inspection/sites/{siteId}/reset-reminder", {
		params: { siteId: String(options.siteId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_snooze(request, options) {
	return request("POST", "/inspection/sites/{siteId}/snooze", {
		params: { siteId: String(options.siteId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_unarchive(request, options) {
	return request("POST", "/inspection/sites/{siteId}/unarchive", {
		params: { siteId: String(options.siteId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionSite_update(request, options) {
	return request("PATCH", "/inspection/sites/{siteId}", {
		params: { siteId: String(options.siteId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionSiteController(request) {
	return {
		archive: (options) => InspectionSite_archive(request, options),
		bulkDismiss: (options) => InspectionSite_bulkDismiss(request, options),
		bulkSnooze: (options) => InspectionSite_bulkSnooze(request, options),
		create: (options) => InspectionSite_create(request, options),
		dismiss: (options) => InspectionSite_dismiss(request, options),
		findAll: (options) => InspectionSite_findAll(request, options),
		findOne: (options) => InspectionSite_findOne(request, options),
		markDue: (options) => InspectionSite_markDue(request, options),
		nearby: (options) => InspectionSite_nearby(request, options),
		resetReminder: (options) => InspectionSite_resetReminder(request, options),
		snooze: (options) => InspectionSite_snooze(request, options),
		unarchive: (options) => InspectionSite_unarchive(request, options),
		update: (options) => InspectionSite_update(request, options)
	};
}
//#endregion
//#region src/inspection-template/index.ts
async function InspectionTemplate_archive(request, options) {
	return request("POST", "/inspection/templates/{templateId}/archive", {
		params: { templateId: String(options.templateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_create(request, options) {
	return request("POST", "/inspection/templates", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_duplicate(request, options) {
	return request("POST", "/inspection/templates/{templateId}/duplicate", {
		params: { templateId: String(options.templateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_findOne(request, options) {
	return request("GET", "/inspection/templates/{templateId}", {
		params: { templateId: String(options.templateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_list(request, options) {
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.trade !== void 0) query["trade"] = options.query.trade;
	if (options.query?.includeArchived !== void 0) query["includeArchived"] = options.query.includeArchived;
	return request("GET", "/inspection/templates", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_listPublished(request, options) {
	return request("GET", "/inspection/templates/published", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_listVersions(request, options) {
	return request("GET", "/inspection/templates/{templateId}/versions", {
		params: { templateId: String(options.templateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_publish(request, options) {
	return request("POST", "/inspection/templates/{templateId}/publish", {
		params: { templateId: String(options.templateId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_remove(request, options) {
	return request("DELETE", "/inspection/templates/{templateId}", {
		params: { templateId: String(options.templateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_restoreVersion(request, options) {
	return request("POST", "/inspection/templates/{templateId}/restore-version", {
		params: { templateId: String(options.templateId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_saveDraft(request, options) {
	return request("PATCH", "/inspection/templates/{templateId}", {
		params: { templateId: String(options.templateId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_unarchive(request, options) {
	return request("POST", "/inspection/templates/{templateId}/unarchive", {
		params: { templateId: String(options.templateId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function InspectionTemplate_uploadLogo(request, options) {
	const params = { templateId: String(options.templateId) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/inspection/templates/{templateId}/logo", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInspectionTemplateController(request) {
	return {
		archive: (options) => InspectionTemplate_archive(request, options),
		create: (options) => InspectionTemplate_create(request, options),
		duplicate: (options) => InspectionTemplate_duplicate(request, options),
		findOne: (options) => InspectionTemplate_findOne(request, options),
		list: (options) => InspectionTemplate_list(request, options),
		listPublished: (options) => InspectionTemplate_listPublished(request, options),
		listVersions: (options) => InspectionTemplate_listVersions(request, options),
		publish: (options) => InspectionTemplate_publish(request, options),
		remove: (options) => InspectionTemplate_remove(request, options),
		restoreVersion: (options) => InspectionTemplate_restoreVersion(request, options),
		saveDraft: (options) => InspectionTemplate_saveDraft(request, options),
		unarchive: (options) => InspectionTemplate_unarchive(request, options),
		uploadLogo: (options) => InspectionTemplate_uploadLogo(request, options)
	};
}
//#endregion
//#region src/internal-migration/index.ts
/** Backfill one device-token row per user-company context from existing registrations

Idempotent. For every existing device token, creates the sibling context rows for the other
companies its owner belongs to, so a device active in companies A and B carries a row for each and
push delivery can scope by company. `dryRun: true` reports the row count without writing;
`dryRun: false` requires the confirmation phrase. */
async function InternalMigration_backfillDeviceTokenContexts(request, options) {
	return request("POST", "/admin/internal/backfill-device-token-contexts", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Tag every existing meeting with its origin (CRM lead/deal, booking, or manual)

Idempotent. Tags meetings linked from a booking slot as BOOKING and meetings linked from a CRM
MEETING scheduled-activity as CRM (carrying the lead/deal id), then marks the remainder MANUAL.
Every write is guarded on `source` being unset, so a re-run is a no-op. `dryRun: true` reports the
counts without writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_backfillMeetingSource(request, options) {
	return request("POST", "/admin/internal/backfill-meeting-source", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Give inspection sites made before addresses were located their map position

Resolves each site with an address and no position the way a save does (place details, else geocoding),
at most `limit` sites per run (200 by default) since each is a Google lookup. A site the lookup cannot place
stays as it is, so a re-run only finds what is still missing. `dryRun: true` reports the count without
writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_backfillSitePositions(request, options) {
	return request("POST", "/admin/internal/backfill-site-positions", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed default staffing categories + skill levels per chain and backfill posting categories

Idempotent. Seeds the default staffing categories and skill levels for every chain
parent that lacks them, then maps each categoryless posting's legacy type onto that
chain's available/needed category. Skips postings that already have a categoryId.
`dryRun: true` reports counts without writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_backfillStaffingTaxonomy(request, options) {
	return request("POST", "/admin/internal/backfill-staffing-taxonomy", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Consolidate legacy identities into the single `user` store

Kept as its own endpoint: this is the production identity migration and is run
(and verified) on its own before anything else. Runs synchronously and returns
the JSON manifest (the rollback artifact). `dryRun: true` computes the manifest
without writing. `dryRun: false` requires the confirmation phrase in `confirm`. */
async function InternalMigration_consolidateIdentities(request, options) {
	return request("POST", "/admin/internal/consolidate-identities", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Progress and final report of the Elfag chain seed */
async function InternalMigration_elfagChainSeedStatus(request, options) {
	return request("GET", "/admin/internal/seed-elfag-chain/status", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Run the one-time enum -> role migration (idempotent, snapshot-first)

Kept as its own endpoint: this is the production role migration and is run
(and verified) on its own. Also materializes the extra-role presets: companies
with `extraRolesEnabled` get all five custom roles, other companies only the
enum values they actually hold. */
async function InternalMigration_migrateCompanyRoles(request, options) {
	return request("POST", "/admin/internal/migrate-company-roles", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Backfill CompetenceType.requiredForRoleIds from the legacy requiredForRoles enum

Idempotent. For every competence type that still carries a legacy enum requiredForRoles
and no requiredForRoleIds, resolves each enum value to the matching RoleEntity id in the
type's own company (system role by systemKey, migrated custom role by name) and writes it.
`dryRun: true` reports counts without writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_migrateCompetenceRequiredRoles(request, options) {
	return request("POST", "/admin/internal/migrate-competence-required-roles", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed default contact categories and backfill legacy contacts onto them

Idempotent. Ensures the three default categories exist per chain parent that has
contacts or members, then maps each categoryless contact's legacy contactType onto
its default category and moves facet columns into fieldValues. `dryRun: true` reports
counts without writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_migrateContactsToCategories(request, options) {
	return request("POST", "/admin/internal/migrate-contacts-to-categories", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Migrate the single smsContent string into the smsTemplates {en, no} composite

Idempotent. Moves each company's existing smsContent into its default-locale slot (the owner's
user language, Norwegian fallback) and seeds the other locale with the default template.
`dryRun: true` reports the split without writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_migrateSmsTemplates(request, options) {
	return request("POST", "/admin/internal/migrate-sms-templates", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Normalize offer artifact storage keys stored as full CDN URLs down to bare object keys

Idempotent. Strips a leading `https?://host/` prefix off each offer version's plainPdfKey,
sealedPdfKey and signatureImageKey so a key persisted under a past CDN domain resolves again
(VAD-1287). `dryRun: true` reports the candidate counts without writing; `dryRun: false` requires
the confirmation phrase. */
async function InternalMigration_normalizeOfferStorageKeys(request, options) {
	return request("POST", "/admin/internal/normalize-offer-storage-keys", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Unset the retired BookingConfigEntity.brandColor field

Idempotent. Unsets brandColor on every booking config that still carries it, so the retired
per-booking brand colour no longer overrides the resolved tenant brand. `dryRun: true` reports
the candidate count without writing; `dryRun: false` requires the confirmation phrase. */
async function InternalMigration_removeBookingBrandColor(request, options) {
	return request("POST", "/admin/internal/remove-booking-brand-color", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Seed the real Elfag chain from members.json (production onboarding)

Creates the Elfag head office as chain parent with its branding, every member
company with its published profile, org number, address, logo and coverage
area, ACTIVE memberships, premium billing, default roles, pipelines and
taxonomies, and every employee with an address of their own as a login. A
shared inbox becomes one mailbox login named after the company and is its
OWNER; the people behind it are reported, never invented. No emails are sent.
Every login the run creates gets its own 8-character first-login password,
derived from the email and printed by the super-admin chain page CSV.
Existing companies and users are only filled where blank; an existing user
keeps their password. Idempotent.
`dryRun: true` answers the plan synchronously; `dryRun: false` requires the
confirmation phrase, starts the apply in the background and answers `running`
at once. Poll `GET seed-elfag-chain/status` for progress and the final report;
every company is also logged as it is seeded. */
async function InternalMigration_seedElfagChain(request, options) {
	return request("POST", "/admin/internal/seed-elfag-chain", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInternalMigrationController(request) {
	return {
		backfillDeviceTokenContexts: (options) => InternalMigration_backfillDeviceTokenContexts(request, options),
		backfillMeetingSource: (options) => InternalMigration_backfillMeetingSource(request, options),
		backfillSitePositions: (options) => InternalMigration_backfillSitePositions(request, options),
		backfillStaffingTaxonomy: (options) => InternalMigration_backfillStaffingTaxonomy(request, options),
		consolidateIdentities: (options) => InternalMigration_consolidateIdentities(request, options),
		elfagChainSeedStatus: (options) => InternalMigration_elfagChainSeedStatus(request, options),
		migrateCompanyRoles: (options) => InternalMigration_migrateCompanyRoles(request, options),
		migrateCompetenceRequiredRoles: (options) => InternalMigration_migrateCompetenceRequiredRoles(request, options),
		migrateContactsToCategories: (options) => InternalMigration_migrateContactsToCategories(request, options),
		migrateSmsTemplates: (options) => InternalMigration_migrateSmsTemplates(request, options),
		normalizeOfferStorageKeys: (options) => InternalMigration_normalizeOfferStorageKeys(request, options),
		removeBookingBrandColor: (options) => InternalMigration_removeBookingBrandColor(request, options),
		seedElfagChain: (options) => InternalMigration_seedElfagChain(request, options)
	};
}
//#endregion
//#region src/invoicing/index.ts
/** Cancel an issued, unpaid Vadio-sent invoice; it stays as a cancelled version */
async function Invoicing_cancel(request, options) {
	return request("POST", "/invoicing/invoices/{id}/cancel", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete an uploaded draft invoice so a new PDF can be uploaded */
async function Invoicing_deleteDraft(request, options) {
	return request("DELETE", "/invoicing/invoices/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_detail(request, options) {
	return request("GET", "/invoicing/invoices/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_issue(request, options) {
	return request("POST", "/invoicing/invoices/{id}/issue", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_list(request, options) {
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.source !== void 0) query["source"] = options.query.source;
	if (options.query?.issuedFrom !== void 0) query["issuedFrom"] = options.query.issuedFrom;
	if (options.query?.issuedTo !== void 0) query["issuedTo"] = options.query.issuedTo;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	return request("GET", "/invoicing/invoices", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_markPaid(request, options) {
	return request("POST", "/invoicing/invoices/{id}/mark-paid", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_pdf(request, options) {
	return request("GET", "/invoicing/invoices/{id}/pdf", {
		params: { id: String(options.id) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_prepare(request, options) {
	return request("POST", "/invoicing/orders/{orderId}/prepare", {
		params: { orderId: String(options.orderId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Cancel an issued invoice and open the next version as a draft with the same lines */
async function Invoicing_reissue(request, options) {
	return request("POST", "/invoicing/invoices/{id}/reissue", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_resend(request, options) {
	return request("POST", "/invoicing/invoices/{id}/resend", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_retryPush(request, options) {
	return request("POST", "/invoicing/invoices/{id}/retry-push", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_update(request, options) {
	return request("PATCH", "/invoicing/invoices/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_updateLines(request, options) {
	return request("PUT", "/invoicing/invoices/{id}/lines", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload the invoice PDF issued elsewhere for a completed order (triggers AI parsing)
Only for a company without the Invoicing module, once per order. */
async function Invoicing_upload(request, options) {
	const params = { orderId: String(options.orderId) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/invoicing/orders/{orderId}/upload", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Invoicing_voidInvoice(request, options) {
	return request("POST", "/invoicing/invoices/{id}/void", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createInvoicingController(request) {
	return {
		cancel: (options) => Invoicing_cancel(request, options),
		deleteDraft: (options) => Invoicing_deleteDraft(request, options),
		detail: (options) => Invoicing_detail(request, options),
		issue: (options) => Invoicing_issue(request, options),
		list: (options) => Invoicing_list(request, options),
		markPaid: (options) => Invoicing_markPaid(request, options),
		pdf: (options) => Invoicing_pdf(request, options),
		prepare: (options) => Invoicing_prepare(request, options),
		reissue: (options) => Invoicing_reissue(request, options),
		resend: (options) => Invoicing_resend(request, options),
		retryPush: (options) => Invoicing_retryPush(request, options),
		update: (options) => Invoicing_update(request, options),
		updateLines: (options) => Invoicing_updateLines(request, options),
		upload: (options) => Invoicing_upload(request, options),
		voidInvoice: (options) => Invoicing_voidInvoice(request, options)
	};
}
//#endregion
//#region src/itx-admin/index.ts
/**
* Create ITX integration
*
* Creates a new ITX integration row for a company.
* Generates a webhook URL to share with ITX support during onboarding.
*/
async function ItxAdmin_createIntegration(request, options) {
	return request("POST", "/admin/itx-integrations", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Disable ITX integration
*
* Sets status to DISABLED. Subsequent webhooks return 410.
*/
async function ItxAdmin_disableIntegration(request, options) {
	return request("POST", "/admin/itx-integrations/{id}/disable", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get ITX integration status
*
* Returns current status, last error, and sync stats.
*/
async function ItxAdmin_getIntegration(request, options) {
	return request("GET", "/admin/itx-integrations/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List backfill collisions
*
* Returns paginated list of ItxBackfillCollisionEntity rows.
* Defaults to NEEDS_REVIEW; pass status=ALL for all statuses.
*/
async function ItxAdmin_listCollisions(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.skip !== void 0) query["skip"] = options.query.skip;
	if (options.query?.take !== void 0) query["take"] = options.query.take;
	return request("GET", "/admin/itx-integrations/{id}/collisions", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List failed outbound syncs
*
* Returns CrmContactEntity rows where itxLastSyncError is set for
* the given integration's company. Paginated.
*/
async function ItxAdmin_listFailedSyncs(request, options) {
	const params = { id: String(options.id) };
	const query = {};
	if (options.query?.skip !== void 0) query["skip"] = options.query.skip;
	if (options.query?.take !== void 0) query["take"] = options.query.take;
	return request("GET", "/admin/itx-integrations/{id}/failed-syncs", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Execute the pre-connection bulk push
*
* Enqueues one outbound.create-private-customer job per eligible
* Vadio contact. Idempotent — re-running on a fully-synced tenant enqueues 0 jobs.
* Integration must be ACTIVE.
*/
async function ItxAdmin_preConnectionPushConfirm(request, options) {
	return request("POST", "/admin/itx-integrations/{id}/push-unsynced/confirm", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Dry-run preview of pre-connection push
*
* Returns the count and a sample (up to 10) of Vadio contacts
* that would be pushed to ITX. No side-effects. Integration must be ACTIVE.
*/
async function ItxAdmin_preConnectionPushDryRun(request, options) {
	return request("POST", "/admin/itx-integrations/{id}/push-unsynced", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Resolve a backfill collision
*
* Actions: CLAIM (requires clientId), CREATE_NEW, IGNORE.
*/
async function ItxAdmin_resolveCollision(request, options) {
	return request("POST", "/admin/itx-integrations/{id}/collisions/{collisionId}/resolve", {
		params: {
			id: String(options.id),
			collisionId: String(options.collisionId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Retry a failed outbound sync
*
* Clears itxLastSyncError and re-enqueues outbound.create-private-customer.
* The client must belong to the integration's company.
*/
async function ItxAdmin_retryFailedSync(request, options) {
	return request("POST", "/admin/itx-integrations/{id}/retry-failed-sync/{clientId}", {
		params: {
			id: String(options.id),
			clientId: String(options.clientId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Trigger backfill
*
* Sets status to BACKFILLING and enqueues the first page of the corporate phase.
* Pass reset=true to restart from offset 0 and clear all cursors.
*/
async function ItxAdmin_triggerBackfill(request, options) {
	return request("POST", "/admin/itx-integrations/{id}/backfill", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update ITX integration credentials
*
* Updates credentials and validates by calling GET /api/state against ITX.
* On success transitions status to ACTIVE.
*/
async function ItxAdmin_updateIntegration(request, options) {
	return request("PATCH", "/admin/itx-integrations/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createItxAdminController(request) {
	return {
		createIntegration: (options) => ItxAdmin_createIntegration(request, options),
		disableIntegration: (options) => ItxAdmin_disableIntegration(request, options),
		getIntegration: (options) => ItxAdmin_getIntegration(request, options),
		listCollisions: (options) => ItxAdmin_listCollisions(request, options),
		listFailedSyncs: (options) => ItxAdmin_listFailedSyncs(request, options),
		preConnectionPushConfirm: (options) => ItxAdmin_preConnectionPushConfirm(request, options),
		preConnectionPushDryRun: (options) => ItxAdmin_preConnectionPushDryRun(request, options),
		resolveCollision: (options) => ItxAdmin_resolveCollision(request, options),
		retryFailedSync: (options) => ItxAdmin_retryFailedSync(request, options),
		triggerBackfill: (options) => ItxAdmin_triggerBackfill(request, options),
		updateIntegration: (options) => ItxAdmin_updateIntegration(request, options)
	};
}
//#endregion
//#region src/itx-frame/index.ts
/**
* ITX frame — start instant inspection
*
* Creates a meeting starting now (non-scheduled) for the given client and
* returns the join link so the iframe can present it in a copy/open-in-new-tab modal.
*/
async function ItxFrame_createInstantMeeting(request, options) {
	return request("POST", "/itx-frame/instant-meeting", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* ITX frame — customer overview
*
* Returns the client, aggregated stats, upcoming and recent meetings
* in a single response so the iframe can render with one network call.
*/
async function ItxFrame_getCustomerOverview(request, options) {
	return request("GET", "/itx-frame/customer/{clientID}/overview", {
		params: { clientID: String(options.clientID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* ITX frame — schedule inspection
*
* Creates a future-dated meeting for the given client. Returns the join link.
*/
async function ItxFrame_scheduleMeeting(request, options) {
	return request("POST", "/itx-frame/schedule-meeting", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createItxFrameController(request) {
	return {
		createInstantMeeting: (options) => ItxFrame_createInstantMeeting(request, options),
		getCustomerOverview: (options) => ItxFrame_getCustomerOverview(request, options),
		scheduleMeeting: (options) => ItxFrame_scheduleMeeting(request, options)
	};
}
//#endregion
//#region src/itx-frame-resolver/index.ts
/** ITX frame — resolve Vadio company from an ITX corp id */
async function ItxFrameResolver_resolveCompany(request, options) {
	const query = {};
	if (options.query?.corpId !== void 0) query["corpId"] = options.query.corpId;
	return request("GET", "/itx-frame/resolve-company", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createItxFrameResolverController(request) {
	return { resolveCompany: (options) => ItxFrameResolver_resolveCompany(request, options) };
}
//#endregion
//#region src/itx-webhook/index.ts
/**
* ITX webhook receiver
*
* Receives inbound ITX webhook events, verifies the HMAC signature,
* and enqueues each event for asynchronous processing.
*/
async function ItxWebhook_handleWebhook(request, options) {
	return request("POST", "/webhooks/itx/{webhookToken}", {
		params: { webhookToken: String(options.webhookToken) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createItxWebhookController(request) {
	return { handleWebhook: (options) => ItxWebhook_handleWebhook(request, options) };
}
//#endregion
//#region src/lead-deal/index.ts
/**
* Archive a lead or deal
*
* Archives the record, which hides it from the default list. Archiving an archived record changes nothing. A record owned by another company in your chain returns 404.
*/
async function LeadDeal_archive(request, options) {
	return request("POST", "/crm/lead-deals/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_assignOwner(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/owner", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_batchArchive(request, options) {
	return request("POST", "/crm/lead-deals/batch-archive", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_batchRestore(request, options) {
	return request("POST", "/crm/lead-deals/batch-restore", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_clearNext(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/next-activity/clear", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_convertToOrder(request, options) {
	return request("POST", "/crm/lead-deals/{id}/convert", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create a lead or deal
*
* Creates a lead, or a deal when `phase` is `DEAL`, at the top of its stage. Without `pipelineId` it goes to your default pipeline, and without `stageId` to the pipeline's first stage. A stage from another pipeline or an owner who is not a member of your company returns 400; a pipeline, contact or company you cannot see returns 404.
*/
async function LeadDeal_create(request, options) {
	return request("POST", "/crm/lead-deals", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_exportLeadDeals(request, options) {
	const query = {};
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	if (options.query?.stageId !== void 0) query["stageId"] = options.query.stageId;
	if (options.query?.contactId !== void 0) query["contactId"] = options.query.contactId;
	if (options.query?.phase !== void 0) query["phase"] = options.query.phase;
	if (options.query?.outcome !== void 0) query["outcome"] = options.query.outcome;
	if (options.query?.ownerId !== void 0) query["ownerId"] = options.query.ownerId;
	if (options.query?.tag !== void 0) query["tag"] = options.query.tag;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.order !== void 0) query["order"] = options.query.order;
	if (options.query?.recordSet !== void 0) query["recordSet"] = options.query.recordSet;
	if (options.query?.chain !== void 0) query["chain"] = options.query.chain;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/lead-deals/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a lead or deal
*
* Returns the record with its tags, custom field values, owner, next step and linked order, plus the tag options and lost reasons you can pick from. Archived records are returned too. An id from another company returns 404.
*/
async function LeadDeal_get(request, options) {
	return request("GET", "/crm/lead-deals/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_linkCompany(request, options) {
	return request("POST", "/crm/lead-deals/{id}/link-company", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_linkContact(request, options) {
	return request("POST", "/crm/lead-deals/{id}/link-contact", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List leads and deals
*
* Returns one page of leads and deals, in board order unless `sort` says otherwise. By default the list holds the active deals only; use `recordSet` to see leads or archived records.
*
* Query parameters:
*
* - `recordSet`: `active` (default) for deals that are not archived, `unclaimed` for unassigned leads, `archived` for archived records, or `all` for everything.
* - `pipelineId`, `stageId`, `contactId`, `ownerId`: only records in that pipeline or stage, for that contact, or owned by that member.
* - `phase`: `LEAD` or `DEAL`.
* - `outcome`: `Open`, `Won` or `Lost`.
* - `tag`: a tag option id. Only records carrying that tag.
* - `search`: matches the title ignoring case, or the record number when the term is a whole number.
* - `page`: page number, starting at 1. Defaults to 1.
* - `pageSize`: records per page, 1 to 200. Defaults to 25.
* - `sort`: `rank` (default, board order), `createdAt`, `updatedAt`, `value` or `lastActivityAt`.
* - `order`: `asc` or `desc`. Defaults to `asc`, and to `desc` when sorting by `lastActivityAt`.
* - `chain`: `all` (default) for every record you can see, `own` to leave out records shared through your chain, or `chain` for only those.
* - `createdFrom`, `createdTo`: ISO 8601 date-times. Only records created within the range, both ends included.
*
* Leads and deals need the CRM add-on; without it every call returns 403.
*/
async function LeadDeal_list(request, options) {
	const query = {};
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	if (options.query?.stageId !== void 0) query["stageId"] = options.query.stageId;
	if (options.query?.contactId !== void 0) query["contactId"] = options.query.contactId;
	if (options.query?.phase !== void 0) query["phase"] = options.query.phase;
	if (options.query?.outcome !== void 0) query["outcome"] = options.query.outcome;
	if (options.query?.ownerId !== void 0) query["ownerId"] = options.query.ownerId;
	if (options.query?.tag !== void 0) query["tag"] = options.query.tag;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.order !== void 0) query["order"] = options.query.order;
	if (options.query?.recordSet !== void 0) query["recordSet"] = options.query.recordSet;
	if (options.query?.chain !== void 0) query["chain"] = options.query.chain;
	if (options.query?.createdFrom !== void 0) query["createdFrom"] = options.query.createdFrom;
	if (options.query?.createdTo !== void 0) query["createdTo"] = options.query.createdTo;
	return request("GET", "/crm/lead-deals", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Move a lead or deal to a stage
*
* Moves the record to another stage in its pipeline, or to a new position in its stage. Pass the record's `updatedAt` as `lastSeenUpdatedAt` to get 409 instead of overwriting a change someone else made since you read it. A stage from another pipeline returns 400.
*/
async function LeadDeal_moveStage(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/stage", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_moveToPipeline(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/pipeline", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Restore a lead or deal
*
* Brings back an archived record. Restoring a record that is not archived changes nothing.
*/
async function LeadDeal_restore(request, options) {
	return request("POST", "/crm/lead-deals/{id}/restore", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_scheduleNext(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/next-activity", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Set the outcome of a lead or deal
*
* Marks the record `Won` or `Lost`, which stamps `closedAt`, or reopens it with `Open`. The stage does not change. A `lostReason` is kept only for `Lost`.
*/
async function LeadDeal_setOutcome(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}/outcome", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update a lead or deal
*
* Changes the title, value, notes, contact or company. Only the fields you send change. Archived records return 404.
*/
async function LeadDeal_update(request, options) {
	return request("PATCH", "/crm/lead-deals/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LeadDeal_upgrade(request, options) {
	return request("POST", "/crm/lead-deals/{id}/upgrade", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createLeadDealController(request) {
	return {
		archive: (options) => LeadDeal_archive(request, options),
		assignOwner: (options) => LeadDeal_assignOwner(request, options),
		batchArchive: (options) => LeadDeal_batchArchive(request, options),
		batchRestore: (options) => LeadDeal_batchRestore(request, options),
		clearNext: (options) => LeadDeal_clearNext(request, options),
		convertToOrder: (options) => LeadDeal_convertToOrder(request, options),
		create: (options) => LeadDeal_create(request, options),
		exportLeadDeals: (options) => LeadDeal_exportLeadDeals(request, options),
		get: (options) => LeadDeal_get(request, options),
		linkCompany: (options) => LeadDeal_linkCompany(request, options),
		linkContact: (options) => LeadDeal_linkContact(request, options),
		list: (options) => LeadDeal_list(request, options),
		moveStage: (options) => LeadDeal_moveStage(request, options),
		moveToPipeline: (options) => LeadDeal_moveToPipeline(request, options),
		restore: (options) => LeadDeal_restore(request, options),
		scheduleNext: (options) => LeadDeal_scheduleNext(request, options),
		setOutcome: (options) => LeadDeal_setOutcome(request, options),
		update: (options) => LeadDeal_update(request, options),
		upgrade: (options) => LeadDeal_upgrade(request, options)
	};
}
//#endregion
//#region src/learning/index.ts
/** Download certificate PDF
Only available if enrollment is COMPLETED and belongs to the current user. */
async function Learning_getCertificate(request, options) {
	return request("GET", "/my/learning/{enrollmentId}/certificate", {
		params: { enrollmentId: String(options.enrollmentId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all enrollments for the current user (across all companies)
Filterable by status, categoryId, and date range. */
async function Learning_getMyLearning(request, options) {
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	if (options.query?.dateFrom !== void 0) query["dateFrom"] = options.query.dateFrom;
	if (options.query?.dateTo !== void 0) query["dateTo"] = options.query.dateTo;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/my/learning", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createLearningController(request) {
	return {
		getCertificate: (options) => Learning_getCertificate(request, options),
		getMyLearning: (options) => Learning_getMyLearning(request, options)
	};
}
//#endregion
//#region src/link-mobility-webhook/index.ts
/** Handle LinkMobility SMS delivery reports */
async function LinkMobilityWebhook_handleDeliveryReport(request, options) {
	return request("POST", "/webhooks/linkmobility/delivery-report", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createLinkMobilityWebhookController(request) {
	return { handleDeliveryReport: (options) => LinkMobilityWebhook_handleDeliveryReport(request, options) };
}
//#endregion
//#region src/livekit-webhook/index.ts
async function LivekitWebhook_handleWebhook(request, options) {
	return request("POST", "/livekit-webhook", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createLivekitWebhookController(request) {
	return { handleWebhook: (options) => LivekitWebhook_handleWebhook(request, options) };
}
//#endregion
//#region src/login-security/index.ts
/** Read the thresholds and switches alerting currently answers to */
async function LoginSecurity_getSettings(request, options) {
	return request("GET", "/admin/login-security/settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List raised auth security alerts, newest first */
async function LoginSecurity_listAlerts(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.kind !== void 0) query["kind"] = options.query.kind;
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.instance !== void 0) query["instance"] = options.query.instance;
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	return request("GET", "/admin/login-security/alerts", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update the thresholds and switches, one partial edit at a time */
async function LoginSecurity_updateSettings(request, options) {
	return request("PATCH", "/admin/login-security/settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createLoginSecurityController(request) {
	return {
		getSettings: (options) => LoginSecurity_getSettings(request, options),
		listAlerts: (options) => LoginSecurity_listAlerts(request, options),
		updateSettings: (options) => LoginSecurity_updateSettings(request, options)
	};
}
//#endregion
//#region src/lost-reason/index.ts
async function LostReason_create(request, options) {
	return request("POST", "/crm/lost-reasons", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LostReason_delete(request, options) {
	return request("DELETE", "/crm/lost-reasons/{lostReasonId}", {
		params: { lostReasonId: String(options.lostReasonId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LostReason_list(request, options) {
	return request("GET", "/crm/lost-reasons", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function LostReason_update(request, options) {
	return request("PATCH", "/crm/lost-reasons/{lostReasonId}", {
		params: { lostReasonId: String(options.lostReasonId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createLostReasonController(request) {
	return {
		create: (options) => LostReason_create(request, options),
		delete: (options) => LostReason_delete(request, options),
		list: (options) => LostReason_list(request, options),
		update: (options) => LostReason_update(request, options)
	};
}
//#endregion
//#region src/media/index.ts
/** Upload one image (PNG, JPEG, WebP or GIF) for use inside rich text.

Accepts multipart/form-data with a `file` field. Returns the stored URL and pixel size. */
async function Media_uploadImage(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/member-portal/manage/media/images", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMediaController(request) {
	return { uploadImage: (options) => Media_uploadImage(request, options) };
}
//#endregion
//#region src/media-progress-sse/index.ts
/** SSE stream for one inspection's media preprocessing progress. */
async function MediaProgressSse_stream(request, options) {
	const params = { inspectionId: String(options.inspectionId) };
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	const result = await request("GET", "/inspections/{inspectionId}/media-progress/stream", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
function createMediaProgressSseController(request) {
	return { stream: (options) => MediaProgressSse_stream(request, options) };
}
//#endregion
//#region src/meeting-background-image-stream/index.ts
async function MeetingBackgroundImageStream_streamImage(request, options) {
	const params = { imageId: String(options.imageId) };
	const query = {};
	if (options.query?.t !== void 0) query["t"] = options.query.t;
	return request("GET", "/profile/meeting-background/images/{imageId}/raw", {
		params,
		query,
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingBackgroundImageStreamController(request) {
	return { streamImage: (options) => MeetingBackgroundImageStream_streamImage(request, options) };
}
//#endregion
//#region src/meeting/index.ts
/**
* Create a meeting
*
* Books a meeting for the company with the caller as host, then emails the invitations. A scheduled
* meeting is also added to the host's connected calendars.
*/
async function Meeting_createMeeting(request, options) {
	return request("POST", "/meeting", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Meeting_createMeetingNote(request, options) {
	return request("POST", "/meeting/{meetingID}/notes", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** correct meeting AI summary ✍️ */
async function Meeting_createSummaryCorrection(request, options) {
	return request("POST", "/meeting/{meetingID}/ai-summary/corrections", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete a meeting
*
* Deletes the meeting and its recordings for good, and emails a cancellation to the host and the clients.
*/
async function Meeting_deleteMeeting(request, options) {
	return request("DELETE", "/meeting/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Meeting_deleteMeetingNote(request, options) {
	return request("DELETE", "/meeting/{meetingID}/notes/{noteID}", {
		params: {
			meetingID: String(options.meetingID),
			noteID: String(options.noteID)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** rewrite a selected passage of the summary draft ✨ */
async function Meeting_editSummarySelection(request, options) {
	return request("POST", "/meeting/{meetingID}/ai-summary/selection-edit", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** send meeting summary PDF via email 📧 */
async function Meeting_emailMeetingSummary(request, options) {
	return request("POST", "/meeting/{meetingID}/email-summary", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** export meeting summary as PDF 📄 */
async function Meeting_exportMeetingPdf(request, options) {
	const params = { meetingID: String(options.meetingID) };
	const query = {};
	if (options.query?.sections !== void 0) query["sections"] = options.query.sections;
	return request("GET", "/meeting/{meetingID}/export-pdf", {
		params,
		query,
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Meeting_getClientMeetings(request, options) {
	const params = { clientID: String(options.clientID) };
	const query = {};
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.orderBy !== void 0) query["orderBy"] = options.query.orderBy;
	return request("GET", "/meeting/get-meetings/{clientID}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Meeting_getMeetingChatHistory(request, options) {
	return request("GET", "/meeting/chat/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Meeting_getMeetingNotes(request, options) {
	return request("GET", "/meeting/notes/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List meetings
*
* Lists the company's meetings, 30 to a page by default, earliest start first unless `sortBy` says
* otherwise. Narrow it with `from` and `to` (days as `YYYY-MM-DD`, matched on the start time in UTC),
* `ended` (`true` or `false`), a title `search`, or the `scope` and `userIds` of whose meetings to show.
* Without the `meetings.read.all` permission, the list holds only the meetings the caller takes part in;
* with an API key, the caller is the key's creator.
*/
async function Meeting_getMeetings(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.ended !== void 0) query["ended"] = options.query.ended;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.userIds !== void 0) query["userIds"] = options.query.userIds;
	return request("GET", "/meeting", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** meeting end and summary pipeline state */
async function Meeting_getPipelineState(request, options) {
	return request("GET", "/meeting/{meetingID}/pipeline-state", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get a meeting
*
* Returns one meeting with its participants, recordings, screenshots, transcript and every version of
* its AI summary.
*/
async function Meeting_getSinglemeeting(request, options) {
	return request("GET", "/meeting/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** meeting summary run state 📡 */
async function Meeting_getSummaryRunState(request, options) {
	return request("GET", "/meeting/{meetingID}/summary-run", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** regenerate meeting AI summary 🔄 */
async function Meeting_regenerateSummary(request, options) {
	return request("POST", "/meeting/{meetingID}/regenerate-summary", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** meeting reminder 📆 */
async function Meeting_sendReminder(request, options) {
	return request("POST", "/meeting/{meetingID}/reminder", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update a meeting
*
* Changes the fields sent and leaves the rest as they are. Participants and clients added here are
* invited, and moving the start time notifies everyone on the meeting.
*/
async function Meeting_updateMeeting(request, options) {
	return request("PATCH", "/meeting/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Meeting_updateMeetingNote(request, options) {
	return request("PATCH", "/meeting/{meetingID}/notes/{noteID}", {
		params: {
			meetingID: String(options.meetingID),
			noteID: String(options.noteID)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingController(request) {
	return {
		createMeeting: (options) => Meeting_createMeeting(request, options),
		createMeetingNote: (options) => Meeting_createMeetingNote(request, options),
		createSummaryCorrection: (options) => Meeting_createSummaryCorrection(request, options),
		deleteMeeting: (options) => Meeting_deleteMeeting(request, options),
		deleteMeetingNote: (options) => Meeting_deleteMeetingNote(request, options),
		editSummarySelection: (options) => Meeting_editSummarySelection(request, options),
		emailMeetingSummary: (options) => Meeting_emailMeetingSummary(request, options),
		exportMeetingPdf: (options) => Meeting_exportMeetingPdf(request, options),
		getClientMeetings: (options) => Meeting_getClientMeetings(request, options),
		getMeetingChatHistory: (options) => Meeting_getMeetingChatHistory(request, options),
		getMeetingNotes: (options) => Meeting_getMeetingNotes(request, options),
		getMeetings: (options) => Meeting_getMeetings(request, options),
		getPipelineState: (options) => Meeting_getPipelineState(request, options),
		getSinglemeeting: (options) => Meeting_getSinglemeeting(request, options),
		getSummaryRunState: (options) => Meeting_getSummaryRunState(request, options),
		regenerateSummary: (options) => Meeting_regenerateSummary(request, options),
		sendReminder: (options) => Meeting_sendReminder(request, options),
		updateMeeting: (options) => Meeting_updateMeeting(request, options),
		updateMeetingNote: (options) => Meeting_updateMeetingNote(request, options)
	};
}
//#endregion
//#region src/meeting-dev/index.ts
async function MeetingDev_backdateMeetingEnd(request, options) {
	return request("POST", "/meeting-dev/backdate-meeting-end/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingDev_getMeetings(request, options) {
	const params = { egressID: String(options.egressID) };
	const query = {};
	if (options.query?.token !== void 0) query["token"] = options.query.token;
	return request("POST", "/meeting-dev/record-finished/{egressID}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingDev_runPipelineRepair(request, options) {
	return request("POST", "/meeting-dev/run-pipeline-repair/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Test support: seed an active recording row for a meeting so e2e can exercise
* the pause/resume/auto-stop flow without real LiveKit egress.
*/
async function MeetingDev_seedRecording(request, options) {
	return request("POST", "/meeting-dev/seed-recording/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Test support: seed an AI summary chain with agent traces and mark the meeting COMPLETED, so the browser
* spec can drive the refinement surface deterministically. Defaults to the two-version chain (v1 draft +
* v2 refine); `variant` opts into the Correction and legacy shapes without moving the default.
*/
async function MeetingDev_seedSummary(request, options) {
	return request("POST", "/meeting-dev/seed-summary/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Test support: write the transcript rows the real transcription worker would have written. Speech-to-text
* needs a provider key the e2e stack deliberately does not carry, so this stands in for the produced
* transcript and lets a test prove the summary run consumes it.
*/
async function MeetingDev_seedTranscript(request, options) {
	return request("POST", "/meeting-dev/seed-transcript/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Test support: toggle the per-meeting `allowParticipantPause` setting. */
async function MeetingDev_setAllowParticipantPause(request, options) {
	return request("POST", "/meeting-dev/allow-participant-pause/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Test support: fire the pause auto-stop guard immediately, bypassing the
* 5-minute BullMQ delay. Returns whether the recording was paused (and thus
* eligible to be auto-stopped).
*/
async function MeetingDev_triggerAutoStop(request, options) {
	return request("POST", "/meeting-dev/trigger-auto-stop/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingDevController(request) {
	return {
		backdateMeetingEnd: (options) => MeetingDev_backdateMeetingEnd(request, options),
		getMeetings: (options) => MeetingDev_getMeetings(request, options),
		runPipelineRepair: (options) => MeetingDev_runPipelineRepair(request, options),
		seedRecording: (options) => MeetingDev_seedRecording(request, options),
		seedSummary: (options) => MeetingDev_seedSummary(request, options),
		seedTranscript: (options) => MeetingDev_seedTranscript(request, options),
		setAllowParticipantPause: (options) => MeetingDev_setAllowParticipantPause(request, options),
		triggerAutoStop: (options) => MeetingDev_triggerAutoStop(request, options)
	};
}
//#endregion
//#region src/meeting-pipeline-sse/index.ts
async function MeetingPipelineSse_stream(request, options) {
	const result = await request("GET", "/meeting-pipeline/{meetingID}/stream", {
		params: { meetingID: String(options.meetingID) },
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
function createMeetingPipelineSseController(request) {
	return { stream: (options) => MeetingPipelineSse_stream(request, options) };
}
//#endregion
//#region src/meeting-room/index.ts
async function MeetingRoom_decideJoinRequest(request, options) {
	return request("POST", "/join-meeting/requests/{meetingID}/{requestID}/decide", {
		params: {
			meetingID: String(options.meetingID),
			requestID: String(options.requestID)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_endMeetingForAll(request, options) {
	return request("POST", "/join-meeting/end/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_getPublicMeetingState(request, options) {
	return request("GET", "/join-meeting/state/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_getTokenForClient(request, options) {
	return request("GET", "/join-meeting/token/client/{participantID}", {
		params: { participantID: String(options.participantID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_getTokenForInternalUser(request, options) {
	return request("GET", "/join-meeting/token/internal/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_kickParticipant(request, options) {
	return request("PATCH", "/join-meeting/kick/{meetingID}/{participantID}", {
		params: {
			meetingID: String(options.meetingID),
			participantID: String(options.participantID)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_listInvitedNotJoined(request, options) {
	return request("GET", "/join-meeting/invited/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_lowerAllHands(request, options) {
	return request("POST", "/join-meeting/lower-all-hands/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_pauseRecordingMeeting(request, options) {
	return request("POST", "/join-meeting/record/{meetingID}/pause", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_recordMeeting(request, options) {
	return request("POST", "/join-meeting/record/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** A room client reports that it connected to or left the LiveKit room */
async function MeetingRoom_reportRoomPresence(request, options) {
	return request("POST", "/join-meeting/presence/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_resumeRecordingMeeting(request, options) {
	return request("POST", "/join-meeting/record/{meetingID}/resume", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_saveLocation(request, options) {
	return request("POST", "/join-meeting/save-location/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_sendChatMessage(request, options) {
	const params = { meetingID: String(options.meetingID) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/join-meeting/chat/{meetingID}", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_setPinForAll(request, options) {
	return request("POST", "/join-meeting/pin-for-all/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_setSpotlight(request, options) {
	return request("POST", "/join-meeting/spotlight/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_stopRecordingMeeting(request, options) {
	return request("POST", "/join-meeting/record/{meetingID}/stop", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_takeScreenshot(request, options) {
	const params = { meetingID: String(options.meetingID) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/join-meeting/screenshot/{meetingID}", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingRoom_toggleMute(request, options) {
	return request("PATCH", "/join-meeting/mute/{meetingID}/{participantID}/{trackID}", {
		params: {
			meetingID: String(options.meetingID),
			participantID: String(options.participantID),
			trackID: String(options.trackID)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingRoomController(request) {
	return {
		decideJoinRequest: (options) => MeetingRoom_decideJoinRequest(request, options),
		endMeetingForAll: (options) => MeetingRoom_endMeetingForAll(request, options),
		getPublicMeetingState: (options) => MeetingRoom_getPublicMeetingState(request, options),
		getTokenForClient: (options) => MeetingRoom_getTokenForClient(request, options),
		getTokenForInternalUser: (options) => MeetingRoom_getTokenForInternalUser(request, options),
		kickParticipant: (options) => MeetingRoom_kickParticipant(request, options),
		listInvitedNotJoined: (options) => MeetingRoom_listInvitedNotJoined(request, options),
		lowerAllHands: (options) => MeetingRoom_lowerAllHands(request, options),
		pauseRecordingMeeting: (options) => MeetingRoom_pauseRecordingMeeting(request, options),
		recordMeeting: (options) => MeetingRoom_recordMeeting(request, options),
		reportRoomPresence: (options) => MeetingRoom_reportRoomPresence(request, options),
		resumeRecordingMeeting: (options) => MeetingRoom_resumeRecordingMeeting(request, options),
		saveLocation: (options) => MeetingRoom_saveLocation(request, options),
		sendChatMessage: (options) => MeetingRoom_sendChatMessage(request, options),
		setPinForAll: (options) => MeetingRoom_setPinForAll(request, options),
		setSpotlight: (options) => MeetingRoom_setSpotlight(request, options),
		stopRecordingMeeting: (options) => MeetingRoom_stopRecordingMeeting(request, options),
		takeScreenshot: (options) => MeetingRoom_takeScreenshot(request, options),
		toggleMute: (options) => MeetingRoom_toggleMute(request, options)
	};
}
//#endregion
//#region src/meeting-room-sse/index.ts
async function MeetingRoomSse_joinMeetingAsAnonymous(request, options) {
	const params = { meetingID: String(options.meetingID) };
	const query = {};
	if (options.query?.name !== void 0) query["name"] = options.query.name;
	if (options.query?.email !== void 0) query["email"] = options.query.email;
	if (options.query?.diagnostics !== void 0) query["diagnostics"] = options.query.diagnostics;
	const result = await request("GET", "/join-meeting/anonymous/{meetingID}", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
async function MeetingRoomSse_subscribeToJoinRequests(request, options) {
	const params = { meetingID: String(options.meetingID) };
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	const result = await request("GET", "/join-meeting/subscribe/{meetingID}", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
async function MeetingRoomSse_subscribeToPublicMeeting(request, options) {
	const params = { meetingID: String(options.meetingID) };
	const query = {};
	if (options.query?.p !== void 0) query["p"] = options.query.p;
	const result = await request("GET", "/join-meeting/subscribe-public/{meetingID}", {
		params,
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
function createMeetingRoomSseController(request) {
	return {
		joinMeetingAsAnonymous: (options) => MeetingRoomSse_joinMeetingAsAnonymous(request, options),
		subscribeToJoinRequests: (options) => MeetingRoomSse_subscribeToJoinRequests(request, options),
		subscribeToPublicMeeting: (options) => MeetingRoomSse_subscribeToPublicMeeting(request, options)
	};
}
//#endregion
//#region src/meeting-summary-public/index.ts
async function MeetingSummaryPublic_downloadPdf(request, options) {
	return request("GET", "/meeting-summary/public/{token}/pdf", {
		params: { token: String(options.token) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function MeetingSummaryPublic_resolve(request, options) {
	return request("GET", "/meeting-summary/public/{token}", {
		params: { token: String(options.token) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingSummaryPublicController(request) {
	return {
		downloadPdf: (options) => MeetingSummaryPublic_downloadPdf(request, options),
		resolve: (options) => MeetingSummaryPublic_resolve(request, options)
	};
}
//#endregion
//#region src/meeting-telemetry-public/index.ts
/** Accept a batch of meeting diagnostics from a meeting client */
async function MeetingTelemetryPublic_ingestBatch(request, options) {
	return request("POST", "/meeting-diagnostics/batches", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingTelemetryPublicController(request) {
	return { ingestBatch: (options) => MeetingTelemetryPublic_ingestBatch(request, options) };
}
//#endregion
//#region src/meeting-telemetry-ticket/index.ts
/** Issue a meeting diagnostics ticket to a signed-in participant on prejoin */
async function MeetingTelemetryTicket_getTicket(request, options) {
	return request("GET", "/meeting-diagnostics/ticket/{meetingId}", {
		params: { meetingId: String(options.meetingId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingTelemetryTicketController(request) {
	return { getTicket: (options) => MeetingTelemetryTicket_getTicket(request, options) };
}
//#endregion
//#region src/meeting-telemetry-types-public/index.ts
async function MeetingTelemetryTypesPublic_sharedMeetingTelemetryTypes(request, options) {
	return request("GET", "/_shared-types/meeting-telemetry", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMeetingTelemetryTypesPublicController(request) {
	return { sharedMeetingTelemetryTypes: (options) => MeetingTelemetryTypesPublic_sharedMeetingTelemetryTypes(request, options) };
}
//#endregion
//#region src/merge/index.ts
async function Merge_count(request, options) {
	return request("GET", "/crm/duplicates/count", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_dismiss(request, options) {
	return request("POST", "/crm/duplicates/dismiss", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_listPairs(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.recordType !== void 0) query["recordType"] = options.query.recordType;
	if (options.query?.matchReason !== void 0) query["matchReason"] = options.query.matchReason;
	if (options.query?.recordId !== void 0) query["recordId"] = options.query.recordId;
	return request("GET", "/crm/duplicates", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_merge(request, options) {
	return request("POST", "/crm/duplicates/merge", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_redirect(request, options) {
	const query = {};
	if (options.query?.recordType !== void 0) query["recordType"] = options.query.recordType;
	if (options.query?.recordId !== void 0) query["recordId"] = options.query.recordId;
	return request("GET", "/crm/duplicates/redirect", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_scan(request, options) {
	return request("POST", "/crm/duplicates/scan", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_undo(request, options) {
	return request("POST", "/crm/duplicates/undo", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Merge_undoDismiss(request, options) {
	return request("POST", "/crm/duplicates/undo-dismiss", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMergeController(request) {
	return {
		count: (options) => Merge_count(request, options),
		dismiss: (options) => Merge_dismiss(request, options),
		listPairs: (options) => Merge_listPairs(request, options),
		merge: (options) => Merge_merge(request, options),
		redirect: (options) => Merge_redirect(request, options),
		scan: (options) => Merge_scan(request, options),
		undo: (options) => Merge_undo(request, options),
		undoDismiss: (options) => Merge_undoDismiss(request, options)
	};
}
//#endregion
//#region src/my-ai-settings/index.ts
/** Get the current user's personal AI summary settings for a company */
async function MyAiSettings_getMyAiSettings(request, options) {
	return request("GET", "/company/me/ai-settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update the current user's personal AI summary settings; null resets a field to the company setting */
async function MyAiSettings_updateMyAiSettings(request, options) {
	return request("PATCH", "/company/me/ai-settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createMyAiSettingsController(request) {
	return {
		getMyAiSettings: (options) => MyAiSettings_getMyAiSettings(request, options),
		updateMyAiSettings: (options) => MyAiSettings_updateMyAiSettings(request, options)
	};
}
//#endregion
//#region src/normalize-transcription-language/index.ts
async function NormalizeTranscriptionLanguage_normalize(request, options) {
	return request("POST", "/admin/internal/normalize-transcription-language", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createNormalizeTranscriptionLanguageController(request) {
	return { normalize: (options) => NormalizeTranscriptionLanguage_normalize(request, options) };
}
//#endregion
//#region src/notification/index.ts
/** Backfill `companyId` on legacy NotificationEntity rows (super-admin only).
Idempotent — only touches rows where companyId is null. */
async function Notification_backfillCompanyId(request, options) {
	return request("POST", "/notifications/backfill-company-id", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Notification_getNotifications(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.isRead !== void 0) query["isRead"] = options.query.isRead;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/notifications", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Notification_getPreferences(request, options) {
	return request("GET", "/notifications/preferences", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Notification_getUnreadCount(request, options) {
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	return request("GET", "/notifications/unread-count", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Notification_markAllAsRead(request, options) {
	return request("PATCH", "/notifications/mark-all-read", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Notification_markAsRead(request, options) {
	return request("PATCH", "/notifications/{id}/read", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Notification_notificationStream(request, options) {
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	const result = await request("GET", "/notifications/stream", {
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
async function Notification_updatePreferences(request, options) {
	return request("PUT", "/notifications/preferences", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createNotificationController(request) {
	return {
		backfillCompanyId: (options) => Notification_backfillCompanyId(request, options),
		getNotifications: (options) => Notification_getNotifications(request, options),
		getPreferences: (options) => Notification_getPreferences(request, options),
		getUnreadCount: (options) => Notification_getUnreadCount(request, options),
		markAllAsRead: (options) => Notification_markAllAsRead(request, options),
		markAsRead: (options) => Notification_markAsRead(request, options),
		notificationStream: (options) => Notification_notificationStream(request, options),
		updatePreferences: (options) => Notification_updatePreferences(request, options)
	};
}
//#endregion
//#region src/nudges/index.ts
/** Get the signed-in user's nudge state */
async function Nudges_getNudgeState(request, options) {
	return request("GET", "/nudges/state", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Record what the user did with a nudge */
async function Nudges_resolveNudge(request, options) {
	return request("POST", "/nudges/{nudgeId}/resolve", {
		params: { nudgeId: String(options.nudgeId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createNudgesController(request) {
	return {
		getNudgeState: (options) => Nudges_getNudgeState(request, options),
		resolveNudge: (options) => Nudges_resolveNudge(request, options)
	};
}
//#endregion
//#region src/oauth-company/index.ts
/** List the companies an OAuth grant can be bound to */
async function OAuthCompany_companies(request, options) {
	return request("GET", "/auth/oauth/companies", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Revoke an OAuth consent and every refresh token it issued */
async function OAuthCompany_disconnect(request, options) {
	return request("POST", "/auth/oauth/disconnect", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Bind the signed-in session to a company before OAuth consent */
async function OAuthCompany_selectCompany(request, options) {
	return request("POST", "/auth/oauth/select-company", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOAuthCompanyController(request) {
	return {
		companies: (options) => OAuthCompany_companies(request, options),
		disconnect: (options) => OAuthCompany_disconnect(request, options),
		selectCompany: (options) => OAuthCompany_selectCompany(request, options)
	};
}
//#endregion
//#region src/offer/index.ts
/**
* Create an offer draft
*
* Starts a new offer on the deal as a version 1 draft, titled after the deal and filled with the company's default terms, validity and acceptance depth. If your company already has a draft open on the deal, that draft is returned instead. Fill it with update, then send it.
*/
async function Offer_createDraft(request, options) {
	return request("POST", "/crm/offers", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Discard an offer draft
*
* Deletes the draft. When it is the only version of its offer, the offer goes too. A version that is no longer a draft returns 409. Returns no body.
*/
async function Offer_discardDraft(request, options) {
	return request("POST", "/crm/offers/{versionId}/discard", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Download an offer's certificate
*
* Returns the certificate of completion on its own: who accepted the offer, when, and the hash of the document they accepted. Returns 409 until `sealState` is `SEALED`.
*/
async function Offer_downloadCertificate(request, options) {
	return request("GET", "/crm/offers/{versionId}/certificate", {
		params: { versionId: String(options.versionId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Download an offer PDF
*
* Returns the offer document the customer received, as a PDF. A draft has no document yet and returns 409; preview it with the draft PDF route instead.
*/
async function Offer_downloadPdf(request, options) {
	return request("GET", "/crm/offers/{versionId}/pdf", {
		params: { versionId: String(options.versionId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Download a sealed offer PDF
*
* Returns the tamper-evident PDF of an offer accepted with a signature: the offer followed by its certificate of completion. Returns 409 until `sealState` is `SEALED`.
*/
async function Offer_downloadSealedPdf(request, options) {
	return request("GET", "/crm/offers/{versionId}/sealed-pdf", {
		params: { versionId: String(options.versionId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Preview an offer draft as a PDF
*
* Returns the draft as a PDF watermarked as a preview, so you can check it before sending. Once the version is sent this returns 409 with code `OFFER_NOT_DRAFT`; download the sent document instead.
*/
async function Offer_draftPdf(request, options) {
	return request("GET", "/crm/offers/{versionId}/draft-pdf", {
		params: { versionId: String(options.versionId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get an offer's customer link
*
* Returns the token and path of the page where the customer reads and accepts the offer. Only a `SENT` or `EXPIRED` version has a link to copy; any other status returns 409.
*/
async function Offer_getLink(request, options) {
	return request("GET", "/crm/offers/{versionId}/link", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get an offer version
*
* Returns the version with its lines, totals, recipient, audit trail (newest first) and the other versions in its offer. An id from another company returns 404.
*/
async function Offer_getVersion(request, options) {
	return request("GET", "/crm/offers/{versionId}", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List the company's offers
*
* Returns one page of the company's offers across every deal, newest first, one row per offer carrying its current version, status, totals, recipient and deal. Without `status` the list leaves out superseded versions. Filter with `search` and `status`, and page with `page` and `pageSize` (at most 100).
*/
async function Offer_list(request, options) {
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	return request("GET", "/crm/offers/list", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a deal's offers
*
* Returns every offer on the deal, newest first, each with its versions, newest version first. An offer is a thread of versions: sending a revised version supersedes the one sent before it, and every other offer route acts on one version by its `versionId`. `dealId` is required; leaving it out or sending a malformed id returns 400. A deal from another company returns 404.
*/
async function Offer_listByDeal(request, options) {
	const query = {};
	if (options.query?.dealId !== void 0) query["dealId"] = options.query.dealId;
	return request("GET", "/crm/offers", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Start a new offer version
*
* Copies the version into a new draft in the same offer, to revise an offer that was already sent. If the offer already has a draft, that draft is returned instead. Sending the new version supersedes the one sent before it.
*/
async function Offer_newVersion(request, options) {
	return request("POST", "/crm/offers/{versionId}/new-version", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Record a withdrawn acceptance
*
* Records that the customer withdrew their acceptance, as a consumer may under the right of withdrawal (angrerett). The deal reopens; the sealed document and the trail stay as they are. Only an `ACCEPTED` version whose acceptance is not already withdrawn qualifies; anything else returns 409.
*/
async function Offer_recordAcceptanceWithdrawal(request, options) {
	return request("POST", "/crm/offers/{versionId}/record-acceptance-withdrawal", {
		params: { versionId: String(options.versionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Record a customer's decision
*
* Records that the customer accepted or rejected a sent offer outside the link, for example over the phone. An acceptance marks the deal won, as a decision through the link would, and the customer gets a confirmation email. A recorded decision is not sealed. Only a `SENT` version can be decided; any other status returns 409.
*/
async function Offer_recordDecision(request, options) {
	return request("POST", "/crm/offers/{versionId}/record-decision", {
		params: { versionId: String(options.versionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Resend an offer
*
* Emails a sent offer to its recipient again, with the same number and link, for example when the customer lost the email. Only a `SENT` version can be resent; any other status returns 409. Returns 503 when the email could not be delivered.
*/
async function Offer_resend(request, options) {
	return request("POST", "/crm/offers/{versionId}/resend", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Retry an offer's delivery
*
* Emails a sent offer to its recipient again, with the same number and link. Use it after a send returned 503 or the trail shows `DELIVERY_FAILED`. Only a `SENT` version can be delivered; any other status returns 409.
*/
async function Offer_retryDelivery(request, options) {
	return request("POST", "/crm/offers/{versionId}/retry-delivery", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Retry sealing an accepted offer
*
* Queues sealing again for an offer accepted with a signature, after `sealState` came back `FAILED`. Sealing produces the tamper-evident PDF and the certificate of completion. An offer that is already sealed is returned unchanged. An offer that is not accepted, or was accepted with a click, returns 409.
*/
async function Offer_retrySeal(request, options) {
	return request("POST", "/crm/offers/{versionId}/retry-seal", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Send an offer
*
* Freezes the draft and emails the recipient a link to the offer with the PDF attached, plus a text message when `smsPhone` is set. The first send gives the offer its number, and sending a newer version supersedes the one sent before it. If the deal has no value yet, it takes the offer's net total. Sending a version that is already sent returns it unchanged.
*
* Refusals:
*
* - 400: the draft has no lines or more than 200, a line quantity or unit price is out of range, or the total is too large.
* - 400: `recipientEmail` differs from the email of the deal's contact and `divergenceAttested` is not true.
* - 503: the offer is sent but the email could not be delivered. Call retry-delivery.
*/
async function Offer_sendOffer(request, options) {
	return request("POST", "/crm/offers/{versionId}/send", {
		params: { versionId: String(options.versionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update an offer draft
*
* Changes the draft. Only the fields you send change, and `lines` replaces every line. More than 200 lines returns 400. A version that is no longer a draft returns 409; start a new version to change a sent offer.
*/
async function Offer_updateDraft(request, options) {
	return request("PATCH", "/crm/offers/{versionId}", {
		params: { versionId: String(options.versionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Withdraw an offer
*
* Takes back a sent offer: its link stops working and the customer can no longer accept it. The deal is left as it is. Withdrawing an offer that is already withdrawn changes nothing; any other status than `SENT` returns 409.
*/
async function Offer_withdraw(request, options) {
	return request("POST", "/crm/offers/{versionId}/withdraw", {
		params: { versionId: String(options.versionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOfferController(request) {
	return {
		createDraft: (options) => Offer_createDraft(request, options),
		discardDraft: (options) => Offer_discardDraft(request, options),
		downloadCertificate: (options) => Offer_downloadCertificate(request, options),
		downloadPdf: (options) => Offer_downloadPdf(request, options),
		downloadSealedPdf: (options) => Offer_downloadSealedPdf(request, options),
		draftPdf: (options) => Offer_draftPdf(request, options),
		getLink: (options) => Offer_getLink(request, options),
		getVersion: (options) => Offer_getVersion(request, options),
		list: (options) => Offer_list(request, options),
		listByDeal: (options) => Offer_listByDeal(request, options),
		newVersion: (options) => Offer_newVersion(request, options),
		recordAcceptanceWithdrawal: (options) => Offer_recordAcceptanceWithdrawal(request, options),
		recordDecision: (options) => Offer_recordDecision(request, options),
		resend: (options) => Offer_resend(request, options),
		retryDelivery: (options) => Offer_retryDelivery(request, options),
		retrySeal: (options) => Offer_retrySeal(request, options),
		sendOffer: (options) => Offer_sendOffer(request, options),
		updateDraft: (options) => Offer_updateDraft(request, options),
		withdraw: (options) => Offer_withdraw(request, options)
	};
}
//#endregion
//#region src/offer-internal/index.ts
/** [Internal] Run the offer auto-expiry check for this company */
async function OfferInternal_runExpiryCheck(request, options) {
	return request("POST", "/crm/offers/internal/run-expiry-check", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** [Internal] Run the pre-expiry reminder pass for this company */
async function OfferInternal_runReminders(request, options) {
	return request("POST", "/crm/offers/internal/run-reminders", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** [Internal] Run the retention sweep for this company */
async function OfferInternal_runRetentionSweep(request, options) {
	return request("POST", "/crm/offers/internal/run-retention-sweep", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOfferInternalController(request) {
	return {
		runExpiryCheck: (options) => OfferInternal_runExpiryCheck(request, options),
		runReminders: (options) => OfferInternal_runReminders(request, options),
		runRetentionSweep: (options) => OfferInternal_runRetentionSweep(request, options)
	};
}
//#endregion
//#region src/offer-proposal/index.ts
async function OfferProposal_acceptLine(request, options) {
	return request("POST", "/crm/offer-proposals/{proposalId}/lines/{lineId}/accept", {
		params: {
			proposalId: String(options.proposalId),
			lineId: String(options.lineId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferProposal_editLine(request, options) {
	return request("POST", "/crm/offer-proposals/{proposalId}/lines/{lineId}/edit", {
		params: {
			proposalId: String(options.proposalId),
			lineId: String(options.lineId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferProposal_getByReport(request, options) {
	return request("GET", "/crm/offer-proposals/by-report/{reportId}", {
		params: { reportId: String(options.reportId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferProposal_promoteLine(request, options) {
	return request("POST", "/crm/offer-proposals/{proposalId}/lines/{lineId}/promote", {
		params: {
			proposalId: String(options.proposalId),
			lineId: String(options.lineId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferProposal_rejectLine(request, options) {
	return request("POST", "/crm/offer-proposals/{proposalId}/lines/{lineId}/reject", {
		params: {
			proposalId: String(options.proposalId),
			lineId: String(options.lineId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferProposal_start(request, options) {
	return request("POST", "/crm/offer-proposals/by-report/{reportId}/start", {
		params: { reportId: String(options.reportId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOfferProposalController(request) {
	return {
		acceptLine: (options) => OfferProposal_acceptLine(request, options),
		editLine: (options) => OfferProposal_editLine(request, options),
		getByReport: (options) => OfferProposal_getByReport(request, options),
		promoteLine: (options) => OfferProposal_promoteLine(request, options),
		rejectLine: (options) => OfferProposal_rejectLine(request, options),
		start: (options) => OfferProposal_start(request, options)
	};
}
//#endregion
//#region src/offer-public/index.ts
async function OfferPublic_acceptClick(request, options) {
	return request("POST", "/offers/public/{token}/accept-click", {
		params: { token: String(options.token) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_acceptSignature(request, options) {
	return request("POST", "/offers/public/{token}/accept-signature", {
		params: { token: String(options.token) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_downloadCertificate(request, options) {
	return request("GET", "/offers/public/certificate/{downloadToken}", {
		params: { downloadToken: String(options.downloadToken) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_downloadPdf(request, options) {
	return request("GET", "/offers/public/{token}/pdf", {
		params: { token: String(options.token) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_downloadSealed(request, options) {
	return request("GET", "/offers/public/sealed/{downloadToken}", {
		params: { downloadToken: String(options.downloadToken) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_reject(request, options) {
	return request("POST", "/offers/public/{token}/reject", {
		params: { token: String(options.token) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_resolve(request, options) {
	return request("GET", "/offers/public/{token}", {
		params: { token: String(options.token) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferPublic_retrySeal(request, options) {
	return request("POST", "/offers/public/{token}/retry-seal", {
		params: { token: String(options.token) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOfferPublicController(request) {
	return {
		acceptClick: (options) => OfferPublic_acceptClick(request, options),
		acceptSignature: (options) => OfferPublic_acceptSignature(request, options),
		downloadCertificate: (options) => OfferPublic_downloadCertificate(request, options),
		downloadPdf: (options) => OfferPublic_downloadPdf(request, options),
		downloadSealed: (options) => OfferPublic_downloadSealed(request, options),
		reject: (options) => OfferPublic_reject(request, options),
		resolve: (options) => OfferPublic_resolve(request, options),
		retrySeal: (options) => OfferPublic_retrySeal(request, options)
	};
}
//#endregion
//#region src/offer-settings/index.ts
async function OfferSettings_get(request, options) {
	return request("GET", "/crm/offer-settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OfferSettings_update(request, options) {
	return request("PUT", "/crm/offer-settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOfferSettingsController(request) {
	return {
		get: (options) => OfferSettings_get(request, options),
		update: (options) => OfferSettings_update(request, options)
	};
}
//#endregion
//#region src/order-attachment/index.ts
async function OrderAttachment_delete(request, options) {
	return request("DELETE", "/orders/{orderId}/attachments/{attachmentId}", {
		params: {
			orderId: String(options.orderId),
			attachmentId: String(options.attachmentId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderAttachment_list(request, options) {
	return request("GET", "/orders/{orderId}/attachments", {
		params: { orderId: String(options.orderId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderAttachment_upload(request, options) {
	const params = { orderId: String(options.orderId) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/orders/{orderId}/attachments", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOrderAttachmentController(request) {
	return {
		delete: (options) => OrderAttachment_delete(request, options),
		list: (options) => OrderAttachment_list(request, options),
		upload: (options) => OrderAttachment_upload(request, options)
	};
}
//#endregion
//#region src/order-company-sse/index.ts
/** SSE stream for a company's order messages - delivers new messages in real-time.
Auth via ?accessToken=... (EventSource can't send headers). The caller must be a
member of the requested company; the stream is scoped to that company's channel so
only that order's company users receive the event. */
async function OrderCompanySse_companyStream(request, options) {
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	const result = await request("GET", "/orders/company/stream", {
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
function createOrderCompanySseController(request) {
	return { companyStream: (options) => OrderCompanySse_companyStream(request, options) };
}
//#endregion
//#region src/order/index.ts
async function Order_board(request, options) {
	return request("GET", "/orders/board", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Cancel an order
*
* Cancels the order with a reason and moves it to the company's first `CANCELLED` stage. Refusals, all 400:
*
* - `COMPLETION_EXISTS`: the order is completed.
* - `INVOICE_EXISTS`: the order has an invoice.
*/
async function Order_cancel(request, options) {
	return request("POST", "/orders/{id}/cancel", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Complete an order
*
* Records that the work is done, with a note and optional photos, and emails the customer. The order stays in its stage; move it to a `DONE` stage afterwards if you want it there. Completing an order that is already completed changes nothing.
*/
async function Order_complete(request, options) {
	return request("POST", "/orders/{id}/complete", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Create an order
*
* Creates an order, placed last in its stage. Pass `dealId` to create the order from a deal: an open deal is marked Won, and a deal that already has an active order returns 409 with code `ORDER_ALREADY_EXISTS` and the existing `orderId`.
*/
async function Order_create(request, options) {
	return request("POST", "/orders", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get an order
*
* Returns the order with its lines, totals, completion history, cancellation and invoice. An id from another company returns 404.
*/
async function Order_detail(request, options) {
	return request("GET", "/orders/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Order_keep(request, options) {
	return request("POST", "/orders/{id}/keep", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Order_linkContact(request, options) {
	return request("POST", "/orders/{id}/link-contact", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Order_linkedForDeal(request, options) {
	return request("GET", "/orders/for-deal/{dealId}", {
		params: { dealId: String(options.dealId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List orders
*
* Returns one page of the company's orders, newest first unless `sort` and `order` say otherwise.
*
* Query parameters:
*
* - `search`: matches the order number, title, customer name or customer email, ignoring case.
* - `stageId`, `assigneeId`, `contactId`: only orders in that stage, assigned to that member, or for that contact.
* - `assignedCompanyId`: only chain orders handed to that member company.
* - `openOnly`: `true` leaves out orders in `DONE` and `CANCELLED` stages.
* - `page`: page number, starting at 1. Defaults to 1.
* - `pageSize`: orders per page, 1 to 100. Defaults to 25.
* - `sort`: `createdAt` (default), `plannedDate` or `number`.
* - `order`: `desc` (default) or `asc`.
* - `chain`: omit it for every order your company owns. `own` leaves out the orders handed to a chain member, `chain` returns only chain orders (handed out by you or to you), and `all` adds the orders a chain parent handed to you.
*/
async function Order_list(request, options) {
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.stageId !== void 0) query["stageId"] = options.query.stageId;
	if (options.query?.assigneeId !== void 0) query["assigneeId"] = options.query.assigneeId;
	if (options.query?.assignedCompanyId !== void 0) query["assignedCompanyId"] = options.query.assignedCompanyId;
	if (options.query?.contactId !== void 0) query["contactId"] = options.query.contactId;
	if (options.query?.openOnly !== void 0) query["openOnly"] = options.query.openOnly;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.order !== void 0) query["order"] = options.query.order;
	if (options.query?.chain !== void 0) query["chain"] = options.query.chain;
	return request("GET", "/orders", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Move an order to a stage
*
* Moves the order to another stage, or to a new position in its stage. Refusals, all 400:
*
* - `ORDER_CANCELLED`: the order is cancelled.
* - `COMPLETION_REQUIRED`: the target is a `DONE` stage and the order is not completed. Call complete first.
* - `INVOICE_EXISTS`: the order is completed and invoiced, so it cannot leave its `DONE` stage.
*
* Moving a completed order out of a `DONE` stage reverses its completion.
*/
async function Order_moveStage(request, options) {
	return request("POST", "/orders/{id}/move", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Order_report(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/orders/reports", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Order_reverseCompletion(request, options) {
	return request("POST", "/orders/{id}/reverse-completion", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update an order
*
* Changes the title, planned date, assignee or contact. Only the fields you send change; send null to clear a nullable field. Setting a new planned date emails the customer.
*/
async function Order_update(request, options) {
	return request("PATCH", "/orders/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Replace an order's lines
*
* Replaces every line on the order with the lines you send. The totals are recomputed from the new lines.
*/
async function Order_updateLines(request, options) {
	return request("PUT", "/orders/{id}/lines", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOrderController(request) {
	return {
		board: (options) => Order_board(request, options),
		cancel: (options) => Order_cancel(request, options),
		complete: (options) => Order_complete(request, options),
		create: (options) => Order_create(request, options),
		detail: (options) => Order_detail(request, options),
		keep: (options) => Order_keep(request, options),
		linkContact: (options) => Order_linkContact(request, options),
		linkedForDeal: (options) => Order_linkedForDeal(request, options),
		list: (options) => Order_list(request, options),
		moveStage: (options) => Order_moveStage(request, options),
		report: (options) => Order_report(request, options),
		reverseCompletion: (options) => Order_reverseCompletion(request, options),
		update: (options) => Order_update(request, options),
		updateLines: (options) => Order_updateLines(request, options)
	};
}
//#endregion
//#region src/order-customer/index.ts
/**
* Change the authenticated customer's password
*
* When `mustChangePassword` is true on the customer, the
* current password is not required (forced reset). Otherwise the
* `currentPassword` must be supplied and will be verified by BetterAuth.
* Returns `{ success: true }` on success.
*/
async function OrderCustomer_changePassword(request, options) {
	return request("POST", "/orders/customer/profile/change-password", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** SSE stream for the customer - delivers new messages and notifications in real-time.
Authentication via ?accessToken=... query param (EventSource doesn't support custom headers).
IMPORTANT: Declared BEFORE :id routes to avoid route shadowing. */
async function OrderCustomer_customerStream(request, options) {
	const query = {};
	if (options.query?.accessToken !== void 0) query["accessToken"] = options.query.accessToken;
	const result = await request("GET", "/orders/customer/stream", {
		query,
		responseType: "sse",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
	if (result.error) return result;
	return {
		data: new SSEConnection(result.response, (s) => JSON.parse(s)),
		error: null,
		response: result.response
	};
}
/**
* Get (or mint) a feedback form access token for a Done-category order
*
* Returns a token that the customer can use to access the public
* feedback form for this order. Returns `{ token: null }` when the order has
* not reached the Done category, feedback has already been submitted, or the
* company has no published FEEDBACK-purpose form.
*/
async function OrderCustomer_getFeedbackToken(request, options) {
	return request("GET", "/orders/customer/{id}/feedback-token", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the authenticated customer's profile */
async function OrderCustomer_getProfile(request, options) {
	return request("GET", "/orders/customer/profile", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List the authenticated customer's own orders (portal category projection)
*
* Returns the fixed customer-facing category per order, never the
* tenant's internal stage name.
*/
async function OrderCustomer_listOrders(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/orders/customer/list", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload or replace the authenticated customer's avatar (multipart/form-data) */
async function OrderCustomer_updateAvatar(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PATCH", "/orders/customer/profile/avatar", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update the customer's preferred portal language
*
* Persists `'en' | 'no'` on the customer record so that
* subsequent portal sessions hydrate the chosen locale without relying on
* `?lang=`. Returns the updated profile.
*/
async function OrderCustomer_updateLanguage(request, options) {
	return request("PATCH", "/orders/customer/profile/language", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update the authenticated customer's profile fields
*
* Updates `firstName`, `lastName`, and/or `phone`. Password
* changes are handled by `POST /orders/customer/profile/change-password`.
*/
async function OrderCustomer_updateProfile(request, options) {
	return request("PATCH", "/orders/customer/profile", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOrderCustomerController(request) {
	return {
		changePassword: (options) => OrderCustomer_changePassword(request, options),
		customerStream: (options) => OrderCustomer_customerStream(request, options),
		getFeedbackToken: (options) => OrderCustomer_getFeedbackToken(request, options),
		getProfile: (options) => OrderCustomer_getProfile(request, options),
		listOrders: (options) => OrderCustomer_listOrders(request, options),
		updateAvatar: (options) => OrderCustomer_updateAvatar(request, options),
		updateLanguage: (options) => OrderCustomer_updateLanguage(request, options),
		updateProfile: (options) => OrderCustomer_updateProfile(request, options)
	};
}
//#endregion
//#region src/order-message/index.ts
async function OrderMessage_list(request, options) {
	const params = { orderId: String(options.orderId) };
	const query = {};
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/orders/{orderId}/messages", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderMessage_markRead(request, options) {
	return request("POST", "/orders/{orderId}/messages/read", {
		params: { orderId: String(options.orderId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderMessage_send(request, options) {
	return request("POST", "/orders/{orderId}/messages", {
		params: { orderId: String(options.orderId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOrderMessageController(request) {
	return {
		list: (options) => OrderMessage_list(request, options),
		markRead: (options) => OrderMessage_markRead(request, options),
		send: (options) => OrderMessage_send(request, options)
	};
}
//#endregion
//#region src/order-pipeline/index.ts
async function OrderPipeline_createStage(request, options) {
	return request("POST", "/order-pipeline/stages", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderPipeline_deleteStage(request, options) {
	return request("DELETE", "/order-pipeline/stages/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List order stages
*
* Returns the company's order pipeline stages in board order, with how many orders each holds. Use a stage `id` to create or move an order.
*/
async function OrderPipeline_listStages(request, options) {
	return request("GET", "/order-pipeline/stages", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderPipeline_reorderStages(request, options) {
	return request("POST", "/order-pipeline/stages/reorder", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function OrderPipeline_updateStage(request, options) {
	return request("PATCH", "/order-pipeline/stages/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOrderPipelineController(request) {
	return {
		createStage: (options) => OrderPipeline_createStage(request, options),
		deleteStage: (options) => OrderPipeline_deleteStage(request, options),
		listStages: (options) => OrderPipeline_listStages(request, options),
		reorderStages: (options) => OrderPipeline_reorderStages(request, options),
		updateStage: (options) => OrderPipeline_updateStage(request, options)
	};
}
//#endregion
//#region src/order-public/index.ts
/**
* Get additional form definition by access token
*
* Returns form definition, order context and branding for public display.
* No authentication required, token provides access.
*/
async function OrderPublic_getFormByToken(request, options) {
	return request("GET", "/orders/additional-form/{token}", {
		params: { token: String(options.token) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Submit additional form response
*
* Submits a form response and links it to the additional form entry.
* No authentication required, token provides access.
*/
async function OrderPublic_submitResponse(request, options) {
	return request("POST", "/orders/additional-form/{token}/submit", {
		params: { token: String(options.token) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createOrderPublicController(request) {
	return {
		getFormByToken: (options) => OrderPublic_getFormByToken(request, options),
		submitResponse: (options) => OrderPublic_submitResponse(request, options)
	};
}
//#endregion
//#region src/pipeline/index.ts
async function Pipeline_createPipeline(request, options) {
	return request("POST", "/crm/pipelines", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_createStage(request, options) {
	return request("POST", "/crm/pipelines/{pipelineId}/stages", {
		params: { pipelineId: String(options.pipelineId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_deletePipeline(request, options) {
	return request("DELETE", "/crm/pipelines/{pipelineId}", {
		params: { pipelineId: String(options.pipelineId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_deleteStage(request, options) {
	return request("DELETE", "/crm/pipelines/{pipelineId}/stages/{stageId}", {
		params: {
			pipelineId: String(options.pipelineId),
			stageId: String(options.stageId)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List pipelines
*
* Returns your sales pipelines in order, each with its stages and how many open leads and deals it holds. A chain member also gets its chain's shared pipeline first. Use the ids to create leads and deals or move them between stages. Needs the CRM add-on; without it the call returns 403.
*/
async function Pipeline_list(request, options) {
	return request("GET", "/crm/pipelines", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_reorderStages(request, options) {
	return request("PATCH", "/crm/pipelines/{pipelineId}/stages/reorder", {
		params: { pipelineId: String(options.pipelineId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_setDefault(request, options) {
	return request("POST", "/crm/pipelines/{pipelineId}/default", {
		params: { pipelineId: String(options.pipelineId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_updatePipeline(request, options) {
	return request("PATCH", "/crm/pipelines/{pipelineId}", {
		params: { pipelineId: String(options.pipelineId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Pipeline_updateStage(request, options) {
	return request("PATCH", "/crm/pipelines/{pipelineId}/stages/{stageId}", {
		params: {
			pipelineId: String(options.pipelineId),
			stageId: String(options.stageId)
		},
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createPipelineController(request) {
	return {
		createPipeline: (options) => Pipeline_createPipeline(request, options),
		createStage: (options) => Pipeline_createStage(request, options),
		deletePipeline: (options) => Pipeline_deletePipeline(request, options),
		deleteStage: (options) => Pipeline_deleteStage(request, options),
		list: (options) => Pipeline_list(request, options),
		reorderStages: (options) => Pipeline_reorderStages(request, options),
		setDefault: (options) => Pipeline_setDefault(request, options),
		updatePipeline: (options) => Pipeline_updatePipeline(request, options),
		updateStage: (options) => Pipeline_updateStage(request, options)
	};
}
//#endregion
//#region src/placement/index.ts
async function Placement_retryPlacement(request, options) {
	return request("POST", "/inspection/inspections/{inspectionId}/placement/retry", {
		params: { inspectionId: String(options.inspectionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createPlacementController(request) {
	return { retryPlacement: (options) => Placement_retryPlacement(request, options) };
}
//#endregion
//#region src/portal-invoice/index.ts
/** Get the issued invoice for one of the signed-in customer's orders */
async function PortalInvoice_getInvoice(request, options) {
	return request("GET", "/portal/orders/{id}/invoice", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Download the issued invoice PDF for one of the signed-in customer's orders */
async function PortalInvoice_pdf(request, options) {
	return request("GET", "/portal/orders/{id}/invoice/pdf", {
		params: { id: String(options.id) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createPortalInvoiceController(request) {
	return {
		getInvoice: (options) => PortalInvoice_getInvoice(request, options),
		pdf: (options) => PortalInvoice_pdf(request, options)
	};
}
//#endregion
//#region src/portal-order/index.ts
/** Get the portal projection of one order for the signed-in customer */
async function PortalOrder_getOrder(request, options) {
	return request("GET", "/portal/orders/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the order's message thread, attributed to the company */
async function PortalOrder_listMessages(request, options) {
	return request("GET", "/portal/orders/{id}/messages", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Post a message onto the order thread (customer side)
*
* Writes to the same OrderMessageEntity thread the internal feed
* reads; the portal read of it shows company attribution only.
*/
async function PortalOrder_sendMessage(request, options) {
	return request("POST", "/portal/orders/{id}/messages", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createPortalOrderController(request) {
	return {
		getOrder: (options) => PortalOrder_getOrder(request, options),
		listMessages: (options) => PortalOrder_listMessages(request, options),
		sendMessage: (options) => PortalOrder_sendMessage(request, options)
	};
}
//#endregion
//#region src/profile/index.ts
async function Profile_deleteMeetingBackgroundImage(request, options) {
	return request("DELETE", "/profile/meeting-background/images/{imageId}", {
		params: { imageId: String(options.imageId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_dismissPrompt(request, options) {
	return request("POST", "/profile/me/dismiss-prompt", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_getMeetingBackground(request, options) {
	return request("GET", "/profile/meeting-background", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_getProfile(request, options) {
	return request("GET", "/profile/me", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_getPublicProfile(request, options) {
	return request("GET", "/profile/{userId}", {
		params: { userId: String(options.userId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_listMeetingBackgroundImages(request, options) {
	return request("GET", "/profile/meeting-background/images", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_updateAvatar(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PATCH", "/profile/avatar", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_updateMeetingBackground(request, options) {
	return request("PATCH", "/profile/meeting-background", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_updateProfile(request, options) {
	return request("PATCH", "/profile/me", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Profile_uploadMeetingBackgroundImage(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/profile/meeting-background/image", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createProfileController(request) {
	return {
		deleteMeetingBackgroundImage: (options) => Profile_deleteMeetingBackgroundImage(request, options),
		dismissPrompt: (options) => Profile_dismissPrompt(request, options),
		getMeetingBackground: (options) => Profile_getMeetingBackground(request, options),
		getProfile: (options) => Profile_getProfile(request, options),
		getPublicProfile: (options) => Profile_getPublicProfile(request, options),
		listMeetingBackgroundImages: (options) => Profile_listMeetingBackgroundImages(request, options),
		updateAvatar: (options) => Profile_updateAvatar(request, options),
		updateMeetingBackground: (options) => Profile_updateMeetingBackground(request, options),
		updateProfile: (options) => Profile_updateProfile(request, options),
		uploadMeetingBackgroundImage: (options) => Profile_uploadMeetingBackgroundImage(request, options)
	};
}
//#endregion
//#region src/public-device-token/index.ts
/** Forget a device token */
async function PublicDeviceToken_forget(request, options) {
	return request("POST", "/device-tokens/forget", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createPublicDeviceTokenController(request) {
	return { forget: (options) => PublicDeviceToken_forget(request, options) };
}
//#endregion
//#region src/quick-link/index.ts
/**
* Create a quick link. ADMIN+ required.
*
* Chain link enforcement:
* - If isChainLink = true, the requesting company must be the parent
*   (i.e. companyId must NOT be a chain member — it must be a parent).
* - Local links: any ADMIN of the company can create.
*/
async function QuickLink_create(request, options) {
	return request("POST", "/quick-links", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete a quick link. ADMIN+ required.
* Cannot delete chain links from a member context.
*/
async function QuickLink_delete(request, options) {
	return request("DELETE", "/quick-links/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Returns the company's own links (chain links reach members via explicit push).
* Accessible to MEMBER role and above.
*/
async function QuickLink_findAll(request, options) {
	const query = {};
	if (options.query?.isChainLink !== void 0) query["isChainLink"] = options.query.isChainLink;
	return request("GET", "/quick-links", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Reorder quick links within a scope. ADMIN+ required.
*
* Body must include `isChainLink` to specify scope.
* Only the owner of the links can reorder them.
*
* MUST be declared BEFORE :id to avoid route conflict.
*/
async function QuickLink_reorder(request, options) {
	return request("PATCH", "/quick-links/reorder", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Update a quick link. ADMIN+ required.
* Cannot edit chain links from a member context.
*/
async function QuickLink_update(request, options) {
	return request("PATCH", "/quick-links/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createQuickLinkController(request) {
	return {
		create: (options) => QuickLink_create(request, options),
		delete: (options) => QuickLink_delete(request, options),
		findAll: (options) => QuickLink_findAll(request, options),
		reorder: (options) => QuickLink_reorder(request, options),
		update: (options) => QuickLink_update(request, options)
	};
}
//#endregion
//#region src/reconcile-stuck-transcriptions/index.ts
/**
* Remove duplicate stuck audio tracks, re-enqueue unprocessed ones, and trigger
* pending summaries. Idempotent; safe to re-run after deploys.
*/
async function ReconcileStuckTranscriptions_reconcileStuckTranscriptions(request, options) {
	return request("POST", "/admin/internal/reconcile-stuck-transcriptions", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createReconcileStuckTranscriptionsController(request) {
	return { reconcileStuckTranscriptions: (options) => ReconcileStuckTranscriptions_reconcileStuckTranscriptions(request, options) };
}
//#endregion
//#region src/record-and-send-admin/index.ts
/** Read the Vadio-wide capture numbers every phone records with */
async function RecordAndSendAdmin_getCaptureSettings(request, options) {
	return request("GET", "/admin/record-and-send/settings", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read a company's Record and send SMS parts today against its daily cap */
async function RecordAndSendAdmin_getCompanyRecordAndSend(request, options) {
	return request("GET", "/admin/companies/{targetCompanyId}/record-and-send", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Set a company's daily SMS cap

A value equal to the default removes the company's own cap. The change is audited with the
Vadio admin, the old cap and the new one, and applies from the company's next invite. */
async function RecordAndSendAdmin_setCompanySmsCap(request, options) {
	return request("PATCH", "/admin/companies/{targetCompanyId}/record-and-send/sms-cap", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Change some of the Vadio-wide capture numbers

The numbers left out keep their value. The next customer link lookup serves the change.
Refused with 400 `CAPTURE_SETTINGS_CONFLICT` when the chunk gaps or frame rates contradict each other. */
async function RecordAndSendAdmin_updateCaptureSettings(request, options) {
	return request("PATCH", "/admin/record-and-send/settings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRecordAndSendAdminController(request) {
	return {
		getCaptureSettings: (options) => RecordAndSendAdmin_getCaptureSettings(request, options),
		getCompanyRecordAndSend: (options) => RecordAndSendAdmin_getCompanyRecordAndSend(request, options),
		setCompanySmsCap: (options) => RecordAndSendAdmin_setCompanySmsCap(request, options),
		updateCaptureSettings: (options) => RecordAndSendAdmin_updateCaptureSettings(request, options)
	};
}
//#endregion
//#region src/record-and-send-home/index.ts
/**
* Read the Record and send home
*
* The company's Video types with paused ones first and then by name, and whether the daily SMS cap
* is reached today. The videos themselves come from `GET /record-and-send/submissions`.
*/
async function RecordAndSendHome_read(request, options) {
	return request("GET", "/record-and-send/home", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRecordAndSendHomeController(request) {
	return { read: (options) => RecordAndSendHome_read(request, options) };
}
//#endregion
//#region src/record-and-send-public-form/index.ts
/**
* Read a Video type's public form
*
* No sign-in: the company comes from the public id alone. An unknown id answers 404
* `VIDEO_TYPE_NOT_FOUND` and names no company. `accepting` is false when the Video type is paused or
* Record and send is off for the company, without saying which.
*/
async function RecordAndSendPublicForm_getForm(request, options) {
	return request("GET", "/public/record-and-send/forms/{publicId}", {
		params: { publicId: String(options.publicId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Ask for a recording link from the public form
*
* Checks the robot-check token, then makes or reuses the Video request for this phone and sends
* the invite. Answers 200 with `SENT` and where the link went (masked), or `REFUSED` with one public reason:
* the company's daily SMS cap reads `COMPANY_CANNOT_SEND_TODAY`, and every other abuse limit, the invite pauses
* and a failed send read `SENDING_FAILED`. 10 submits per 10 minutes per visitor; past that the route answers 429.
*/
async function RecordAndSendPublicForm_requestLink(request, options) {
	return request("POST", "/public/record-and-send/forms/{publicId}/requests", {
		params: { publicId: String(options.publicId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRecordAndSendPublicFormController(request) {
	return {
		getForm: (options) => RecordAndSendPublicForm_getForm(request, options),
		requestLink: (options) => RecordAndSendPublicForm_requestLink(request, options)
	};
}
//#endregion
//#region src/record-and-send-sms-status/index.ts
/** Read whether the company's daily SMS cap is reached today */
async function RecordAndSendSmsStatus_getSmsStatus(request, options) {
	return request("GET", "/record-and-send/sms-status", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRecordAndSendSmsStatusController(request) {
	return { getSmsStatus: (options) => RecordAndSendSmsStatus_getSmsStatus(request, options) };
}
//#endregion
//#region src/recordings/index.ts
async function Recordings_deleteRecording(request, options) {
	return request("DELETE", "/recordings/{meetingID}/{recordingID}", {
		params: {
			meetingID: String(options.meetingID),
			recordingID: String(options.recordingID)
		},
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Recordings_getRecordings(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.meetingID !== void 0) query["meetingID"] = options.query.meetingID;
	if (options.query?.sortDirection !== void 0) query["sortDirection"] = options.query.sortDirection;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.scope !== void 0) query["scope"] = options.query.scope;
	if (options.query?.userIds !== void 0) query["userIds"] = options.query.userIds;
	return request("GET", "/recordings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRecordingsController(request) {
	return {
		deleteRecording: (options) => Recordings_deleteRecording(request, options),
		getRecordings: (options) => Recordings_getRecordings(request, options)
	};
}
//#endregion
//#region src/relay-control/index.ts
async function RelayControl_getMetrics(request, options) {
	return request("GET", "/platform-events/operator/relay/metrics", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function RelayControl_pause(request, options) {
	return request("POST", "/platform-events/operator/relay/pause", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function RelayControl_resume(request, options) {
	return request("POST", "/platform-events/operator/relay/resume", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function RelayControl_state(request, options) {
	return request("GET", "/platform-events/operator/relay/state", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRelayControlController(request) {
	return {
		getMetrics: (options) => RelayControl_getMetrics(request, options),
		pause: (options) => RelayControl_pause(request, options),
		resume: (options) => RelayControl_resume(request, options),
		state: (options) => RelayControl_state(request, options)
	};
}
//#endregion
//#region src/reports/index.ts
/**
* Export report data as CSV
*
* Streams a CSV file of order data with applied date range filter.
*/
async function Reports_exportCsv(request, options) {
	const query = {};
	if (options.query?.startDate !== void 0) query["startDate"] = options.query.startDate;
	if (options.query?.endDate !== void 0) query["endDate"] = options.query.endDate;
	return request("GET", "/reports/export", {
		query,
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get order distribution breakdowns
*
* Returns orders by service type and by status (current snapshot).
*/
async function Reports_getDistribution(request, options) {
	return request("GET", "/reports/distribution", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get KPI summary cards
*
* Returns revenue growth (current vs prior quarter), completion rate, and avg cycle time.
*/
async function Reports_getKpis(request, options) {
	return request("GET", "/reports/kpis", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get performance time series
*
* Returns monthly completed order counts and SLA compliance percentage.
*/
async function Reports_getPerformanceOverTime(request, options) {
	const query = {};
	if (options.query?.startDate !== void 0) query["startDate"] = options.query.startDate;
	if (options.query?.endDate !== void 0) query["endDate"] = options.query.endDate;
	return request("GET", "/reports/performance-over-time", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get monthly revenue from confirmed invoices
*
* Only includes invoices with READY status. Supports date range filter.
*/
async function Reports_getRevenue(request, options) {
	const query = {};
	if (options.query?.startDate !== void 0) query["startDate"] = options.query.startDate;
	if (options.query?.endDate !== void 0) query["endDate"] = options.query.endDate;
	return request("GET", "/reports/revenue", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get per-installer performance metrics
*
* Returns a table of installer stats: job counts, avg response time, completion rate.
*/
async function Reports_getTeamPerformance(request, options) {
	return request("GET", "/reports/team-performance", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createReportsController(request) {
	return {
		exportCsv: (options) => Reports_exportCsv(request, options),
		getDistribution: (options) => Reports_getDistribution(request, options),
		getKpis: (options) => Reports_getKpis(request, options),
		getPerformanceOverTime: (options) => Reports_getPerformanceOverTime(request, options),
		getRevenue: (options) => Reports_getRevenue(request, options),
		getTeamPerformance: (options) => Reports_getTeamPerformance(request, options)
	};
}
//#endregion
//#region src/resource-category/index.ts
/** Create a new resource category */
async function ResourceCategory_create(request, options) {
	return request("POST", "/resource-categories", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a resource category (blocked if resources exist) */
async function ResourceCategory_delete(request, options) {
	return request("DELETE", "/resource-categories/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all resource categories for the company */
async function ResourceCategory_findAll(request, options) {
	return request("GET", "/resource-categories", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reorder resource categories
Must be declared BEFORE :id to avoid route conflict. */
async function ResourceCategory_reorder(request, options) {
	return request("PATCH", "/resource-categories/reorder", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a resource category */
async function ResourceCategory_update(request, options) {
	return request("PATCH", "/resource-categories/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createResourceCategoryController(request) {
	return {
		create: (options) => ResourceCategory_create(request, options),
		delete: (options) => ResourceCategory_delete(request, options),
		findAll: (options) => ResourceCategory_findAll(request, options),
		reorder: (options) => ResourceCategory_reorder(request, options),
		update: (options) => ResourceCategory_update(request, options)
	};
}
//#endregion
//#region src/resource/index.ts
/** Delete a resource (removes from S3 and database) */
async function Resource_delete(request, options) {
	return request("DELETE", "/resources/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Download a resource (tracked, 302 redirect to file URL) */
async function Resource_download(request, options) {
	return request("GET", "/resources/{id}/download", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List resources (filterable by category, searchable by title/fileName) */
async function Resource_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	return request("GET", "/resources", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get download stats for a resource */
async function Resource_getStats(request, options) {
	return request("GET", "/resources/{id}/stats", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Replace the resource file (upload new, delete old) */
async function Resource_replaceFile(request, options) {
	return request("PUT", "/resources/{id}/file", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update resource metadata (title, description, category) */
async function Resource_update(request, options) {
	return request("PATCH", "/resources/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Upload a new resource file */
async function Resource_upload(request, options) {
	return request("POST", "/resources", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createResourceController(request) {
	return {
		delete: (options) => Resource_delete(request, options),
		download: (options) => Resource_download(request, options),
		findAll: (options) => Resource_findAll(request, options),
		getStats: (options) => Resource_getStats(request, options),
		replaceFile: (options) => Resource_replaceFile(request, options),
		update: (options) => Resource_update(request, options),
		upload: (options) => Resource_upload(request, options)
	};
}
//#endregion
//#region src/roles/index.ts
/** Create a custom role */
async function Roles_createRole(request, options) {
	return request("POST", "/roles", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a custom role */
async function Roles_deleteRole(request, options) {
	return request("DELETE", "/roles/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Duplicate a role into a new editable custom role */
async function Roles_duplicateRole(request, options) {
	return request("POST", "/roles/{id}/duplicate", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** The code-owned permission catalog, grouped by area and scoped to the company's chain status */
async function Roles_getCatalog(request, options) {
	return request("GET", "/roles/catalog", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single role */
async function Roles_getRole(request, options) {
	return request("GET", "/roles/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List role options (id, name, icon) for pickers, without permission strings

Open to any company member (no `companySettings.manageMembers` read), so pickers
that only need to label roles — e.g. competence "required for roles" — can resolve
names without the sensitive full-role read. */
async function Roles_listRoleOptions(request, options) {
	return request("GET", "/roles/options", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all roles for a company with holder counts

Open to any company member so the members screen can show every role read only. */
async function Roles_listRoles(request, options) {
	return request("GET", "/roles", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** The current user's effective permission set for this company */
async function Roles_myEffectivePermissions(request, options) {
	return request("GET", "/roles/my-effective-permissions", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a custom role (optimistic concurrency via version) */
async function Roles_updateRole(request, options) {
	return request("PATCH", "/roles/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createRolesController(request) {
	return {
		createRole: (options) => Roles_createRole(request, options),
		deleteRole: (options) => Roles_deleteRole(request, options),
		duplicateRole: (options) => Roles_duplicateRole(request, options),
		getCatalog: (options) => Roles_getCatalog(request, options),
		getRole: (options) => Roles_getRole(request, options),
		listRoleOptions: (options) => Roles_listRoleOptions(request, options),
		listRoles: (options) => Roles_listRoles(request, options),
		myEffectivePermissions: (options) => Roles_myEffectivePermissions(request, options),
		updateRole: (options) => Roles_updateRole(request, options)
	};
}
//#endregion
//#region src/saved-view/index.ts
async function SavedView_create(request, options) {
	return request("POST", "/crm/saved-views", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_delete(request, options) {
	return request("DELETE", "/crm/saved-views/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_list(request, options) {
	const query = {};
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	return request("GET", "/crm/saved-views", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_myWork(request, options) {
	const query = {};
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	return request("GET", "/crm/saved-views/my-work", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_myWorkCount(request, options) {
	return request("GET", "/crm/saved-views/my-work/count", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_reorder(request, options) {
	return request("PATCH", "/crm/saved-views/reorder", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_resolveDefault(request, options) {
	const query = {};
	if (options.query?.pipelineId !== void 0) query["pipelineId"] = options.query.pipelineId;
	return request("GET", "/crm/saved-views/default", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_setDefault(request, options) {
	return request("PATCH", "/crm/saved-views/{id}/default", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SavedView_update(request, options) {
	return request("PATCH", "/crm/saved-views/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSavedViewController(request) {
	return {
		create: (options) => SavedView_create(request, options),
		delete: (options) => SavedView_delete(request, options),
		list: (options) => SavedView_list(request, options),
		myWork: (options) => SavedView_myWork(request, options),
		myWorkCount: (options) => SavedView_myWorkCount(request, options),
		reorder: (options) => SavedView_reorder(request, options),
		resolveDefault: (options) => SavedView_resolveDefault(request, options),
		setDefault: (options) => SavedView_setDefault(request, options),
		update: (options) => SavedView_update(request, options)
	};
}
//#endregion
//#region src/search/index.ts
async function Search_globalSearch(request, options) {
	const query = {};
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/search", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSearchController(request) {
	return { globalSearch: (options) => Search_globalSearch(request, options) };
}
//#endregion
//#region src/service-type/index.ts
/** Add an entry to the master catalog (parent company only) */
async function ServiceType_createMasterCatalogEntry(request, options) {
	return request("POST", "/service-types/master", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a service type for a company */
async function ServiceType_createServiceType(request, options) {
	return request("POST", "/service-types", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a service type (and its member clones when it is a master catalog entry) */
async function ServiceType_deleteServiceType(request, options) {
	return request("DELETE", "/service-types/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List master catalog entries (parent company only) */
async function ServiceType_listMasterCatalog(request, options) {
	return request("GET", "/service-types/master", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all active service types for a company */
async function ServiceType_listServiceTypes(request, options) {
	return request("GET", "/service-types", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Push master catalog entries to all active member companies */
async function ServiceType_pushMasterCatalog(request, options) {
	return request("POST", "/service-types/master/push", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a service type */
async function ServiceType_updateServiceType(request, options) {
	return request("PATCH", "/service-types/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createServiceTypeController(request) {
	return {
		createMasterCatalogEntry: (options) => ServiceType_createMasterCatalogEntry(request, options),
		createServiceType: (options) => ServiceType_createServiceType(request, options),
		deleteServiceType: (options) => ServiceType_deleteServiceType(request, options),
		listMasterCatalog: (options) => ServiceType_listMasterCatalog(request, options),
		listServiceTypes: (options) => ServiceType_listServiceTypes(request, options),
		pushMasterCatalog: (options) => ServiceType_pushMasterCatalog(request, options),
		updateServiceType: (options) => ServiceType_updateServiceType(request, options)
	};
}
//#endregion
//#region src/shared-push-types/index.ts
async function SharedPushTypes_sharedPushNotificationTypes(request, options) {
	return request("GET", "/_shared-types/push-notifications", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSharedPushTypesController(request) {
	return { sharedPushNotificationTypes: (options) => SharedPushTypes_sharedPushNotificationTypes(request, options) };
}
//#endregion
//#region src/shared-types/index.ts
async function SharedTypes_session(request, options) {
	return request("GET", "/_shared-types/session", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SharedTypes_sessionUser(request, options) {
	return request("GET", "/_shared-types/session-user", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSharedTypesController(request) {
	return {
		session: (options) => SharedTypes_session(request, options),
		sessionUser: (options) => SharedTypes_sessionUser(request, options)
	};
}
//#endregion
//#region src/sign-in-help/index.ts
/** Send a sign-in help request to support */
async function SignInHelp_submit(request, options) {
	return request("POST", "/auth/sign-in-help", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSignInHelpController(request) {
	return { submit: (options) => SignInHelp_submit(request, options) };
}
//#endregion
//#region src/sign-in-report/index.ts
/** Read the sign-in funnel counters by day */
async function SignInReport_getSignInReport(request, options) {
	const query = {};
	if (options.query?.days !== void 0) query["days"] = options.query.days;
	return request("GET", "/admin/reports/sign-in", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSignInReportController(request) {
	return { getSignInReport: (options) => SignInReport_getSignInReport(request, options) };
}
//#endregion
//#region src/signup/index.ts
async function Signup_complete(request, options) {
	return request("POST", "/auth/signup/complete", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Reachable while the User is still incomplete — this endpoint is how it stops being incomplete,
* so the guard's `SIGNUP_INCOMPLETE` refusal would lock the person out of their own signup.
*/
async function Signup_completeSocial(request, options) {
	return request("POST", "/auth/signup/complete-social", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Signup_linkProofStart(request, options) {
	return request("POST", "/auth/signup/link-proof/start", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Signup_linkProofVerify(request, options) {
	return request("POST", "/auth/signup/link-proof/verify", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Signup_resend(request, options) {
	return request("POST", "/auth/signup/resend", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Signup_start(request, options) {
	return request("POST", "/auth/signup/start", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Signup_verify(request, options) {
	return request("POST", "/auth/signup/verify", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSignupController(request) {
	return {
		complete: (options) => Signup_complete(request, options),
		completeSocial: (options) => Signup_completeSocial(request, options),
		linkProofStart: (options) => Signup_linkProofStart(request, options),
		linkProofVerify: (options) => Signup_linkProofVerify(request, options),
		resend: (options) => Signup_resend(request, options),
		start: (options) => Signup_start(request, options),
		verify: (options) => Signup_verify(request, options)
	};
}
//#endregion
//#region src/signup-e2e/index.ts
async function SignupE2e_backdate(request, options) {
	return request("POST", "/e2e/signup/backdate", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Puts a User into the state a social tap leaves behind, so the details step can be driven. */
async function SignupE2e_markIncomplete(request, options) {
	return request("POST", "/e2e/signup/mark-incomplete", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SignupE2e_runSweep(request, options) {
	return request("POST", "/e2e/signup/run-sweep", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSignupE2eController(request) {
	return {
		backdate: (options) => SignupE2e_backdate(request, options),
		markIncomplete: (options) => SignupE2e_markIncomplete(request, options),
		runSweep: (options) => SignupE2e_runSweep(request, options)
	};
}
//#endregion
//#region src/staffing-admin/index.ts
/** Hard-delete a chain posting (moderation)
Chain admin only. Logs the supplied reason. 404 if the posting is not in the
caller's chain. */
async function StaffingAdmin_deletePosting(request, options) {
	return request("DELETE", "/staffing/admin/postings/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all chain postings for moderation
Chain admin only. Returns every posting in the chain, any status, paginated
and filterable by status/type/company. */
async function StaffingAdmin_listPostings(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.postingCompanyId !== void 0) query["postingCompanyId"] = options.query.postingCompanyId;
	return request("GET", "/staffing/admin/postings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingAdminController(request) {
	return {
		deletePosting: (options) => StaffingAdmin_deletePosting(request, options),
		listPostings: (options) => StaffingAdmin_listPostings(request, options)
	};
}
//#endregion
//#region src/staffing-analytics/index.ts
/** CSV export of per-company staffing activity */
async function StaffingAnalytics_exportCsv(request, options) {
	return request("GET", "/chain/analytics/staffing/export", {
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Chain staffing analytics (active postings, skill demand/supply, fill rate) */
async function StaffingAnalytics_getAnalytics(request, options) {
	return request("GET", "/chain/analytics/staffing", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingAnalyticsController(request) {
	return {
		exportCsv: (options) => StaffingAnalytics_exportCsv(request, options),
		getAnalytics: (options) => StaffingAnalytics_getAnalytics(request, options)
	};
}
//#endregion
//#region src/staffing-category/index.ts
/** Create a staffing category
Chain admin only (ADMIN on the parent / chain access). */
async function StaffingCategory_create(request, options) {
	return request("POST", "/staffing/categories", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List chain-scoped staffing categories
Available to any chain member. */
async function StaffingCategory_list(request, options) {
	return request("GET", "/staffing/categories", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a staffing category
Chain admin only. Returns 409 if referenced by an ACTIVE posting. */
async function StaffingCategory_remove(request, options) {
	return request("DELETE", "/staffing/categories/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a staffing category
Chain admin only. */
async function StaffingCategory_update(request, options) {
	return request("PATCH", "/staffing/categories/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingCategoryController(request) {
	return {
		create: (options) => StaffingCategory_create(request, options),
		list: (options) => StaffingCategory_list(request, options),
		remove: (options) => StaffingCategory_remove(request, options),
		update: (options) => StaffingCategory_update(request, options)
	};
}
//#endregion
//#region src/staffing-interest/index.ts
/** Bookmark a posting
Any chain member. 409 on a duplicate bookmark. */
async function StaffingInterest_addBookmark(request, options) {
	return request("POST", "/staffing/postings/{id}/bookmark", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Approve a pending interest
Admin of the interested employee's company only. */
async function StaffingInterest_approve(request, options) {
	return request("PATCH", "/staffing/interests/{id}/approve", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Express interest in a posting
ADMIN → recorded immediately (`NOT_REQUIRED`) + notifies the owner.
MEMBER → recorded `PENDING` + requests own-company-admin approval. */
async function StaffingInterest_express(request, options) {
	return request("POST", "/staffing/postings/{id}/interest", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the caller's bookmarks
Any chain member. Includes posting summaries. */
async function StaffingInterest_listBookmarks(request, options) {
	return request("GET", "/staffing/bookmarks", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the caller's own interests
Any chain member. Includes status + posting summary. */
async function StaffingInterest_myInterests(request, options) {
	return request("GET", "/staffing/my-interests", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List PENDING interests awaiting the caller's approval
Scoped to the caller-company's employees. */
async function StaffingInterest_pendingApprovals(request, options) {
	return request("GET", "/staffing/pending-approvals", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Reject a pending interest with a reason
Admin of the interested employee's company only. */
async function StaffingInterest_reject(request, options) {
	return request("PATCH", "/staffing/interests/{id}/reject", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove a bookmark
Bookmark owner only. */
async function StaffingInterest_removeBookmark(request, options) {
	return request("DELETE", "/staffing/postings/{id}/bookmark", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Withdraw the caller's interest in a posting
Interest owner only. Decrements the count if it was counted. */
async function StaffingInterest_withdraw(request, options) {
	return request("DELETE", "/staffing/postings/{id}/interest", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingInterestController(request) {
	return {
		addBookmark: (options) => StaffingInterest_addBookmark(request, options),
		approve: (options) => StaffingInterest_approve(request, options),
		express: (options) => StaffingInterest_express(request, options),
		listBookmarks: (options) => StaffingInterest_listBookmarks(request, options),
		myInterests: (options) => StaffingInterest_myInterests(request, options),
		pendingApprovals: (options) => StaffingInterest_pendingApprovals(request, options),
		reject: (options) => StaffingInterest_reject(request, options),
		removeBookmark: (options) => StaffingInterest_removeBookmark(request, options),
		withdraw: (options) => StaffingInterest_withdraw(request, options)
	};
}
//#endregion
//#region src/staffing-internal/index.ts
/** [Internal] Run the staffing expiry check for this company
Flips past-due ACTIVE postings to EXPIRED and fires the one-time 3-day
expiry warning, scoped to the acting company. Idempotent — safe to re-run. */
async function StaffingInternal_runExpiryCheck(request, options) {
	return request("POST", "/staffing/internal/run-expiry-check", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingInternalController(request) {
	return { runExpiryCheck: (options) => StaffingInternal_runExpiryCheck(request, options) };
}
//#endregion
//#region src/staffing-posting/index.ts
/** Browse ACTIVE chain postings
Chain-scoped feed excluding the acting company; paginated and filtered. */
async function StaffingPosting_browse(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	if (options.query?.location !== void 0) query["location"] = options.query.location;
	if (options.query?.counties !== void 0) query["counties"] = options.query.counties;
	if (options.query?.skills !== void 0) query["skills"] = options.query.skills;
	if (options.query?.categoryId !== void 0) query["categoryId"] = options.query.categoryId;
	if (options.query?.categoryIds !== void 0) query["categoryIds"] = options.query.categoryIds;
	if (options.query?.skillLevelId !== void 0) query["skillLevelId"] = options.query.skillLevelId;
	if (options.query?.bookmarkedOnly !== void 0) query["bookmarkedOnly"] = options.query.bookmarkedOnly;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.startDateFrom !== void 0) query["startDateFrom"] = options.query.startDateFrom;
	if (options.query?.startDateTo !== void 0) query["startDateTo"] = options.query.startDateTo;
	if (options.query?.sortField !== void 0) query["sortField"] = options.query.sortField;
	return request("GET", "/staffing/postings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Close a posting (→ CLOSED)
Posting owner (ADMIN) only. */
async function StaffingPosting_close(request, options) {
	return request("POST", "/staffing/postings/{id}/close", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a staffing posting (DRAFT)
ADMIN only. 400 if the company is not part of a chain. */
async function StaffingPosting_create(request, options) {
	return request("POST", "/staffing/postings", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a posting's detail
Visible only within the same chain (else 404). Increments viewCount for
non-owner views. */
async function StaffingPosting_detail(request, options) {
	return request("GET", "/staffing/postings/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Duplicate a posting as a fresh DRAFT
Posting owner (ADMIN) only. */
async function StaffingPosting_duplicate(request, options) {
	return request("POST", "/staffing/postings/{id}/duplicate", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get the current staffing legal agreement
Returns the versioned legal text the caller must accept to create/publish. */
async function StaffingPosting_getLegalAgreement(request, options) {
	const query = {};
	if (options.query?.lang !== void 0) query["lang"] = options.query.lang;
	return request("GET", "/staffing/legal-agreement", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List interests on one of the acting company's postings
Posting owner only (403 otherwise). Includes employee contact details. */
async function StaffingPosting_interests(request, options) {
	return request("GET", "/staffing/my-postings/{id}/interests", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the acting company's own postings
ADMIN only. Status / type tab filters + pagination. */
async function StaffingPosting_myPostings(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.sortBy !== void 0) query["sortBy"] = options.query.sortBy;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.type !== void 0) query["type"] = options.query.type;
	return request("GET", "/staffing/my-postings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Publish a posting (DRAFT → ACTIVE)
Posting owner (ADMIN) only. Fires `staffing.posting_published`. */
async function StaffingPosting_publish(request, options) {
	return request("POST", "/staffing/postings/{id}/publish", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a posting (DRAFT only)
Posting owner (ADMIN) only. 409 if not a draft. */
async function StaffingPosting_remove(request, options) {
	return request("DELETE", "/staffing/postings/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Edit a posting
Posting owner (ADMIN) only. */
async function StaffingPosting_update(request, options) {
	return request("PATCH", "/staffing/postings/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingPostingController(request) {
	return {
		browse: (options) => StaffingPosting_browse(request, options),
		close: (options) => StaffingPosting_close(request, options),
		create: (options) => StaffingPosting_create(request, options),
		detail: (options) => StaffingPosting_detail(request, options),
		duplicate: (options) => StaffingPosting_duplicate(request, options),
		getLegalAgreement: (options) => StaffingPosting_getLegalAgreement(request, options),
		interests: (options) => StaffingPosting_interests(request, options),
		myPostings: (options) => StaffingPosting_myPostings(request, options),
		publish: (options) => StaffingPosting_publish(request, options),
		remove: (options) => StaffingPosting_remove(request, options),
		update: (options) => StaffingPosting_update(request, options)
	};
}
//#endregion
//#region src/staffing-skill/index.ts
/** Create a staffing skill
Chain admin only (ADMIN on the parent / chain access). */
async function StaffingSkill_create(request, options) {
	return request("POST", "/staffing/skills", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List chain-scoped staffing skills
Available to any chain member. */
async function StaffingSkill_list(request, options) {
	return request("GET", "/staffing/skills", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a staffing skill
Chain admin only. Returns 409 if referenced by an ACTIVE posting. */
async function StaffingSkill_remove(request, options) {
	return request("DELETE", "/staffing/skills/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a staffing skill
Chain admin only. */
async function StaffingSkill_update(request, options) {
	return request("PATCH", "/staffing/skills/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingSkillController(request) {
	return {
		create: (options) => StaffingSkill_create(request, options),
		list: (options) => StaffingSkill_list(request, options),
		remove: (options) => StaffingSkill_remove(request, options),
		update: (options) => StaffingSkill_update(request, options)
	};
}
//#endregion
//#region src/staffing-skill-level/index.ts
/** Create a staffing skill level
Chain admin only (ADMIN on the parent / chain access). */
async function StaffingSkillLevel_create(request, options) {
	return request("POST", "/staffing/skill-levels", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List chain-scoped staffing skill levels
Available to any chain member. */
async function StaffingSkillLevel_list(request, options) {
	return request("GET", "/staffing/skill-levels", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete a staffing skill level
Chain admin only. Returns 409 if referenced by an ACTIVE posting. */
async function StaffingSkillLevel_remove(request, options) {
	return request("DELETE", "/staffing/skill-levels/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update a staffing skill level
Chain admin only. */
async function StaffingSkillLevel_update(request, options) {
	return request("PATCH", "/staffing/skill-levels/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStaffingSkillLevelController(request) {
	return {
		create: (options) => StaffingSkillLevel_create(request, options),
		list: (options) => StaffingSkillLevel_list(request, options),
		remove: (options) => StaffingSkillLevel_remove(request, options),
		update: (options) => StaffingSkillLevel_update(request, options)
	};
}
//#endregion
//#region src/stripe/index.ts
/**
* Get customer billing history (invoices)
*
* Fetches all Stripe invoices for the given company.
*/
async function Stripe_getBillingHistory(request, options) {
	return request("GET", "/stripe/billing/history", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Stripe_getCustomerDetails(request, options) {
	return request("GET", "/stripe/setup-billing-address", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Stripe_getPortalLink(request, options) {
	return request("GET", "/stripe/portal", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Stripe_handleWebhook(request, options) {
	return request("POST", "/stripe/webhook", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Stripe_hasBilling(request, options) {
	return request("GET", "/stripe/billing", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Stripe_updateCustomerDetails(request, options) {
	return request("POST", "/stripe/setup-billing-address", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createStripeController(request) {
	return {
		getBillingHistory: (options) => Stripe_getBillingHistory(request, options),
		getCustomerDetails: (options) => Stripe_getCustomerDetails(request, options),
		getPortalLink: (options) => Stripe_getPortalLink(request, options),
		handleWebhook: (options) => Stripe_handleWebhook(request, options),
		hasBilling: (options) => Stripe_hasBilling(request, options),
		updateCustomerDetails: (options) => Stripe_updateCustomerDetails(request, options)
	};
}
//#endregion
//#region src/submission/index.ts
/**
* Delete several Submissions' videos for good
*
* Each video goes the way a single delete does, and each entry keeps the customer, the source
* and the tasks. When any id is not one of the company's Submissions nothing is deleted and the answer is
* 404 `SUBMISSION_NOT_FOUND`.
*/
async function Submission_bulkDeleteVideos(request, options) {
	return request("POST", "/record-and-send/submissions/bulk-delete-video", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Turn a Submission into a lead
*
* Opens one lead in the chosen pipeline through the same intake as form and booking leads, with
* the source Record and send, and links it to the Submission. A Submission that already has a lead answers as
* it stands, so the button and automatic leads together still make one. Needs Create leads (403
* `LEADS_PERMISSION_REQUIRED`) and Sales (403 `SALES_REQUIRED`); an unknown pipeline answers 404
* `PIPELINE_NOT_FOUND`.
*/
async function Submission_convertToLead(request, options) {
	return request("POST", "/record-and-send/submissions/{submissionId}/lead", {
		params: { submissionId: String(options.submissionId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete a Submission's video for good
*
* Removes the original file and the playback copy. The entry keeps the customer, the source and
* the tasks with their times. Deleting an already deleted video answers the entry as it stands.
*/
async function Submission_deleteVideo(request, options) {
	return request("DELETE", "/record-and-send/submissions/{submissionId}/video", {
		params: { submissionId: String(options.submissionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List the company's Submissions across every Video type, newest first
*
* 25 per page unless `pageSize` says otherwise. `videoTypeId` keeps one Video type, `show` keeps the
* new videos or the ones that could not be prepared, `sort=oldest` turns the order round and `q` matches part of
* the customer's name. `newCount` and `notPreparedCount` follow the search and the Video type but not `show`. A
* Video type of another company answers 404 `VIDEO_TYPE_NOT_FOUND`.
*/
async function Submission_inbox(request, options) {
	const query = {};
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	if (options.query?.videoTypeId !== void 0) query["videoTypeId"] = options.query.videoTypeId;
	if (options.query?.show !== void 0) query["show"] = options.query.show;
	return request("GET", "/record-and-send/submissions", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List a Video type's Submissions, newest first
*
* 25 per page unless `pageSize` says otherwise. `sort=oldest` turns the order round and `q`
* matches part of the customer's name. A Video type of another company answers 404 `VIDEO_TYPE_NOT_FOUND`.
*/
async function Submission_list(request, options) {
	const params = { videoTypeId: String(options.videoTypeId) };
	const query = {};
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	if (options.query?.sort !== void 0) query["sort"] = options.query.sort;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	return request("GET", "/record-and-send/video-types/{videoTypeId}/submissions", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Get addresses to play and download a Submission's video
*
* The addresses carry a ticket tied to this signed-in session. Each request on them checks the
* session is still live and See videos is still held, and they stop after 10 idle minutes.
* A deleted video answers 409 `VIDEO_DELETED`.
*/
async function Submission_mintPlaybackTicket(request, options) {
	return request("POST", "/record-and-send/submissions/{submissionId}/playback-ticket", {
		params: { submissionId: String(options.submissionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Read a still of a Submission's video
*
* A small JPEG taken from the playback copy, for list tiles. Answers 404 `SUBMISSION_NOT_FOUND`
* when `hasPoster` is false: while the video is prepared, when the still could not be made, or once the video is deleted.
*/
async function Submission_poster(request, options) {
	return request("GET", "/record-and-send/submissions/{submissionId}/poster", {
		params: { submissionId: String(options.submissionId) },
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Read a Submission
*
* The customer, the source, each task with its times and outcome, the pause marks and the
* playback state. The first read by a staff member stamps when it was first opened. A Submission of
* another company answers 404 `SUBMISSION_NOT_FOUND`.
*/
async function Submission_read(request, options) {
	return request("GET", "/record-and-send/submissions/{submissionId}", {
		params: { submissionId: String(options.submissionId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Find the Submission a lead came from
*
* For a lead with the source Record and send. A lead no Submission made answers 404
* `SUBMISSION_NOT_FOUND`.
*/
async function Submission_submissionOfLead(request, options) {
	return request("GET", "/record-and-send/leads/{leadId}/submission", {
		params: { leadId: String(options.leadId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSubmissionController(request) {
	return {
		bulkDeleteVideos: (options) => Submission_bulkDeleteVideos(request, options),
		convertToLead: (options) => Submission_convertToLead(request, options),
		deleteVideo: (options) => Submission_deleteVideo(request, options),
		inbox: (options) => Submission_inbox(request, options),
		list: (options) => Submission_list(request, options),
		mintPlaybackTicket: (options) => Submission_mintPlaybackTicket(request, options),
		poster: (options) => Submission_poster(request, options),
		read: (options) => Submission_read(request, options),
		submissionOfLead: (options) => Submission_submissionOfLead(request, options)
	};
}
//#endregion
//#region src/submission-stream/index.ts
/**
* Stream or download a Submission's video
*
* Honours a single `Range` (206, or 416 outside the file). `variant=playback` serves the
* playback copy and `variant=original` the customer's file; `download=1` answers as an attachment. Every
* request re-checks the ticket's session, See videos and the video, and any failure is a bare 404.
*/
async function SubmissionStream_stream(request, options) {
	const params = { submissionId: String(options.submissionId) };
	const query = {};
	if (options.query?.ticket !== void 0) query["ticket"] = options.query.ticket;
	if (options.query?.variant !== void 0) query["variant"] = options.query.variant;
	if (options.query?.download !== void 0) query["download"] = options.query.download;
	return request("GET", "/record-and-send/submissions/{submissionId}/stream", {
		params,
		query,
		responseType: "blob",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSubmissionStreamController(request) {
	return { stream: (options) => SubmissionStream_stream(request, options) };
}
//#endregion
//#region src/subscription/index.ts
async function Subscription_cancelSubscription(request, options) {
	return request("DELETE", "/subscription/cancel-subscription", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Called when browser popup session is completed to check for payment success or failure */
async function Subscription_checkSession(request, options) {
	return request("GET", "/subscription/check-session/{sessionID}", {
		params: { sessionID: String(options.sessionID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_eligibleForAiSummaryTrial(request, options) {
	return request("GET", "/subscription/eligible-for-ai-summary-trial", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get usage for current month */
async function Subscription_getUsage(request, options) {
	return request("GET", "/subscription/usage", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** is trial possible */
async function Subscription_isTrialPossible(request, options) {
	return request("GET", "/subscription/is-trial-possible", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_purchaseAiSummaryAddon(request, options) {
	return request("POST", "/subscription/purchase-add-on/ai-summary", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_purchaseStorageAddon(request, options) {
	return request("POST", "/subscription/purchase-add-on/storage", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_removeAiSummaryAddon(request, options) {
	return request("DELETE", "/subscription/remove-add-on/ai-summary", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_removeStorageAddon(request, options) {
	return request("DELETE", "/subscription/remove-add-on/storage", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_runManually(request, options) {
	return request("GET", "/subscription/send/trial-expiry/emails-to-queue/{day}", {
		params: { day: String(options.day) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_startAiSummaryTrial(request, options) {
	return request("POST", "/subscription/start-ai-summary-trial", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Start trial */
async function Subscription_startTrial(request, options) {
	return request("POST", "/subscription/start-trial", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function Subscription_updateStorageQuantity(request, options) {
	return request("PATCH", "/subscription/update-add-on/storage", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Upgrade subscription
*
* Called when user is upgrading subscription from existing plan that is not trial or free, this will not open a stripe session and handle the upgrade in background
*/
async function Subscription_upgradeSubscription(request, options) {
	return request("POST", "/subscription/upgrade/{plan}", {
		params: { plan: String(options.plan) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Called when user is first time upgrading subscription this will open a stripe session as a popup in frontend */
async function Subscription_upgradeWithSession(request, options) {
	const params = { plan: String(options.plan) };
	const query = {};
	if (options.query?.aiAddonSelected !== void 0) query["aiAddonSelected"] = options.query.aiAddonSelected;
	return request("POST", "/subscription/upgrade-with-session/{plan}", {
		params,
		query,
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSubscriptionController(request) {
	return {
		cancelSubscription: (options) => Subscription_cancelSubscription(request, options),
		checkSession: (options) => Subscription_checkSession(request, options),
		eligibleForAiSummaryTrial: (options) => Subscription_eligibleForAiSummaryTrial(request, options),
		getUsage: (options) => Subscription_getUsage(request, options),
		isTrialPossible: (options) => Subscription_isTrialPossible(request, options),
		purchaseAiSummaryAddon: (options) => Subscription_purchaseAiSummaryAddon(request, options),
		purchaseStorageAddon: (options) => Subscription_purchaseStorageAddon(request, options),
		removeAiSummaryAddon: (options) => Subscription_removeAiSummaryAddon(request, options),
		removeStorageAddon: (options) => Subscription_removeStorageAddon(request, options),
		runManually: (options) => Subscription_runManually(request, options),
		startAiSummaryTrial: (options) => Subscription_startAiSummaryTrial(request, options),
		startTrial: (options) => Subscription_startTrial(request, options),
		updateStorageQuantity: (options) => Subscription_updateStorageQuantity(request, options),
		upgradeSubscription: (options) => Subscription_upgradeSubscription(request, options),
		upgradeWithSession: (options) => Subscription_upgradeWithSession(request, options)
	};
}
//#endregion
//#region src/super-admin/index.ts
async function SuperAdmin_createMeetingNote(request, options) {
	return request("POST", "/superadmin/meetings/{meetingID}/notes", {
		params: { meetingID: String(options.meetingID) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperAdmin_getCompanyMeetings(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.title !== void 0) query["title"] = options.query.title;
	if (options.query?.userIds !== void 0) query["userIds"] = options.query.userIds;
	if (options.query?.startDate !== void 0) query["startDate"] = options.query.startDate;
	if (options.query?.endDate !== void 0) query["endDate"] = options.query.endDate;
	return request("GET", "/superadmin/meetings", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperAdmin_getMeetingChatHistory(request, options) {
	return request("GET", "/superadmin/meetings/{meetingID}/chat", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperAdmin_getMeetingNotes(request, options) {
	return request("GET", "/superadmin/meetings/{meetingID}/notes", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperAdmin_getSinglemeeting(request, options) {
	return request("GET", "/superadmin/meetings/{meetingID}", {
		params: { meetingID: String(options.meetingID) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSuperAdminController(request) {
	return {
		createMeetingNote: (options) => SuperAdmin_createMeetingNote(request, options),
		getCompanyMeetings: (options) => SuperAdmin_getCompanyMeetings(request, options),
		getMeetingChatHistory: (options) => SuperAdmin_getMeetingChatHistory(request, options),
		getMeetingNotes: (options) => SuperAdmin_getMeetingNotes(request, options),
		getSinglemeeting: (options) => SuperAdmin_getSinglemeeting(request, options)
	};
}
//#endregion
//#region src/superadmin-audit/index.ts
async function SuperadminAudit_getRetention(request, options) {
	return request("GET", "/admin/audit/retention/{targetCompanyId}", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperadminAudit_list(request, options) {
	const query = {};
	if (options.query?.companyId !== void 0) query["companyId"] = options.query.companyId;
	if (options.query?.actorId !== void 0) query["actorId"] = options.query.actorId;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.targetType !== void 0) query["targetType"] = options.query.targetType;
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.cursor !== void 0) query["cursor"] = options.query.cursor;
	return request("GET", "/admin/audit", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperadminAudit_setRetention(request, options) {
	return request("POST", "/admin/audit/retention/{targetCompanyId}", {
		params: { targetCompanyId: String(options.targetCompanyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSuperadminAuditController(request) {
	return {
		getRetention: (options) => SuperadminAudit_getRetention(request, options),
		list: (options) => SuperadminAudit_list(request, options),
		setRetention: (options) => SuperadminAudit_setRetention(request, options)
	};
}
//#endregion
//#region src/superadmin-inspection-library/index.ts
async function SuperadminInspectionLibrary_create(request, options) {
	return request("POST", "/superadmin/inspection-library", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperadminInspectionLibrary_list(request, options) {
	return request("GET", "/superadmin/inspection-library", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperadminInspectionLibrary_remove(request, options) {
	return request("DELETE", "/superadmin/inspection-library/{starterId}", {
		params: { starterId: String(options.starterId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperadminInspectionLibrary_seed(request, options) {
	return request("POST", "/superadmin/inspection-library/seed", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SuperadminInspectionLibrary_update(request, options) {
	return request("PATCH", "/superadmin/inspection-library/{starterId}", {
		params: { starterId: String(options.starterId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSuperadminInspectionLibraryController(request) {
	return {
		create: (options) => SuperadminInspectionLibrary_create(request, options),
		list: (options) => SuperadminInspectionLibrary_list(request, options),
		remove: (options) => SuperadminInspectionLibrary_remove(request, options),
		seed: (options) => SuperadminInspectionLibrary_seed(request, options),
		update: (options) => SuperadminInspectionLibrary_update(request, options)
	};
}
//#endregion
//#region src/supplier/index.ts
/** Archive a published supplier. */
async function Supplier_archive(request, options) {
	return request("POST", "/member-portal/manage/suppliers/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a new supplier (status defaults to DRAFT).
Accepts multipart/form-data with an optional `file` field for the logo. */
async function Supplier_create(request, options) {
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("POST", "/member-portal/manage/suppliers", {
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all suppliers (all statuses), with optional search/status/category/isHidden filter. */
async function Supplier_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.isHidden !== void 0) query["isHidden"] = options.query.isHidden;
	return request("GET", "/member-portal/manage/suppliers", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single supplier by ID. */
async function Supplier_findOne(request, options) {
	return request("GET", "/member-portal/manage/suppliers/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Bulk-import suppliers from a CSV file.

Accepts multipart/form-data with a `file` field containing a CSV.
First row must be headers (see SupplierCsvService for column contract).
Bad rows are accumulated into the errors array; they do not abort the batch. */
async function Supplier_importCsv(request, options) {
	return request("POST", "/member-portal/manage/suppliers/import-csv", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Distinct supplier categories for the filter picker. */
async function Supplier_listCategories(request, options) {
	return request("GET", "/member-portal/manage/suppliers/categories", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Manual "notify members" action. Sends a `member_portal.manual`
notification to the supplier's audience (or the optional audience override
in the body) and records the intent in the audit trail. Suppliers have no
automatic `*_published` type — this manual action is the only notification. */
async function Supplier_notify(request, options) {
	return request("POST", "/member-portal/manage/suppliers/{id}/notify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Render the branded notification email + resolved recipient count for
a given audience, so the authoring UI can preview before sending. */
async function Supplier_notifyPreview(request, options) {
	return request("POST", "/member-portal/manage/suppliers/{id}/notify-preview", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Transition supplier from DRAFT or SCHEDULED → PUBLISHED.
Stamps publishedAt + publishedByUserId. No notification fires — suppliers
are catalog entries without a registered notification type. */
async function Supplier_publish(request, options) {
	return request("POST", "/member-portal/manage/suppliers/{id}/publish", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a supplier. */
async function Supplier_remove(request, options) {
	return request("DELETE", "/member-portal/manage/suppliers/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Schedule the supplier for future publication.
Body must include `scheduledAt` (ISO 8601 date-time string). */
async function Supplier_schedule(request, options) {
	return request("POST", "/member-portal/manage/suppliers/{id}/schedule", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an existing supplier.
Accepts multipart/form-data with an optional `file` field to replace the logo. */
async function Supplier_update(request, options) {
	const params = { id: String(options.id) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PUT", "/member-portal/manage/suppliers/{id}", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSupplierController(request) {
	return {
		archive: (options) => Supplier_archive(request, options),
		create: (options) => Supplier_create(request, options),
		findAll: (options) => Supplier_findAll(request, options),
		findOne: (options) => Supplier_findOne(request, options),
		importCsv: (options) => Supplier_importCsv(request, options),
		listCategories: (options) => Supplier_listCategories(request, options),
		notify: (options) => Supplier_notify(request, options),
		notifyPreview: (options) => Supplier_notifyPreview(request, options),
		publish: (options) => Supplier_publish(request, options),
		remove: (options) => Supplier_remove(request, options),
		schedule: (options) => Supplier_schedule(request, options),
		update: (options) => Supplier_update(request, options)
	};
}
//#endregion
//#region src/supplier-viewer/index.ts
/** List published suppliers visible to the requesting member company. */
async function SupplierViewer_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	if (options.query?.productArea !== void 0) query["productArea"] = options.query.productArea;
	if (options.query?.hasActiveAgreement !== void 0) query["hasActiveAgreement"] = options.query.hasActiveAgreement;
	return request("GET", "/member-portal/suppliers", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a published supplier detail by ID (404 if not visible or not published).

Response shape:
- All supplier fields
- `agreements`: published agreement summary cards for this supplier
(id, title, agreementType, standardDiscountPct, maxDiscountPct, validUntil, category)
- `contacts`: SUPPLIER-type contacts linked to this supplier
(id, name, role, email, phone, photoUrl, productArea) */
async function SupplierViewer_findOne(request, options) {
	return request("GET", "/member-portal/suppliers/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSupplierViewerController(request) {
	return {
		findAll: (options) => SupplierViewer_findAll(request, options),
		findOne: (options) => SupplierViewer_findOne(request, options)
	};
}
//#endregion
//#region src/system-prompt/index.ts
async function SystemPrompt_deletePrompt(request, options) {
	return request("DELETE", "/system-prompt", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SystemPrompt_getPrompt(request, options) {
	return request("GET", "/system-prompt", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function SystemPrompt_setPrompt(request, options) {
	return request("PATCH", "/system-prompt", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createSystemPromptController(request) {
	return {
		deletePrompt: (options) => SystemPrompt_deletePrompt(request, options),
		getPrompt: (options) => SystemPrompt_getPrompt(request, options),
		setPrompt: (options) => SystemPrompt_setPrompt(request, options)
	};
}
//#endregion
//#region src/team-learning/index.ts
/** Get per-employee learning summary (ADMIN+)
Returns: name, enrolled count, completed count, lastCompletedAt.
NOTE: Must be declared BEFORE /:userId to avoid route collision. */
async function TeamLearning_exportTeamLearning(request, options) {
	return request("GET", "/team-learning/export", {
		responseType: "text",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Per-course participation breakdown for the company (ADMIN+)
Enrolled / completed / not-started counts per course.
NOTE: Must be declared BEFORE /:userId to avoid route collision. */
async function TeamLearning_getCourseBreakdown(request, options) {
	return request("GET", "/team-learning/course-breakdown", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get team learning overview — per-employee summary (ADMIN+) */
async function TeamLearning_getTeamLearning(request, options) {
	const query = {};
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/team-learning", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get full enrollment history for a specific user (ADMIN+) */
async function TeamLearning_getUserDetail(request, options) {
	const params = { userId: String(options.userId) };
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/team-learning/{userId}", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createTeamLearningController(request) {
	return {
		exportTeamLearning: (options) => TeamLearning_exportTeamLearning(request, options),
		getCourseBreakdown: (options) => TeamLearning_getCourseBreakdown(request, options),
		getTeamLearning: (options) => TeamLearning_getTeamLearning(request, options),
		getUserDetail: (options) => TeamLearning_getUserDetail(request, options)
	};
}
//#endregion
//#region src/tenant-billing/index.ts
async function TenantBilling_addonCheckout(request, options) {
	return request("POST", "/billing/addons/checkout", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_getEntitlement(request, options) {
	return request("GET", "/billing/entitlement", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_getMemberUsage(request, options) {
	const query = {};
	if (options.query?.from !== void 0) query["from"] = options.query.from;
	if (options.query?.to !== void 0) query["to"] = options.query.to;
	return request("GET", "/billing/usage/members", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_getPriceCatalog(request, options) {
	return request("GET", "/billing/price-catalog", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_getSelfUsage(request, options) {
	return request("GET", "/billing/usage/self", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_getTokenCatalog(request, options) {
	return request("GET", "/billing/token-catalog", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_getUsage(request, options) {
	return request("GET", "/billing/usage", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_removeAddon(request, options) {
	return request("DELETE", "/billing/addons/{addon}", {
		params: { addon: String(options.addon) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
async function TenantBilling_subscriptionCheckout(request, options) {
	return request("POST", "/billing/subscription/checkout", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createTenantBillingController(request) {
	return {
		addonCheckout: (options) => TenantBilling_addonCheckout(request, options),
		getEntitlement: (options) => TenantBilling_getEntitlement(request, options),
		getMemberUsage: (options) => TenantBilling_getMemberUsage(request, options),
		getPriceCatalog: (options) => TenantBilling_getPriceCatalog(request, options),
		getSelfUsage: (options) => TenantBilling_getSelfUsage(request, options),
		getTokenCatalog: (options) => TenantBilling_getTokenCatalog(request, options),
		getUsage: (options) => TenantBilling_getUsage(request, options),
		removeAddon: (options) => TenantBilling_removeAddon(request, options),
		subscriptionCheckout: (options) => TenantBilling_subscriptionCheckout(request, options)
	};
}
//#endregion
//#region src/tool/index.ts
/** Archive a published tool. */
async function Tool_archive(request, options) {
	return request("POST", "/member-portal/manage/tools/{id}/archive", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create a new tool (status defaults to DRAFT).
Validates displayMode: EMBED requires embedUrl; EXTERNAL_LINK requires externalUrl. */
async function Tool_create(request, options) {
	return request("POST", "/member-portal/manage/tools", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List all tools (all statuses), with optional search/status/area/category filter. */
async function Tool_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.area !== void 0) query["area"] = options.query.area;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	return request("GET", "/member-portal/manage/tools", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single tool by ID. */
async function Tool_findOne(request, options) {
	return request("GET", "/member-portal/manage/tools/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Send a manual "notify members" push for this tool.
Dispatches a notification to all audience-resolved member users. */
async function Tool_notify(request, options) {
	return request("POST", "/member-portal/manage/tools/{id}/notify", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Transition tool from DRAFT or SCHEDULED → PUBLISHED.
Stamps publishedAt, publishedByUserId, and fires the publish notification. */
async function Tool_publish(request, options) {
	return request("POST", "/member-portal/manage/tools/{id}/publish", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Soft-delete a tool. */
async function Tool_remove(request, options) {
	return request("DELETE", "/member-portal/manage/tools/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Schedule the tool for future publication.
Body must include `scheduledAt` (ISO 8601 date-time string). */
async function Tool_schedule(request, options) {
	return request("POST", "/member-portal/manage/tools/{id}/schedule", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Update an existing tool.
Re-validates displayMode constraints after merge. */
async function Tool_update(request, options) {
	return request("PUT", "/member-portal/manage/tools/{id}", {
		params: { id: String(options.id) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createToolController(request) {
	return {
		archive: (options) => Tool_archive(request, options),
		create: (options) => Tool_create(request, options),
		findAll: (options) => Tool_findAll(request, options),
		findOne: (options) => Tool_findOne(request, options),
		notify: (options) => Tool_notify(request, options),
		publish: (options) => Tool_publish(request, options),
		remove: (options) => Tool_remove(request, options),
		schedule: (options) => Tool_schedule(request, options),
		update: (options) => Tool_update(request, options)
	};
}
//#endregion
//#region src/tool-viewer/index.ts
/** List published tools visible to the requesting member company.
Supports optional filtering by area and category. Results ordered by area then name. */
async function ToolViewer_findAll(request, options) {
	const query = {};
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.area !== void 0) query["area"] = options.query.area;
	if (options.query?.category !== void 0) query["category"] = options.query.category;
	return request("GET", "/member-portal/tools", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get a single published tool by ID (404 if not visible or not published).
Detail response embeds resolved related-tool summaries (published + visible only). */
async function ToolViewer_findOne(request, options) {
	return request("GET", "/member-portal/tools/{id}", {
		params: { id: String(options.id) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createToolViewerController(request) {
	return {
		findAll: (options) => ToolViewer_findAll(request, options),
		findOne: (options) => ToolViewer_findOne(request, options)
	};
}
//#endregion
//#region src/trace/index.ts
async function Trace_traceEvent(request, options) {
	return request("GET", "/platform-events/operator/trace/{eventId}", {
		params: { eventId: String(options.eventId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createTraceController(request) {
	return { traceEvent: (options) => Trace_traceEvent(request, options) };
}
//#endregion
//#region src/video-request/index.ts
/**
* List a Video type's requests, newest first
*
* 25 per page unless `pageSize` says otherwise. `status` keeps one status and `q` matches part of
* the customer's name. `openLinkCount` counts the Video type's Sent, Not delivered, Opened and Uploading
* requests whatever the filters. A Video type of another company answers 404 `VIDEO_TYPE_NOT_FOUND`.
*/
async function VideoRequest_list(request, options) {
	const params = { videoTypeId: String(options.videoTypeId) };
	const query = {};
	if (options.query?.status !== void 0) query["status"] = options.query.status;
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	return request("GET", "/record-and-send/video-types/{videoTypeId}/requests", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* List the company's open links across every Video type, the soonest to expire first
*
* A link is open while it is Sent, Not delivered, Opened or Uploading and has not expired; an upload
* keeps it open past its 7 days. 25 per page unless `pageSize` says otherwise. `videoTypeId` keeps one Video type
* and `q` matches part of the customer's name. A Video type of another company answers 404 `VIDEO_TYPE_NOT_FOUND`.
*/
async function VideoRequest_listOpen(request, options) {
	const query = {};
	if (options.query?.videoTypeId !== void 0) query["videoTypeId"] = options.query.videoTypeId;
	if (options.query?.q !== void 0) query["q"] = options.query.q;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.pageSize !== void 0) query["pageSize"] = options.query.pageSize;
	return request("GET", "/record-and-send/open-requests", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Send a Video request's link again
*
* The one resend, 5 minutes after the last send, and never past the link's 7 days.
* A received or expired request answers 409 `VIDEO_REQUEST_NOT_RESENDABLE`; an uploading request can still be resent.
*/
async function VideoRequest_resend(request, options) {
	return request("POST", "/record-and-send/requests/{requestId}/resend", {
		params: { requestId: String(options.requestId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Send a customer a link to film a video
*
* Reuses the open Video request for the same phone on this Video type: inside 5 minutes of its
* last send the answer is `LINK_JUST_SENT`, after that the same link goes again once (`RESENT`), and then
* `SENT_TWICE`. Every other refusal (`VIDEO_TYPE_PAUSED`, the per-number limits, `SMS_DAILY_LIMIT`,
* `SEND_FAILED`) also answers 200 with `kind: REFUSED`, and nothing is sent.
*/
async function VideoRequest_send(request, options) {
	return request("POST", "/record-and-send/video-types/{videoTypeId}/requests", {
		params: { videoTypeId: String(options.videoTypeId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createVideoRequestController(request) {
	return {
		list: (options) => VideoRequest_list(request, options),
		listOpen: (options) => VideoRequest_listOpen(request, options),
		resend: (options) => VideoRequest_resend(request, options),
		send: (options) => VideoRequest_send(request, options)
	};
}
//#endregion
//#region src/video-type/index.ts
/**
* Create a Video type
*
* Refused with 403 `CHECKLIST_MODULE_OFF` while the company has Checklist switched off.
* Turning automatic leads on also needs `leads.create` (`LEADS_PERMISSION_REQUIRED`) and Sales (`SALES_REQUIRED`).
*/
async function VideoType_create(request, options) {
	return request("POST", "/record-and-send/video-types", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Read a Video type with its live checklist tasks */
async function VideoType_get(request, options) {
	return request("GET", "/record-and-send/video-types/{videoTypeId}", {
		params: { videoTypeId: String(options.videoTypeId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** List the company's Video types */
async function VideoType_list(request, options) {
	return request("GET", "/record-and-send/video-types", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Pause a Video type, so the form and open links stop taking videos */
async function VideoType_pause(request, options) {
	return request("POST", "/record-and-send/video-types/{videoTypeId}/pause", {
		params: { videoTypeId: String(options.videoTypeId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Delete a Video type with no Submission and no open link
*
* Refused with 409 `VIDEO_TYPE_HAS_SUBMISSIONS`, else 409 `VIDEO_TYPE_HAS_ACTIVE_LINKS`.
*/
async function VideoType_remove(request, options) {
	return request("DELETE", "/record-and-send/video-types/{videoTypeId}", {
		params: { videoTypeId: String(options.videoTypeId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Remove the intro video */
async function VideoType_removeIntroVideo(request, options) {
	return request("DELETE", "/record-and-send/video-types/{videoTypeId}/intro-video", {
		params: { videoTypeId: String(options.videoTypeId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Resume a paused Video type */
async function VideoType_resume(request, options) {
	return request("POST", "/record-and-send/video-types/{videoTypeId}/resume", {
		params: { videoTypeId: String(options.videoTypeId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Change a Video type's settings
*
* Changing the checklist is refused with `CHECKLIST_MODULE_OFF` while Checklist is off.
* The automatic lead fields also need `leads.create` (`LEADS_PERMISSION_REQUIRED`) and Sales (`SALES_REQUIRED`).
*/
async function VideoType_update(request, options) {
	return request("PATCH", "/record-and-send/video-types/{videoTypeId}", {
		params: { videoTypeId: String(options.videoTypeId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/**
* Upload or replace the intro video
*
* MP4 or MOV, at most 200 MB and 2 minutes.
*/
async function VideoType_uploadIntroVideo(request, options) {
	const params = { videoTypeId: String(options.videoTypeId) };
	const formData = options.body instanceof FormData ? options.body : buildFormData(options.body);
	let uploadBody = formData;
	let uploadContentType;
	if (options.onUploadProgress) {
		const encoder = new Request("", {
			method: "POST",
			body: formData
		});
		uploadContentType = encoder.headers.get("content-type") ?? void 0;
		const total = Number(encoder.headers.get("content-length") ?? 0);
		let loaded = 0;
		const progress = new TransformStream({ transform(chunk, controller) {
			loaded += chunk.byteLength;
			options.onUploadProgress({
				loaded,
				total,
				percent: total > 0 ? Math.round(loaded / total * 100) : 0
			});
			controller.enqueue(chunk);
		} });
		if (encoder.body) uploadBody = encoder.body.pipeThrough(progress);
	}
	return request("PUT", "/record-and-send/video-types/{videoTypeId}/intro-video", {
		params,
		body: uploadBody,
		contentType: uploadContentType ?? "multipart/form-data",
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createVideoTypeController(request) {
	return {
		create: (options) => VideoType_create(request, options),
		get: (options) => VideoType_get(request, options),
		list: (options) => VideoType_list(request, options),
		pause: (options) => VideoType_pause(request, options),
		remove: (options) => VideoType_remove(request, options),
		removeIntroVideo: (options) => VideoType_removeIntroVideo(request, options),
		resume: (options) => VideoType_resume(request, options),
		update: (options) => VideoType_update(request, options),
		uploadIntroVideo: (options) => VideoType_uploadIntroVideo(request, options)
	};
}
//#endregion
//#region src/v1/api-key-context/index.ts
/**
* Get the credential's context
*
* Returns the company, the user and the access level the credential in this request acts with, plus the web app origin for deep links. Call it once to learn which company an API key is bound to before sending `x-company-id`.
*/
async function ApiKeyContext_context(request, options) {
	return request("GET", "/v1/public-api-keys/context", {
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createApiKeyContextController(request) {
	return { context: (options) => ApiKeyContext_context(request, options) };
}
//#endregion
//#region src/v1/api-key/index.ts
/** List api key authentication attempts */
async function ApiKey_audit(request, options) {
	const params = { apiKeyId: String(options.apiKeyId) };
	const query = {};
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/v1/public-api-keys/{apiKeyId}/audit", {
		params,
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Create api key */
async function ApiKey_create(request, options) {
	return request("POST", "/v1/public-api-keys", {
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Get api keys */
async function ApiKey_list(request, options) {
	const query = {};
	if (options.query?.sortDirection !== void 0) query["sortDirection"] = options.query.sortDirection;
	if (options.query?.search !== void 0) query["search"] = options.query.search;
	if (options.query?.page !== void 0) query["page"] = options.query.page;
	if (options.query?.limit !== void 0) query["limit"] = options.query.limit;
	return request("GET", "/v1/public-api-keys", {
		query,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Delete api key */
async function ApiKey_revoke(request, options) {
	return request("DELETE", "/v1/public-api-keys/{apiKeyId}", {
		params: { apiKeyId: String(options.apiKeyId) },
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
/** Change api key access level */
async function ApiKey_updateAccessLevel(request, options) {
	return request("PATCH", "/v1/public-api-keys/{apiKeyId}/access-level", {
		params: { apiKeyId: String(options.apiKeyId) },
		body: options.body,
		signal: options?.signal,
		headers: options?.headers,
		...options?.responseType && { responseType: options.responseType },
		...options?.contentType && { contentType: options.contentType }
	});
}
function createApiKeyController(request) {
	return {
		audit: (options) => ApiKey_audit(request, options),
		create: (options) => ApiKey_create(request, options),
		list: (options) => ApiKey_list(request, options),
		revoke: (options) => ApiKey_revoke(request, options),
		updateAccessLevel: (options) => ApiKey_updateAccessLevel(request, options)
	};
}
//#endregion
//#region src/index.ts
function createClient(config) {
	const request = createRequestFn(config);
	return {
		accessRequest: createAccessRequestController(request),
		accountDeletion: createAccountDeletionController(request),
		accounting: createAccountingController(request),
		addressSuggestion: createAddressSuggestionController(request),
		adminAnalytics: createAdminAnalyticsController(request),
		adminBilling: createAdminBillingController(request),
		adminBooking: createAdminBookingController(request),
		adminChainCap: createAdminChainCapController(request),
		adminChain: createAdminChainController(request),
		adminChainSeedSse: createAdminChainSeedSseController(request),
		admin: createAdminController(request),
		adminMcp: createAdminMcpController(request),
		adminMeetingTelemetry: createAdminMeetingTelemetryController(request),
		adminRoles: createAdminRolesController(request),
		adminTranscription: createAdminTranscriptionController(request),
		adminUserMeetings: createAdminUserMeetingsController(request),
		agenda: createAgendaController(request),
		agent: createAgentController(request),
		agentRun: createAgentRunController(request),
		agentRunSse: createAgentRunSseController(request),
		agreement: createAgreementController(request),
		agreementViewer: createAgreementViewerController(request),
		aiOps: createAiOpsController(request),
		analytics: createAnalyticsController(request),
		announcement: createAnnouncementController(request),
		article: createArticleController(request),
		articleViewer: createArticleViewerController(request),
		audit: createAuditController(request),
		auth: createAuthController(request),
		blackoutDate: createBlackoutDateController(request),
		bookingApi: createBookingApiController(request),
		brand: createBrandController(request),
		branding: createBrandingController(request),
		brandingViewer: createBrandingViewerController(request),
		calendar: createCalendarController(request),
		campaign: createCampaignController(request),
		campaignViewer: createCampaignViewerController(request),
		catalog: createCatalogController(request),
		chainAnalytics: createChainAnalyticsController(request),
		chainBoards: createChainBoardsController(request),
		chain: createChainController(request),
		chainCoverage: createChainCoverageController(request),
		chainDashboard: createChainDashboardController(request),
		chainDistribution: createChainDistributionController(request),
		chainLeaderboard: createChainLeaderboardController(request),
		chainOrders: createChainOrdersController(request),
		chainOversight: createChainOversightController(request),
		chainWebsite: createChainWebsiteController(request),
		checklist: createChecklistController(request),
		checklistItem: createChecklistItemController(request),
		clientBooking: createClientBookingController(request),
		clients: createClientsController(request),
		communication: createCommunicationController(request),
		communicationMedia: createCommunicationMediaController(request),
		communicationViewer: createCommunicationViewerController(request),
		communicationViewerMedia: createCommunicationViewerMediaController(request),
		companies: createCompaniesController(request),
		company: createCompanyController(request),
		companyPublic: createCompanyPublicController(request),
		companyServiceArea: createCompanyServiceAreaController(request),
		companyWhiteLabeling: createCompanyWhiteLabelingController(request),
		competenceApproval: createCompetenceApprovalController(request),
		competenceChain: createCompetenceChainController(request),
		competenceEvidence: createCompetenceEvidenceController(request),
		competenceExport: createCompetenceExportController(request),
		competenceInternal: createCompetenceInternalController(request),
		competenceRecord: createCompetenceRecordController(request),
		competenceType: createCompetenceTypeController(request),
		configPush: createConfigPushController(request),
		contactCategory: createContactCategoryController(request),
		contact: createContactController(request),
		contactViewer: createContactViewerController(request),
		courseAnalytics: createCourseAnalyticsController(request),
		courseCategory: createCourseCategoryController(request),
		course: createCourseController(request),
		courseMaterial: createCourseMaterialController(request),
		crmActivity: createCrmActivityController(request),
		crmCompany: createCrmCompanyController(request),
		crmContacts: createCrmContactsController(request),
		crmEntitlementAdmin: createCrmEntitlementAdminController(request),
		crmEntitlement: createCrmEntitlementController(request),
		crmImport: createCrmImportController(request),
		crmReporting: createCrmReportingController(request),
		crmScheduledActivity: createCrmScheduledActivityController(request),
		customField: createCustomFieldController(request),
		customerAccount: createCustomerAccountController(request),
		customerLink: createCustomerLinkController(request),
		deadLetter: createDeadLetterController(request),
		dealInspection: createDealInspectionController(request),
		demoDataSeed: createDemoDataSeedController(request),
		deviceTestProvisioning: createDeviceTestProvisioningController(request),
		deviceToken: createDeviceTokenController(request),
		e2ECalendar: createE2ECalendarController(request),
		e2E: createE2EController(request),
		e2EMeetingTelemetry: createE2EMeetingTelemetryController(request),
		e2ERecordAndSend: createE2ERecordAndSendController(request),
		e2ERoutes: createE2ERoutesController(request),
		email: createEmailController(request),
		emailWebhook: createEmailWebhookController(request),
		endorsement: createEndorsementController(request),
		engagementCronTest: createEngagementCronTestController(request),
		enrollment: createEnrollmentController(request),
		feedbackAdmin: createFeedbackAdminController(request),
		feedback: createFeedbackController(request),
		formAdmin: createFormAdminController(request),
		formPublic: createFormPublicController(request),
		guidedInspection: createGuidedInspectionController(request),
		health: createHealthController(request),
		identities: createIdentitiesController(request),
		impersonation: createImpersonationController(request),
		incoming: createIncomingController(request),
		industryPreset: createIndustryPresetController(request),
		inspectionAddressSearch: createInspectionAddressSearchController(request),
		inspection: createInspectionController(request),
		inspectionLibrary: createInspectionLibraryController(request),
		inspectionList: createInspectionListController(request),
		inspectionMedia: createInspectionMediaController(request),
		inspectionMediaLegacyOpen: createInspectionMediaLegacyOpenController(request),
		inspectionMediaOpen: createInspectionMediaOpenController(request),
		inspectionReport: createInspectionReportController(request),
		inspectionSite: createInspectionSiteController(request),
		inspectionTemplate: createInspectionTemplateController(request),
		internalMigration: createInternalMigrationController(request),
		invoicing: createInvoicingController(request),
		itxAdmin: createItxAdminController(request),
		itxFrame: createItxFrameController(request),
		itxFrameResolver: createItxFrameResolverController(request),
		itxWebhook: createItxWebhookController(request),
		leadDeal: createLeadDealController(request),
		learning: createLearningController(request),
		linkMobilityWebhook: createLinkMobilityWebhookController(request),
		livekitWebhook: createLivekitWebhookController(request),
		loginSecurity: createLoginSecurityController(request),
		lostReason: createLostReasonController(request),
		media: createMediaController(request),
		mediaProgressSse: createMediaProgressSseController(request),
		meetingBackgroundImageStream: createMeetingBackgroundImageStreamController(request),
		meeting: createMeetingController(request),
		meetingDev: createMeetingDevController(request),
		meetingPipelineSse: createMeetingPipelineSseController(request),
		meetingRoom: createMeetingRoomController(request),
		meetingRoomSse: createMeetingRoomSseController(request),
		meetingSummaryPublic: createMeetingSummaryPublicController(request),
		meetingTelemetryPublic: createMeetingTelemetryPublicController(request),
		meetingTelemetryTicket: createMeetingTelemetryTicketController(request),
		meetingTelemetryTypesPublic: createMeetingTelemetryTypesPublicController(request),
		merge: createMergeController(request),
		myAiSettings: createMyAiSettingsController(request),
		normalizeTranscriptionLanguage: createNormalizeTranscriptionLanguageController(request),
		notification: createNotificationController(request),
		nudges: createNudgesController(request),
		oAuthCompany: createOAuthCompanyController(request),
		offer: createOfferController(request),
		offerInternal: createOfferInternalController(request),
		offerProposal: createOfferProposalController(request),
		offerPublic: createOfferPublicController(request),
		offerSettings: createOfferSettingsController(request),
		orderAttachment: createOrderAttachmentController(request),
		orderCompanySse: createOrderCompanySseController(request),
		order: createOrderController(request),
		orderCustomer: createOrderCustomerController(request),
		orderMessage: createOrderMessageController(request),
		orderPipeline: createOrderPipelineController(request),
		orderPublic: createOrderPublicController(request),
		pipeline: createPipelineController(request),
		placement: createPlacementController(request),
		portalInvoice: createPortalInvoiceController(request),
		portalOrder: createPortalOrderController(request),
		profile: createProfileController(request),
		publicDeviceToken: createPublicDeviceTokenController(request),
		quickLink: createQuickLinkController(request),
		reconcileStuckTranscriptions: createReconcileStuckTranscriptionsController(request),
		recordAndSendAdmin: createRecordAndSendAdminController(request),
		recordAndSendHome: createRecordAndSendHomeController(request),
		recordAndSendPublicForm: createRecordAndSendPublicFormController(request),
		recordAndSendSmsStatus: createRecordAndSendSmsStatusController(request),
		recordings: createRecordingsController(request),
		relayControl: createRelayControlController(request),
		reports: createReportsController(request),
		resourceCategory: createResourceCategoryController(request),
		resource: createResourceController(request),
		roles: createRolesController(request),
		savedView: createSavedViewController(request),
		search: createSearchController(request),
		serviceType: createServiceTypeController(request),
		sharedPushTypes: createSharedPushTypesController(request),
		sharedTypes: createSharedTypesController(request),
		signInHelp: createSignInHelpController(request),
		signInReport: createSignInReportController(request),
		signup: createSignupController(request),
		signupE2e: createSignupE2eController(request),
		staffingAdmin: createStaffingAdminController(request),
		staffingAnalytics: createStaffingAnalyticsController(request),
		staffingCategory: createStaffingCategoryController(request),
		staffingInterest: createStaffingInterestController(request),
		staffingInternal: createStaffingInternalController(request),
		staffingPosting: createStaffingPostingController(request),
		staffingSkill: createStaffingSkillController(request),
		staffingSkillLevel: createStaffingSkillLevelController(request),
		stripe: createStripeController(request),
		submission: createSubmissionController(request),
		submissionStream: createSubmissionStreamController(request),
		subscription: createSubscriptionController(request),
		superAdmin: createSuperAdminController(request),
		superadminAudit: createSuperadminAuditController(request),
		superadminInspectionLibrary: createSuperadminInspectionLibraryController(request),
		supplier: createSupplierController(request),
		supplierViewer: createSupplierViewerController(request),
		systemPrompt: createSystemPromptController(request),
		teamLearning: createTeamLearningController(request),
		tenantBilling: createTenantBillingController(request),
		tool: createToolController(request),
		toolViewer: createToolViewerController(request),
		trace: createTraceController(request),
		videoRequest: createVideoRequestController(request),
		videoType: createVideoTypeController(request),
		v1: {
			apiKeyContext: createApiKeyContextController(request),
			apiKey: createApiKeyController(request)
		}
	};
}
//#endregion
export { AccessRequest_requestAccess, AccountDeletion_deleteMyAccount, Accounting_connect, Accounting_disconnect, Accounting_getConnection, Accounting_getVatMapping, Accounting_pollNow, Accounting_putVatMapping, Accounting_recheck, Accounting_updateSettings, AddressSuggestion_list, AdminAnalytics_getCompanyAnalytics, AdminBilling_getPanel, AdminBilling_provision, AdminBilling_setPlan, AdminBilling_setQuotaOverride, AdminBilling_setStorageOverride, AdminBooking_assignUserToBookingSlot, AdminBooking_cancelBooking, AdminBooking_createBookingServiceWithAvailabilityAndFields, AdminBooking_deleteConfig, AdminBooking_getAnalytics, AdminBooking_getAssigneeAvailability, AdminBooking_getCampaignAnalytics, AdminBooking_getCompanyConfig, AdminBooking_getPaginatedBookingSlots, AdminBooking_getPaginatedUsers, AdminBooking_getReferrerAnalytics, AdminBooking_getStaffServiceAvailability, AdminBooking_removeWidgetLogo, AdminBooking_rescheduleSlot, AdminBooking_updateBookingServiceWithAvailabilityAndFields, AdminBooking_updateConfigSettings, AdminBooking_uploadWidgetLogo, AdminChainCap_clearGuardrails, AdminChainCap_clearPool, AdminChainCap_getCapBoard, AdminChainCap_setGuardrails, AdminChainCap_setPool, AdminChainSeedSse_resetStream, AdminChainSeedSse_stream, AdminChain_addMember, AdminChain_downloadCredentialsCsv, AdminChain_getChainDetail, AdminChain_getChainMembers, AdminChain_getChainModules, AdminChain_listChains, AdminChain_removeMember, AdminChain_resetChainData, AdminChain_resetChainDataStatus, AdminChain_searchEligibleCompanies, AdminChain_seedChainDemoData, AdminChain_seedChainDemoDataStatus, AdminChain_seedDemoChain, AdminChain_setChainActive, AdminChain_setChainModules, AdminChain_updateMember, AdminMcp_adoption, AdminMcp_calls, AdminMcp_context, AdminMcp_spec, AdminMeetingTelemetry_eraseUser, AdminMeetingTelemetry_getDigest, AdminMeetingTelemetry_getMeeting, AdminMeetingTelemetry_getPopulation, AdminMeetingTelemetry_getSettings, AdminMeetingTelemetry_listFlags, AdminMeetingTelemetry_listMeetings, AdminMeetingTelemetry_listSamples, AdminMeetingTelemetry_listTimeline, AdminMeetingTelemetry_listUserSessions, AdminMeetingTelemetry_updateSettings, AdminRoles_assignRole, AdminRoles_createRole, AdminRoles_deleteRole, AdminRoles_duplicateRole, AdminRoles_getCatalog, AdminRoles_getRole, AdminRoles_listRoles, AdminRoles_updateRole, AdminTranscription_getMeetingTracks, AdminTranscription_getMeetings, AdminTranscription_getOverview, AdminTranscription_reconcileStuckTracks, AdminTranscription_retryAllFailed, AdminTranscription_retryTrack, AdminUserMeetings_getUserMeeting, AdminUserMeetings_listUserMeetings, Admin_addToSuppressionList, Admin_cancelCompanyInvite, Admin_createAdmin, Admin_deleteAdmin, Admin_exportUsersToCSV, Admin_generateUserPassword, Admin_getCompanies, Admin_getCompanyInvites, Admin_getCompanyModules, Admin_getCompanyUsers, Admin_getEmailDetail, Admin_getEmailLogs, Admin_getEmailStats, Admin_getSmsAlertSettings, Admin_getSmsDetail, Admin_getSmsLogs, Admin_getSmsStats, Admin_getSuppressionList, Admin_getUnsubscribeList, Admin_getUserDetails, Admin_inviteCompanyMember, Admin_listAdmins, Admin_listUsers, Admin_removeCompanyUser, Admin_removeFromSuppressionList, Admin_removeFromUnsubscribeList, Admin_resetUserPassword, Admin_seedCrmDemoData, Admin_setCompanyModules, Admin_syncSuppressionList, Admin_updateCompanyTrialEndDate, Admin_updateSmsAlertSettings, Agenda_list, AgentRunSse_stream, AgentRun_retry, AgentRun_successor, Agent_getSttConfig, Agent_reportFailure, Agent_shouldTranscribe, Agent_submitAudioTracks, AgreementViewer_findAll, AgreementViewer_findOne, AgreementViewer_markUsage, AgreementViewer_unmarkUsage, Agreement_archive, Agreement_create, Agreement_findAll, Agreement_findOne, Agreement_listCategories, Agreement_notify, Agreement_notifyPreview, Agreement_publish, Agreement_remove, Agreement_removeContract, Agreement_removeSupportingDoc, Agreement_schedule, Agreement_update, Agreement_usageOverview, AiOps_getCost, AiOps_getInspectionOutputs, AiOps_getInvoiceParse, AiOps_getMeetingPipeline, AiOps_getMeetingSummaries, AiOps_getMeetingTranscript, AiOps_getProviderChain, AiOps_getRun, AiOps_getSummary, AiOps_listMeetingsAtRisk, AiOps_listRuns, AiOps_lookupMeetings, AiOps_rebuildMeetingFromStorage, AiOps_repairMeeting, AiOps_summarizeMeeting, Analytics_getAnalyticsOverview, Analytics_getChainFeatureUsage, Analytics_getCompanyActivity, Analytics_getCompanyDetail, Analytics_getCompanyFeatureUsage, Analytics_getFeatureAdoption, Analytics_getLeaderboard, Analytics_getMcpAdoption, Analytics_getMcpAdoptionCompanies, Analytics_getMobileAdoption, Analytics_getMobileAdoptionUsers, Announcement_archive, Announcement_create, Announcement_findAll, Announcement_findOne, Announcement_getStats, Announcement_notify, Announcement_notifyPreview, Announcement_publish, Announcement_update, ApiKeyContext_context, ApiKey_audit, ApiKey_create, ApiKey_list, ApiKey_revoke, ApiKey_updateAccessLevel, ArticleViewer_getCurrent, Article_getCurrent, Article_notify, Article_publish, Article_upsert, Audit_getRetention, Audit_list, Audit_timeline, Auth_acceptInvite, Auth_getInvitations, Auth_previewInvite, Auth_previewNewMemberInvite, Auth_register, Auth_registerByCompanyID, BlackoutDate_create, BlackoutDate_delete, BlackoutDate_list, BookingApi_cancelBooking, BookingApi_createBooking, BookingApi_getBooking, BookingApi_getBookingServiceAvailability, BookingApi_listBookingServiceWorkingDays, BookingApi_listBookingServices, BookingApi_listBookings, BookingApi_rescheduleBooking, Brand_resolveBrand, BrandingViewer_findAll, BrandingViewer_findOne, Branding_archive, Branding_create, Branding_findAll, Branding_findOne, Branding_notify, Branding_publish, Branding_remove, Branding_schedule, Branding_update, Calendar_deleteCalendar, Calendar_getCalendars, Calendar_getMicrosoftAdminConsentUrl, Calendar_getRedirectURL, Calendar_handleAdminConsentCallback, Calendar_handleRedirect, Calendar_updateSyncSettings, CampaignViewer_findAll, CampaignViewer_findOne, CampaignViewer_subscribe, CampaignViewer_unsubscribe, Campaign_addAssets, Campaign_archive, Campaign_create, Campaign_findAll, Campaign_findOne, Campaign_notify, Campaign_notifyPreview, Campaign_publish, Campaign_remove, Campaign_schedule, Campaign_update, Catalog_archive, Catalog_bulkArchive, Catalog_bulkRestore, Catalog_create, Catalog_list, Catalog_removeImage, Catalog_restore, Catalog_update, Catalog_uploadImage, ChainAnalytics_exportCsv, ChainAnalytics_getCapacity, ChainAnalytics_getCompanyRankings, ChainAnalytics_getGeographic, ChainAnalytics_getOrders, ChainAnalytics_getOverview, ChainAnalytics_getRatings, ChainAnalytics_getRevenue, ChainBoards_getBoard, ChainBoards_getCandidates, ChainBoards_getParentIncoming, ChainBoards_reclaim, ChainBoards_recommendedMembers, ChainBoards_reject, ChainBoards_reopen, ChainCoverage_getCoverage, ChainDashboard_getHealth, ChainDashboard_getKpis, ChainDashboard_getMemberMetrics, ChainDashboard_getPerformers, ChainDistribution_assign, ChainDistribution_getConfig, ChainDistribution_pickup, ChainDistribution_reassign, ChainDistribution_release, ChainDistribution_setMemberPause, ChainDistribution_sweepLapsed, ChainDistribution_sweepMembership, ChainDistribution_unassign, ChainDistribution_upsertConfig, ChainLeaderboard_getLeaderboard, ChainOrders_cancelMember, ChainOrders_completeMember, ChainOrders_keepMember, ChainOrders_linkContactMember, ChainOrders_listMessagesMember, ChainOrders_markMessagesReadMember, ChainOrders_member, ChainOrders_memberOne, ChainOrders_moveMember, ChainOrders_parent, ChainOrders_reverseMember, ChainOrders_sendMessageMember, ChainOrders_updateLinesMember, ChainOrders_updateMember, ChainOversight_getOversight, ChainWebsite_addressSuggestionsList, ChainWebsite_getEmployees, ChainWebsite_getProfile, ChainWebsite_listMembers, ChainWebsite_search, ChainWebsite_submitInquiry, Chain_createMembership, Chain_getAggregatedCounts, Chain_getChainInfo, Chain_getMembers, ChecklistItem_addComment, ChecklistItem_deleteComment, ChecklistItem_deleteItem, ChecklistItem_toggleItemStatus, Checklist_attachChecklistToMeeting, Checklist_createChecklist, Checklist_deleteChecklist, Checklist_detachChecklistFromMeeting, Checklist_duplicateChecklist, Checklist_getChecklist, Checklist_getChecklistsForMeeting, Checklist_getMeetingsForChecklist, Checklist_getPaginatedChecklists, Checklist_updateChecklist, ClientBooking_bookSlot, ClientBooking_cancelBooking, ClientBooking_generateCalendarUrl, ClientBooking_getServiceAvailability, ClientBooking_getServicesWithFormFields, ClientBooking_getSlot, ClientBooking_getWorkingDaysByServiceId, ClientBooking_rescheduleSlot, ClientBooking_trackAnalyticsEvent, ClientBooking_verifyCaptcha, Clients_createClient, Clients_deleteClient, Clients_getClients, Clients_searchClient, Clients_updateClient, CommunicationMedia_streamAttachment, CommunicationMedia_streamResponseSignature, CommunicationViewerMedia_streamAttachment, CommunicationViewerMedia_streamOwnSignature, CommunicationViewer_findAll, CommunicationViewer_findOne, CommunicationViewer_submitResponse, Communication_addAttachment, Communication_archive, Communication_create, Communication_downloadAnswerPdf, Communication_duplicate, Communication_exportResponses, Communication_findAll, Communication_findOne, Communication_findResponses, Communication_listSends, Communication_remove, Communication_removeAttachment, Communication_renotify, Communication_send, Communication_sendPreview, Communication_update, Companies_checkAvailability, Companies_createCompany, Companies_getCompanies, Companies_lookupRegistryIndustry, Companies_searchRegistry, CompanyPublic_getBranding, CompanyPublic_getMeetingCapability, CompanyServiceArea_deleteServiceArea, CompanyServiceArea_getServiceArea, CompanyServiceArea_putServiceArea, CompanyWhiteLabeling_checkCustomDomain, CompanyWhiteLabeling_createCustomDomain, CompanyWhiteLabeling_resetWhiteLabeling, CompanyWhiteLabeling_updateAppTitle, CompanyWhiteLabeling_updateCustomDomain, CompanyWhiteLabeling_updateCustomLogo, CompanyWhiteLabeling_updateCustomSMSContent, CompanyWhiteLabeling_updateFavicon, CompanyWhiteLabeling_updateSmsTemplates, CompanyWhiteLabeling_updateThemeColor, Company_assignRole, Company_changeRole, Company_completeMyGettingStartedStep, Company_deleteCompany, Company_deleteInvitation, Company_dismissMyGettingStarted, Company_getCapacity, Company_getCompany, Company_getMemberOrderMetrics, Company_getMyGettingStarted, Company_getMyMeetingSettings, Company_inviteMembers, Company_invitedMembers, Company_removeCompanyUser, Company_updateAiSummaryTranscriptionLang, Company_updateBanner, Company_updateCompany, Company_updateCompanyUserFlags, Company_updateInvitation, Company_updateLogo, Company_updateMeetingPolicy, Company_updateMyMeetingSettings, CompetenceApproval_approve, CompetenceApproval_cancel, CompetenceApproval_list, CompetenceApproval_listEvidence, CompetenceApproval_listMine, CompetenceApproval_reject, CompetenceApproval_submit, CompetenceApproval_uploadEvidence, CompetenceChain_exportCsv, CompetenceChain_getCompanies, CompetenceChain_getForecast, CompetenceChain_getOverview, CompetenceEvidence_delete, CompetenceEvidence_list, CompetenceEvidence_upload, CompetenceExport_exportMatrixCsv, CompetenceExport_exportMatrixPdf, CompetenceExport_exportProfilePdf, CompetenceInternal_runExpiryCheck, CompetenceRecord_create, CompetenceRecord_getHistory, CompetenceRecord_getMatrix, CompetenceRecord_getUserProfile, CompetenceRecord_remove, CompetenceRecord_update, CompetenceType_archive, CompetenceType_create, CompetenceType_getArchiveImpact, CompetenceType_getOne, CompetenceType_hardDelete, CompetenceType_list, CompetenceType_remove, CompetenceType_restore, CompetenceType_update, ConfigPush_getHistory, ConfigPush_getPushedMembers, ConfigPush_getReceipts, ConfigPush_push, ConfigPush_rePush, ContactCategory_contactCount, ContactCategory_create, ContactCategory_list, ContactCategory_remove, ContactCategory_update, ContactViewer_addFavorite, ContactViewer_findAll, ContactViewer_findOne, ContactViewer_listCategories, ContactViewer_listFavorites, ContactViewer_listMine, ContactViewer_removeFavorite, Contact_create, Contact_findAll, Contact_findOne, Contact_getDedicated, Contact_importCsv, Contact_remove, Contact_removePhoto, Contact_setDedicated, Contact_update, Contact_uploadPhoto, CourseAnalytics_exportCourseAnalytics, CourseAnalytics_getChainCourseAnalytics, CourseAnalytics_getCourseAnalytics, CourseCategory_create, CourseCategory_delete, CourseCategory_findAll, CourseCategory_reorder, CourseCategory_update, CourseMaterial_addLink, CourseMaterial_delete, CourseMaterial_findAll, CourseMaterial_reorder, CourseMaterial_update, CourseMaterial_uploadFile, Course_cancel, Course_complete, Course_create, Course_delete, Course_findAll, Course_findOne, Course_publish, Course_unpublish, Course_update, CrmActivity_appendNote, CrmActivity_deleteNote, CrmActivity_editNote, CrmActivity_logCall, CrmActivity_read, CrmActivity_rollup, CrmActivity_setPin, CrmCompany_archive, CrmCompany_batchArchive, CrmCompany_batchRestore, CrmCompany_create, CrmCompany_exportCompanies, CrmCompany_filterOptions, CrmCompany_findAll, CrmCompany_findOne, CrmCompany_refresh, CrmCompany_restore, CrmCompany_searchRegistry, CrmCompany_update, CrmContacts_archiveContact, CrmContacts_batchArchiveContacts, CrmContacts_batchRestoreContacts, CrmContacts_countContacts, CrmContacts_createContact, CrmContacts_exportContacts, CrmContacts_getContact, CrmContacts_getContacts, CrmContacts_restoreContact, CrmContacts_setPrimaryContact, CrmContacts_updateContact, CrmEntitlementAdmin_getState, CrmEntitlementAdmin_provision, CrmEntitlement_countBuriedInboundLeads, CrmEntitlement_getEntitlement, CrmImport_cancelJob, CrmImport_createJob, CrmImport_getFields, CrmImport_getJob, CrmImport_processJob, CrmImport_undoJob, CrmReporting_getReport, CrmScheduledActivity_cancel, CrmScheduledActivity_complete, CrmScheduledActivity_create, CrmScheduledActivity_listUpcoming, CrmScheduledActivity_update, CustomField_create, CustomField_filter, CustomField_getMeta, CustomField_list, CustomField_listValues, CustomField_remove, CustomField_reorder, CustomField_setValues, CustomField_update, CustomerAccount_deleteOwnAccount, CustomerLink_completeUpload, CustomerLink_lookup, CustomerLink_readUpload, CustomerLink_resend, CustomerLink_startUpload, CustomerLink_streamIntro, CustomerLink_uploadPart, DeadLetter_list, DeadLetter_replay, DeadLetter_resolve, DeadLetter_reveal, DealInspection_getCard, DealInspection_getStartContext, DealInspection_linkDeal, DealInspection_reschedule, DealInspection_startFromDeal, DealInspection_unlinkDeal, DemoDataSeed_seed, DeviceTestProvisioning_createCompany, DeviceTestProvisioning_deleteCompany, DeviceTestProvisioning_listCompanies, DeviceToken_register, DeviceToken_unregister, E2ECalendar_listEvents, E2ECalendar_reset, E2ECalendar_setBusy, E2ECalendar_setFailure, E2EMeetingTelemetry_timeline, E2ERecordAndSend_ageRequest, E2ERecordAndSend_expirePlaybackTicket, E2ERecordAndSend_linkSecret, E2ERecordAndSend_reserveInvite, E2ERecordAndSend_runSweeps, E2ERecordAndSend_setSmsPartsToday, E2ERecordAndSend_storedVideos, E2ERecordAndSend_submissionPlayback, E2ERecordAndSend_videoRequestState, E2ERecordAndSend_videoRequestsForPhone, E2ERoutes_listRoutes, E2E_agendaReminderSweep, E2E_clearPushes, E2E_clearSmsMessages, E2E_createMemberUser, E2E_createNotification, E2E_emitNotification, E2E_followUpReminderSweep, E2E_forceAssignOrder, E2E_generationGate, E2E_getBrandedUrl, E2E_getPushes, E2E_getSmsMessages, E2E_platformEventsAccessLog, E2E_platformEventsDeadLetter, E2E_platformEventsForceRedeliver, E2E_platformEventsOutbox, E2E_platformEventsPauseRelay, E2E_platformEventsProcessed, E2E_platformEventsProvingForceFail, E2E_platformEventsReapDeadLetters, E2E_platformEventsReapProcessed, E2E_platformEventsResumeRelay, E2E_platformEventsTriggerRelay, E2E_pushScopedTokens, E2E_rejectSms, E2E_resetCustomerPassword, E2E_resetOrderAssignments, E2E_resetOrderStatus, E2E_seedCrmMyWorkRow, E2E_seedEndorsedOrder, E2E_seedFlaggedAffiliation, E2E_seedMobileFixtures, E2E_seedOrderMessage, E2E_seedSuperAdmin, E2E_seedTenant, E2E_seedUploadedInvoice, E2E_sendCommunication, E2E_setAgentRun, E2E_setBillingStatus, E2E_setChainMembershipVerification, E2E_setChainPoolCycle, E2E_setChainQuotaConfig, E2E_setCompanyLocation, E2E_setCompanyQuotaOverride, E2E_setCompanyUserEnumRole, E2E_setCompanyUserRole, E2E_setCustomerMustChangePassword, E2E_setExtraRoles, E2E_setLegacyPlannedDate, E2E_setOrderCustomer, E2E_setOrderStatus, E2E_setRunLedger, E2E_setSignupIncomplete, E2E_setTokenQuota, E2E_setUserStatus, E2E_setWhiteLabeling, E2E_settleRun, E2E_storedResetOtp, EmailWebhook_handleSesWebhook, Email_getUnsubscribeBrand, Email_handleRedirect, Endorsement_confirmFlagged, Endorsement_dismissFlagged, Endorsement_getControls, Endorsement_getMemberView, Endorsement_listFlagged, Endorsement_updateAffiliationDisplay, Endorsement_updateSwitch, EngagementCronTest_findTwoMonthInactiveUsers, EngagementCronTest_sendManualYearlyReminder, EngagementCronTest_sendReEngagementEmailForUser, Enrollment_bulkEnroll, Enrollment_bulkUpdateStatus, Enrollment_cancelEnrollment, Enrollment_getEnrollableUsers, Enrollment_listEnrollments, Enrollment_selfEnroll, Enrollment_updateStatus, FeedbackAdmin_detail, FeedbackAdmin_list, FeedbackAdmin_updateStatus, Feedback_submit, FormAdmin_archiveForm, FormAdmin_createForm, FormAdmin_deleteForm, FormAdmin_duplicateForm, FormAdmin_exportSubmissions, FormAdmin_getAnalytics, FormAdmin_getForm, FormAdmin_getReferrerAnalytics, FormAdmin_getSubmission, FormAdmin_listForms, FormAdmin_listSubmissions, FormAdmin_publishForm, FormAdmin_unarchiveForm, FormAdmin_unpublishForm, FormAdmin_updateForm, FormAdmin_uploadFormLogo, FormPublic_getPublicForm, FormPublic_submitForm, FormPublic_trackEvent, FormPublic_uploadFile, GuidedInspection_completeFollowUp, GuidedInspection_editFollowUp, GuidedInspection_getTimeline, GuidedInspection_listFollowUps, GuidedInspection_recordSessionEvents, GuidedInspection_reopenFollowUp, GuidedInspection_saveFollowUp, Health_checkHealth, Identities_disconnectIdentity, Identities_listIdentities, Identities_setPassword, Impersonation_current, Impersonation_list, Impersonation_start, Impersonation_stop, Incoming_assign, Incoming_claim, Incoming_count, Incoming_list, Incoming_reclaim, Incoming_reject, Incoming_release, IndustryPreset_assignToCompany, IndustryPreset_create, IndustryPreset_findAll, IndustryPreset_findAllPublic, IndustryPreset_findOne, IndustryPreset_remove, IndustryPreset_update, InspectionAddressSearch_reverse, InspectionAddressSearch_search, InspectionLibrary_clone, InspectionLibrary_listStarters, InspectionList_availabilityAt, InspectionList_cancelScheduled, InspectionList_list, InspectionList_reschedule, InspectionList_schedule, InspectionList_startContext, InspectionList_updateScheduled, InspectionMediaLegacyOpen_open, InspectionMediaOpen_open, InspectionMedia_download, InspectionMedia_thumbnail, InspectionReport_acceptAllClauses, InspectionReport_acceptClause, InspectionReport_answerQuestion, InspectionReport_attest, InspectionReport_correctClause, InspectionReport_dismissQuestion, InspectionReport_download, InspectionReport_editSelection, InspectionReport_estimate, InspectionReport_firstPage, InspectionReport_getByInspection, InspectionReport_preview, InspectionReport_refine, InspectionReport_restore, InspectionReport_retry, InspectionReport_saveEdits, InspectionReport_send, InspectionReport_workingCopy, InspectionSite_archive, InspectionSite_bulkDismiss, InspectionSite_bulkSnooze, InspectionSite_create, InspectionSite_dismiss, InspectionSite_findAll, InspectionSite_findOne, InspectionSite_markDue, InspectionSite_nearby, InspectionSite_resetReminder, InspectionSite_snooze, InspectionSite_unarchive, InspectionSite_update, InspectionTemplate_archive, InspectionTemplate_create, InspectionTemplate_duplicate, InspectionTemplate_findOne, InspectionTemplate_list, InspectionTemplate_listPublished, InspectionTemplate_listVersions, InspectionTemplate_publish, InspectionTemplate_remove, InspectionTemplate_restoreVersion, InspectionTemplate_saveDraft, InspectionTemplate_unarchive, InspectionTemplate_uploadLogo, Inspection_complete, Inspection_completeCaptureUpload, Inspection_createCapture, Inspection_discardCapture, Inspection_discardMedia, Inspection_editCaptureNote, Inspection_excludeMedia, Inspection_finishFieldCapture, Inspection_getOne, Inspection_listCaptures, Inspection_readCaptureUpload, Inspection_remove, Inspection_restoreMedia, Inspection_retryMedia, Inspection_saveItem, Inspection_start, Inspection_startCaptureUpload, Inspection_startGuided, Inspection_startInstant, Inspection_upcoming, Inspection_uploadCapturePart, Inspection_uploadMedia, InternalMigration_backfillDeviceTokenContexts, InternalMigration_backfillMeetingSource, InternalMigration_backfillSitePositions, InternalMigration_backfillStaffingTaxonomy, InternalMigration_consolidateIdentities, InternalMigration_elfagChainSeedStatus, InternalMigration_migrateCompanyRoles, InternalMigration_migrateCompetenceRequiredRoles, InternalMigration_migrateContactsToCategories, InternalMigration_migrateSmsTemplates, InternalMigration_normalizeOfferStorageKeys, InternalMigration_removeBookingBrandColor, InternalMigration_seedElfagChain, Invoicing_cancel, Invoicing_deleteDraft, Invoicing_detail, Invoicing_issue, Invoicing_list, Invoicing_markPaid, Invoicing_pdf, Invoicing_prepare, Invoicing_reissue, Invoicing_resend, Invoicing_retryPush, Invoicing_update, Invoicing_updateLines, Invoicing_upload, Invoicing_voidInvoice, ItxAdmin_createIntegration, ItxAdmin_disableIntegration, ItxAdmin_getIntegration, ItxAdmin_listCollisions, ItxAdmin_listFailedSyncs, ItxAdmin_preConnectionPushConfirm, ItxAdmin_preConnectionPushDryRun, ItxAdmin_resolveCollision, ItxAdmin_retryFailedSync, ItxAdmin_triggerBackfill, ItxAdmin_updateIntegration, ItxFrameResolver_resolveCompany, ItxFrame_createInstantMeeting, ItxFrame_getCustomerOverview, ItxFrame_scheduleMeeting, ItxWebhook_handleWebhook, LeadDeal_archive, LeadDeal_assignOwner, LeadDeal_batchArchive, LeadDeal_batchRestore, LeadDeal_clearNext, LeadDeal_convertToOrder, LeadDeal_create, LeadDeal_exportLeadDeals, LeadDeal_get, LeadDeal_linkCompany, LeadDeal_linkContact, LeadDeal_list, LeadDeal_moveStage, LeadDeal_moveToPipeline, LeadDeal_restore, LeadDeal_scheduleNext, LeadDeal_setOutcome, LeadDeal_update, LeadDeal_upgrade, Learning_getCertificate, Learning_getMyLearning, LinkMobilityWebhook_handleDeliveryReport, LivekitWebhook_handleWebhook, LoginSecurity_getSettings, LoginSecurity_listAlerts, LoginSecurity_updateSettings, LostReason_create, LostReason_delete, LostReason_list, LostReason_update, MediaProgressSse_stream, Media_uploadImage, MeetingBackgroundImageStream_streamImage, MeetingDev_backdateMeetingEnd, MeetingDev_getMeetings, MeetingDev_runPipelineRepair, MeetingDev_seedRecording, MeetingDev_seedSummary, MeetingDev_seedTranscript, MeetingDev_setAllowParticipantPause, MeetingDev_triggerAutoStop, MeetingPipelineSse_stream, MeetingRoomSse_joinMeetingAsAnonymous, MeetingRoomSse_subscribeToJoinRequests, MeetingRoomSse_subscribeToPublicMeeting, MeetingRoom_decideJoinRequest, MeetingRoom_endMeetingForAll, MeetingRoom_getPublicMeetingState, MeetingRoom_getTokenForClient, MeetingRoom_getTokenForInternalUser, MeetingRoom_kickParticipant, MeetingRoom_listInvitedNotJoined, MeetingRoom_lowerAllHands, MeetingRoom_pauseRecordingMeeting, MeetingRoom_recordMeeting, MeetingRoom_reportRoomPresence, MeetingRoom_resumeRecordingMeeting, MeetingRoom_saveLocation, MeetingRoom_sendChatMessage, MeetingRoom_setPinForAll, MeetingRoom_setSpotlight, MeetingRoom_stopRecordingMeeting, MeetingRoom_takeScreenshot, MeetingRoom_toggleMute, MeetingSummaryPublic_downloadPdf, MeetingSummaryPublic_resolve, MeetingTelemetryPublic_ingestBatch, MeetingTelemetryTicket_getTicket, MeetingTelemetryTypesPublic_sharedMeetingTelemetryTypes, Meeting_createMeeting, Meeting_createMeetingNote, Meeting_createSummaryCorrection, Meeting_deleteMeeting, Meeting_deleteMeetingNote, Meeting_editSummarySelection, Meeting_emailMeetingSummary, Meeting_exportMeetingPdf, Meeting_getClientMeetings, Meeting_getMeetingChatHistory, Meeting_getMeetingNotes, Meeting_getMeetings, Meeting_getPipelineState, Meeting_getSinglemeeting, Meeting_getSummaryRunState, Meeting_regenerateSummary, Meeting_sendReminder, Meeting_updateMeeting, Meeting_updateMeetingNote, Merge_count, Merge_dismiss, Merge_listPairs, Merge_merge, Merge_redirect, Merge_scan, Merge_undo, Merge_undoDismiss, MyAiSettings_getMyAiSettings, MyAiSettings_updateMyAiSettings, NormalizeTranscriptionLanguage_normalize, Notification_backfillCompanyId, Notification_getNotifications, Notification_getPreferences, Notification_getUnreadCount, Notification_markAllAsRead, Notification_markAsRead, Notification_notificationStream, Notification_updatePreferences, Nudges_getNudgeState, Nudges_resolveNudge, OAuthCompany_companies, OAuthCompany_disconnect, OAuthCompany_selectCompany, OfferInternal_runExpiryCheck, OfferInternal_runReminders, OfferInternal_runRetentionSweep, OfferProposal_acceptLine, OfferProposal_editLine, OfferProposal_getByReport, OfferProposal_promoteLine, OfferProposal_rejectLine, OfferProposal_start, OfferPublic_acceptClick, OfferPublic_acceptSignature, OfferPublic_downloadCertificate, OfferPublic_downloadPdf, OfferPublic_downloadSealed, OfferPublic_reject, OfferPublic_resolve, OfferPublic_retrySeal, OfferSettings_get, OfferSettings_update, Offer_createDraft, Offer_discardDraft, Offer_downloadCertificate, Offer_downloadPdf, Offer_downloadSealedPdf, Offer_draftPdf, Offer_getLink, Offer_getVersion, Offer_list, Offer_listByDeal, Offer_newVersion, Offer_recordAcceptanceWithdrawal, Offer_recordDecision, Offer_resend, Offer_retryDelivery, Offer_retrySeal, Offer_sendOffer, Offer_updateDraft, Offer_withdraw, OrderAttachment_delete, OrderAttachment_list, OrderAttachment_upload, OrderCompanySse_companyStream, OrderCustomer_changePassword, OrderCustomer_customerStream, OrderCustomer_getFeedbackToken, OrderCustomer_getProfile, OrderCustomer_listOrders, OrderCustomer_updateAvatar, OrderCustomer_updateLanguage, OrderCustomer_updateProfile, OrderMessage_list, OrderMessage_markRead, OrderMessage_send, OrderPipeline_createStage, OrderPipeline_deleteStage, OrderPipeline_listStages, OrderPipeline_reorderStages, OrderPipeline_updateStage, OrderPublic_getFormByToken, OrderPublic_submitResponse, Order_board, Order_cancel, Order_complete, Order_create, Order_detail, Order_keep, Order_linkContact, Order_linkedForDeal, Order_list, Order_moveStage, Order_report, Order_reverseCompletion, Order_update, Order_updateLines, Pipeline_createPipeline, Pipeline_createStage, Pipeline_deletePipeline, Pipeline_deleteStage, Pipeline_list, Pipeline_reorderStages, Pipeline_setDefault, Pipeline_updatePipeline, Pipeline_updateStage, Placement_retryPlacement, PortalInvoice_getInvoice, PortalInvoice_pdf, PortalOrder_getOrder, PortalOrder_listMessages, PortalOrder_sendMessage, Profile_deleteMeetingBackgroundImage, Profile_dismissPrompt, Profile_getMeetingBackground, Profile_getProfile, Profile_getPublicProfile, Profile_listMeetingBackgroundImages, Profile_updateAvatar, Profile_updateMeetingBackground, Profile_updateProfile, Profile_uploadMeetingBackgroundImage, PublicDeviceToken_forget, QuickLink_create, QuickLink_delete, QuickLink_findAll, QuickLink_reorder, QuickLink_update, ReconcileStuckTranscriptions_reconcileStuckTranscriptions, RecordAndSendAdmin_getCaptureSettings, RecordAndSendAdmin_getCompanyRecordAndSend, RecordAndSendAdmin_setCompanySmsCap, RecordAndSendAdmin_updateCaptureSettings, RecordAndSendHome_read, RecordAndSendPublicForm_getForm, RecordAndSendPublicForm_requestLink, RecordAndSendSmsStatus_getSmsStatus, Recordings_deleteRecording, Recordings_getRecordings, RelayControl_getMetrics, RelayControl_pause, RelayControl_resume, RelayControl_state, Reports_exportCsv, Reports_getDistribution, Reports_getKpis, Reports_getPerformanceOverTime, Reports_getRevenue, Reports_getTeamPerformance, ResourceCategory_create, ResourceCategory_delete, ResourceCategory_findAll, ResourceCategory_reorder, ResourceCategory_update, Resource_delete, Resource_download, Resource_findAll, Resource_getStats, Resource_replaceFile, Resource_update, Resource_upload, Roles_createRole, Roles_deleteRole, Roles_duplicateRole, Roles_getCatalog, Roles_getRole, Roles_listRoleOptions, Roles_listRoles, Roles_myEffectivePermissions, Roles_updateRole, SSEConnection, SavedView_create, SavedView_delete, SavedView_list, SavedView_myWork, SavedView_myWorkCount, SavedView_reorder, SavedView_resolveDefault, SavedView_setDefault, SavedView_update, Search_globalSearch, ServiceType_createMasterCatalogEntry, ServiceType_createServiceType, ServiceType_deleteServiceType, ServiceType_listMasterCatalog, ServiceType_listServiceTypes, ServiceType_pushMasterCatalog, ServiceType_updateServiceType, SharedPushTypes_sharedPushNotificationTypes, SharedTypes_session, SharedTypes_sessionUser, SignInHelp_submit, SignInReport_getSignInReport, SignupE2e_backdate, SignupE2e_markIncomplete, SignupE2e_runSweep, Signup_complete, Signup_completeSocial, Signup_linkProofStart, Signup_linkProofVerify, Signup_resend, Signup_start, Signup_verify, StaffingAdmin_deletePosting, StaffingAdmin_listPostings, StaffingAnalytics_exportCsv, StaffingAnalytics_getAnalytics, StaffingCategory_create, StaffingCategory_list, StaffingCategory_remove, StaffingCategory_update, StaffingInterest_addBookmark, StaffingInterest_approve, StaffingInterest_express, StaffingInterest_listBookmarks, StaffingInterest_myInterests, StaffingInterest_pendingApprovals, StaffingInterest_reject, StaffingInterest_removeBookmark, StaffingInterest_withdraw, StaffingInternal_runExpiryCheck, StaffingPosting_browse, StaffingPosting_close, StaffingPosting_create, StaffingPosting_detail, StaffingPosting_duplicate, StaffingPosting_getLegalAgreement, StaffingPosting_interests, StaffingPosting_myPostings, StaffingPosting_publish, StaffingPosting_remove, StaffingPosting_update, StaffingSkillLevel_create, StaffingSkillLevel_list, StaffingSkillLevel_remove, StaffingSkillLevel_update, StaffingSkill_create, StaffingSkill_list, StaffingSkill_remove, StaffingSkill_update, Stripe_getBillingHistory, Stripe_getCustomerDetails, Stripe_getPortalLink, Stripe_handleWebhook, Stripe_hasBilling, Stripe_updateCustomerDetails, SubmissionStream_stream, Submission_bulkDeleteVideos, Submission_convertToLead, Submission_deleteVideo, Submission_inbox, Submission_list, Submission_mintPlaybackTicket, Submission_poster, Submission_read, Submission_submissionOfLead, Subscription_cancelSubscription, Subscription_checkSession, Subscription_eligibleForAiSummaryTrial, Subscription_getUsage, Subscription_isTrialPossible, Subscription_purchaseAiSummaryAddon, Subscription_purchaseStorageAddon, Subscription_removeAiSummaryAddon, Subscription_removeStorageAddon, Subscription_runManually, Subscription_startAiSummaryTrial, Subscription_startTrial, Subscription_updateStorageQuantity, Subscription_upgradeSubscription, Subscription_upgradeWithSession, SuperAdmin_createMeetingNote, SuperAdmin_getCompanyMeetings, SuperAdmin_getMeetingChatHistory, SuperAdmin_getMeetingNotes, SuperAdmin_getSinglemeeting, SuperadminAudit_getRetention, SuperadminAudit_list, SuperadminAudit_setRetention, SuperadminInspectionLibrary_create, SuperadminInspectionLibrary_list, SuperadminInspectionLibrary_remove, SuperadminInspectionLibrary_seed, SuperadminInspectionLibrary_update, SupplierViewer_findAll, SupplierViewer_findOne, Supplier_archive, Supplier_create, Supplier_findAll, Supplier_findOne, Supplier_importCsv, Supplier_listCategories, Supplier_notify, Supplier_notifyPreview, Supplier_publish, Supplier_remove, Supplier_schedule, Supplier_update, SystemPrompt_deletePrompt, SystemPrompt_getPrompt, SystemPrompt_setPrompt, TeamLearning_exportTeamLearning, TeamLearning_getCourseBreakdown, TeamLearning_getTeamLearning, TeamLearning_getUserDetail, TenantBilling_addonCheckout, TenantBilling_getEntitlement, TenantBilling_getMemberUsage, TenantBilling_getPriceCatalog, TenantBilling_getSelfUsage, TenantBilling_getTokenCatalog, TenantBilling_getUsage, TenantBilling_removeAddon, TenantBilling_subscriptionCheckout, ToolViewer_findAll, ToolViewer_findOne, Tool_archive, Tool_create, Tool_findAll, Tool_findOne, Tool_notify, Tool_publish, Tool_remove, Tool_schedule, Tool_update, Trace_traceEvent, VideoRequest_list, VideoRequest_listOpen, VideoRequest_resend, VideoRequest_send, VideoType_create, VideoType_get, VideoType_list, VideoType_pause, VideoType_remove, VideoType_removeIntroVideo, VideoType_resume, VideoType_update, VideoType_uploadIntroVideo, buildFormData, createAccessRequestController, createAccountDeletionController, createAccountingController, createAddressSuggestionController, createAdminAnalyticsController, createAdminBillingController, createAdminBookingController, createAdminChainCapController, createAdminChainController, createAdminChainSeedSseController, createAdminController, createAdminMcpController, createAdminMeetingTelemetryController, createAdminRolesController, createAdminTranscriptionController, createAdminUserMeetingsController, createAgendaController, createAgentController, createAgentRunController, createAgentRunSseController, createAgreementController, createAgreementViewerController, createAiOpsController, createAnalyticsController, createAnnouncementController, createApiKeyContextController, createApiKeyController, createArticleController, createArticleViewerController, createAuditController, createAuthController, createBlackoutDateController, createBookingApiController, createBrandController, createBrandingController, createBrandingViewerController, createCalendarController, createCampaignController, createCampaignViewerController, createCatalogController, createChainAnalyticsController, createChainBoardsController, createChainController, createChainCoverageController, createChainDashboardController, createChainDistributionController, createChainLeaderboardController, createChainOrdersController, createChainOversightController, createChainWebsiteController, createChecklistController, createChecklistItemController, createClient, createClientBookingController, createClientsController, createCommunicationController, createCommunicationMediaController, createCommunicationViewerController, createCommunicationViewerMediaController, createCompaniesController, createCompanyController, createCompanyPublicController, createCompanyServiceAreaController, createCompanyWhiteLabelingController, createCompetenceApprovalController, createCompetenceChainController, createCompetenceEvidenceController, createCompetenceExportController, createCompetenceInternalController, createCompetenceRecordController, createCompetenceTypeController, createConfigPushController, createContactCategoryController, createContactController, createContactViewerController, createCourseAnalyticsController, createCourseCategoryController, createCourseController, createCourseMaterialController, createCrmActivityController, createCrmCompanyController, createCrmContactsController, createCrmEntitlementAdminController, createCrmEntitlementController, createCrmImportController, createCrmReportingController, createCrmScheduledActivityController, createCustomFieldController, createCustomerAccountController, createCustomerLinkController, createDeadLetterController, createDealInspectionController, createDemoDataSeedController, createDeviceTestProvisioningController, createDeviceTokenController, createE2ECalendarController, createE2EController, createE2EMeetingTelemetryController, createE2ERecordAndSendController, createE2ERoutesController, createEmailController, createEmailWebhookController, createEndorsementController, createEngagementCronTestController, createEnrollmentController, createFeedbackAdminController, createFeedbackController, createFormAdminController, createFormPublicController, createGuidedInspectionController, createHealthController, createIdentitiesController, createImpersonationController, createIncomingController, createIndustryPresetController, createInspectionAddressSearchController, createInspectionController, createInspectionLibraryController, createInspectionListController, createInspectionMediaController, createInspectionMediaLegacyOpenController, createInspectionMediaOpenController, createInspectionReportController, createInspectionSiteController, createInspectionTemplateController, createInternalMigrationController, createInvoicingController, createItxAdminController, createItxFrameController, createItxFrameResolverController, createItxWebhookController, createLeadDealController, createLearningController, createLinkMobilityWebhookController, createLivekitWebhookController, createLoginSecurityController, createLostReasonController, createMediaController, createMediaProgressSseController, createMeetingBackgroundImageStreamController, createMeetingController, createMeetingDevController, createMeetingPipelineSseController, createMeetingRoomController, createMeetingRoomSseController, createMeetingSummaryPublicController, createMeetingTelemetryPublicController, createMeetingTelemetryTicketController, createMeetingTelemetryTypesPublicController, createMergeController, createMyAiSettingsController, createNormalizeTranscriptionLanguageController, createNotificationController, createNudgesController, createOAuthCompanyController, createOfferController, createOfferInternalController, createOfferProposalController, createOfferPublicController, createOfferSettingsController, createOrderAttachmentController, createOrderCompanySseController, createOrderController, createOrderCustomerController, createOrderMessageController, createOrderPipelineController, createOrderPublicController, createPipelineController, createPlacementController, createPortalInvoiceController, createPortalOrderController, createProfileController, createPublicDeviceTokenController, createQuickLinkController, createReconcileStuckTranscriptionsController, createRecordAndSendAdminController, createRecordAndSendHomeController, createRecordAndSendPublicFormController, createRecordAndSendSmsStatusController, createRecordingsController, createRelayControlController, createReportsController, createRequestFn, createResourceCategoryController, createResourceController, createRolesController, createSavedViewController, createSearchController, createServiceTypeController, createSharedPushTypesController, createSharedTypesController, createSignInHelpController, createSignInReportController, createSignupController, createSignupE2eController, createStaffingAdminController, createStaffingAnalyticsController, createStaffingCategoryController, createStaffingInterestController, createStaffingInternalController, createStaffingPostingController, createStaffingSkillController, createStaffingSkillLevelController, createStripeController, createSubmissionController, createSubmissionStreamController, createSubscriptionController, createSuperAdminController, createSuperadminAuditController, createSuperadminInspectionLibraryController, createSupplierController, createSupplierViewerController, createSystemPromptController, createTeamLearningController, createTenantBillingController, createToolController, createToolViewerController, createTraceController, createVideoRequestController, createVideoTypeController };
